import React, { useState, useRef, useEffect, Suspense } from 'react';
import {
  UploadCloud, Download, Sparkles, Check, CheckCircle2,
  RefreshCw, Image as ImageIcon, ZoomIn, ZoomOut, Grid, X, Columns2,
  PanelLeft, PanelRight, ShieldCheck, Shield, ShieldAlert,
  Zap, User, LogOut, Package, CreditCard, ChevronDown, ChevronRight,
  ArrowUpRight, ArrowLeft, Layers, HelpCircle, PenTool, AlertCircle, AlertTriangle, Info, ExternalLink,
  Sun, Moon, Calendar, Clock, Sliders, Split, MoveHorizontal, Share2, Tag, Cpu, Lock,
  Eye, EyeOff, PlusCircle, Menu, Bell, Trash2, Settings
} from 'lucide-react';
import DOMPurify from 'dompurify';
import type { LegalPageType } from './components/LegalPage';
import LandingPage from './components/LandingPage';
const AdminConsole = React.lazy(() => import('./components/AdminConsole'));
const SettingsPage = React.lazy(() => import('./components/SettingsPage'));
import { isSettingsSection, type SettingsSection } from './components/settingsRoute';
import { ErrorPage } from './ErrorPage';
const LegalPage = React.lazy(() => import('./components/LegalPage'));
const PublicBlog = React.lazy(() => import('./components/PublicBlog'));
const QualityReportDrawer = React.lazy(() => import('./components/QualityReportDrawer'));
const BatchQueuePanel = React.lazy(() => import('./components/BatchQueuePanel'));
const MetadataEditorPanel = React.lazy(() => import('./components/MetadataEditorPanel'));
const ExportReviewModal = React.lazy(() => import('./components/ExportReviewModal'));
import MaintenanceScreen from './components/MaintenanceScreen';
import LanguageSelector from './components/LanguageSelector';
import GlobalNavbar from './components/GlobalNavbar';
import { CookieConsent } from './components/CookieConsent';
import AccessibleModal from './components/AccessibleModal';
import { planPriceText, planPriceWithCycle, topupPriceText, useBillingContext } from './hooks/useBillingContext';
import UserAvatar from './components/UserAvatar';
import { useI18n } from './i18n';
import { flashCopy, notificationCopy, studioCopy } from './i18n/studio';
import { qualityCopy } from './i18n/quality';
import { landingCopy } from './i18n/landing';
import { applyMetadataReadiness } from './quality/metadataReadiness';
import type { MetadataMarketplace, QualityCheck, QualityReport } from './quality/metadataReadiness';
import { authenticatedRequest } from './auth/session';
import { API_BASE, AUTH_REQUEST_TIMEOUT_MS } from './config/api';
import { apiErrorMessage, localizedStatus } from './api/errorContract';
import { clearPersistedQueue, loadPersistedQueue, mergeQueues, queueOwnerKey, savePersistedQueue } from './batch/queuePersistence';
import { analyzeRasterLocally, vectorizeImageWithVectriaNative } from './vector/vectriaNativeTracer';
import {
  applyThemeToDom,
  getInitialTheme,
  getPersistedTheme,
  runCircularThemeTransition,
  type ThemeMode,
} from './theme/themeTransition';
import {
  animateVectorCompletionReveal,
  runStudioSidebarToggle,
} from './animations/studioGsap';
import { publicHomePath, publicSectionPath, type PublicLandingSection } from './publicNavigation';

const VECTORIZATION_POLICY_VERSION = 'candidate-tournament-v5-outline-consistency';

const sanitizeSvg = (svgString: string | undefined): string => {
  if (!svgString) return '';
  return DOMPurify.sanitize(svgString, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['use'],
  });
};
const QUEUE_LOCAL_RESTORE_TIMEOUT_MS = 4_000;
const QUEUE_MANIFEST_TIMEOUT_MS = 10_000;
const QUEUE_ASSET_TIMEOUT_MS = 20_000;
const QUEUE_RESTORE_WATCHDOG_MS = 12_000;
const MAX_RASTER_UPLOAD_BYTES = 20 * 1024 * 1024;
const ALLOWED_RASTER_EXTENSIONS = /\.(png|jpe?g|webp|bmp|gif)$/i;
const ALLOWED_RASTER_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/bmp',
  'image/x-ms-bmp',
  'image/gif',
]);
const CLOUD_STAGE_CONCURRENCY = 4;

const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> => new Promise((resolve, reject) => {
  const timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  promise.then(
    value => {
      window.clearTimeout(timeoutId);
      resolve(value);
    },
    error => {
      window.clearTimeout(timeoutId);
      reject(error);
    },
  );
});

export const AI_MODELS_OCT_2026: Record<'gemini' | 'openai' | 'custom', { id: string; name: string; tag: string }[]> = {
  gemini: [
    { id: 'gemini-3.8-flash', name: 'Gemini 3.8 Flash', tag: 'Workhorse Multimodal & Recommended' },
    { id: 'gemini-3.8-pro', name: 'Gemini 3.8 Pro', tag: 'Flagship Commercial Taxonomy & Reasoning' },
    { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash', tag: 'High-Throughput Efficiency Fallback' },
    { id: 'gemini-3.0-flash', name: 'Gemini 3.0 Flash', tag: 'Fast Base Fallback' },
    { id: 'custom', name: 'Custom Gemini Model ID...', tag: 'Specify Custom Google Model' },
  ],
  openai: [
    { id: 'gpt-6-mini', name: 'GPT-6 Mini', tag: 'Ultra-Fast Vision & Recommended' },
    { id: 'gpt-6', name: 'GPT-6', tag: 'Flagship Agentic Multimodal' },
    { id: 'gpt-5.6-turbo', name: 'GPT-5.6 Turbo', tag: 'Unified High-Speed Reasoning' },
    { id: 'o3', name: 'o3', tag: 'Deep Reasoning Taxonomy' },
    { id: 'custom', name: 'Custom OpenAI Model ID...', tag: 'Specify Custom Model' },
  ],
  custom: [
    { id: 'openrouter/free', name: 'OpenRouter Free Models Router', tag: 'Auto-selects an available free vision model (Recommended)' },
    { id: 'custom', name: 'Custom Model ID (OpenRouter / Ollama / vLLM)...', tag: 'Specify a current provider model ID' },
  ]
};

export const AI_PROVIDER_DEFAULTS = {
  gemini: {
    name: 'Google Gemini',
    keyPlaceholder: 'AIzaSy...',
    keyHint: 'Google AI Studio key (starts with AIzaSy...). Free tier available at aistudio.google.com',
    keyDocUrl: 'https://aistudio.google.com/apikey',
    defaultModel: 'gemini-3.8-flash',
    defaultFallback: 'gemini-3.5-flash',
  },
  openai: {
    name: 'OpenAI',
    keyPlaceholder: 'sk-proj-...',
    keyHint: 'OpenAI API key (starts with sk-proj-... or sk-...). Get key at platform.openai.com',
    keyDocUrl: 'https://platform.openai.com/api-keys',
    defaultModel: 'gpt-6-mini',
    defaultFallback: 'gpt-5.6-turbo',
  },
  custom: {
    name: 'OpenRouter / Local',
    keyPlaceholder: 'sk-or-v1-...',
    keyHint: 'OpenRouter API key (starts with sk-or-v1-...) or local/custom bearer token',
    keyDocUrl: 'https://openrouter.ai/keys',
    defaultModel: 'openrouter/free',
    defaultFallback: 'none',
    defaultEndpoint: 'https://openrouter.ai/api/v1/chat/completions',
  },
};

interface QueueItem {
  id: string;
  file: File;
  previewUrl: string;
  name: string;
  size: number;
  status: 'uploading' | 'idle' | 'processing' | 'done' | 'error';
  vectorSvg?: string;
  vectorEps?: string;
  savedPath?: string;
  jpgPreviewUrl?: string;
  elapsed?: number;
  processedBy?: 'local' | 'cloud';
  errorMessage?: string;
  conversionId?: string;
  stagedAssetId?: string;
  optimization?: {
    category: string;
    significant_colors: number;
    edge_density: number;
    palette_error: number;
    noise_score: number;
    curve_tolerance: number;
    smooth_factor: number;
    layer_difference: number;
    quantize_colors: number;
    filter_speckle: number;
    corner_threshold: number;
    layer_mode: string;
    strip_white_bg: boolean;
    white_threshold: number;
    erode_halo_px: number;
  };
  validation?: {
    valid: boolean;
    message: string;
    has_vector_paths: boolean;
  };
  qualityReport?: QualityReport;
  visualHeatmap?: string;
  qualityHistory?: Array<{
    id: string;
    createdAt: string;
    report: QualityReport;
    settings: { colors: number; speckle: number; corner: number; curveTolerance: number; smoothing: number; colorMerge: number; layerMode: string };
  }>;
  metadata?: {
    title: string;
    description?: string;
    keywords: string[];
    category: string;
    marketplace?: MetadataMarketplace;
    used_model?: string;
    provider?: string;
    job_id?: string;
    asset_id?: string;
    validation_status?: 'ready' | 'warning' | 'blocked' | 'draft';
    validation?: { status?: string; errors?: string[]; warnings?: string[]; keywords?: string[] };
    ai_source?: 'byok' | 'vectria_managed' | 'vectria_fallback' | string;
    ai_credit_charged?: number;
    fallback_reason?: string;
  };
}

interface BatchProgress {
  current: number;
  total: number;
  fileName: string;
  succeeded: number;
  failed: number;
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  credits: number;
  ai_credits?: number;
  provider?: string;
  plan: 'free' | 'starter' | 'pro' | 'studio' | 'agency';
  role?: 'owner' | 'admin' | 'content_manager' | 'editor' | 'writer' | 'support' | 'user';
  status?: 'active' | 'suspended' | 'banned';
  total_spent?: number;
  subscription?: {
    id?: string;
    provider?: string;
    provider_subscription_id?: string;
    plan_id?: string;
    billing_cycle?: string;
    status?: string;
    current_period_start?: string | null;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean;
    created_at?: string | null;
  } | null;
}

export const formatSubscriptionDate = (dateStr?: string | null, loc?: string): string | null => {
  if (!dateStr) return null;
  try {
    const cleanStr = String(dateStr).trim().replace(' ', 'T');
    const d = new Date(cleanStr.endsWith('Z') || cleanStr.includes('+') ? cleanStr : `${cleanStr}Z`);
    if (isNaN(d.getTime())) {
      const parts = cleanStr.split('T')[0].split('-');
      if (parts.length === 3) {
        const fallbackD = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (!isNaN(fallbackD.getTime())) {
          return fallbackD.toLocaleDateString(loc === 'id' ? 'id-ID' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }
      }
      return dateStr;
    }
    const localeCode = loc === 'id' ? 'id-ID' : 'en-US';
    return d.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

export const PLAN_TIER_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  studio: 3,
  agency: 4
};

export const getPlanBadgeConfig = (plan: string = 'free', currentTheme: 'light' | 'dark' = 'dark') => {
  const isLight = currentTheme === 'light';
  switch (plan?.toLowerCase()) {
    case 'studio':
      return {
        bg: isLight ? 'var(--inline-rgba-16-185-129-0-12)' : 'var(--inline-rgba-16-185-129-0-18)',
        border: isLight ? 'var(--color-success-border)' : 'var(--inline-rgba-52-211-153-0-40)',
        color: isLight ? 'var(--palette-047857)' : 'var(--md-sys-color-tertiary)',
        dot: isLight ? 'var(--palette-059669)' : 'var(--status-emerald)',
        label: 'Studio',
      };
    case 'pro':
      return {
        bg: isLight ? 'var(--alpha-rgba-2-132-199-0-12)' : 'var(--inline-rgba-6-182-212-0-18)',
        border: isLight ? 'var(--inline-rgba-2-132-199-0-35)' : 'var(--inline-rgba-34-211-238-0-40)',
        color: isLight ? 'var(--palette-0369a1)' : 'var(--palette-22d3ee)',
        dot: isLight ? 'var(--palette-0284c7)' : 'var(--palette-00e5ff)',
        label: 'Pro',
      };
    case 'starter':
      return {
        bg: isLight ? 'var(--inline-rgba-147-51-234-0-12)' : 'var(--inline-rgba-168-85-247-0-18)',
        border: isLight ? 'var(--inline-rgba-147-51-234-0-35)' : 'var(--inline-rgba-192-132-252-0-40)',
        color: isLight ? 'var(--palette-7e22ce)' : 'var(--palette-c084fc)',
        dot: isLight ? 'var(--palette-9333ea)' : 'var(--palette-a855f7)',
        label: 'Starter',
      };
    case 'agency':
      return {
        bg: isLight ? 'var(--inline-rgba-217-119-6-0-14)' : 'var(--inline-rgba-245-158-11-0-18)',
        border: isLight ? 'var(--inline-rgba-217-119-6-0-35)' : 'var(--inline-rgba-251-191-36-0-40)',
        color: isLight ? 'var(--palette-b45309)' : 'var(--palette-fbbf24)',
        dot: isLight ? 'var(--palette-d97706)' : 'var(--status-amber)',
        label: 'Agency',
      };
    case 'free':
    default:
      return {
        bg: isLight ? 'var(--inline-rgba-100-116-139-0-12)' : 'var(--inline-rgba-148-163-184-0-14)',
        border: isLight ? 'var(--inline-rgba-100-116-139-0-28)' : 'var(--inline-rgba-148-163-184-0-30)',
        color: isLight ? 'var(--palette-334155)' : 'var(--palette-e2e8f0)',
        dot: isLight ? 'var(--color-disabled-ink)' : 'var(--md-sys-color-on-surface-variant)',
        label: 'Free Plan',
      };
  }
};

type ViewMode = 'vector' | 'raster' | 'side_by_side' | 'split';

const MARKETPLACE_CATEGORIES: Record<MetadataMarketplace, string[]> = {
  adobe: ['Animals','Buildings and Architecture','Business','Drinks','The Environment','States of Mind','Food','Graphic Resources','Hobbies and Leisure','Industry','Landscapes','Lifestyle','People','Plants and Flowers','Religion and Culture','Science','Social Issues','Sports','Technology','Transport','Travel'],
  shutterstock: ['Abstract','Animals/Wildlife','Announcements/Celebrations','Backgrounds/Textures','Beauty/Fashion','Buildings/Landmarks','Business/Finance','Computer/Communications','Education','Food and Drink','Healthcare/Medical','Holidays','Industrial','Interiors','Miscellany','Nature','Objects','Parks/Outdoors','People','Religion','Science','Signs/Symbols','Sports/Recreation','Technology','Transportation','Travel'],
  freepik: ['Vectors'],
  dreamstime: ['Animals','Architecture','Arts and Architecture','Business','Computer and Communication','Editorial','Holidays','Illustrations and Clipart','Industrial','Interiors','Miscellaneous','Nature','People','Signs and Symbols','Sports'],
  depositphotos: ['Abstract','Animals','Backgrounds & Textures','Food & Drink','Isolated','Natural World','Objects','Panoramic','People','Places','Sports & Recreation','Editorial Use Only']
};

const MARKETPLACE_CATEGORY_NOTES: Record<MetadataMarketplace, string> = {
  adobe: 'Adobe Stock contributor taxonomy',
  shutterstock: 'Shutterstock contributor taxonomy · up to 2 categories on portal; CSV currently exports primary category',
  freepik: 'Freepik CSV classification uses asset type and keywords; this vector workflow exports Vectors',
  dreamstime: 'Dreamstime top-level contributor taxonomy',
  depositphotos: 'Depositphotos top-level contributor taxonomy'
};

type PresetName = 'flat-6' | 'vibrant-12' | 'detailed-32' | 'ultra-64' | 'truecolor' | 'monoline' | 'auto';

interface TraceSettings {
  colors: number;
  speckle: number;
  corner: number;
  curveTolerance: number;
  smoothFactor: number;
  layerDifference: number;
  layer: 'stacked' | 'cutout';
  stripBg: boolean;
  whiteThreshold: number;
  erodeHalo: number;
}

const TRACE_PRESETS: Record<Exclude<PresetName, 'auto'>, TraceSettings> = {
  'flat-6': { colors: 8, speckle: 4, corner: 38, curveTolerance: 2.1, smoothFactor: 0.21, layerDifference: 22, layer: 'stacked', stripBg: true, whiteThreshold: 225, erodeHalo: 0 },
  'vibrant-12': { colors: 16, speckle: 6, corner: 44, curveTolerance: 1.8, smoothFactor: 0.20, layerDifference: 18, layer: 'stacked', stripBg: true, whiteThreshold: 225, erodeHalo: 0 },
  'detailed-32': { colors: 32, speckle: 8, corner: 50, curveTolerance: 1.5, smoothFactor: 0.18, layerDifference: 14, layer: 'stacked', stripBg: true, whiteThreshold: 228, erodeHalo: 0 },
  'ultra-64': { colors: 64, speckle: 8, corner: 52, curveTolerance: 1.3, smoothFactor: 0.16, layerDifference: 10, layer: 'stacked', stripBg: true, whiteThreshold: 230, erodeHalo: 0 },
  truecolor: { colors: 256, speckle: 6, corner: 52, curveTolerance: 1.2, smoothFactor: 0.15, layerDifference: 8, layer: 'stacked', stripBg: true, whiteThreshold: 232, erodeHalo: 0 },
  monoline: { colors: 2, speckle: 4, corner: 38, curveTolerance: 2.2, smoothFactor: 0.22, layerDifference: 24, layer: 'stacked', stripBg: true, whiteThreshold: 220, erodeHalo: 0 },
};

export default function App() {
  const { t, locale } = useI18n();
  const studio = studioCopy[locale] || studioCopy.en;
  const flashText = flashCopy[locale] || flashCopy.en;
  const notificationText = notificationCopy[locale] || notificationCopy.en;
  const quality = qualityCopy[locale] || qualityCopy.en;
  const landing = landingCopy[locale] || landingCopy.en;
  const landingPricing = landing.pricing;
  const queueTransferText = ({
    en: {
      uploading: 'UPLOADING',
      restoring: 'Restoring your secure queue...',
      restoringDesc: 'Synchronizing your workspace queue and recent assets...',
      restorePreviewFailed: 'The item was restored, but its original preview could not be loaded yet.',
      removeItem: 'Remove file',
      retry: 'Retry conversion',
      convertItem: 'Convert this image',
      readyStatus: 'READY',
      uploadingBatch: (cur: number, tot: number) => `Uploading ${cur} of ${tot}...`,
      browseFiles: 'Browse files',
      closeQueue: 'Close queue',
    },
    id: {
      uploading: 'MENGUNGGAH',
      restoring: 'Memulihkan antrean aman Anda...',
      restoringDesc: 'Menyelaraskan antrean ruang kerja dan aset Anda...',
      restorePreviewFailed: 'Item berhasil dipulihkan, tetapi pratinjau aslinya belum dapat dimuat.',
      removeItem: 'Hapus file',
      retry: 'Coba konversi lagi',
      convertItem: 'Konversi gambar ini',
      readyStatus: 'SIAP',
      uploadingBatch: (cur: number, tot: number) => `Mengunggah ${cur} dari ${tot}...`,
      browseFiles: 'Pilih file',
      closeQueue: 'Tutup antrean',
    },
    es: {
      uploading: 'SUBIENDO',
      restoring: 'Restaurando tu cola segura...',
      restoringDesc: 'Sincronizando la cola de tu espacio de trabajo y recursos activos...',
      restorePreviewFailed: 'El elemento se restauró, pero su vista previa aún no pudo cargarse.',
      removeItem: 'Eliminar archivo',
      retry: 'Reintentar conversión',
      convertItem: 'Convertir esta imagen',
      readyStatus: 'LISTO',
      uploadingBatch: (cur: number, tot: number) => `Subiendo ${cur} de ${tot}...`,
      browseFiles: 'Examinar archivos',
      closeQueue: 'Cerrar cola',
    },
    'pt-br': {
      uploading: 'ENVIANDO',
      restoring: 'Restaurando sua fila segura...',
      restoringDesc: 'Sincronizando a fila do espaço de trabalho e ativos ativos...',
      restorePreviewFailed: 'O item foi restaurado, mas a prévia original ainda não pôde ser carregada.',
      removeItem: 'Remover arquivo',
      retry: 'Tentar conversão novamente',
      convertItem: 'Converter esta imagem',
      readyStatus: 'PRONTO',
      uploadingBatch: (cur: number, tot: number) => `Enviando ${cur} de ${tot}...`,
      browseFiles: 'Selecionar arquivos',
      closeQueue: 'Fechar fila',
    },
    ru: {
      uploading: 'ЗАГРУЗКА',
      restoring: 'Восстанавливаем защищённую очередь...',
      restoringDesc: 'Синхронизация очереди рабочего пространства и активных ресурсов...',
      restorePreviewFailed: 'Элемент восстановлен, но его исходное превью пока не загружено.',
      removeItem: 'Удалить файл',
      retry: 'Повторить конвертацию',
      convertItem: 'Конвертировать это изображение',
      readyStatus: 'ГОТОВО',
      uploadingBatch: (cur: number, tot: number) => `Загрузка ${cur} из ${tot}...`,
      browseFiles: 'Выбрать файлы',
      closeQueue: 'Закрыть очередь',
    },
    tr: {
      uploading: 'YÜKLENİYOR',
      restoring: 'Güvenli kuyruğunuz geri yükleniyor...',
      restoringDesc: 'Çalışma alanı kuyruğu ve aktif varlıklar eşitleniyor...',
      restorePreviewFailed: 'Öğe geri yüklendi ancak orijinal önizlemesi henüz yüklenemedi.',
      removeItem: 'Dosyayı kaldır',
      retry: 'Dönüştürmeyi tekrar dene',
      convertItem: 'Bu görseli dönüştür',
      readyStatus: 'HAZIR',
      uploadingBatch: (cur: number, tot: number) => `${cur}/${tot} yükleniyor...`,
      browseFiles: 'Dosyalara göz at',
      closeQueue: 'Kuyruğu kapat',
    },
    vi: {
      uploading: 'ĐANG TẢI LÊN',
      restoring: 'Đang khôi phục hàng đợi bảo mật...',
      restoringDesc: 'Đang đồng bộ hóa hàng đợi không gian làm việc và tệp của bạn...',
      restorePreviewFailed: 'Mục đã được khôi phục nhưng chưa thể tải bản xem trước gốc.',
      removeItem: 'Xóa tệp',
      retry: 'Thử chuyển đổi lại',
      convertItem: 'Chuyển đổi hình ảnh này',
      readyStatus: 'SẴN SÀNG',
      uploadingBatch: (cur: number, tot: number) => `Đang tải lên ${cur}/${tot}...`,
      browseFiles: 'Duyệt tệp',
      closeQueue: 'Đóng hàng đợi',
    },
    th: {
      uploading: 'กำลังอัปโหลด',
      restoring: 'กำลังกู้คืนคิวที่ปลอดภัย...',
      restoringDesc: 'กำลังซิงค์คิวพื้นที่ทำงานและเนื้อหาของคุณ...',
      restorePreviewFailed: 'กู้คืนรายการแล้ว แต่ยังโหลดตัวอย่างต้นฉบับไม่ได้',
      removeItem: 'ลบไฟล์',
      retry: 'ลองแปลงอีกครั้ง',
      convertItem: 'แปลงภาพนี้',
      readyStatus: 'พร้อม',
      uploadingBatch: (cur: number, tot: number) => `กำลังอัปโหลด ${cur} จาก ${tot}...`,
      browseFiles: 'เลือกไฟล์',
      closeQueue: 'ปิดคิว',
    },
    ar: {
      uploading: 'جارٍ الرفع',
      restoring: 'جارٍ استعادة قائمة الانتظار الآمنة...',
      restoringDesc: 'مزامنة قائمة انتظار مساحة العمل والأصول النشطة...',
      restorePreviewFailed: 'تمت استعادة العنصر، ولكن تعذر تحميل المعاينة الأصلية بعد.',
      removeItem: 'إزالة الملف',
      retry: 'إعادة محاولة التحويل',
      convertItem: 'تحويل هذه الصورة',
      readyStatus: 'جاهز',
      uploadingBatch: (cur: number, tot: number) => `جارٍ رفع ${cur} من ${tot}...`,
      browseFiles: 'استعراض الملفات',
      closeQueue: 'إغلاق قائمة الانتظار',
    },
    'zh-cn': {
      uploading: '正在上传',
      restoring: '正在恢复安全队列...',
      restoringDesc: '正在同步工作区队列与活动资产...',
      restorePreviewFailed: '项目已恢复，但暂未能加载原始预览。',
      removeItem: '移除文件',
      retry: '重试转换',
      convertItem: '转换此图像',
      readyStatus: '准备就绪',
      uploadingBatch: (cur: number, tot: number) => `正在上传 ${cur}/${tot}...`,
      browseFiles: '浏览文件',
      closeQueue: '关闭队列',
    },
    ja: {
      uploading: 'アップロード中',
      restoring: '安全なキューを復元しています...',
      restoringDesc: 'ワークスペースのキューとアクティブなアセットを同期中...',
      restorePreviewFailed: 'アイテムは復元されましたが、元のプレビューはまだ読み込めません。',
      removeItem: 'ファイルを削除',
      retry: '再変換',
      convertItem: 'この画像を変換',
      readyStatus: '準備完了',
      uploadingBatch: (cur: number, tot: number) => `${cur}/${tot} をアップロード中...`,
      browseFiles: 'ファイルを参照',
      closeQueue: 'キューを閉じる',
    },
    ko: {
      uploading: '업로드 중',
      restoring: '보안 대기열을 복원하는 중...',
      restoringDesc: '작업 공간 대기열과 활성 에셋을 동기화하는 중...',
      restorePreviewFailed: '항목이 복원되었지만 원본 미리보기를 아직 불러오지 못했습니다.',
      removeItem: '파일 제거',
      retry: '변환 다시 시도',
      convertItem: '이 이미지 변환',
      readyStatus: '준비 완료',
      uploadingBatch: (cur: number, tot: number) => `${cur}/${tot} 업로드 중...`,
      browseFiles: '파일 찾기',
      closeQueue: '대기열 닫기',
    },
  } as Record<string, {
    uploading: string;
    restoring: string;
    restoringDesc: string;
    restorePreviewFailed: string;
    removeItem: string;
    retry: string;
    convertItem: string;
    readyStatus: string;
    uploadingBatch: (cur: number, tot: number) => string;
    browseFiles: string;
    closeQueue: string;
  }>)[locale] || {
    uploading: 'UPLOADING',
    restoring: 'Restoring your secure queue...',
    restoringDesc: 'Synchronizing your workspace queue and recent assets...',
    restorePreviewFailed: 'The item was restored, but its original preview could not be loaded yet.',
    removeItem: 'Remove file',
    retry: 'Retry conversion',
    convertItem: 'Convert this image',
    readyStatus: 'READY',
    uploadingBatch: (cur: number, tot: number) => `Uploading ${cur} of ${tot}...`,
    browseFiles: 'Browse files',
    closeQueue: 'Close queue',
  };
  const workspaceText = ({
    en: { eyebrow:'VECTRIA WORKSPACE', fallback:'Vector Production Workspace', description:'Manage vector production, conversion quota, team, plan, and invoices.', loading:'Preparing production workspace…', failed:'Workspace failed to load', retry:'Try again', plan:'Plan', conversions:'Conversions this month', members:'Team members', batch:'Batch limit', subscription:'Subscription', noSubscription:'No active subscription', billing:'Manage billing', team:'Production team', email:'member@company.com', invite:'Invite', owner:'Owner', admin:'Admin', member:'Member', viewer:'Viewer', remove:'Remove', pending:'Pending invitations', revoke:'Revoke', usage:'Usage in the last 30 days', invoices:'Recent invoices', download:'Download', noInvoices:'No invoices yet.', close:'Close workspace' },
    id: { eyebrow:'WORKSPACE VECTRIA', fallback:'Workspace Produksi Vektor', description:'Kelola produksi vektor, kuota konversi, tim, paket, dan invoice.', loading:'Menyiapkan workspace produksi…', failed:'Workspace gagal dimuat', retry:'Coba lagi', plan:'Paket', conversions:'Konversi bulan ini', members:'Anggota tim', batch:'Batas batch', subscription:'Langganan', noSubscription:'Belum ada langganan aktif', billing:'Kelola tagihan', team:'Tim produksi', email:'anggota@perusahaan.com', invite:'Undang', owner:'Pemilik', admin:'Admin', member:'Anggota', viewer:'Pengamat', remove:'Hapus', pending:'Undangan tertunda', revoke:'Batalkan', usage:'Penggunaan 30 hari terakhir', invoices:'Invoice terbaru', download:'Unduh', noInvoices:'Belum ada invoice.', close:'Tutup workspace' },
    'zh-cn': { eyebrow:'VECTRIA 工作区', fallback:'矢量生产工作区', description:'管理矢量生产、转换额度、团队、套餐与发票。', loading:'正在准备生产工作区…', failed:'工作区加载失败', retry:'重试', plan:'套餐', conversions:'本月转换', members:'团队成员', batch:'批处理上限', subscription:'订阅', noSubscription:'暂无有效订阅', billing:'管理账单', team:'制作团队', email:'member@company.com', invite:'邀请', owner:'所有者', admin:'管理员', member:'成员', viewer:'查看者', remove:'移除', pending:'待处理邀请', revoke:'撤销', usage:'最近 30 天用量', invoices:'近期发票', download:'下载', noInvoices:'暂无发票。', close:'关闭工作区' },
    ja: { eyebrow:'VECTRIA ワークスペース', fallback:'ベクター制作ワークスペース', description:'ベクター制作、変換枠、チーム、プラン、請求書を管理します。', loading:'制作ワークスペースを準備中…', failed:'ワークスペースを読み込めません', retry:'再試行', plan:'プラン', conversions:'今月の変換数', members:'チームメンバー', batch:'バッチ上限', subscription:'サブスクリプション', noSubscription:'有効なサブスクリプションはありません', billing:'請求を管理', team:'制作チーム', email:'member@company.com', invite:'招待', owner:'所有者', admin:'管理者', member:'メンバー', viewer:'閲覧者', remove:'削除', pending:'保留中の招待', revoke:'取り消す', usage:'過去30日間の使用量', invoices:'最近の請求書', download:'ダウンロード', noInvoices:'請求書はまだありません。', close:'ワークスペースを閉じる' },
    ko: { eyebrow:'VECTRIA 워크스페이스', fallback:'벡터 제작 워크스페이스', description:'벡터 제작, 변환 할당량, 팀, 요금제 및 청구서를 관리합니다.', loading:'제작 워크스페이스 준비 중…', failed:'워크스페이스를 불러오지 못했습니다', retry:'다시 시도', plan:'요금제', conversions:'이번 달 변환', members:'팀원', batch:'일괄 처리 한도', subscription:'구독', noSubscription:'활성 구독이 없습니다', billing:'결제 관리', team:'제작 팀', email:'member@company.com', invite:'초대', owner:'소유자', admin:'관리자', member:'멤버', viewer:'뷰어', remove:'삭제', pending:'대기 중인 초대', revoke:'취소', usage:'최근 30일 사용량', invoices:'최근 청구서', download:'다운로드', noInvoices:'청구서가 없습니다.', close:'워크스페이스 닫기' },
    es: { eyebrow:'ESPACIO DE TRABAJO VECTRIA', fallback:'Espacio de producción vectorial', description:'Gestiona la producción vectorial, la cuota de conversiones, el equipo, el plan y las facturas.', loading:'Preparando el espacio de producción…', failed:'No se pudo cargar el espacio de trabajo', retry:'Intentar de nuevo', plan:'Plan', conversions:'Conversiones de este mes', members:'Miembros del equipo', batch:'Límite por lote', subscription:'Suscripción', noSubscription:'No hay ninguna suscripción activa', billing:'Gestionar facturación', team:'Equipo de producción', email:'miembro@empresa.com', invite:'Invitar', owner:'Propietario', admin:'Administrador', member:'Miembro', viewer:'Observador', remove:'Eliminar', pending:'Invitaciones pendientes', revoke:'Revocar', usage:'Uso durante los últimos 30 días', invoices:'Facturas recientes', download:'Descargar', noInvoices:'Aún no hay facturas.', close:'Cerrar espacio de trabajo' },
    'pt-br': { eyebrow:'ESPAÇO DE TRABALHO VECTRIA', fallback:'Espaço de produção vetorial', description:'Gerencie a produção vetorial, a cota de conversões, a equipe, o plano e as faturas.', loading:'Preparando o espaço de produção…', failed:'Não foi possível carregar o espaço de trabalho', retry:'Tentar novamente', plan:'Plano', conversions:'Conversões neste mês', members:'Membros da equipe', batch:'Limite por lote', subscription:'Assinatura', noSubscription:'Nenhuma assinatura ativa', billing:'Gerenciar cobrança', team:'Equipe de produção', email:'membro@empresa.com', invite:'Convidar', owner:'Proprietário', admin:'Administrador', member:'Membro', viewer:'Leitor', remove:'Remover', pending:'Convites pendentes', revoke:'Revogar', usage:'Uso nos últimos 30 dias', invoices:'Faturas recentes', download:'Baixar', noInvoices:'Nenhuma fatura ainda.', close:'Fechar espaço de trabalho' },
    ru: { eyebrow:'РАБОЧЕЕ ПРОСТРАНСТВО VECTRIA', fallback:'Рабочее пространство для создания векторной графики', description:'Управляйте векторизацией, квотой конвертаций, командой, тарифом и счетами.', loading:'Подготавливаем рабочее пространство…', failed:'Не удалось загрузить рабочее пространство', retry:'Повторить', plan:'Тариф', conversions:'Конвертации в этом месяце', members:'Участники команды', batch:'Лимит пакетной обработки', subscription:'Подписка', noSubscription:'Нет активной подписки', billing:'Управление оплатой', team:'Производственная команда', email:'участник@компания.рф', invite:'Пригласить', owner:'Владелец', admin:'Администратор', member:'Участник', viewer:'Наблюдатель', remove:'Удалить', pending:'Ожидающие приглашения', revoke:'Отозвать', usage:'Использование за последние 30 дней', invoices:'Последние счета', download:'Скачать', noInvoices:'Счетов пока нет.', close:'Закрыть рабочее пространство' },
    tr: { eyebrow:'VECTRIA ÇALIŞMA ALANI', fallback:'Vektör Üretim Çalışma Alanı', description:'Vektör üretimini, dönüştürme kotasını, ekibi, planı ve faturaları yönetin.', loading:'Üretim çalışma alanı hazırlanıyor…', failed:'Çalışma alanı yüklenemedi', retry:'Yeniden dene', plan:'Plan', conversions:'Bu ayki dönüştürmeler', members:'Ekip üyeleri', batch:'Toplu işlem sınırı', subscription:'Abonelik', noSubscription:'Etkin abonelik yok', billing:'Faturalandırmayı yönet', team:'Üretim ekibi', email:'uye@sirket.com', invite:'Davet et', owner:'Sahip', admin:'Yönetici', member:'Üye', viewer:'Görüntüleyici', remove:'Kaldır', pending:'Bekleyen davetler', revoke:'Geri çek', usage:'Son 30 günlük kullanım', invoices:'Son faturalar', download:'İndir', noInvoices:'Henüz fatura yok.', close:'Çalışma alanını kapat' },
    vi: { eyebrow:'KHÔNG GIAN LÀM VIỆC VECTRIA', fallback:'Không gian sản xuất vector', description:'Quản lý sản xuất vector, hạn mức chuyển đổi, nhóm, gói và hóa đơn.', loading:'Đang chuẩn bị không gian sản xuất…', failed:'Không thể tải không gian làm việc', retry:'Thử lại', plan:'Gói', conversions:'Lần chuyển đổi tháng này', members:'Thành viên nhóm', batch:'Giới hạn hàng loạt', subscription:'Thuê bao', noSubscription:'Không có thuê bao hoạt động', billing:'Quản lý thanh toán', team:'Nhóm sản xuất', email:'thanhvien@congty.com', invite:'Mời', owner:'Chủ sở hữu', admin:'Quản trị viên', member:'Thành viên', viewer:'Người xem', remove:'Xóa', pending:'Lời mời đang chờ', revoke:'Thu hồi', usage:'Mức sử dụng 30 ngày qua', invoices:'Hóa đơn gần đây', download:'Tải xuống', noInvoices:'Chưa có hóa đơn.', close:'Đóng không gian làm việc' },
    th: { eyebrow:'พื้นที่ทำงาน VECTRIA', fallback:'พื้นที่ทำงานผลิตเวกเตอร์', description:'จัดการการผลิตเวกเตอร์ โควตาการแปลง ทีม แพ็กเกจ และใบแจ้งหนี้', loading:'กำลังเตรียมพื้นที่ทำงานผลิต…', failed:'โหลดพื้นที่ทำงานไม่สำเร็จ', retry:'ลองอีกครั้ง', plan:'แพ็กเกจ', conversions:'การแปลงเดือนนี้', members:'สมาชิกทีม', batch:'ขีดจำกัดงานกลุ่ม', subscription:'การสมัครสมาชิก', noSubscription:'ไม่มีการสมัครสมาชิกที่ใช้งานอยู่', billing:'จัดการการเรียกเก็บเงิน', team:'ทีมผลิต', email:'สมาชิก@บริษัท.com', invite:'เชิญ', owner:'เจ้าของ', admin:'ผู้ดูแลระบบ', member:'สมาชิก', viewer:'ผู้ดู', remove:'ลบ', pending:'คำเชิญที่รอดำเนินการ', revoke:'เพิกถอน', usage:'การใช้งานใน 30 วันที่ผ่านมา', invoices:'ใบแจ้งหนี้ล่าสุด', download:'ดาวน์โหลด', noInvoices:'ยังไม่มีใบแจ้งหนี้', close:'ปิดพื้นที่ทำงาน' },
    ar: { eyebrow:'مساحة عمل VECTRIA', fallback:'مساحة إنتاج الرسومات المتجهة', description:'أدر إنتاج الرسومات المتجهة وحصة التحويل والفريق والخطة والفواتير.', loading:'جارٍ تجهيز مساحة الإنتاج…', failed:'تعذر تحميل مساحة العمل', retry:'أعد المحاولة', plan:'الخطة', conversions:'تحويلات هذا الشهر', members:'أعضاء الفريق', batch:'حد المعالجة الجماعية', subscription:'الاشتراك', noSubscription:'لا يوجد اشتراك نشط', billing:'إدارة الفوترة', team:'فريق الإنتاج', email:'member@company.com', invite:'دعوة', owner:'المالك', admin:'المشرف', member:'عضو', viewer:'مشاهد', remove:'إزالة', pending:'الدعوات المعلقة', revoke:'إلغاء', usage:'الاستخدام خلال آخر 30 يومًا', invoices:'الفواتير الأخيرة', download:'تنزيل', noInvoices:'لا توجد فواتير بعد.', close:'إغلاق مساحة العمل' }
  } as Record<string, any>)[locale];
  const workspaceExtraText = ({
    en:{accountType:'Account type',personal:'Personal',access:'Access',internalAccess:'Internal Agency Access • Managed by VECTRIA',freePlan:'Free plan',personalTitle:'Personal account',personalDesc:'Free, Starter, and Pro plans are for one user. Upgrade to Studio or Agency to invite a team and share workspace resources.',teamPlans:'View team plans',observability:'Metadata observability',failureRate:'failure rate',aiMetadata:'AI metadata',metadataExports:'Metadata exports',processed:'Processed assets',failedAssets:'Failed assets',jobsStatus:'Jobs by status',providers:'Providers',jobs:'jobs',assets:'assets',failed:'failed',average:'avg',unknown:'unknown'},
    id:{accountType:'Jenis akun',personal:'Pribadi',access:'Akses',internalAccess:'Akses Agency Internal • Dikelola VECTRIA',freePlan:'Paket gratis',personalTitle:'Akun pribadi',personalDesc:'Paket Free, Starter, dan Pro digunakan oleh satu pengguna. Tingkatkan ke Studio atau Agency untuk mengundang tim dan berbagi resource workspace.',teamPlans:'Lihat paket tim',observability:'Observabilitas metadata',failureRate:'tingkat kegagalan',aiMetadata:'Metadata AI',metadataExports:'Ekspor metadata',processed:'Aset diproses',failedAssets:'Aset gagal',jobsStatus:'Tugas berdasarkan status',providers:'Penyedia',jobs:'tugas',assets:'aset',failed:'gagal',average:'rata-rata',unknown:'tidak diketahui'},
    'zh-cn':{accountType:'账户类型',personal:'个人',access:'访问权限',internalAccess:'内部 Agency 访问权限 • 由 VECTRIA 管理',freePlan:'免费方案',personalTitle:'个人账户',personalDesc:'Free、Starter 和 Pro 方案仅供一名用户使用。升级到 Studio 或 Agency 即可邀请团队并共享工作区资源。',teamPlans:'查看团队方案',observability:'元数据监控',failureRate:'失败率',aiMetadata:'AI 元数据',metadataExports:'元数据导出',processed:'已处理素材',failedAssets:'失败素材',jobsStatus:'按状态统计任务',providers:'提供商',jobs:'项任务',assets:'个素材',failed:'失败',average:'平均',unknown:'未知'},
    ja:{accountType:'アカウント種別',personal:'個人',access:'アクセス',internalAccess:'内部 Agency アクセス • VECTRIA が管理',freePlan:'無料プラン',personalTitle:'個人アカウント',personalDesc:'Free、Starter、Pro プランは1名用です。Studio または Agency にアップグレードすると、チームを招待してワークスペースのリソースを共有できます。',teamPlans:'チームプランを見る',observability:'メタデータ監視',failureRate:'失敗率',aiMetadata:'AI メタデータ',metadataExports:'メタデータ書き出し',processed:'処理済み素材',failedAssets:'失敗した素材',jobsStatus:'ステータス別ジョブ',providers:'プロバイダー',jobs:'ジョブ',assets:'素材',failed:'失敗',average:'平均',unknown:'不明'},
    ko:{accountType:'계정 유형',personal:'개인',access:'접근 권한',internalAccess:'내부 Agency 접근 권한 • VECTRIA 관리',freePlan:'무료 플랜',personalTitle:'개인 계정',personalDesc:'Free, Starter 및 Pro 플랜은 1인용입니다. Studio 또는 Agency로 업그레이드하면 팀을 초대하고 워크스페이스 리소스를 공유할 수 있습니다.',teamPlans:'팀 플랜 보기',observability:'메타데이터 관측',failureRate:'실패율',aiMetadata:'AI 메타데이터',metadataExports:'메타데이터 내보내기',processed:'처리된 에셋',failedAssets:'실패한 에셋',jobsStatus:'상태별 작업',providers:'제공업체',jobs:'작업',assets:'에셋',failed:'실패',average:'평균',unknown:'알 수 없음'},
    es:{accountType:'Tipo de cuenta',personal:'Personal',access:'Acceso',internalAccess:'Acceso interno de Agency • Gestionado por VECTRIA',freePlan:'Plan gratuito',personalTitle:'Cuenta personal',personalDesc:'Los planes Free, Starter y Pro son para una sola persona. Mejora a Studio o Agency para invitar a un equipo y compartir recursos del espacio de trabajo.',teamPlans:'Ver planes para equipos',observability:'Observabilidad de metadatos',failureRate:'tasa de fallos',aiMetadata:'Metadatos de IA',metadataExports:'Exportaciones de metadatos',processed:'Recursos procesados',failedAssets:'Recursos fallidos',jobsStatus:'Tareas por estado',providers:'Proveedores',jobs:'tareas',assets:'recursos',failed:'fallidos',average:'promedio',unknown:'desconocido'},
    'pt-br':{accountType:'Tipo de conta',personal:'Pessoal',access:'Acesso',internalAccess:'Acesso interno da Agency • Gerenciado pela VECTRIA',freePlan:'Plano gratuito',personalTitle:'Conta pessoal',personalDesc:'Os planos Free, Starter e Pro são para uma pessoa. Faça upgrade para Studio ou Agency para convidar uma equipe e compartilhar recursos do espaço de trabalho.',teamPlans:'Ver planos para equipes',observability:'Observabilidade de metadados',failureRate:'taxa de falhas',aiMetadata:'Metadados de IA',metadataExports:'Exportações de metadados',processed:'Ativos processados',failedAssets:'Ativos com falha',jobsStatus:'Tarefas por status',providers:'Provedores',jobs:'tarefas',assets:'ativos',failed:'com falha',average:'média',unknown:'desconhecido'},
    ru:{accountType:'Тип аккаунта',personal:'Личный',access:'Доступ',internalAccess:'Внутренний доступ Agency • Управляется VECTRIA',freePlan:'Бесплатный тариф',personalTitle:'Личный аккаунт',personalDesc:'Тарифы Free, Starter и Pro предназначены для одного пользователя. Перейдите на Studio или Agency, чтобы пригласить команду и совместно использовать ресурсы рабочего пространства.',teamPlans:'Посмотреть командные тарифы',observability:'Мониторинг метаданных',failureRate:'доля ошибок',aiMetadata:'ИИ-метаданные',metadataExports:'Экспорт метаданных',processed:'Обработанные материалы',failedAssets:'Материалы с ошибками',jobsStatus:'Задачи по статусу',providers:'Провайдеры',jobs:'задач',assets:'материалов',failed:'с ошибкой',average:'среднее',unknown:'неизвестно'},
    tr:{accountType:'Hesap türü',personal:'Kişisel',access:'Erişim',internalAccess:'Dahili Agency Erişimi • VECTRIA tarafından yönetilir',freePlan:'Ücretsiz plan',personalTitle:'Kişisel hesap',personalDesc:'Free, Starter ve Pro planları tek kullanıcı içindir. Ekip davet etmek ve çalışma alanı kaynaklarını paylaşmak için Studio veya Agency planına yükseltin.',teamPlans:'Ekip planlarını görüntüle',observability:'Meta veri gözlemlenebilirliği',failureRate:'hata oranı',aiMetadata:'AI meta verileri',metadataExports:'Meta veri dışa aktarımları',processed:'İşlenen varlıklar',failedAssets:'Başarısız varlıklar',jobsStatus:'Duruma göre işler',providers:'Sağlayıcılar',jobs:'iş',assets:'varlık',failed:'başarısız',average:'ortalama',unknown:'bilinmiyor'},
    vi:{accountType:'Loại tài khoản',personal:'Cá nhân',access:'Quyền truy cập',internalAccess:'Quyền truy cập Agency nội bộ • Do VECTRIA quản lý',freePlan:'Gói miễn phí',personalTitle:'Tài khoản cá nhân',personalDesc:'Các gói Free, Starter và Pro dành cho một người dùng. Nâng cấp lên Studio hoặc Agency để mời nhóm và chia sẻ tài nguyên không gian làm việc.',teamPlans:'Xem gói nhóm',observability:'Khả năng quan sát metadata',failureRate:'tỷ lệ lỗi',aiMetadata:'Metadata AI',metadataExports:'Lần xuất metadata',processed:'Tài sản đã xử lý',failedAssets:'Tài sản lỗi',jobsStatus:'Tác vụ theo trạng thái',providers:'Nhà cung cấp',jobs:'tác vụ',assets:'tài sản',failed:'lỗi',average:'trung bình',unknown:'không rõ'},
    th:{accountType:'ประเภทบัญชี',personal:'ส่วนบุคคล',access:'สิทธิ์เข้าถึง',internalAccess:'สิทธิ์เข้าถึง Agency ภายใน • จัดการโดย VECTRIA',freePlan:'แพ็กเกจฟรี',personalTitle:'บัญชีส่วนบุคคล',personalDesc:'แพ็กเกจ Free, Starter และ Pro สำหรับผู้ใช้หนึ่งคน อัปเกรดเป็น Studio หรือ Agency เพื่อเชิญทีมและแชร์ทรัพยากรพื้นที่ทำงาน',teamPlans:'ดูแพ็กเกจทีม',observability:'การติดตามเมทาดาทา',failureRate:'อัตราความล้มเหลว',aiMetadata:'เมทาดาทา AI',metadataExports:'การส่งออกเมทาดาทา',processed:'สินทรัพย์ที่ประมวลผลแล้ว',failedAssets:'สินทรัพย์ที่ล้มเหลว',jobsStatus:'งานตามสถานะ',providers:'ผู้ให้บริการ',jobs:'งาน',assets:'สินทรัพย์',failed:'ล้มเหลว',average:'เฉลี่ย',unknown:'ไม่ทราบ'},
    ar:{accountType:'نوع الحساب',personal:'شخصي',access:'الوصول',internalAccess:'وصول Agency داخلي • تديره VECTRIA',freePlan:'الخطة المجانية',personalTitle:'حساب شخصي',personalDesc:'خطط Free وStarter وPro لمستخدم واحد. رقِّ إلى Studio أو Agency لدعوة فريق ومشاركة موارد مساحة العمل.',teamPlans:'عرض خطط الفرق',observability:'مراقبة البيانات الوصفية',failureRate:'معدل الفشل',aiMetadata:'بيانات وصفية بالذكاء الاصطناعي',metadataExports:'عمليات تصدير البيانات الوصفية',processed:'الأصول المعالجة',failedAssets:'الأصول الفاشلة',jobsStatus:'المهام حسب الحالة',providers:'المزودون',jobs:'مهام',assets:'أصول',failed:'فاشلة',average:'المتوسط',unknown:'غير معروف'}
  } as Record<string, any>)[locale];
  const metadataSaasText = ({
    en:{batch:'Generate Batch',history:'Job History',hideHistory:'Hide History',empty:'No metadata jobs yet.',loadError:'Could not load metadata history.',batchEmpty:'Convert at least one image before generating batch metadata.',batchDone:(count:number)=>`Generated metadata for ${count} assets.`,assets:'assets',failed:'failed',download:'Download CSV',retry:'Retry failed',editorLabel:'Metadata preview and editor',closeEditor:'Close metadata editor',regenerateHint:'Generate or regenerate keywords with the current AI model',previous:'Previous metadata asset',previousTitle:'Previous asset',selectAsset:'Select metadata asset',next:'Next metadata asset',nextTitle:'Next asset',ready:'Ready',noMetadata:'No metadata',metadataReady:'metadata ready',titleLabel:'Metadata title',descriptionLabel:'Metadata description',descriptionPlaceholder:'Marketplace description',categoryLabel:(market:string)=>`${market} metadata category`,selectCategory:(market:string)=>`Select ${market} category`,applyAll:'Apply to all',applyAllTitle:'Apply this category to all generated assets',autoSave:'Changes are saved when a field loses focus; category changes are saved immediately.',keywordsLabel:'Metadata keywords',removeDuplicates:'Remove duplicates',sort:'Sort A–Z',validation:'Validation',copyKeywords:'Copy all keywords as a comma-separated list',generating:'Generating metadata',metadataStatus:(status:string)=>`Metadata ${status}`,previewEdit:'Preview and edit',analyzing:'AI is analyzing assets',defaultModel:'default model',generateAllTitle:'Generate metadata for every converted asset',downloadCsvTitle:'Download marketplace-compatible metadata CSV',targetMarketplace:'Target microstock marketplace',noExports:'No ZIP exports yet.',items:'items',downloadExport:'Download',exportHistory:'Export history',showExports:'Show exports',hideExports:'Hide exports',loadingExports:'Loading exports…',vectorizationFailed:'Vectorization failed',downloadSvg:'Download SVG'},
    id:{batch:'Buat Metadata Batch',history:'Riwayat Tugas',hideHistory:'Tutup Riwayat',empty:'Belum ada tugas metadata.',loadError:'Riwayat metadata gagal dimuat.',batchEmpty:'Konversi minimal satu gambar sebelum membuat metadata batch.',batchDone:(count:number)=>`Metadata dibuat untuk ${count} aset.`,assets:'aset',failed:'gagal',download:'Unduh CSV',retry:'Ulangi yang gagal',editorLabel:'Pratinjau dan editor metadata',closeEditor:'Tutup editor metadata',regenerateHint:'Buat atau perbarui kata kunci dengan model AI saat ini',previous:'Aset metadata sebelumnya',previousTitle:'Aset sebelumnya',selectAsset:'Pilih aset metadata',next:'Aset metadata berikutnya',nextTitle:'Aset berikutnya',ready:'Siap',noMetadata:'Belum ada metadata',metadataReady:'metadata siap',titleLabel:'Judul metadata',descriptionLabel:'Deskripsi metadata',descriptionPlaceholder:'Deskripsi marketplace',categoryLabel:(market:string)=>`Kategori metadata ${market}`,selectCategory:(market:string)=>`Pilih kategori ${market}`,applyAll:'Terapkan ke semua',applyAllTitle:'Terapkan kategori ini ke semua aset yang sudah dibuat',autoSave:'Perubahan disimpan saat kolom kehilangan fokus; perubahan kategori langsung disimpan.',keywordsLabel:'Kata kunci metadata',removeDuplicates:'Hapus duplikat',sort:'Urutkan A–Z',validation:'Validasi',copyKeywords:'Salin semua kata kunci sebagai daftar yang dipisahkan koma',generating:'Membuat metadata',metadataStatus:(status:string)=>`Metadata ${status}`,previewEdit:'Pratinjau dan edit',analyzing:'AI sedang menganalisis aset',defaultModel:'model default',generateAllTitle:'Buat metadata untuk semua aset yang sudah dikonversi',downloadCsvTitle:'Unduh CSV metadata yang kompatibel dengan marketplace',targetMarketplace:'Marketplace microstock tujuan',noExports:'Belum ada ekspor ZIP.',items:'item',downloadExport:'Unduh',exportHistory:'Riwayat ekspor',showExports:'Tampilkan ekspor',hideExports:'Sembunyikan ekspor',loadingExports:'Memuat ekspor…',vectorizationFailed:'Vektorisasi gagal',downloadSvg:'Unduh SVG'},
    'zh-cn':{batch:'批量生成',history:'任务历史',hideHistory:'隐藏历史',empty:'暂无元数据任务。',loadError:'无法加载元数据历史。',batchEmpty:'请先转换至少一张图像。',batchDone:(count:number)=>`已为 ${count} 个素材生成元数据。`,assets:'素材',failed:'失败',download:'下载 CSV',retry:'重试失败项',editorLabel:'元数据预览与编辑器',closeEditor:'关闭元数据编辑器',regenerateHint:'使用当前 AI 模型生成或重新生成关键词',previous:'上一个元数据素材',previousTitle:'上一个素材',selectAsset:'选择元数据素材',next:'下一个元数据素材',nextTitle:'下一个素材',ready:'已就绪',noMetadata:'暂无元数据',metadataReady:'份元数据已就绪',titleLabel:'元数据标题',descriptionLabel:'元数据描述',descriptionPlaceholder:'图库描述',categoryLabel:(market:string)=>`${market} 元数据分类`,selectCategory:(market:string)=>`选择 ${market} 分类`,applyAll:'全部应用',applyAllTitle:'将此分类应用到所有已生成的素材',autoSave:'输入框失去焦点时自动保存;分类更改会立即保存。',keywordsLabel:'元数据关键词',removeDuplicates:'删除重复项',sort:'按 A–Z 排序',validation:'验证',copyKeywords:'将所有关键词复制为逗号分隔的列表',generating:'正在生成元数据',metadataStatus:(status:string)=>`元数据${status}`,previewEdit:'预览与编辑',analyzing:'AI 正在分析素材',defaultModel:'默认模型',generateAllTitle:'为所有已转换素材生成元数据',downloadCsvTitle:'下载与图库兼容的元数据 CSV',targetMarketplace:'目标微利图库',noExports:'暂无 ZIP 导出。',items:'项',downloadExport:'下载',exportHistory:'导出历史',showExports:'显示导出',hideExports:'隐藏导出',loadingExports:'正在加载导出…',vectorizationFailed:'矢量化失败',downloadSvg:'下载 SVG'},
    ja:{batch:'一括生成',history:'ジョブ履歴',hideHistory:'履歴を閉じる',empty:'メタデータジョブはまだありません。',loadError:'メタデータ履歴を読み込めません。',batchEmpty:'一括生成前に画像を1件以上変換してください。',batchDone:(count:number)=>`${count} 件の素材のメタデータを生成しました。`,assets:'素材',failed:'失敗',download:'CSVをダウンロード',retry:'失敗を再試行',editorLabel:'メタデータのプレビューと編集',closeEditor:'メタデータエディターを閉じる',regenerateHint:'現在の AI モデルでキーワードを生成または再生成',previous:'前のメタデータ素材',previousTitle:'前の素材',selectAsset:'メタデータ素材を選択',next:'次のメタデータ素材',nextTitle:'次の素材',ready:'準備完了',noMetadata:'メタデータなし',metadataReady:'件のメタデータが準備完了',titleLabel:'メタデータのタイトル',descriptionLabel:'メタデータの説明',descriptionPlaceholder:'マーケットプレイス向け説明',categoryLabel:(market:string)=>`${market} のメタデータカテゴリー`,selectCategory:(market:string)=>`${market} のカテゴリーを選択`,applyAll:'すべてに適用',applyAllTitle:'このカテゴリーを生成済みの全素材に適用',autoSave:'入力欄からフォーカスが外れると自動保存されます。カテゴリーはすぐに保存されます。',keywordsLabel:'メタデータのキーワード',removeDuplicates:'重複を削除',sort:'A–Z 順に並べ替え',validation:'検証',copyKeywords:'すべてのキーワードをカンマ区切りでコピー',generating:'メタデータを生成中',metadataStatus:(status:string)=>`メタデータ:${status}`,previewEdit:'プレビューと編集',analyzing:'AI が素材を分析中',defaultModel:'デフォルトモデル',generateAllTitle:'変換済みの全素材にメタデータを生成',downloadCsvTitle:'マーケットプレイス対応のメタデータ CSV をダウンロード',targetMarketplace:'対象のマイクロストック',noExports:'ZIP エクスポートはまだありません。',items:'件',downloadExport:'ダウンロード',exportHistory:'エクスポート履歴',showExports:'エクスポートを表示',hideExports:'エクスポートを隠す',loadingExports:'エクスポートを読み込み中…',vectorizationFailed:'ベクター変換に失敗しました',downloadSvg:'SVG をダウンロード'},
    ko:{batch:'일괄 생성',history:'작업 기록',hideHistory:'기록 닫기',empty:'메타데이터 작업이 없습니다.',loadError:'메타데이터 기록을 불러오지 못했습니다.',batchEmpty:'일괄 생성 전에 이미지를 하나 이상 변환하세요.',batchDone:(count:number)=>`${count}개 에셋의 메타데이터를 생성했습니다.`,assets:'에셋',failed:'실패',download:'CSV 다운로드',retry:'실패 항목 재시도',editorLabel:'메타데이터 미리보기 및 편집기',closeEditor:'메타데이터 편집기 닫기',regenerateHint:'현재 AI 모델로 키워드 생성 또는 재생성',previous:'이전 메타데이터 에셋',previousTitle:'이전 에셋',selectAsset:'메타데이터 에셋 선택',next:'다음 메타데이터 에셋',nextTitle:'다음 에셋',ready:'준비됨',noMetadata:'메타데이터 없음',metadataReady:'개 메타데이터 준비됨',titleLabel:'메타데이터 제목',descriptionLabel:'메타데이터 설명',descriptionPlaceholder:'마켓플레이스 설명',categoryLabel:(market:string)=>`${market} 메타데이터 카테고리`,selectCategory:(market:string)=>`${market} 카테고리 선택`,applyAll:'모두 적용',applyAllTitle:'이 카테고리를 생성된 모든 에셋에 적용',autoSave:'입력란에서 포커스가 벗어나면 자동 저장됩니다. 카테고리는 즉시 저장됩니다.',keywordsLabel:'메타데이터 키워드',removeDuplicates:'중복 제거',sort:'A–Z 정렬',validation:'검증',copyKeywords:'모든 키워드를 쉼표로 구분된 목록으로 복사',generating:'메타데이터 생성 중',metadataStatus:(status:string)=>`메타데이터 ${status}`,previewEdit:'미리보기 및 편집',analyzing:'AI가 에셋을 분석 중입니다',defaultModel:'기본 모델',generateAllTitle:'변환된 모든 에셋의 메타데이터 생성',downloadCsvTitle:'마켓플레이스 호환 메타데이터 CSV 다운로드',targetMarketplace:'대상 마이크로스톡 마켓플레이스',noExports:'아직 ZIP 내보내기가 없습니다.',items:'개 항목',downloadExport:'다운로드',exportHistory:'내보내기 기록',showExports:'내보내기 표시',hideExports:'내보내기 숨기기',loadingExports:'내보내기 불러오는 중…',vectorizationFailed:'벡터 변환 실패',downloadSvg:'SVG 다운로드'},
    es:{batch:'Generar por lote',history:'Historial de tareas',hideHistory:'Ocultar historial',empty:'Aún no hay tareas de metadatos.',loadError:'No se pudo cargar el historial de metadatos.',batchEmpty:'Convierte al menos una imagen antes de generar metadatos por lote.',batchDone:(count:number)=>`Se generaron metadatos para ${count} recursos.`,assets:'recursos',failed:'fallidos',download:'Descargar CSV',retry:'Reintentar los fallidos',editorLabel:'Vista previa y editor de metadatos',closeEditor:'Cerrar editor de metadatos',regenerateHint:'Genera o vuelve a generar palabras clave con el modelo de IA actual',previous:'Recurso de metadatos anterior',previousTitle:'Recurso anterior',selectAsset:'Seleccionar recurso de metadatos',next:'Siguiente recurso de metadatos',nextTitle:'Siguiente recurso',ready:'Listo',noMetadata:'Sin metadatos',metadataReady:'metadatos listos',titleLabel:'Título de metadatos',descriptionLabel:'Descripción de metadatos',descriptionPlaceholder:'Descripción para el mercado',categoryLabel:(market:string)=>`Categoría de metadatos de ${market}`,selectCategory:(market:string)=>`Seleccionar categoría de ${market}`,applyAll:'Aplicar a todos',applyAllTitle:'Aplicar esta categoría a todos los recursos generados',autoSave:'Los cambios se guardan cuando el campo pierde el foco; los cambios de categoría se guardan de inmediato.',keywordsLabel:'Palabras clave de metadatos',removeDuplicates:'Eliminar duplicados',sort:'Ordenar A–Z',validation:'Validación',copyKeywords:'Copiar todas las palabras clave como lista separada por comas',generating:'Generando metadatos',metadataStatus:(status:string)=>`Metadatos: ${status}`,previewEdit:'Ver y editar',analyzing:'La IA está analizando los recursos',defaultModel:'modelo predeterminado',generateAllTitle:'Generar metadatos para todos los recursos convertidos',downloadCsvTitle:'Descargar CSV de metadatos compatible con el mercado',targetMarketplace:'Mercado de microstock de destino',noExports:'Aún no hay exportaciones ZIP.',items:'elementos',downloadExport:'Descargar',exportHistory:'Historial de exportaciones',showExports:'Mostrar exportaciones',hideExports:'Ocultar exportaciones',loadingExports:'Cargando exportaciones…',vectorizationFailed:'Falló la vectorización',downloadSvg:'Descargar SVG'},
    'pt-br':{batch:'Gerar em lote',history:'Histórico de tarefas',hideHistory:'Ocultar histórico',empty:'Nenhuma tarefa de metadados ainda.',loadError:'Não foi possível carregar o histórico de metadados.',batchEmpty:'Converta pelo menos uma imagem antes de gerar metadados em lote.',batchDone:(count:number)=>`Metadados gerados para ${count} ativos.`,assets:'ativos',failed:'com falha',download:'Baixar CSV',retry:'Tentar novamente os que falharam',editorLabel:'Prévia e editor de metadados',closeEditor:'Fechar editor de metadados',regenerateHint:'Gere ou gere novamente palavras-chave com o modelo de IA atual',previous:'Ativo de metadados anterior',previousTitle:'Ativo anterior',selectAsset:'Selecionar ativo de metadados',next:'Próximo ativo de metadados',nextTitle:'Próximo ativo',ready:'Pronto',noMetadata:'Sem metadados',metadataReady:'metadados prontos',titleLabel:'Título dos metadados',descriptionLabel:'Descrição dos metadados',descriptionPlaceholder:'Descrição para o marketplace',categoryLabel:(market:string)=>`Categoria de metadados da ${market}`,selectCategory:(market:string)=>`Selecionar categoria da ${market}`,applyAll:'Aplicar a todos',applyAllTitle:'Aplicar esta categoria a todos os ativos gerados',autoSave:'As alterações são salvas quando o campo perde o foco; alterações de categoria são salvas imediatamente.',keywordsLabel:'Palavras-chave dos metadados',removeDuplicates:'Remover duplicadas',sort:'Ordenar de A–Z',validation:'Validação',copyKeywords:'Copiar todas as palavras-chave como lista separada por vírgulas',generating:'Gerando metadados',metadataStatus:(status:string)=>`Metadados: ${status}`,previewEdit:'Visualizar e editar',analyzing:'A IA está analisando os ativos',defaultModel:'modelo padrão',generateAllTitle:'Gerar metadados para todos os ativos convertidos',downloadCsvTitle:'Baixar CSV de metadados compatível com o marketplace',targetMarketplace:'Marketplace de microstock de destino',noExports:'Nenhuma exportação ZIP ainda.',items:'itens',downloadExport:'Baixar',exportHistory:'Histórico de exportações',showExports:'Mostrar exportações',hideExports:'Ocultar exportações',loadingExports:'Carregando exportações…',vectorizationFailed:'Falha na vetorização',downloadSvg:'Baixar SVG'},
    ru:{batch:'Создать пакетно',history:'История задач',hideHistory:'Скрыть историю',empty:'Задач метаданных пока нет.',loadError:'Не удалось загрузить историю метаданных.',batchEmpty:'Перед пакетным созданием метаданных конвертируйте хотя бы одно изображение.',batchDone:(count:number)=>`Метаданные созданы для ${count} материалов.`,assets:'материалов',failed:'с ошибкой',download:'Скачать CSV',retry:'Повторить неудачные',editorLabel:'Предпросмотр и редактор метаданных',closeEditor:'Закрыть редактор метаданных',regenerateHint:'Создать или обновить ключевые слова с помощью текущей модели ИИ',previous:'Предыдущий материал с метаданными',previousTitle:'Предыдущий материал',selectAsset:'Выбрать материал',next:'Следующий материал с метаданными',nextTitle:'Следующий материал',ready:'Готово',noMetadata:'Нет метаданных',metadataReady:'метаданные готовы',titleLabel:'Заголовок метаданных',descriptionLabel:'Описание метаданных',descriptionPlaceholder:'Описание для фотобанка',categoryLabel:(market:string)=>`Категория метаданных ${market}`,selectCategory:(market:string)=>`Выберите категорию ${market}`,applyAll:'Применить ко всем',applyAllTitle:'Применить эту категорию ко всем созданным материалам',autoSave:'Изменения сохраняются при потере фокуса; категория сохраняется сразу.',keywordsLabel:'Ключевые слова',removeDuplicates:'Удалить дубликаты',sort:'Сортировать А–Я',validation:'Проверка',copyKeywords:'Скопировать все ключевые слова списком через запятую',generating:'Создание метаданных',metadataStatus:(status:string)=>`Метаданные: ${status}`,previewEdit:'Просмотреть и изменить',analyzing:'ИИ анализирует материалы',defaultModel:'модель по умолчанию',generateAllTitle:'Создать метаданные для всех конвертированных материалов',downloadCsvTitle:'Скачать совместимый с фотобанком CSV метаданных',targetMarketplace:'Целевой микросток',noExports:'Экспортов ZIP пока нет.',items:'элементов',downloadExport:'Скачать',exportHistory:'История экспорта',showExports:'Показать экспорт',hideExports:'Скрыть экспорт',loadingExports:'Загрузка экспорта…',vectorizationFailed:'Ошибка векторизации',downloadSvg:'Скачать SVG'},
    tr:{batch:'Toplu Oluştur',history:'İş Geçmişi',hideHistory:'Geçmişi Gizle',empty:'Henüz meta veri işi yok.',loadError:'Meta veri geçmişi yüklenemedi.',batchEmpty:'Toplu meta veri oluşturmadan önce en az bir görseli dönüştürün.',batchDone:(count:number)=>`${count} varlık için meta veri oluşturuldu.`,assets:'varlık',failed:'başarısız',download:'CSV İndir',retry:'Başarısızları yeniden dene',editorLabel:'Meta veri önizleme ve düzenleyici',closeEditor:'Meta veri düzenleyicisini kapat',regenerateHint:'Geçerli AI modeliyle anahtar kelimeleri oluştur veya yeniden oluştur',previous:'Önceki meta veri varlığı',previousTitle:'Önceki varlık',selectAsset:'Meta veri varlığı seç',next:'Sonraki meta veri varlığı',nextTitle:'Sonraki varlık',ready:'Hazır',noMetadata:'Meta veri yok',metadataReady:'meta veri hazır',titleLabel:'Meta veri başlığı',descriptionLabel:'Meta veri açıklaması',descriptionPlaceholder:'Pazar yeri açıklaması',categoryLabel:(market:string)=>`${market} meta veri kategorisi`,selectCategory:(market:string)=>`${market} kategorisi seç`,applyAll:'Tümüne uygula',applyAllTitle:'Bu kategoriyi oluşturulan tüm varlıklara uygula',autoSave:'Alan odağı kaybettiğinde değişiklikler kaydedilir; kategori değişiklikleri hemen kaydedilir.',keywordsLabel:'Meta veri anahtar kelimeleri',removeDuplicates:'Yinelenenleri kaldır',sort:'A–Z sırala',validation:'Doğrulama',copyKeywords:'Tüm anahtar kelimeleri virgülle ayrılmış liste olarak kopyala',generating:'Meta veri oluşturuluyor',metadataStatus:(status:string)=>`Meta veri: ${status}`,previewEdit:'Önizle ve düzenle',analyzing:'AI varlıkları analiz ediyor',defaultModel:'varsayılan model',generateAllTitle:'Dönüştürülen tüm varlıklar için meta veri oluştur',downloadCsvTitle:'Pazar yeri uyumlu meta veri CSV dosyasını indir',targetMarketplace:'Hedef mikro stok pazarı',noExports:'Henüz ZIP dışa aktarımı yok.',items:'öğe',downloadExport:'İndir',exportHistory:'Dışa aktarma geçmişi',showExports:'Dışa aktarımları göster',hideExports:'Dışa aktarımları gizle',loadingExports:'Dışa aktarımlar yükleniyor…',vectorizationFailed:'Vektörleştirme başarısız',downloadSvg:'SVG İndir'},
    vi:{batch:'Tạo hàng loạt',history:'Lịch sử tác vụ',hideHistory:'Ẩn lịch sử',empty:'Chưa có tác vụ metadata.',loadError:'Không thể tải lịch sử metadata.',batchEmpty:'Hãy chuyển đổi ít nhất một ảnh trước khi tạo metadata hàng loạt.',batchDone:(count:number)=>`Đã tạo metadata cho ${count} tài sản.`,assets:'tài sản',failed:'lỗi',download:'Tải CSV',retry:'Thử lại mục lỗi',editorLabel:'Xem trước và chỉnh sửa metadata',closeEditor:'Đóng trình chỉnh sửa metadata',regenerateHint:'Tạo hoặc tạo lại từ khóa bằng mô hình AI hiện tại',previous:'Tài sản metadata trước',previousTitle:'Tài sản trước',selectAsset:'Chọn tài sản metadata',next:'Tài sản metadata tiếp theo',nextTitle:'Tài sản tiếp theo',ready:'Sẵn sàng',noMetadata:'Không có metadata',metadataReady:'metadata sẵn sàng',titleLabel:'Tiêu đề metadata',descriptionLabel:'Mô tả metadata',descriptionPlaceholder:'Mô tả cho thị trường',categoryLabel:(market:string)=>`Danh mục metadata ${market}`,selectCategory:(market:string)=>`Chọn danh mục ${market}`,applyAll:'Áp dụng cho tất cả',applyAllTitle:'Áp dụng danh mục này cho mọi tài sản đã tạo',autoSave:'Thay đổi được lưu khi trường mất tiêu điểm; thay đổi danh mục được lưu ngay.',keywordsLabel:'Từ khóa metadata',removeDuplicates:'Xóa trùng lặp',sort:'Sắp xếp A–Z',validation:'Xác thực',copyKeywords:'Sao chép mọi từ khóa thành danh sách phân cách bằng dấu phẩy',generating:'Đang tạo metadata',metadataStatus:(status:string)=>`Metadata: ${status}`,previewEdit:'Xem trước và chỉnh sửa',analyzing:'AI đang phân tích tài sản',defaultModel:'mô hình mặc định',generateAllTitle:'Tạo metadata cho mọi tài sản đã chuyển đổi',downloadCsvTitle:'Tải CSV metadata tương thích với thị trường',targetMarketplace:'Thị trường microstock mục tiêu',noExports:'Chưa có lần xuất ZIP.',items:'mục',downloadExport:'Tải xuống',exportHistory:'Lịch sử xuất',showExports:'Hiện lần xuất',hideExports:'Ẩn lần xuất',loadingExports:'Đang tải lần xuất…',vectorizationFailed:'Vector hóa thất bại',downloadSvg:'Tải SVG'},
    th:{batch:'สร้างแบบกลุ่ม',history:'ประวัติงาน',hideHistory:'ซ่อนประวัติ',empty:'ยังไม่มีงานเมทาดาทา',loadError:'โหลดประวัติเมทาดาทาไม่สำเร็จ',batchEmpty:'แปลงภาพอย่างน้อยหนึ่งภาพก่อนสร้างเมทาดาทาแบบกลุ่ม',batchDone:(count:number)=>`สร้างเมทาดาทาสำหรับ ${count} สินทรัพย์แล้ว`,assets:'สินทรัพย์',failed:'ล้มเหลว',download:'ดาวน์โหลด CSV',retry:'ลองรายการที่ล้มเหลวอีกครั้ง',editorLabel:'ตัวอย่างและตัวแก้ไขเมทาดาทา',closeEditor:'ปิดตัวแก้ไขเมทาดาทา',regenerateHint:'สร้างหรือสร้างคีย์เวิร์ดใหม่ด้วยโมเดล AI ปัจจุบัน',previous:'สินทรัพย์เมทาดาทาก่อนหน้า',previousTitle:'สินทรัพย์ก่อนหน้า',selectAsset:'เลือกสินทรัพย์เมทาดาทา',next:'สินทรัพย์เมทาดาทาถัดไป',nextTitle:'สินทรัพย์ถัดไป',ready:'พร้อม',noMetadata:'ไม่มีเมทาดาทา',metadataReady:'เมทาดาทาพร้อม',titleLabel:'ชื่อเมทาดาทา',descriptionLabel:'คำอธิบายเมทาดาทา',descriptionPlaceholder:'คำอธิบายสำหรับมาร์เก็ตเพลส',categoryLabel:(market:string)=>`หมวดหมู่เมทาดาทา ${market}`,selectCategory:(market:string)=>`เลือกหมวดหมู่ ${market}`,applyAll:'ใช้กับทั้งหมด',applyAllTitle:'ใช้หมวดหมู่นี้กับสินทรัพย์ที่สร้างแล้วทั้งหมด',autoSave:'บันทึกการเปลี่ยนแปลงเมื่อช่องเสียโฟกัส ส่วนหมวดหมู่บันทึกทันที',keywordsLabel:'คีย์เวิร์ดเมทาดาทา',removeDuplicates:'ลบรายการซ้ำ',sort:'เรียง A–Z',validation:'การตรวจสอบ',copyKeywords:'คัดลอกคีย์เวิร์ดทั้งหมดเป็นรายการคั่นด้วยจุลภาค',generating:'กำลังสร้างเมทาดาทา',metadataStatus:(status:string)=>`เมทาดาทา: ${status}`,previewEdit:'ดูตัวอย่างและแก้ไข',analyzing:'AI กำลังวิเคราะห์สินทรัพย์',defaultModel:'โมเดลเริ่มต้น',generateAllTitle:'สร้างเมทาดาทาสำหรับสินทรัพย์ที่แปลงแล้วทั้งหมด',downloadCsvTitle:'ดาวน์โหลด CSV เมทาดาทาที่เข้ากันได้กับมาร์เก็ตเพลส',targetMarketplace:'ตลาดไมโครสต็อกเป้าหมาย',noExports:'ยังไม่มีการส่งออก ZIP',items:'รายการ',downloadExport:'ดาวน์โหลด',exportHistory:'ประวัติการส่งออก',showExports:'แสดงการส่งออก',hideExports:'ซ่อนการส่งออก',loadingExports:'กำลังโหลดการส่งออก…',vectorizationFailed:'การแปลงเวกเตอร์ล้มเหลว',downloadSvg:'ดาวน์โหลด SVG'},
    ar:{batch:'إنشاء جماعي',history:'سجل المهام',hideHistory:'إخفاء السجل',empty:'لا توجد مهام بيانات وصفية بعد.',loadError:'تعذر تحميل سجل البيانات الوصفية.',batchEmpty:'حوّل صورة واحدة على الأقل قبل إنشاء البيانات الوصفية جماعيًا.',batchDone:(count:number)=>`تم إنشاء بيانات وصفية لـ ${count} من الأصول.`,assets:'أصول',failed:'فاشلة',download:'تنزيل CSV',retry:'إعادة محاولة الفاشل',editorLabel:'معاينة البيانات الوصفية ومحررها',closeEditor:'إغلاق محرر البيانات الوصفية',regenerateHint:'إنشاء الكلمات المفتاحية أو إعادة إنشائها باستخدام نموذج الذكاء الاصطناعي الحالي',previous:'أصل البيانات الوصفية السابق',previousTitle:'الأصل السابق',selectAsset:'اختيار أصل البيانات الوصفية',next:'أصل البيانات الوصفية التالي',nextTitle:'الأصل التالي',ready:'جاهز',noMetadata:'لا توجد بيانات وصفية',metadataReady:'البيانات الوصفية جاهزة',titleLabel:'عنوان البيانات الوصفية',descriptionLabel:'وصف البيانات الوصفية',descriptionPlaceholder:'وصف السوق',categoryLabel:(market:string)=>`فئة بيانات ${market} الوصفية`,selectCategory:(market:string)=>`اختر فئة ${market}`,applyAll:'تطبيق على الكل',applyAllTitle:'تطبيق هذه الفئة على جميع الأصول المُنشأة',autoSave:'تُحفظ التغييرات عند مغادرة الحقل، وتُحفظ تغييرات الفئة فورًا.',keywordsLabel:'الكلمات المفتاحية للبيانات الوصفية',removeDuplicates:'إزالة التكرارات',sort:'ترتيب A–Z',validation:'التحقق',copyKeywords:'نسخ جميع الكلمات المفتاحية كقائمة مفصولة بفواصل',generating:'جارٍ إنشاء البيانات الوصفية',metadataStatus:(status:string)=>`البيانات الوصفية: ${status}`,previewEdit:'معاينة وتحرير',analyzing:'يحلل الذكاء الاصطناعي الأصول',defaultModel:'النموذج الافتراضي',generateAllTitle:'إنشاء بيانات وصفية لكل أصل محوّل',downloadCsvTitle:'تنزيل CSV متوافق مع السوق',targetMarketplace:'سوق الميكروستوك المستهدف',noExports:'لا توجد صادرات ZIP بعد.',items:'عناصر',downloadExport:'تنزيل',exportHistory:'سجل التصدير',showExports:'إظهار الصادرات',hideExports:'إخفاء الصادرات',loadingExports:'جارٍ تحميل الصادرات…',vectorizationFailed:'فشل التحويل المتجهي',downloadSvg:'تنزيل SVG'}
  } as Record<string, any>)[locale];
  const appExtraText = ({
    en:{loadingWorkspace:'Loading workspace…',downloadSvg:'Download SVG vector file',downloadEps:'Download EPS 10 file for CorelDRAW and Adobe Illustrator',clearQueue:'Clear queue',resetToken:'Paste reset token',newPassword:'Enter at least 8 characters',close:'Close',original:'Original image',thumbnail:'Thumbnail',adminConsole:'Owner Admin Console',avatar:'Avatar',toggleTheme:'Toggle theme',inviteSignIn:'Sign in or create an account with the invited email to join the workspace.',verifyFailed:'Email verification failed.',verifySuccess:'Email verified successfully.',inviteFailed:'Could not accept workspace invitation.',resetFailed:'Password reset failed.',resetRequestFailed:'Could not request password reset.',authFailed:'Authentication failed. Please check your credentials.',googleFailed:'Google sign-in failed on server.',requestFailed:(status:number)=>`Request failed (${status}).`,workspaceFailed:'Workspace operation failed.',billingUnavailable:'Billing portal unavailable.',aiSettingsLoadFailed:'Could not load workspace AI settings.',connectionFailed:'Connection test failed.',aiSettingsSaveFailed:'Could not save workspace AI settings.',metadataFailed:'Metadata generation failed.',batchMetadataFailed:'Batch metadata generation failed.',retryFailed:'Retry failed.',csvExportFailed:'CSV export failed.',exportHistoryFailed:'Could not load export history.',exportDownloadFailed:'Export download failed.',metadataSaveFailed:'Could not save metadata.',bulkCategoryFailed:'Bulk category update failed.',mobileUpload:'Upload',mobileSettings:'Settings',mobileMetadata:'Metadata',mobileQueue:'Queue',mobileConvert:'Convert',mobileExport:'Export',splitView:'Split Slider',splitViewTitle:'Interactive before/after comparison slider',share:'Share',shareTitle:'Share vector artwork'},
    id:{loadingWorkspace:'Memuat workspace…',downloadSvg:'Unduh file vektor SVG',downloadEps:'Unduh file EPS 10 untuk CorelDRAW dan Adobe Illustrator',clearQueue:'Kosongkan antrean',resetToken:'Tempel token reset',newPassword:'Masukkan minimal 8 karakter',close:'Tutup',original:'Gambar asli',thumbnail:'Gambar mini',adminConsole:'Konsol Admin Pemilik',avatar:'Avatar',toggleTheme:'Ganti tema',inviteSignIn:'Masuk atau buat akun dengan email yang diundang untuk bergabung ke workspace.',verifyFailed:'Verifikasi email gagal.',verifySuccess:'Email berhasil diverifikasi.',inviteFailed:'Undangan workspace tidak dapat diterima.',resetFailed:'Pengaturan ulang kata sandi gagal.',resetRequestFailed:'Permintaan pengaturan ulang kata sandi gagal.',authFailed:'Autentikasi gagal. Periksa kembali kredensial Anda.',googleFailed:'Login Google gagal di server.',requestFailed:(status:number)=>`Permintaan gagal (${status}).`,workspaceFailed:'Operasi workspace gagal.',billingUnavailable:'Portal tagihan tidak tersedia.',aiSettingsLoadFailed:'Pengaturan AI workspace gagal dimuat.',connectionFailed:'Uji koneksi gagal.',aiSettingsSaveFailed:'Pengaturan AI workspace gagal disimpan.',metadataFailed:'Pembuatan metadata gagal.',batchMetadataFailed:'Pembuatan metadata batch gagal.',retryFailed:'Percobaan ulang gagal.',csvExportFailed:'Ekspor CSV gagal.',exportHistoryFailed:'Riwayat ekspor gagal dimuat.',exportDownloadFailed:'Unduhan ekspor gagal.',metadataSaveFailed:'Metadata gagal disimpan.',bulkCategoryFailed:'Pembaruan kategori massal gagal.',mobileUpload:'Unggah',mobileSettings:'Pengaturan',mobileMetadata:'Metadata',mobileQueue:'Antrean',mobileConvert:'Konversi',mobileExport:'Ekspor',splitView:'Slider Geser',splitViewTitle:'Slider perbandingan sebelum/sesudah interaktif',share:'Bagikan',shareTitle:'Bagikan karya seni vektor'},
    'zh-cn':{loadingWorkspace:'正在加载工作区…',downloadSvg:'下载 SVG 矢量文件',downloadEps:'下载适用于 CorelDRAW 和 Adobe Illustrator 的 EPS 10 文件',clearQueue:'清空队列',resetToken:'粘贴重置令牌',newPassword:'请输入至少 8 个字符',close:'关闭',original:'原始图像',thumbnail:'缩略图',adminConsole:'所有者管理控制台',avatar:'头像',toggleTheme:'切换主题',inviteSignIn:'请使用受邀邮箱登录或创建账户以加入工作区。',verifyFailed:'邮箱验证失败。',verifySuccess:'邮箱验证成功。',inviteFailed:'无法接受工作区邀请。',resetFailed:'密码重置失败。',resetRequestFailed:'无法申请重置密码。',authFailed:'身份验证失败。请检查登录信息。',googleFailed:'服务器端 Google 登录失败。',requestFailed:(status:number)=>`请求失败 (${status})。`,workspaceFailed:'工作区操作失败。',billingUnavailable:'账单门户不可用。',aiSettingsLoadFailed:'无法加载工作区 AI 设置。',connectionFailed:'连接测试失败。',aiSettingsSaveFailed:'无法保存工作区 AI 设置。',metadataFailed:'元数据生成失败。',batchMetadataFailed:'批量元数据生成失败。',retryFailed:'重试失败。',csvExportFailed:'CSV 导出失败。',exportHistoryFailed:'无法加载导出历史。',exportDownloadFailed:'导出文件下载失败。',metadataSaveFailed:'无法保存元数据。',bulkCategoryFailed:'批量更新分类失败。',mobileUpload:'上传',mobileSettings:'设置',mobileMetadata:'元数据',mobileQueue:'队列',mobileConvert:'转换',mobileExport:'导出',splitView:'滑动对比',splitViewTitle:'交互式前后对比滑块',share:'分享',shareTitle:'分享矢量作品'},
    ja:{loadingWorkspace:'ワークスペースを読み込み中…',downloadSvg:'SVG ベクターファイルをダウンロード',downloadEps:'CorelDRAW・Adobe Illustrator 向け EPS 10 ファイルをダウンロード',clearQueue:'キューを空にする',resetToken:'再設定トークンを貼り付け',newPassword:'8文字以上で入力してください',close:'閉じる',original:'元画像',thumbnail:'サムネイル',adminConsole:'所有者管理コンソール',avatar:'アバター',toggleTheme:'テーマを切り替え',inviteSignIn:'招待されたメールアドレスでログインするかアカウントを作成して、ワークスペースに参加してください。',verifyFailed:'メールアドレスを確認できませんでした。',verifySuccess:'メールアドレスを確認しました。',inviteFailed:'ワークスペースへの招待を承認できませんでした。',resetFailed:'パスワードの再設定に失敗しました。',resetRequestFailed:'パスワード再設定をリクエストできませんでした。',authFailed:'認証に失敗しました。ログイン情報を確認してください。',googleFailed:'サーバーで Google ログインに失敗しました。',requestFailed:(status:number)=>`リクエストに失敗しました (${status})。`,workspaceFailed:'ワークスペース操作に失敗しました。',billingUnavailable:'請求ポータルを利用できません。',aiSettingsLoadFailed:'ワークスペースの AI 設定を読み込めませんでした。',connectionFailed:'接続テストに失敗しました。',aiSettingsSaveFailed:'ワークスペースの AI 設定を保存できませんでした。',metadataFailed:'メタデータ生成に失敗しました。',batchMetadataFailed:'メタデータの一括生成に失敗しました。',retryFailed:'再試行に失敗しました。',csvExportFailed:'CSV のエクスポートに失敗しました。',exportHistoryFailed:'エクスポート履歴を読み込めませんでした。',exportDownloadFailed:'エクスポートのダウンロードに失敗しました。',metadataSaveFailed:'メタデータを保存できませんでした。',bulkCategoryFailed:'カテゴリーの一括更新に失敗しました。',mobileUpload:'アップロード',mobileSettings:'設定',mobileMetadata:'メタデータ',mobileQueue:'キュー',mobileConvert:'変換',mobileExport:'エクスポート',splitView:'スライダー比較',splitViewTitle:'インタラクティブな比較スライダー',share:'共有',shareTitle:'ベクターアートを共有'},
    ko:{loadingWorkspace:'워크스페이스 불러오는 중…',downloadSvg:'SVG 벡터 파일 다운로드',downloadEps:'CorelDRAW 및 Adobe Illustrator용 EPS 10 파일 다운로드',clearQueue:'대기열 비우기',resetToken:'재설정 토큰 붙여넣기',newPassword:'8자 이상 입력하세요',close:'닫기',original:'원본 이미지',thumbnail:'썸네일',adminConsole:'소유자 관리자 콘솔',avatar:'아바타',toggleTheme:'테마 전환',inviteSignIn:'초대받은 이메일로 로그인하거나 계정을 만들어 워크스페이스에 참여하세요.',verifyFailed:'이메일 인증에 실패했습니다.',verifySuccess:'이메일 인증이 완료되었습니다.',inviteFailed:'워크스페이스 초대를 수락하지 못했습니다.',resetFailed:'비밀번호 재설정에 실패했습니다.',resetRequestFailed:'비밀번호 재설정을 요청하지 못했습니다.',authFailed:'인증에 실패했습니다. 로그인 정보를 확인하세요.',googleFailed:'서버에서 Google 로그인에 실패했습니다.',requestFailed:(status:number)=>`요청 실패 (${status}).`,workspaceFailed:'워크스페이스 작업에 실패했습니다.',billingUnavailable:'결제 포털을 사용할 수 없습니다.',aiSettingsLoadFailed:'워크스페이스 AI 설정을 불러오지 못했습니다.',connectionFailed:'연결 테스트에 실패했습니다.',aiSettingsSaveFailed:'워크스페이스 AI 설정을 저장하지 못했습니다.',metadataFailed:'메타데이터 생성에 실패했습니다.',batchMetadataFailed:'메타데이터 일괄 생성에 실패했습니다.',retryFailed:'재시도에 실패했습니다.',csvExportFailed:'CSV 내보내기에 실패했습니다.',exportHistoryFailed:'내보내기 기록을 불러오지 못했습니다.',exportDownloadFailed:'내보내기 다운로드에 실패했습니다.',metadataSaveFailed:'메타데이터를 저장하지 못했습니다.',bulkCategoryFailed:'카테고리 일괄 업데이트에 실패했습니다.',mobileUpload:'업로드',mobileSettings:'설정',mobileMetadata:'메타데이터',mobileQueue:'대기열',mobileConvert:'변환',mobileExport:'내보내기',splitView:'슬라이더 비교',splitViewTitle:'대화형 전후 비교 슬라이더',share:'공유',shareTitle:'벡터 아트 공유'},
    es:{loadingWorkspace:'Cargando espacio de trabajo…',downloadSvg:'Descargar archivo vectorial SVG',downloadEps:'Descargar archivo EPS 10 para CorelDRAW y Adobe Illustrator',clearQueue:'Vaciar cola',resetToken:'Pegar token de restablecimiento',newPassword:'Introduce al menos 8 caracteres',close:'Cerrar',original:'Imagen original',thumbnail:'Miniatura',adminConsole:'Consola de administración del propietario',avatar:'Avatar',toggleTheme:'Cambiar tema',inviteSignIn:'Inicia sesión o crea una cuenta con el correo invitado para unirte al espacio de trabajo.',verifyFailed:'No se pudo verificar el correo.',verifySuccess:'Correo verificado correctamente.',inviteFailed:'No se pudo aceptar la invitación al espacio de trabajo.',resetFailed:'No se pudo restablecer la contraseña.',resetRequestFailed:'No se pudo solicitar el restablecimiento de contraseña.',authFailed:'Falló la autenticación. Revisa tus credenciales.',googleFailed:'Falló el inicio de sesión con Google en el servidor.',requestFailed:(status:number)=>`La solicitud falló (${status}).`,workspaceFailed:'Falló la operación del espacio de trabajo.',billingUnavailable:'El portal de facturación no está disponible.',aiSettingsLoadFailed:'No se pudo cargar la configuración de IA del espacio de trabajo.',connectionFailed:'Falló la prueba de conexión.',aiSettingsSaveFailed:'No se pudo guardar la configuración de IA del espacio de trabajo.',metadataFailed:'Falló la generación de metadatos.',batchMetadataFailed:'Falló la generación de metadatos por lote.',retryFailed:'Falló el nuevo intento.',csvExportFailed:'Falló la exportación CSV.',exportHistoryFailed:'No se pudo cargar el historial de exportaciones.',exportDownloadFailed:'Falló la descarga de la exportación.',metadataSaveFailed:'No se pudieron guardar los metadatos.',bulkCategoryFailed:'Falló la actualización masiva de categorías.',mobileUpload:'Subir',mobileSettings:'Ajustes',mobileMetadata:'Metadatos',mobileQueue:'Cola',mobileConvert:'Convertir',mobileExport:'Exportar',splitView:'Deslizador',splitViewTitle:'Deslizador interactivo de comparación antes/después',share:'Compartir',shareTitle:'Compartir arte vectorial'},
    'pt-br':{loadingWorkspace:'Carregando espaço de trabalho…',downloadSvg:'Baixar arquivo vetorial SVG',downloadEps:'Baixar arquivo EPS 10 para CorelDRAW e Adobe Illustrator',clearQueue:'Limpar fila',resetToken:'Cole o token de redefinição',newPassword:'Digite pelo menos 8 caracteres',close:'Fechar',original:'Imagem original',thumbnail:'Miniatura',adminConsole:'Painel administrativo do proprietário',avatar:'Avatar',toggleTheme:'Alternar tema',inviteSignIn:'Entre ou crie uma conta com o e-mail convidado para participar do espaço de trabalho.',verifyFailed:'Não foi possível verificar o e-mail.',verifySuccess:'E-mail verificado com sucesso.',inviteFailed:'Não foi possível aceitar o convite para o espaço de trabalho.',resetFailed:'Não foi possível redefinir a senha.',resetRequestFailed:'Não foi possível solicitar a redefinição da senha.',authFailed:'Falha na autenticação. Verifique suas credenciais.',googleFailed:'Falha no login com Google no servidor.',requestFailed:(status:number)=>`Falha na solicitação (${status}).`,workspaceFailed:'Falha na operação do espaço de trabalho.',billingUnavailable:'O portal de cobrança não está disponível.',aiSettingsLoadFailed:'Não foi possível carregar as configurações de IA do espaço de trabalho.',connectionFailed:'Falha no teste de conexão.',aiSettingsSaveFailed:'Não foi possível salvar as configurações de IA do espaço de trabalho.',metadataFailed:'Falha na geração de metadados.',batchMetadataFailed:'Falha na geração de metadados em lote.',retryFailed:'Falha na nova tentativa.',csvExportFailed:'Falha na exportação CSV.',exportHistoryFailed:'Não foi possível carregar o historical de exportações.',exportDownloadFailed:'Falha ao baixar a exportação.',metadataSaveFailed:'Não foi possível salvar os metadados.',bulkCategoryFailed:'Falha na atualização de categorias em massa.',mobileUpload:'Enviar',mobileSettings:'Configurações',mobileMetadata:'Metadados',mobileQueue:'Fila',mobileConvert:'Converter',mobileExport:'Exportar',splitView:'Contraste Deslizante',splitViewTitle:'Contraste deslizante interativo antes/depois',share:'Compartilhar',shareTitle:'Compartilhar arte vetorial'},
    ru:{loadingWorkspace:'Загрузка рабочего пространства…',downloadSvg:'Скачать векторный файл SVG',downloadEps:'Скачать файл EPS 10 для CorelDRAW и Adobe Illustrator',clearQueue:'Очистить очередь',resetToken:'Вставьте токен сброса',newPassword:'Введите не менее 8 символов',close:'Закрыть',original:'Исходное изображение',thumbnail:'Миниатюра',adminConsole:'Панель администратора владельца',avatar:'Аватар',toggleTheme:'Переключить тему',inviteSignIn:'Войдите или создайте аккаунт с приглашённым адресом электронной почты, чтобы присоединиться к рабочему пространству.',verifyFailed:'Не удалось подтвердить адрес электронной почты.',verifySuccess:'Адрес электронной почты подтверждён.',inviteFailed:'Не удалось принять приглашение в рабочее пространство.',resetFailed:'Не удалось сбросить пароль.',resetRequestFailed:'Не удалось запросить сброс пароля.',authFailed:'Ошибка аутентификации. Проверьте учётные данные.',googleFailed:'Ошибка входа через Google на сервере.',requestFailed:(status:number)=>`Ошибка запроса (${status}).`,workspaceFailed:'Ошибка операции в рабочем пространстве.',billingUnavailable:'Портал оплаты недоступен.',aiSettingsLoadFailed:'Не удалось загрузить настройки ИИ рабочего пространства.',connectionFailed:'Проверка подключения завершилась ошибкой.',aiSettingsSaveFailed:'Не удалось сохранить настройки ИИ рабочего пространства.',metadataFailed:'Не удалось создать метаданные.',batchMetadataFailed:'Не удалось создать метаданные пакетно.',retryFailed:'Повторная попытка завершилась ошибкой.',csvExportFailed:'Не удалось экспортировать CSV.',exportHistoryFailed:'Не удалось загрузить историю экспорта.',exportDownloadFailed:'Не удалось скачать экспорт.',metadataSaveFailed:'Не удалось сохранить метаданные.',bulkCategoryFailed:'Не удалось массово обновить категории.',mobileUpload:'Загрузить',mobileSettings:'Настройки',mobileMetadata:'Метаданные',mobileQueue:'Очередь',mobileConvert:'Конвертировать',mobileExport:'Экспорт',splitView:'Слайдер сравнения',splitViewTitle:'Интерактивный слайдер сравнения до/после',share:'Поделиться',shareTitle:'Поделиться векторным файлом'},
    tr:{loadingWorkspace:'Çalışma alanı yükleniyor…',downloadSvg:'SVG vektör dosyasını indir',downloadEps:'CorelDRAW ve Adobe Illustrator için EPS 10 dosyasını indir',clearQueue:'Kuyruğu temizle',resetToken:'Sıfırlama belirtecini yapıştır',newPassword:'En az 8 karakter girin',close:'Kapat',original:'Özgün görsel',thumbnail:'Küçük resim',adminConsole:'Sahip Yönetim Paneli',avatar:'Avatar',toggleTheme:'Temayı değiştir',inviteSignIn:'Çalışma alanına katılmak için davet edilen e-postayla giriş yapın veya hesap oluşturun.',verifyFailed:'E-posta doğrulaması başarısız.',verifySuccess:'E-posta başarıyla doğrulandı.',inviteFailed:'Çalışma alanı daveti kabul edilemedi.',resetFailed:'Şifre sıfırlama başarısız.',resetRequestFailed:'Şifre sıfırlama isteği gönderilemedi.',authFailed:'Kimlik doğrulama başarısız. Bilgilerinizi kontrol edin.',googleFailed:'Sunucuda Google girişi başarısız.',requestFailed:(status:number)=>`İstek başarısız (${status}).`,workspaceFailed:'Çalışma alanı işlemi başarısız.',billingUnavailable:'Faturalandırma portalı kullanılamıyor.',aiSettingsLoadFailed:'Çalışma alanı AI ayarları yüklenemedi.',connectionFailed:'Bağlantı testi başarısız.',aiSettingsSaveFailed:'Çalışma alanı AI ayarları kaydedilemedi.',metadataFailed:'Meta veri oluşturma başarısız.',batchMetadataFailed:'Toplu meta veri oluşturma başarısız.',retryFailed:'Yeniden deneme başarısız.',csvExportFailed:'CSV dışa aktarma başarısız.',exportHistoryFailed:'Dışa aktarma geçmişi yüklenemedi.',exportDownloadFailed:'Dışa aktarım indirilemedi.',metadataSaveFailed:'Meta veri kaydedilemedi.',bulkCategoryFailed:'Toplu kategori güncellemesi başarısız.',mobileUpload:'Yükle',mobileSettings:'Ayarlar',mobileMetadata:'Meta Veri',mobileQueue:'Kuyruk',mobileConvert:'Dönüştür',mobileExport:'Dışa Aktar',splitView:'Kaydırıcı',splitViewTitle:'Etkileşimli öncesi/sonrası karşılaştırma kaydırıcısı',share:'Paylaş',shareTitle:'Vektör çalışmasını paylaş'},
    vi:{loadingWorkspace:'Đang tải không gian làm việc…',downloadSvg:'Tải tệp vector SVG',downloadEps:'Tải tệp EPS 10 cho CorelDRAW và Adobe Illustrator',clearQueue:'Xóa hàng đợi',resetToken:'Dán mã đặt lại',newPassword:'Nhập ít nhất 8 ký tự',close:'Đóng',original:'Ảnh gốc',thumbnail:'Ảnh thu nhỏ',adminConsole:'Bảng quản trị chủ sở hữu',avatar:'Ảnh đại diện',toggleTheme:'Đổi giao diện',inviteSignIn:'Đăng nhập hoặc tạo tài khoản bằng email được mời để tham gia không gian làm việc.',verifyFailed:'Xác minh email thất bại.',verifySuccess:'Xác minh email thành công.',inviteFailed:'Không thể chấp nhận lời mời vào không gian làm việc.',resetFailed:'Đặt lại mật khẩu thất bại.',resetRequestFailed:'Không thể yêu cầu đặt lại mật khẩu.',authFailed:'Xác thực thất bại. Hãy kiểm tra thông tin đăng nhập.',googleFailed:'Đăng nhập Google trên máy chủ thất bại.',requestFailed:(status:number)=>`Yêu cầu thất bại (${status}).`,workspaceFailed:'Thao tác không gian làm việc thất bại.',billingUnavailable:'Cổng thanh toán không khả dụng.',aiSettingsLoadFailed:'Không thể tải cài đặt AI của không gian làm việc.',connectionFailed:'Kiểm tra kết nối thất bại.',aiSettingsSaveFailed:'Không thể lưu cài đặt AI của không gian làm việc.',metadataFailed:'Tạo metadata thất bại.',batchMetadataFailed:'Tạo metadata hàng loạt thất bại.',retryFailed:'Thử lại thất bại.',csvExportFailed:'Xuất CSV thất bại.',exportHistoryFailed:'Không thể tải lịch sử xuất.',exportDownloadFailed:'Tải tệp xuất thất bại.',metadataSaveFailed:'Không thể lưu metadata.',bulkCategoryFailed:'Cập nhật danh mục hàng loạt thất bại.',mobileUpload:'Tải lên',mobileSettings:'Cài đặt',mobileMetadata:'Metadata',mobileQueue:'Hàng đợi',mobileConvert:'Chuyển đổi',mobileExport:'Xuất',splitView:'Thanh trượt so sánh',splitViewTitle:'Thanh trượt so sánh trước/sau tương tác',share:'Chia sẻ',shareTitle:'Chia sẻ tác phẩm vector'},
    th:{loadingWorkspace:'กำลังโหลดพื้นที่ทำงาน…',downloadSvg:'ดาวน์โหลดไฟล์เวกเตอร์ SVG',downloadEps:'ดาวน์โหลดไฟล์ EPS 10 สำหรับ CorelDRAW และ Adobe Illustrator',clearQueue:'ล้างคิว',resetToken:'วางโทเค็นรีเซ็ต',newPassword:'กรอกอย่างน้อย 8 ตัวอักษร',close:'ปิด',original:'ภาพต้นฉบับ',thumbnail:'ภาพขนาดย่อ',adminConsole:'แผงผู้ดูแลระบบเจ้าของ',avatar:'รูปโปรไฟล์',toggleTheme:'สลับธีม',inviteSignIn:'เข้าสู่ระบบหรือสร้างบัญชีด้วยอีเมลที่ได้รับเชิญเพื่อเข้าร่วมพื้นที่ทำงาน',verifyFailed:'ยืนยันอีเมลไม่สำเร็จ',verifySuccess:'ยืนยันอีเมลสำเร็จ',inviteFailed:'ไม่สามารถตอบรับคำเชิญเข้าพื้นที่ทำงาน',resetFailed:'รีเซ็ตรหัสผ่านไม่สำเร็จ',resetRequestFailed:'ไม่สามารถขอรีเซ็ตรหัสผ่าน',authFailed:'การยืนยันตัวตนล้มเหลว โปรดตรวจสอบข้อมูลเข้าสู่ระบบ',googleFailed:'การเข้าสู่ระบบ Google บนเซิร์ฟเวอร์ล้มเหลว',requestFailed:(status:number)=>`คำขอล้มเหลว (${status})`,workspaceFailed:'การดำเนินการพื้นที่ทำงานล้มเหลว',billingUnavailable:'พอร์ทัลการเรียกเก็บเงินไม่พร้อมใช้งาน',aiSettingsLoadFailed:'โหลดการตั้งค่า AI ของพื้นที่ทำงานไม่สำเร็จ',connectionFailed:'ทดสอบการเชื่อมต่อไม่สำเร็จ',aiSettingsSaveFailed:'บันทึกการตั้งค่า AI ของพื้นที่ทำงานไม่สำเร็จ',metadataFailed:'สร้างเมทาดาทาไม่สำเร็จ',batchMetadataFailed:'สร้างเมทาดาทาแบบกลุ่มไม่สำเร็จ',retryFailed:'ลองใหม่ไม่สำเร็จ',csvExportFailed:'ส่งออก CSV ไม่สำเร็จ',exportHistoryFailed:'โหลดประวัติการส่งออกไม่สำเร็จ',exportDownloadFailed:'ดาวน์โหลดไฟล์ส่งออกไม่สำเร็จ',metadataSaveFailed:'บันทึกเมทาดาทาไม่สำเร็จ',bulkCategoryFailed:'อัปเดตหมวดหมู่แบบกลุ่มไม่สำเร็จ',mobileUpload:'อัปโหลด',mobileSettings:'การตั้งค่า',mobileMetadata:'เมทาดาทา',mobileQueue:'คิว',mobileConvert:'แปลง',mobileExport:'ส่งออก',splitView:'สไลเดอร์เปรียบเทียบ',splitViewTitle:'แถบเลื่อนเปรียบเทียบก่อน/หลังแบบโต้ตอบ',share:'แชร์',shareTitle:'แชร์งานเวกเตอร์'},
    ar:{loadingWorkspace:'جارٍ تحميل مساحة العمل…',downloadSvg:'تنزيل ملف SVG المتجهي',downloadEps:'تنزيل ملف EPS 10 لـ CorelDRAW وAdobe Illustrator',clearQueue:'مسح الطابور',resetToken:'ألصق رمز إعادة التعيين',newPassword:'أدخل 8 أحرف على الأقل',close:'إغلاق',original:'الصورة الأصلية',thumbnail:'صورة مصغرة',adminConsole:'لوحة تحكم المالك',avatar:'الصورة الشخصية',toggleTheme:'تبديل السمة',inviteSignIn:'سجّل الدخول أو أنشئ حسابًا بالبريد المدعو للانضمام إلى مساحة العمل',verifyFailed:'فشل التحقق من البريد الإلكتروني',verifySuccess:'تم التحقق من البريد الإلكتروني',inviteFailed:'تعذر قبول دعوة مساحة العمل',resetFailed:'فشلت إعادة تعيين كلمة المرور',resetRequestFailed:'تعذر طلب إعادة تعيين كلمة المرور',authFailed:'فشلت المصادقة. تحقق من بيانات تسجيل الدخول.',googleFailed:'فشل تسجيل الدخول إلى Google على الخادم',requestFailed:(status:number)=>`فشل الطلب (${status})`,workspaceFailed:'فشلت عملية مساحة العمل',billingUnavailable:'بوابة الفوترة غير متاحة',aiSettingsLoadFailed:'تعذر تحميل إعدادات الذكاء الاصطناعي لمساحة العمل',connectionFailed:'فشل اختبار الاتصال',aiSettingsSaveFailed:'تعذر حفظ إعدادات الذكاء الاصطناعي لمساحة العمل',metadataFailed:'فشل إنشاء البيانات الوصفية',batchMetadataFailed:'فشل إنشاء البيانات الوصفية الجماعية',retryFailed:'فشلت إعادة المحاولة',csvExportFailed:'فشل تصدير CSV',exportHistoryFailed:'تعذر تحميل سجل التصدير',exportDownloadFailed:'فشل تنزيل ملف التصدير',metadataSaveFailed:'فشل حفظ البيانات الوصفية',bulkCategoryFailed:'فشل تحديث الفئة جماعيًا',mobileUpload:'رفع',mobileSettings:'الإعدادات',mobileMetadata:'البيانات الوصفية',mobileQueue:'الطابور',mobileConvert:'تحويل',mobileExport:'تصدير',splitView:'شريط مقارنة',splitViewTitle:'شريط تمرير تفاعلي للمقارنة قبل وبعد',share:'مشاركة',shareTitle:'مشاركة العمل الفني المتجهي'}
  } as Record<string, any>)[locale];
  const spanishRuntimeText = {
    authError:'Se produjo un error durante la autenticación.', signedGoogle:(name:string,email:string)=>`Sesión iniciada como ${name} (${email}) mediante Google`, googleAuthFailed:'La autenticación con Google ha fallado.', googlePromptFailed:'La ventana de acceso de Google ha fallado.', googleUnavailable:'Los servicios de identidad de Google no están disponibles.',
    signInAiTest:'Inicia sesión para probar la configuración cifrada de IA del espacio de trabajo.', aiAuthenticated:(provider:string)=>`Autenticación correcta con ${provider}.`, serverUnreachable:'No se pudo contactar con el servidor de Vectria.', signInAiSave:'Inicia sesión para guardar la configuración de IA del espacio de trabajo.', maxApiKeys:'Se permite un máximo de 10 claves API.', aiSettingsSaved:(provider:string)=>`Configuración de IA guardada para ${provider}.`, resetKeysConfirm:(provider:string)=>`¿Eliminar todas las claves API de ${provider} guardadas en este espacio de trabajo? Las claves de otros proveedores se conservarán.`, keysRemoved:(provider:string)=>`Se eliminaron todas las claves API guardadas de ${provider}.`, resetKeysFailed:'No se pudieron restablecer las claves API guardadas.',
    metadataError:'Error al generar metadatos', batchMetadataError:'Error de metadatos por lotes', metadataJobStatus:(status:string,count:number)=>`Tarea de metadatos ${status}. ${count} recursos fallaron.`, categoryApplied:(count:number)=>`Categoría aplicada a ${count} recursos.`, csvExported:(label:string,count:number)=>`CSV de metadatos ${label} exportado para ${count} elementos.`, convertBeforeCsv:'Convierte al menos una imagen antes de exportar el CSV.',
    engineOutdated:'El worker de vectorización del backend está desactualizado. Reinicia el backend e inténtalo de nuevo.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`Detectado ${category}: ${colors} grupos de color, error de paleta ${error}, tolerancia de curva ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`Lote completado: ${ok} ${ok===1?'imagen vectorizada':'imágenes vectorizadas'}.`:`Lote completado: ${ok} correctos, ${failed} fallidos.`,
    downloadAuth:'Crea una cuenta gratuita o inicia sesión para descargar tus vectores (incluye 10 créditos gratis).', convertBeforeZip:'Convierte al menos una imagen antes de exportar el ZIP.', zipGenerateFailed:'No se pudo generar el paquete ZIP.', zipDownloadFailed:'El paquete de exportación se creó, pero no pudo descargarse.', zipExported:(count:number)=>`${count} vectores exportados como paquete ZIP.`, zipError:'Error de exportación ZIP',
    upgraded:(plan:string,credits:number)=>`Actualización al plan ${plan} completada. Saldo de créditos: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`Plan activado: ${plan} (${credits.toLocaleString()} créditos).`,
  };
  const portugueseRuntimeText = {
    authError:'Ocorreu um erro durante a autenticação.', signedGoogle:(name:string,email:string)=>`Conectado como ${name} (${email}) pelo Google`, googleAuthFailed:'A autenticação com o Google falhou.', googlePromptFailed:'A janela de login do Google falhou.', googleUnavailable:'Os Serviços de Identidade do Google não estão disponíveis.',
    signInAiTest:'Entre para testar as configurações criptografadas de IA do espaço de trabalho.', aiAuthenticated:(provider:string)=>`Autenticação com ${provider} concluída.`, serverUnreachable:'Não foi possível acessar o servidor da Vectria.', signInAiSave:'Entre para salvar as configurações de IA do espaço de trabalho.', maxApiKeys:'É permitido um máximo de 10 chaves de API.', aiSettingsSaved:(provider:string)=>`Configurações de IA do espaço de trabalho salvas para ${provider}.`, resetKeysConfirm:(provider:string)=>`Remover todas as chaves de API salvas de ${provider} deste espaço de trabalho? As chaves dos outros provedores serão mantidas.`, keysRemoved:(provider:string)=>`Todas as chaves de API salvas de ${provider} foram removidas.`, resetKeysFailed:'Não foi possível redefinir as chaves de API salvas.',
    metadataError:'Erro ao gerar metadados', batchMetadataError:'Erro de metadados em lote', metadataJobStatus:(status:string,count:number)=>`Tarefa de metadados ${status}. ${count} recursos falharam.`, categoryApplied:(count:number)=>`Categoria aplicada a ${count} recursos.`, csvExported:(label:string,count:number)=>`CSV de metadados ${label} exportado para ${count} itens.`, convertBeforeCsv:'Converta pelo menos uma imagem antes de exportar o CSV.',
    engineOutdated:'O worker de vetorização do backend está desatualizado. Reinicie o backend e tente novamente.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`Detectado ${category}: ${colors} grupos de cores, erro de paleta ${error}, tolerância de curva ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`Lote concluído: ${ok} ${ok===1?'imagem vetorizada':'imagens vetorizadas'}.`:`Lote concluído: ${ok} com sucesso, ${failed} com falha.`,
    downloadAuth:'Crie uma conta gratuita ou entre para baixar seus vetores (inclui 10 créditos grátis).', convertBeforeZip:'Converta pelo menos uma imagem antes de exportar o ZIP.', zipGenerateFailed:'Não foi possível gerar o pacote ZIP.', zipDownloadFailed:'O pacote de exportação foi criado, mas não pôde ser baixado.', zipExported:(count:number)=>`${count} vetores exportados como pacote ZIP.`, zipError:'Erro na exportação ZIP',
    upgraded:(plan:string,credits:number)=>`Upgrade para o plano ${plan} concluído. Saldo de créditos: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`Plano ativado: ${plan} (${credits.toLocaleString()} créditos).`,
  };
  const russianRuntimeText = {
    authError:'Во время аутентификации произошла ошибка.', signedGoogle:(name:string,email:string)=>`Выполнен вход как ${name} (${email}) через Google`, googleAuthFailed:'Ошибка аутентификации Google.', googlePromptFailed:'Не удалось открыть окно входа Google.', googleUnavailable:'Службы идентификации Google недоступны.',
    signInAiTest:'Войдите, чтобы проверить зашифрованные настройки ИИ рабочего пространства.', aiAuthenticated:(provider:string)=>`Аутентификация через ${provider} выполнена.`, serverUnreachable:'Не удалось связаться с сервером Vectria.', signInAiSave:'Войдите, чтобы сохранить настройки ИИ рабочего пространства.', maxApiKeys:'Допускается не более 10 API-ключей.', aiSettingsSaved:(provider:string)=>`Настройки ИИ для ${provider} сохранены.`, resetKeysConfirm:(provider:string)=>`Удалить все сохранённые API-ключи ${provider} из этого рабочего пространства? Ключи других провайдеров сохранятся.`, keysRemoved:(provider:string)=>`Все сохранённые API-ключи ${provider} удалены.`, resetKeysFailed:'Не удалось сбросить сохранённые API-ключи.',
    metadataError:'Ошибка создания метаданных', batchMetadataError:'Ошибка пакетных метаданных', metadataJobStatus:(status:string,count:number)=>`Задача метаданных: ${status}. Ошибок ресурсов: ${count}.`, categoryApplied:(count:number)=>`Категория применена к ${count} ресурсам.`, csvExported:(label:string,count:number)=>`CSV метаданных ${label} экспортирован для ${count} элементов.`, convertBeforeCsv:'Перед экспортом CSV конвертируйте хотя бы одно изображение.',
    engineOutdated:'Backend-обработчик векторизации устарел. Перезапустите backend и повторите попытку.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`Определено: ${category}; групп цветов: ${colors}, ошибка палитры: ${error}, допуск кривых: ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`Пакет завершён: векторизовано изображений — ${ok}.`:`Пакет завершён: успешно — ${ok}, с ошибкой — ${failed}.`,
    downloadAuth:'Создайте бесплатный аккаунт или войдите, чтобы скачать векторы (включает 10 бесплатных кредитов).', convertBeforeZip:'Перед экспортом ZIP конвертируйте хотя бы одно изображение.', zipGenerateFailed:'Не удалось создать ZIP-пакет.', zipDownloadFailed:'Пакет экспорта создан, но скачать его не удалось.', zipExported:(count:number)=>`Векторов экспортировано в ZIP: ${count}.`, zipError:'Ошибка экспорта ZIP',
    upgraded:(plan:string,credits:number)=>`Переход на план ${plan} выполнен. Баланс кредитов: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`План активирован: ${plan} (${credits.toLocaleString()} кредитов).`,
  };
  const turkishRuntimeText = {
    authError:'Kimlik doğrulama sırasında bir hata oluştu.', signedGoogle:(name:string,email:string)=>`Google ile ${name} (${email}) olarak giriş yapıldı`, googleAuthFailed:'Google kimlik doğrulaması başarısız oldu.', googlePromptFailed:'Google giriş penceresi başarısız oldu.', googleUnavailable:'Google Kimlik Hizmetleri kullanılamıyor.',
    signInAiTest:'Şifrelenmiş çalışma alanı AI ayarlarını test etmek için giriş yapın.', aiAuthenticated:(provider:string)=>`${provider} ile kimlik doğrulandı.`, serverUnreachable:'Vectria sunucusuna ulaşılamadı.', signInAiSave:'Çalışma alanı AI ayarlarını kaydetmek için giriş yapın.', maxApiKeys:'En fazla 10 API anahtarına izin verilir.', aiSettingsSaved:(provider:string)=>`${provider} için çalışma alanı AI ayarları kaydedildi.`, resetKeysConfirm:(provider:string)=>`Bu çalışma alanındaki kayıtlı tüm ${provider} API anahtarları kaldırılsın mı? Diğer sağlayıcıların anahtarları korunacaktır.`, keysRemoved:(provider:string)=>`Kayıtlı tüm ${provider} API anahtarları kaldırıldı.`, resetKeysFailed:'Kayıtlı API anahtarları sıfırlanamadı.',
    metadataError:'Meta veri oluşturma hatası', batchMetadataError:'Toplu meta veri hatası', metadataJobStatus:(status:string,count:number)=>`Meta veri görevi: ${status}. ${count} varlık başarısız oldu.`, categoryApplied:(count:number)=>`Kategori ${count} varlığa uygulandı.`, csvExported:(label:string,count:number)=>`${count} öğe için ${label} meta veri CSV dosyası dışa aktarıldı.`, convertBeforeCsv:'CSV dışa aktarmadan önce en az bir görüntüyü dönüştürün.',
    engineOutdated:'Backend vektörleştirme worker sürümü eski. Backend hizmetini yeniden başlatıp tekrar deneyin.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`${category} algılandı: ${colors} renk grubu, palet hatası ${error}, eğri toleransı ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`Toplu işlem tamamlandı: ${ok} görüntü vektörleştirildi.`:`Toplu işlem tamamlandı: ${ok} başarılı, ${failed} başarısız.`,
    downloadAuth:'Vektörlerinizi indirmek için ücretsiz hesap oluşturun veya giriş yapın (10 ücretsiz kredi içerir).', convertBeforeZip:'ZIP dışa aktarmadan önce en az bir görüntüyü dönüştürün.', zipGenerateFailed:'ZIP paketi oluşturulamadı.', zipDownloadFailed:'Dışa aktarma paketi oluşturuldu ancak indirilemedi.', zipExported:(count:number)=>`${count} vektör ZIP paketi olarak dışa aktarıldı.`, zipError:'ZIP dışa aktarma hatası',
    upgraded:(plan:string,credits:number)=>`${plan} planına yükseltme tamamlandı. Kredi bakiyesi: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`Plan etkinleştirildi: ${plan} (${credits.toLocaleString()} kredi).`,
  };
  const vietnameseRuntimeText = {
    authError:'Đã xảy ra lỗi trong quá trình xác thực.', signedGoogle:(name:string,email:string)=>`Đã đăng nhập với tên ${name} (${email}) qua Google`, googleAuthFailed:'Xác thực Google thất bại.', googlePromptFailed:'Cửa sổ đăng nhập Google thất bại.', googleUnavailable:'Dịch vụ Nhận dạng Google không khả dụng.',
    signInAiTest:'Đăng nhập để kiểm tra thiết lập AI được mã hóa của không gian làm việc.', aiAuthenticated:(provider:string)=>`Đã xác thực thành công với ${provider}.`, serverUnreachable:'Không thể kết nối với máy chủ Vectria.', signInAiSave:'Đăng nhập để lưu thiết lập AI của không gian làm việc.', maxApiKeys:'Chỉ được phép tối đa 10 khóa API.', aiSettingsSaved:(provider:string)=>`Đã lưu thiết lập AI của không gian làm việc cho ${provider}.`, resetKeysConfirm:(provider:string)=>`Xóa tất cả khóa API ${provider} đã lưu khỏi không gian làm việc này? Khóa của các nhà cung cấp khác sẽ được giữ lại.`, keysRemoved:(provider:string)=>`Đã xóa tất cả khóa API ${provider} đã lưu.`, resetKeysFailed:'Không thể đặt lại các khóa API đã lưu.',
    metadataError:'Lỗi tạo metadata', batchMetadataError:'Lỗi metadata hàng loạt', metadataJobStatus:(status:string,count:number)=>`Tác vụ metadata ${status}. ${count} tài nguyên thất bại.`, categoryApplied:(count:number)=>`Đã áp dụng danh mục cho ${count} tài nguyên.`, csvExported:(label:string,count:number)=>`Đã xuất CSV metadata ${label} cho ${count} mục.`, convertBeforeCsv:'Hãy chuyển đổi ít nhất một ảnh trước khi xuất CSV.',
    engineOutdated:'Worker vector hóa của backend đã lỗi thời. Hãy khởi động lại backend rồi thử lại.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`Đã phát hiện ${category}: ${colors} nhóm màu, sai số bảng màu ${error}, dung sai đường cong ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`Đã hoàn tất lô: vector hóa ${ok} ảnh.`:`Đã hoàn tất lô: ${ok} thành công, ${failed} thất bại.`,
    downloadAuth:'Tạo tài khoản miễn phí hoặc đăng nhập để tải vector của bạn (bao gồm 10 tín dụng miễn phí).', convertBeforeZip:'Hãy chuyển đổi ít nhất một ảnh trước khi xuất ZIP.', zipGenerateFailed:'Không thể tạo gói ZIP.', zipDownloadFailed:'Gói xuất đã được tạo nhưng không thể tải xuống.', zipExported:(count:number)=>`Đã xuất ${count} vector dưới dạng gói ZIP.`, zipError:'Lỗi xuất ZIP',
    upgraded:(plan:string,credits:number)=>`Đã nâng cấp thành công lên gói ${plan}. Số dư tín dụng: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`Đã kích hoạt gói: ${plan} (${credits.toLocaleString()} tín dụng).`,
  };
  const thaiRuntimeText = {
    authError:'เกิดข้อผิดพลาดระหว่างการยืนยันตัวตน', signedGoogle:(name:string,email:string)=>`เข้าสู่ระบบเป็น ${name} (${email}) ผ่าน Google แล้ว`, googleAuthFailed:'การยืนยันตัวตนด้วย Google ล้มเหลว', googlePromptFailed:'หน้าต่างเข้าสู่ระบบ Google ล้มเหลว', googleUnavailable:'บริการ Google Identity ไม่พร้อมใช้งาน',
    signInAiTest:'เข้าสู่ระบบเพื่อทดสอบการตั้งค่า AI ที่เข้ารหัสของเวิร์กสเปซ', aiAuthenticated:(provider:string)=>`ยืนยันตัวตนกับ ${provider} สำเร็จ`, serverUnreachable:'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ Vectria ได้', signInAiSave:'เข้าสู่ระบบเพื่อบันทึกการตั้งค่า AI ของเวิร์กสเปซ', maxApiKeys:'อนุญาต API key ได้สูงสุด 10 คีย์', aiSettingsSaved:(provider:string)=>`บันทึกการตั้งค่า AI ของเวิร์กสเปซสำหรับ ${provider} แล้ว`, resetKeysConfirm:(provider:string)=>`ลบ API key ของ ${provider} ที่บันทึกไว้ทั้งหมดออกจากเวิร์กสเปซนี้หรือไม่ คีย์ของผู้ให้บริการอื่นจะยังคงอยู่`, keysRemoved:(provider:string)=>`ลบ API key ของ ${provider} ที่บันทึกไว้ทั้งหมดแล้ว`, resetKeysFailed:'ไม่สามารถรีเซ็ต API key ที่บันทึกไว้ได้',
    metadataError:'ข้อผิดพลาดในการสร้างเมทาดาทา', batchMetadataError:'ข้อผิดพลาดของเมทาดาทาแบบกลุ่ม', metadataJobStatus:(status:string,count:number)=>`งานเมทาดาทา: ${status} มี ${count} แอสเซตล้มเหลว`, categoryApplied:(count:number)=>`ใช้หมวดหมู่กับ ${count} แอสเซตแล้ว`, csvExported:(label:string,count:number)=>`ส่งออก CSV เมทาดาทา ${label} สำหรับ ${count} รายการแล้ว`, convertBeforeCsv:'โปรดแปลงภาพอย่างน้อยหนึ่งภาพก่อนส่งออก CSV',
    engineOutdated:'เวอร์ชัน worker สำหรับทำเวกเตอร์ของ backend ล้าสมัย โปรดเริ่มบริการ backend ใหม่แล้วลองอีกครั้ง', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`ตรวจพบ ${category}: ${colors} กลุ่มสี, ความคลาดเคลื่อนพาเลต ${error}, ค่าความคลาดเคลื่อนเส้นโค้ง ${tolerance}px`, batchComplete:(ok:number,failed:number)=>failed===0?`ประมวลผลชุดงานเสร็จแล้ว: ทำเวกเตอร์ ${ok} ภาพ`:`ประมวลผลชุดงานเสร็จแล้ว: สำเร็จ ${ok}, ล้มเหลว ${failed}`,
    downloadAuth:'สร้างบัญชีฟรีหรือเข้าสู่ระบบเพื่อดาวน์โหลดเวกเตอร์ของคุณ (รวมเครดิตฟรี 10 เครดิต)', convertBeforeZip:'โปรดแปลงภาพอย่างน้อยหนึ่งภาพก่อนส่งออก ZIP', zipGenerateFailed:'ไม่สามารถสร้างแพ็กเกจ ZIP ได้', zipDownloadFailed:'สร้างแพ็กเกจส่งออกแล้วแต่ไม่สามารถดาวน์โหลดได้', zipExported:(count:number)=>`ส่งออกเวกเตอร์ ${count} รายการเป็นแพ็กเกจ ZIP แล้ว`, zipError:'ข้อผิดพลาดในการส่งออก ZIP',
    upgraded:(plan:string,credits:number)=>`อัปเกรดเป็นแผน ${plan} สำเร็จ ยอดเครดิต: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`เปิดใช้งานแผน ${plan} แล้ว (${credits.toLocaleString()} เครดิต)`,
  };
  const arabicRuntimeText = {
    authError:'حدث خطأ أثناء المصادقة.', signedGoogle:(name:string,email:string)=>`تم تسجيل الدخول باسم ${name} (${email}) عبر Google`, googleAuthFailed:'فشلت المصادقة عبر Google.', googlePromptFailed:'فشلت نافذة تسجيل الدخول عبر Google.', googleUnavailable:'خدمات هوية Google غير متاحة.',
    signInAiTest:'سجّل الدخول لاختبار إعدادات الذكاء الاصطناعي المشفرة لمساحة العمل.', aiAuthenticated:(provider:string)=>`تمت المصادقة بنجاح مع ${provider}.`, serverUnreachable:'تعذر الوصول إلى خادم Vectria.', signInAiSave:'سجّل الدخول لحفظ إعدادات الذكاء الاصطناعي لمساحة العمل.', maxApiKeys:'يُسمح بحد أقصى 10 مفاتيح API.', aiSettingsSaved:(provider:string)=>`تم حفظ إعدادات الذكاء الاصطناعي لمساحة العمل لدى ${provider}.`, resetKeysConfirm:(provider:string)=>`هل تريد إزالة جميع مفاتيح API المحفوظة لدى ${provider} من مساحة العمل هذه؟ ستبقى مفاتيح المزودين الآخرين محفوظة.`, keysRemoved:(provider:string)=>`تمت إزالة جميع مفاتيح API المحفوظة لدى ${provider}.`, resetKeysFailed:'تعذرت إعادة تعيين مفاتيح API المحفوظة.',
    metadataError:'خطأ في إنشاء البيانات الوصفية', batchMetadataError:'خطأ في البيانات الوصفية المجمعة', metadataJobStatus:(status:string,count:number)=>`مهمة البيانات الوصفية: ${status}. فشل ${count} من الأصول.`, categoryApplied:(count:number)=>`تم تطبيق الفئة على ${count} من الأصول.`, csvExported:(label:string,count:number)=>`تم تصدير CSV للبيانات الوصفية ${label} لعدد ${count} من العناصر.`, convertBeforeCsv:'يرجى تحويل صورة واحدة على الأقل قبل تصدير CSV.',
    engineOutdated:'إصدار عامل التحويل إلى متجه في الخادم الخلفي قديم. أعد تشغيل الخادم الخلفي ثم حاول مرة أخرى.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`تم اكتشاف ${category}: مجموعات الألوان ${colors}، وخطأ لوحة الألوان ${error}، وتفاوت المنحنى ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`اكتملت الدفعة: تم تحويل ${ok} من الصور إلى متجهات.`:`اكتملت الدفعة: نجح ${ok} وفشل ${failed}.`,
    downloadAuth:'أنشئ حسابًا مجانيًا أو سجّل الدخول لتنزيل متجهاتك (يتضمن 10 اعتمادات مجانية).', convertBeforeZip:'يرجى تحويل صورة واحدة على الأقل قبل تصدير ZIP.', zipGenerateFailed:'تعذر إنشاء حزمة ZIP.', zipDownloadFailed:'تم إنشاء حزمة التصدير لكن تعذر تنزيلها.', zipExported:(count:number)=>`تم تصدير ${count} من المتجهات كحزمة ZIP.`, zipError:'خطأ في تصدير ZIP',
    upgraded:(plan:string,credits:number)=>`تمت الترقية إلى خطة ${plan} بنجاح. رصيد الاعتمادات: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`تم تفعيل الخطة: ${plan} (${credits.toLocaleString()} من الاعتمادات).`,
  };
  const chineseRuntimeText = {
    authError:'身份验证过程中发生错误。', signedGoogle:(name:string,email:string)=>`已通过 Google 以 ${name}（${email}）身份登录`, googleAuthFailed:'Google 身份验证失败。', googlePromptFailed:'Google 登录窗口失败。', googleUnavailable:'Google 身份服务不可用。',
    signInAiTest:'请登录以测试工作区的加密 AI 设置。', aiAuthenticated:(provider:string)=>`已成功通过 ${provider} 验证。`, serverUnreachable:'无法连接 Vectria 服务器。', signInAiSave:'请登录以保存工作区 AI 设置。', maxApiKeys:'最多允许 10 个 API 密钥。', aiSettingsSaved:(provider:string)=>`已保存 ${provider} 的工作区 AI 设置。`, resetKeysConfirm:(provider:string)=>`是否从此工作区移除所有已保存的 ${provider} API 密钥？其他提供商的密钥将继续保留。`, keysRemoved:(provider:string)=>`已移除所有已保存的 ${provider} API 密钥。`, resetKeysFailed:'无法重置已保存的 API 密钥。',
    metadataError:'元数据生成错误', batchMetadataError:'批量元数据错误', metadataJobStatus:(status:string,count:number)=>`元数据任务状态：${status}。${count} 个素材失败。`, categoryApplied:(count:number)=>`已将类别应用到 ${count} 个素材。`, csvExported:(label:string,count:number)=>`已为 ${count} 个项目导出 ${label} 元数据 CSV。`, convertBeforeCsv:'导出 CSV 前请至少转换一张图像。',
    engineOutdated:'后端矢量化工作进程版本过旧。请重启后端服务，然后重试。', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`检测到 ${category}：${colors} 个颜色组，调色板误差 ${error}，曲线容差 ${tolerance}px。`, batchComplete:(ok:number,failed:number)=>failed===0?`批次完成：已矢量化 ${ok} 张图像。`:`批次完成：${ok} 个成功，${failed} 个失败。`,
    downloadAuth:'创建免费账户或登录即可下载矢量文件（含 10 个免费额度）。', convertBeforeZip:'导出 ZIP 前请至少转换一张图像。', zipGenerateFailed:'无法生成 ZIP 压缩包。', zipDownloadFailed:'导出包已创建，但无法下载。', zipExported:(count:number)=>`已将 ${count} 个矢量文件导出为 ZIP 压缩包。`, zipError:'ZIP 导出错误',
    upgraded:(plan:string,credits:number)=>`已成功升级到 ${plan} 套餐。额度余额：${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`套餐已激活：${plan}（${credits.toLocaleString()} 个额度）。`,
  };
  const japaneseRuntimeText = {
    authError:'認証中にエラーが発生しました。', signedGoogle:(name:string,email:string)=>`Google 経由で ${name}（${email}）としてログインしました`, googleAuthFailed:'Google 認証に失敗しました。', googlePromptFailed:'Google ログイン画面を開けませんでした。', googleUnavailable:'Google Identity Services を利用できません。',
    signInAiTest:'暗号化されたワークスペース AI 設定をテストするにはログインしてください。', aiAuthenticated:(provider:string)=>`${provider} での認証に成功しました。`, serverUnreachable:'Vectria サーバーに接続できませんでした。', signInAiSave:'ワークスペース AI 設定を保存するにはログインしてください。', maxApiKeys:'API キーは最大 10 個まで登録できます。', aiSettingsSaved:(provider:string)=>`${provider} のワークスペース AI 設定を保存しました。`, resetKeysConfirm:(provider:string)=>`このワークスペースから保存済みの ${provider} API キーをすべて削除しますか？他のプロバイダーのキーは保持されます。`, keysRemoved:(provider:string)=>`保存済みの ${provider} API キーをすべて削除しました。`, resetKeysFailed:'保存済み API キーをリセットできませんでした。',
    metadataError:'メタデータ生成エラー', batchMetadataError:'一括メタデータエラー', metadataJobStatus:(status:string,count:number)=>`メタデータ処理：${status}。${count} 件の素材が失敗しました。`, categoryApplied:(count:number)=>`${count} 件の素材にカテゴリを適用しました。`, csvExported:(label:string,count:number)=>`${count} 件の ${label} メタデータ CSV を書き出しました。`, convertBeforeCsv:'CSV を書き出す前に、少なくとも 1 枚の画像を変換してください。',
    engineOutdated:'バックエンドのベクター化ワーカーが古いバージョンです。バックエンドを再起動して、もう一度お試しください。', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`${category} を検出：カラーグループ ${colors}、パレット誤差 ${error}、曲線許容差 ${tolerance}px。`, batchComplete:(ok:number,failed:number)=>failed===0?`バッチ完了：${ok} 枚をベクター化しました。`:`バッチ完了：成功 ${ok} 件、失敗 ${failed} 件。`,
    downloadAuth:'ベクターをダウンロードするには無料アカウントを作成するかログインしてください（無料 10 クレジット付き）。', convertBeforeZip:'ZIP を書き出す前に、少なくとも 1 枚の画像を変換してください。', zipGenerateFailed:'ZIP パッケージを生成できませんでした。', zipDownloadFailed:'書き出しパッケージは作成されましたが、ダウンロードできませんでした。', zipExported:(count:number)=>`${count} 件のベクターを ZIP パッケージとして書き出しました。`, zipError:'ZIP 書き出しエラー',
    upgraded:(plan:string,credits:number)=>`${plan} プランへのアップグレードが完了しました。クレジット残高：${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`プランを有効化しました：${plan}（${credits.toLocaleString()} クレジット）。`,
  };
  const koreanRuntimeText = {
    authError:'인증 중 오류가 발생했습니다.', signedGoogle:(name:string,email:string)=>`Google을 통해 ${name}(${email})님으로 로그인했습니다`, googleAuthFailed:'Google 인증에 실패했습니다.', googlePromptFailed:'Google 로그인 창을 열지 못했습니다.', googleUnavailable:'Google Identity Services를 사용할 수 없습니다.',
    signInAiTest:'암호화된 워크스페이스 AI 설정을 테스트하려면 로그인하세요.', aiAuthenticated:(provider:string)=>`${provider} 인증에 성공했습니다.`, serverUnreachable:'Vectria 서버에 연결할 수 없습니다.', signInAiSave:'워크스페이스 AI 설정을 저장하려면 로그인하세요.', maxApiKeys:'API 키는 최대 10개까지 등록할 수 있습니다.', aiSettingsSaved:(provider:string)=>`${provider} 워크스페이스 AI 설정을 저장했습니다.`, resetKeysConfirm:(provider:string)=>`이 워크스페이스에서 저장된 ${provider} API 키를 모두 제거하시겠습니까? 다른 제공업체의 키는 유지됩니다.`, keysRemoved:(provider:string)=>`저장된 ${provider} API 키를 모두 제거했습니다.`, resetKeysFailed:'저장된 API 키를 재설정할 수 없습니다.',
    metadataError:'메타데이터 생성 오류', batchMetadataError:'일괄 메타데이터 오류', metadataJobStatus:(status:string,count:number)=>`메타데이터 작업: ${status}. ${count}개 에셋이 실패했습니다.`, categoryApplied:(count:number)=>`${count}개 에셋에 카테고리를 적용했습니다.`, csvExported:(label:string,count:number)=>`${count}개 항목의 ${label} 메타데이터 CSV를 내보냈습니다.`, convertBeforeCsv:'CSV를 내보내기 전에 이미지를 하나 이상 변환하세요.',
    engineOutdated:'백엔드 벡터화 워커 버전이 오래되었습니다. 백엔드를 다시 시작한 후 다시 시도하세요.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`${category} 감지: 색상 그룹 ${colors}, 팔레트 오차 ${error}, 곡선 허용치 ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`배치 완료: 이미지 ${ok}개를 벡터화했습니다.`:`배치 완료: ${ok}개 성공, ${failed}개 실패.`,
    downloadAuth:'벡터를 다운로드하려면 무료 계정을 만들거나 로그인하세요(무료 크레딧 10개 포함).', convertBeforeZip:'ZIP을 내보내기 전에 이미지를 하나 이상 변환하세요.', zipGenerateFailed:'ZIP 패키지를 생성할 수 없습니다.', zipDownloadFailed:'내보내기 패키지는 생성되었지만 다운로드할 수 없습니다.', zipExported:(count:number)=>`벡터 ${count}개를 ZIP 패키지로 내보냈습니다.`, zipError:'ZIP 내보내기 오류',
    upgraded:(plan:string,credits:number)=>`${plan} 플랜으로 업그레이드했습니다. 크레딧 잔액: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`플랜 활성화 완료: ${plan}(${credits.toLocaleString()} 크레딧).`,
  };
  const runtimeText = locale === 'es' ? spanishRuntimeText : locale === 'pt-br' ? portugueseRuntimeText : locale === 'ru' ? russianRuntimeText : locale === 'tr' ? turkishRuntimeText : locale === 'vi' ? vietnameseRuntimeText : locale === 'th' ? thaiRuntimeText : locale === 'ar' ? arabicRuntimeText : locale === 'zh-cn' ? chineseRuntimeText : locale === 'ja' ? japaneseRuntimeText : locale === 'ko' ? koreanRuntimeText : locale === 'id' ? {
    authError:'Terjadi kesalahan saat autentikasi.', signedGoogle:(name:string,email:string)=>`Masuk sebagai ${name} (${email}) melalui Google`, googleAuthFailed:'Autentikasi Google gagal.', googlePromptFailed:'Prompt login Google gagal.', googleUnavailable:'Layanan Identitas Google tidak tersedia.',
    signInAiTest:'Masuk untuk menguji pengaturan AI workspace terenkripsi.', aiAuthenticated:(provider:string)=>`Berhasil terautentikasi dengan ${provider}.`, serverUnreachable:'Server Vectria tidak dapat dihubungi.', signInAiSave:'Masuk untuk menyimpan pengaturan AI workspace.', maxApiKeys:'Maksimal 10 kunci API yang diizinkan.', aiSettingsSaved:(provider:string)=>`Pengaturan AI workspace untuk ${provider} berhasil disimpan.`, resetKeysConfirm:(provider:string)=>`Hapus semua kunci API ${provider} yang tersimpan dari workspace ini? Kunci penyedia lain akan tetap tersimpan.`, keysRemoved:(provider:string)=>`Semua kunci API ${provider} yang tersimpan telah dihapus.`, resetKeysFailed:'Kunci API tersimpan tidak dapat dihapus.',
    metadataError:'Kesalahan Pembuatan Metadata', batchMetadataError:'Kesalahan Metadata Batch', metadataJobStatus:(status:string,count:number)=>`Tugas metadata ${status}. ${count} aset gagal.`, categoryApplied:(count:number)=>`Kategori diterapkan ke ${count} aset.`, csvExported:(label:string,count:number)=>`Metadata CSV ${label} untuk ${count} item berhasil diekspor.`, convertBeforeCsv:'Konversi minimal satu gambar sebelum mengekspor CSV.',
    engineOutdated:'Worker vektorisasi backend sudah usang. Mulai ulang backend, lalu coba lagi.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`Terdeteksi ${category}: ${colors} kelompok warna, galat palet ${error}, toleransi kurva ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`Batch selesai: ${ok} gambar berhasil divektorisasi.`:`Batch selesai: ${ok} berhasil, ${failed} gagal.`,
    downloadAuth:'Buat akun gratis atau masuk untuk mengunduh vektor Anda (termasuk 10 Kredit Gratis).', convertBeforeZip:'Konversi minimal satu gambar sebelum mengekspor ZIP.', zipGenerateFailed:'Paket ZIP gagal dibuat.', zipDownloadFailed:'Paket ekspor berhasil dibuat tetapi tidak dapat diunduh.', zipExported:(count:number)=>`${count} vektor berhasil diekspor sebagai paket ZIP.`, zipError:'Kesalahan ekspor ZIP',
    upgraded:(plan:string,credits:number)=>`Berhasil meningkatkan ke paket ${plan}. Saldo kredit: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`Paket diaktifkan: ${plan} (${credits.toLocaleString()} kredit).`,
  } : {
    authError:'An error occurred during authentication.', signedGoogle:(name:string,email:string)=>`Signed in as ${name} (${email}) via Google`, googleAuthFailed:'Google authentication failed.', googlePromptFailed:'Google Sign-In prompt failed.', googleUnavailable:'Google Identity Services is unavailable.',
    signInAiTest:'Sign in to test encrypted workspace AI settings.', aiAuthenticated:(provider:string)=>`Successfully authenticated with ${provider}.`, serverUnreachable:'Failed to reach Vectria server.', signInAiSave:'Sign in to save workspace AI settings.', maxApiKeys:'A maximum of 10 API keys is allowed.', aiSettingsSaved:(provider:string)=>`Workspace AI settings saved for ${provider}.`, resetKeysConfirm:(provider:string)=>`Remove all saved ${provider} API keys from this workspace? Keys for other providers will remain saved.`, keysRemoved:(provider:string)=>`All saved ${provider} API keys were removed.`, resetKeysFailed:'Could not reset saved API keys.',
    metadataError:'Metadata Generation Error', batchMetadataError:'Batch Metadata Error', metadataJobStatus:(status:string,count:number)=>`Metadata job ${status}. ${count} assets failed.`, categoryApplied:(count:number)=>`Category applied to ${count} assets.`, csvExported:(label:string,count:number)=>`Exported ${label} metadata CSV for ${count} items.`, convertBeforeCsv:'Please convert at least one image before exporting CSV.',
    engineOutdated:'Backend vectorization worker is outdated. Restart the backend, then try again.', detectedOptimization:(category:string,colors:number,error:number,tolerance:number)=>`Detected ${category}: ${colors} color bins, palette error ${error}, curve tolerance ${tolerance}px.`, batchComplete:(ok:number,failed:number)=>failed===0?`Batch complete: ${ok} image${ok===1?'':'s'} vectorized.`:`Batch complete: ${ok} succeeded, ${failed} failed.`,
    downloadAuth:'Create a free account or sign in to download your vectors (Includes 10 Free Credits).', convertBeforeZip:'Please convert at least one image before exporting ZIP.', zipGenerateFailed:'Failed to generate ZIP package.', zipDownloadFailed:'Export package was created but could not be downloaded.', zipExported:(count:number)=>`Exported ${count} vectors as ZIP package.`, zipError:'ZIP export error',
    upgraded:(plan:string,credits:number)=>`Successfully upgraded to ${plan} plan! Credit balance: ${credits.toLocaleString()}`, activated:(plan:string,credits:number)=>`Plan activated: ${plan} (${credits.toLocaleString()} credits).`,
  };
  // Theme State (Dark vs Light)
  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme());

  useEffect(() => {
    applyThemeToDom(theme, { persist: true });
  }, [theme]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (!getPersistedTheme()) {
        const nextSystemTheme: ThemeMode = e.matches ? 'light' : 'dark';
        setTheme(nextSystemTheme);
        applyThemeToDom(nextSystemTheme, { persist: false });
      }
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }
    return undefined;
  }, []);

  const toggleTheme = (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    const nextTheme: ThemeMode = theme === 'dark' ? 'light' : 'dark';
    runCircularThemeTransition({
      nextTheme,
      origin: event?.currentTarget ?? null,
      event: event ?? null,
      applyState: (updatedTheme) => {
        setTheme(updatedTheme);
      },
      persist: true,
    });
  };

  // Authentication & Credits State
  const [user, setUser] = useState<UserProfile | null>(() => {
    try {
      const savedUser = localStorage.getItem('vectria_auth_user') || sessionStorage.getItem('vectria_auth_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [authToken, setAuthToken] = useState<string | null>(() => {
    return localStorage.getItem('vectria_auth_token') || sessionStorage.getItem('vectria_auth_token');
  });
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [authEmail, setAuthEmail] = useState<string>('');
  const [authPassword, setAuthPassword] = useState<string>('');
  const [authNewPassword, setAuthNewPassword] = useState<string>('');
  const [authResetToken, setAuthResetToken] = useState<string>(() => {
    try {
      return new URLSearchParams(window.location.search).get('reset_token') || '';
    } catch {
      return '';
    }
  });
  const [pendingWorkspaceInvite, setPendingWorkspaceInvite] = useState<string>(() => {
    try {
      return new URLSearchParams(window.location.search).get('workspace_invite') || localStorage.getItem('vectria_workspace_invite') || '';
    } catch {
      return '';
    }
  });
  const [authName, setAuthName] = useState<string>('');
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccessMsg, setAuthSuccessMsg] = useState<string | null>(null);
  const [authLegalConsent, setAuthLegalConsent] = useState<boolean>(false);
  const [authRememberMe, setAuthRememberMe] = useState<boolean>(true);
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const [showCreditMenu, setShowCreditMenu] = useState<boolean>(false);
  const [showNotificationMenu, setShowNotificationMenu] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState<number>(0);
  const [notificationLoading, setNotificationLoading] = useState<boolean>(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState<boolean>(false);
  const [notificationCenterItems, setNotificationCenterItems] = useState<any[]>([]);
  const [notificationCenterPage, setNotificationCenterPage] = useState<number>(1);
  const [notificationCenterPages, setNotificationCenterPages] = useState<number>(1);
  const [notificationCenterUnreadOnly, setNotificationCenterUnreadOnly] = useState<boolean>(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState<boolean>(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  const [showTopupModal, setShowTopupModal] = useState<boolean>(false);
  const [topupCreditType, setTopupCreditType] = useState<'vector' | 'ai'>('vector');
  const [isTopupLoading, setIsTopupLoading] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState<boolean>(false);
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const creditMenuRef = useRef<HTMLDivElement>(null);
  const notificationMenuRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  // Auto-close popovers when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setShowUserMenu(false);
      }
      if (creditMenuRef.current && !creditMenuRef.current.contains(target)) {
        setShowCreditMenu(false);
      }
      if (notificationMenuRef.current && !notificationMenuRef.current.contains(target)) {
        setShowNotificationMenu(false);
      }
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  // Custom App Modal state (replaces native alert and confirm)
  const [appModal, setAppModal] = useState<{
    isOpen: boolean;
    type: 'alert' | 'confirm';
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    isDestructive?: boolean;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'alert',
    message: '',
  });

  const showAppAlert = (message: string, title?: string) => {
    setAppModal({
      isOpen: true,
      type: 'alert',
      title: title || flashText.notificationTitle,
      message,
      confirmLabel: 'OK',
    });
  };

  const showAppConfirm = (
    message: string,
    onConfirm: () => void,
    options?: { title?: string; confirmLabel?: string; cancelLabel?: string; isDestructive?: boolean }
  ) => {
    setAppModal({
      isOpen: true,
      type: 'confirm',
      title: options?.title || flashText.confirmationTitle,
      message,
      confirmLabel: options?.confirmLabel || flashText.continueLabel,
      cancelLabel: options?.cancelLabel || flashText.cancelLabel,
      isDestructive: options?.isDestructive ?? false,
      onConfirm,
    });
  };

  const googleBtnContainerRef = useRef<HTMLDivElement>(null);
  const [showPricingModal, setShowPricingModal] = useState<boolean>(false);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState<boolean>(false);
  const [saasOverview, setSaasOverview] = useState<any>(null);
  const [saasPeople, setSaasPeople] = useState<any>(null);
  const [saasUsage, setSaasUsage] = useState<any>(null);
  const [metadataObservability, setMetadataObservability] = useState<any>(null);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [saasLoading, setSaasLoading] = useState<boolean>(false);
  const [saasError, setSaasError] = useState<string | null>(null);
  const [pricingBillingCycle, setPricingBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const billingContext = useBillingContext();

  // Credit packs follow the same server-resolved currency as the plan cards,
  // so the modal never quotes USD when checkout will settle in Rupiah.
  const topupPrice = (packId: string) => topupPriceText(billingContext, packId);
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState<boolean>(false);
  const [systemStatus, setSystemStatus] = useState<{
    maintenance_mode: boolean;
    maintenance_message: string;
    broadcast_banner?: string;
  } | null>(() => {
    try {
      const cached = localStorage.getItem('vectria_system_status');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed.maintenance_mode === 'boolean') {
          return parsed;
        }
      }
    } catch {}
    return null;
  });
  const [statusChecked, setStatusChecked] = useState<boolean>(() => {
    try {
      return Boolean(localStorage.getItem('vectria_system_status'));
    } catch {
      return false;
    }
  });

  const fetchSystemStatus = () => {
    fetch(`${API_BASE}/api/public/system-status`)
      .then(r => r.json())
      .then(data => {
        if (data && typeof data.maintenance_mode === 'boolean') {
          setSystemStatus(data);
          try {
            localStorage.setItem('vectria_system_status', JSON.stringify(data));
          } catch {}
        }
      })
      .catch(() => {})
      .finally(() => {
        setStatusChecked(true);
      });
  };

  useEffect(() => {
    fetchSystemStatus();
    window.addEventListener('focus', fetchSystemStatus);
    return () => window.removeEventListener('focus', fetchSystemStatus);
  }, []);

  const isMaintenanceBlocked = Boolean(
    systemStatus?.maintenance_mode && (!user || (user.role !== 'admin' && user.role !== 'owner'))
  );

  const [currentView, setCurrentView] = useState<'landing' | 'studio' | 'admin' | 'settings' | 'blog' | LegalPageType>(() => {
    try {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const locales = new Set(['en','id','es','pt','pt-br','ru','ru-ru','tr','tr-tr','vi','vi-vn','th','th-th','ar','ar-sa','zh','zh-cn','ja','ko']);
      const route = locales.has(pathSegments[0]?.toLowerCase()) ? pathSegments.slice(1) : pathSegments;
      if (route[0] === 'blog' || route[0] === 'tutorials' || route[0] === 'changelog') {
        return 'blog';
      }
      const settingsIndex = pathSegments.findIndex(segment => segment.toLowerCase() === 'settings');
      if (settingsIndex >= 0) return 'settings';
      const hash = window.location.hash;
      if (hash === '#blog') return 'blog';
      if (hash === '#studio') return 'studio';
      if (hash === '#admin') return 'admin';
      if (hash === '#settings') return 'settings';
      if (hash === '#about') return 'about';
      if (hash === '#terms') return 'terms';
      if (hash === '#privacy') return 'privacy';
      return localStorage.getItem('vectria_auth_user') ? 'studio' : 'landing';
    } catch {
      return 'landing';
    }
  });

  const [blogSlug, setBlogSlug] = useState<string | undefined>(() => {
    try {
      const segments = window.location.pathname.split('/').filter(Boolean);
      const locales = new Set(['en','id','es','pt','pt-br','ru','ru-ru','tr','tr-tr','vi','vi-vn','th','th-th','ar','ar-sa','zh','zh-cn','ja','ko']);
      const route = locales.has(segments[0]?.toLowerCase()) ? segments.slice(1) : segments;
      if (route[0] === 'blog' || route[0] === 'tutorials' || route[0] === 'changelog') {
        return route[1];
      }
    } catch {}
    return undefined;
  });

  const [blogType, setBlogType] = useState<string | undefined>(() => {
    try {
      const segments = window.location.pathname.split('/').filter(Boolean);
      const locales = new Set(['en','id','es','pt','pt-br','ru','ru-ru','tr','tr-tr','vi','vi-vn','th','th-th','ar','ar-sa','zh','zh-cn','ja','ko']);
      const route = locales.has(segments[0]?.toLowerCase()) ? segments.slice(1) : segments;
      if (route[0] === 'tutorials') return 'tutorial';
      if (route[0] === 'changelog') return 'changelog';
    } catch {}
    return undefined;
  });

  const navigateBlog = (targetSlug?: string, targetType?: string) => {
    setBlogSlug(targetSlug);
    setBlogType(targetType);
    setCurrentView('blog');
    const prefix = targetType === 'tutorial' ? 'tutorials' : (targetType === 'changelog' ? 'changelog' : 'blog');
    const nextPath = targetSlug ? `/${prefix}/${targetSlug}` : `/${prefix}`;
    window.history.pushState({}, document.title, nextPath);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigatePublicHome = () => {
    const nextPath = publicHomePath(window.location.pathname);
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath !== nextPath) {
      window.history.pushState({}, document.title, nextPath);
    }
    setBlogSlug(undefined);
    setBlogType(undefined);
    setCurrentView('landing');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [settingsSection, setSettingsSection] = useState<SettingsSection>(() => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const settingsIndex = segments.findIndex(segment => segment.toLowerCase() === 'settings');
    const candidate = settingsIndex >= 0 ? segments[settingsIndex + 1]?.toLowerCase() : undefined;
    return isSettingsSection(candidate) ? candidate : 'profile';
  });

  const navigateSettings = (section: SettingsSection, replace = false) => {
    setSettingsSection(section);
    setCurrentView('settings');
    const localePrefix = window.location.pathname.split('/').filter(Boolean)[0];
    const localePath = localePrefix && ['en','id','es','pt','pt-br','ru','ru-ru','tr','tr-tr','vi','vi-vn','th','th-th','ar','ar-sa','zh','zh-cn','ja','ko'].includes(localePrefix.toLowerCase()) ? `/${localePrefix}` : '';
    const nextPath = `${localePath}/settings/${section}`;
    window.history[replace ? 'replaceState' : 'pushState']({}, document.title, `${nextPath}${window.location.search}`);
  };

  const navigateToStudio = () => {
    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
    const localePath = firstSegment && ['en','id','es','pt','pt-br','ru','ru-ru','tr','tr-tr','vi','vi-vn','th','th-th','ar','ar-sa','zh','zh-cn','ja','ko'].includes(firstSegment.toLowerCase()) ? `/${firstSegment}/` : '/';
    window.history.pushState({}, document.title, `${localePath}#studio`);
    setCurrentView('studio');
  };

  useEffect(() => {
    const syncSettingsPath = () => {
      const hash = window.location.hash.toLowerCase();
      if (hash === '#login' || hash === '#register') {
        setAuthMode(hash === '#register' ? 'register' : 'login');
        setAuthError(null);
        setShowAuthModal(true);
      }
      const segments = window.location.pathname.split('/').filter(Boolean);
      const locales = new Set(['en','id','es','pt','pt-br','ru','ru-ru','tr','tr-tr','vi','vi-vn','th','th-th','ar','ar-sa','zh','zh-cn','ja','ko']);
      const route = locales.has(segments[0]?.toLowerCase()) ? segments.slice(1) : segments;
      if (route[0] === 'blog' || route[0] === 'tutorials' || route[0] === 'changelog') {
        setBlogType(route[0] === 'tutorials' ? 'tutorial' : (route[0] === 'changelog' ? 'changelog' : undefined));
        setBlogSlug(route[1]);
        setCurrentView('blog');
        return;
      }
      const settingsIndex = segments.findIndex(segment => segment.toLowerCase() === 'settings');
      if (settingsIndex < 0) {
        if (currentView === 'settings' || currentView === 'blog') {
          setCurrentView(user ? 'studio' : 'landing');
        }
        return;
      }
      const candidate = segments[settingsIndex + 1]?.toLowerCase();
      setSettingsSection(isSettingsSection(candidate) ? candidate : 'profile');
      setCurrentView('settings');
    };
    window.addEventListener('popstate', syncSettingsPath);
    return () => window.removeEventListener('popstate', syncSettingsPath);
  }, [currentView, user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('reset_token');
    const verifyToken = params.get('verify_token');
    const workspaceInvite = params.get('workspace_invite');
    const billingStatus = params.get('billing');
    const topupStatus = params.get('topup');
    if (billingStatus === 'success' || topupStatus === 'success') {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
      const successMsg = topupStatus === 'success'
        ? flashText.topupProcessed
        : flashText.paymentProcessed;
      setSaveStatusMsg(successMsg);
      setTimeout(() => setSaveStatusMsg(null), 8000);

      // Webhooks arrive asynchronously; poll a few times to sync immediately
      refreshCurrentUser();
      const poll1 = setTimeout(() => refreshCurrentUser(), 2000);
      const poll2 = setTimeout(() => refreshCurrentUser(), 4500);
      const poll3 = setTimeout(() => refreshCurrentUser(), 8000);
      return () => {
        clearTimeout(poll1);
        clearTimeout(poll2);
        clearTimeout(poll3);
      };
    }
    if (billingStatus === 'cancel' || billingStatus === 'cancelled' || topupStatus === 'cancel' || topupStatus === 'cancelled') {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
    }
    if (workspaceInvite) {
      localStorage.setItem('vectria_workspace_invite', workspaceInvite);
      setPendingWorkspaceInvite(workspaceInvite);
      if (!user) {
        setAuthMode('login');
        setAuthSuccessMsg(appExtraText.inviteSignIn);
        setShowAuthModal(true);
      }
      return;
    }
    if (resetToken) {
      setAuthResetToken(resetToken);
      setAuthMode('forgot');
      setShowAuthModal(true);
      return;
    }
    if (verifyToken) {
      setAuthLoading(true);
      fetch(`${API_BASE}/api/auth/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verifyToken }),
        signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS)
      })
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.verifyFailed));
          setAuthSuccessMsg(data.message || appExtraText.verifySuccess);
          setAuthMode('login');
          setShowAuthModal(true);
          window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
        })
        .catch((error) => {
          setAuthError(error instanceof Error ? error.message : appExtraText.verifyFailed);
          setAuthMode('login');
          setShowAuthModal(true);
        })
        .finally(() => setAuthLoading(false));
    }
  }, []);

  // Tracing Engine Parameters
  const [outputFormat, setOutputFormat] = useState<'both' | 'svg_only' | 'eps_only'>('both');
  const [quantizeColors, setQuantizeColors] = useState<number>(24);
  const [filterSpeckle, setFilterSpeckle] = useState<number>(14);
  const [cornerThreshold, setCornerThreshold] = useState<number>(55);
  const [curveTolerance, setCurveTolerance] = useState<number>(1.4);
  const [smoothFactor, setSmoothFactor] = useState<number>(0.20);
  const [layerDifference, setLayerDifference] = useState<number>(16);
  const [layerMode, setLayerMode] = useState<'stacked' | 'cutout'>('stacked');
  const [stripWhiteBg, setStripWhiteBg] = useState<boolean>(true);
  const [whiteThreshold, setWhiteThreshold] = useState<number>(220);
  const [erodeHaloPx, setErodeHaloPx] = useState<number>(1);
  const [activePreset, setActivePreset] = useState<PresetName | null>(null);
  const [computeResource, setComputeResource] = useState<'local' | 'cloud'>(() => {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('vectria_compute_resource');
      if (saved === 'cloud' || saved === 'local') return saved;
    }
    return 'local';
  });

  // Storage / Notifications
  const [saveStatusMsg, setSaveStatusMsg] = useState<string | null>(null);
  const [optimizeNotice, setOptimizeNotice] = useState<string | null>(null);
  const [isAnalyzingOptimize, setIsAnalyzingOptimize] = useState<boolean>(false);
  useEffect(() => {
    if (!optimizeNotice || isAnalyzingOptimize) return;
    const timeoutId = window.setTimeout(() => setOptimizeNotice(null), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [optimizeNotice, isAnalyzingOptimize]);
  const [showPresetGuide, setShowPresetGuide] = useState<boolean>(false);
  const [isExportingZip, setIsExportingZip] = useState<boolean>(false);
  const [showExportReviewModal, setShowExportReviewModal] = useState<boolean>(false);

  // BYOK AI Settings
  const [aiEnabled, setAiEnabled] = useState<boolean>(false);
  const [allowVectriaFallback, setAllowVectriaFallback] = useState<boolean>(false);
  const [aiProvider, setAiProvider] = useState<'custom' | 'gemini' | 'openai'>('gemini');
  const [aiKey, setAiKey] = useState<string>('');
  const [aiKeyCount, setAiKeyCount] = useState<number>(0);
  const [aiStoredMask, setAiStoredMask] = useState<string>('');
  const aiProviderDraftsRef = useRef<Partial<Record<'custom' | 'gemini' | 'openai', { key: string; keyCount: number; storedMask: string; model: string; fallbackModel: string; endpoint: string }>>>({});
  const [aiModel, setAiModel] = useState<string>('gemini-3.8-flash');
  const [aiFallbackModel, setAiFallbackModel] = useState<string>('gemini-3.5-flash');
  const [aiCustomModel, setAiCustomModel] = useState<string>('');
  const [aiCustomFallbackModel, setAiCustomFallbackModel] = useState<string>('');
  const [aiEndpoint, setAiEndpoint] = useState<string>('https://openrouter.ai/api/v1/chat/completions');
  const [isTestingAi, setIsTestingAi] = useState<boolean>(false);
  const [isResettingAiKeys, setIsResettingAiKeys] = useState<boolean>(false);
  const [aiTestResult, setAiTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [isGeneratingItemMetadata, setIsGeneratingItemMetadata] = useState<boolean>(false);
  const [isGeneratingBatchMetadata, setIsGeneratingBatchMetadata] = useState<boolean>(false);
  const [metadataJobs, setMetadataJobs] = useState<any[]>([]);
  const [activeMetadataJob, setActiveMetadataJob] = useState<any | null>(null);
  const [showMetadataHistory, setShowMetadataHistory] = useState<boolean>(false);
  const [metadataEditorExpanded, setMetadataEditorExpanded] = useState<boolean>(false);
  const [metadataHistoryLoading, setMetadataHistoryLoading] = useState<boolean>(false);
  const [exportJobs, setExportJobs] = useState<any[]>([]);
  const [showExportHistory, setShowExportHistory] = useState<boolean>(false);
  const [exportHistoryLoading, setExportHistoryLoading] = useState<boolean>(false);
  const [downloadingExportId, setDownloadingExportId] = useState<string | null>(null);

  // Batch Queue & Active Selection
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const queueRef = useRef<QueueItem[]>([]);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [isProcessingBatch, setIsProcessingBatch] = useState<boolean>(false);
  const [isRestoringQueue, setIsRestoringQueue] = useState<boolean>(true);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [qualityFilter, setQualityFilter] = useState<'all' | 'passed' | 'warning' | 'failed'>('all');
  const queueStorageKey = queueOwnerKey(user?.id);
  const hydratedQueueKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let restoreWatchdogId: number | undefined;
    setIsRestoringQueue(true);
    const finishRestore = () => {
      if (cancelled) return;
      if (restoreWatchdogId !== undefined) {
        window.clearTimeout(restoreWatchdogId);
        restoreWatchdogId = undefined;
      }
      hydratedQueueKeyRef.current = queueStorageKey;
      setIsRestoringQueue(false);
    };
    restoreWatchdogId = window.setTimeout(() => {
      console.warn('Queue restoration exceeded its UI wait budget; continuing hydration in the background.');
      finishRestore();
    }, QUEUE_RESTORE_WATCHDOG_MS);
    const previousQueue = queue;
    const previousStorageKey = hydratedQueueKeyRef.current;
    const migrateGuestQueue = previousStorageKey === 'guest'
      && queueStorageKey.startsWith('user:')
      && previousQueue.length > 0;
    hydratedQueueKeyRef.current = null;

    if (migrateGuestQueue) {
      queueRef.current = previousQueue;
      setQueue(previousQueue);
      setActiveItemId(current => current || previousQueue[0]?.id || null);
      finishRestore();
      void withTimeout(
        loadPersistedQueue<QueueItem>(queueStorageKey),
        QUEUE_LOCAL_RESTORE_TIMEOUT_MS,
        'Local user queue restore timed out.',
      )
        .catch(error => {
          console.warn('Could not read the signed-in queue cache; continuing with the visible guest queue:', error);
          return [] as QueueItem[];
        })
        .then(async storedUserQueue => {
          if (cancelled) {
            storedUserQueue.forEach(item => URL.revokeObjectURL(item.previewUrl));
            return;
          }
          let mergedQueue = mergeQueues(previousQueue, storedUserQueue);
          const stagedQueue = await Promise.all(mergedQueue.map(async item => {
            if (item.stagedAssetId) return item;
            try {
              const upload = new FormData();
              upload.append('file', item.file);
              upload.append('client_id', item.id);
              const response = await authenticatedFetch('/api/batch-assets', { method: 'POST', body: upload });
              const data = await response.json().catch(() => ({}));
              if (!response.ok || !data.asset) return item;
              return { ...item, stagedAssetId: data.asset.id, conversionId: data.asset.conversion_id };
            } catch (error) {
              console.warn('Could not stage guest batch asset after sign-in:', error);
              return item;
            }
          }));
          mergedQueue = stagedQueue;
          queueRef.current = mergedQueue;
          setQueue(mergedQueue);
          setActiveItemId(current => current || mergedQueue[0]?.id || null);
          finishRestore();
          void Promise.allSettled([
            savePersistedQueue(queueStorageKey, mergedQueue),
            clearPersistedQueue('guest'),
          ]).then(results => {
            results.forEach(result => {
              if (result.status === 'rejected') console.warn('Could not finish guest queue migration:', result.reason);
            });
          });
        })
        .catch(error => {
          console.warn('Could not migrate the guest batch queue:', error);
          if (!cancelled) {
            finishRestore();
          }
        });
      return () => {
        cancelled = true;
        if (restoreWatchdogId !== undefined) window.clearTimeout(restoreWatchdogId);
      };
    }

    queueRef.current = [];
    setQueue([]);
    setActiveItemId(null);
    setBatchProgress(null);
    setIsProcessingBatch(false);
    previousQueue.forEach(item => {
      if (item.previewUrl.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
    });
    void withTimeout(
      loadPersistedQueue<QueueItem>(queueStorageKey),
      QUEUE_LOCAL_RESTORE_TIMEOUT_MS,
      'Local queue restore timed out.',
    )
      .catch(error => {
        console.warn('Could not read the local queue cache; continuing with the server manifest:', error);
        return [] as QueueItem[];
      })
      .then(async restored => {
        if (cancelled) {
          restored.forEach(item => URL.revokeObjectURL(item.previewUrl));
          return;
        }
        let recovered = restored;
        queueRef.current = restored;
        setQueue(restored);
        setActiveItemId(restored[0]?.id || null);
        if (user) {
          try {
            const manifestResponse = await authenticatedFetch('/api/batch-assets', {
              signal: AbortSignal.timeout(QUEUE_MANIFEST_TIMEOUT_MS),
            });
            const manifestData = await manifestResponse.json().catch(() => ({}));
            if (!manifestResponse.ok) throw new Error(apiErrorMessage(manifestData, 'Could not restore the server batch queue.'));
            const manifestAssets = Array.isArray(manifestData.assets) ? manifestData.assets : [];
            const serverShells = manifestAssets.map((asset: any): QueueItem => {
              const cached = restored.find(item => (asset.id && item.stagedAssetId === asset.id) || (asset.client_id && item.id === asset.client_id) || item.name === asset.name);
              const pendingResult = asset.status === 'done' && Boolean(asset.has_result);
              return {
                ...(cached || {
                  id: asset.client_id,
                  file: new File([], asset.name || 'restored-image', { type: asset.mime_type || 'application/octet-stream' }),
                  previewUrl: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22/%3E',
                  name: asset.name || 'restored-image',
                  size: Number(asset.size || 0),
                }),
                stagedAssetId: asset.id,
                conversionId: asset.conversion_id,
                status: pendingResult || asset.status === 'processing'
                  ? 'processing'
                  : asset.status === 'error'
                    ? 'error'
                    : asset.status === 'done' && Boolean(cached?.vectorSvg)
                      ? 'done'
                      : 'idle',
              };
            });
            const localOnly = restored.filter(item => !item.stagedAssetId);
            const visibleQueue = mergeQueues(serverShells, localOnly);
            queueRef.current = visibleQueue;
            setQueue(visibleQueue);
            setActiveItemId(current => current && visibleQueue.some(item => item.id === current) ? current : visibleQueue[0]?.id || null);
            finishRestore();
            const serverItems = (await Promise.all(manifestAssets.map(async (asset: any): Promise<QueueItem | null> => {
              const cached = restored.find(item => (asset.id && item.stagedAssetId === asset.id) || (asset.client_id && item.id === asset.client_id) || item.name === asset.name);
              try {
                const contentResponse = await authenticatedFetch(`/api/batch-assets/${encodeURIComponent(asset.id)}/content`, {
                  signal: AbortSignal.timeout(QUEUE_ASSET_TIMEOUT_MS),
                });
                if (!contentResponse.ok) throw new Error(`Staged content request failed (${contentResponse.status}).`);
                const blob = await contentResponse.blob();
                const file = new File([blob], asset.name, { type: asset.mime_type || blob.type });
                let result: any = null;
                if (asset.has_result) {
                  const resultResponse = await authenticatedFetch(`/api/batch-assets/${encodeURIComponent(asset.id)}/result`, {
                    signal: AbortSignal.timeout(QUEUE_ASSET_TIMEOUT_MS),
                  });
                  if (resultResponse.ok) result = await resultResponse.json();
                }
                const done = asset.status === 'done' && Boolean(result?.svg);
                return {
                  ...(cached || {}),
                  id: asset.client_id,
                  stagedAssetId: asset.id,
                  conversionId: asset.conversion_id,
                  file,
                  previewUrl: URL.createObjectURL(file),
                  name: asset.name,
                  size: Number(asset.size || file.size),
                  status: done ? 'done' : asset.status === 'processing' ? 'processing' : asset.status === 'error' ? 'error' : 'idle',
                  vectorSvg: result?.svg || cached?.vectorSvg,
                  vectorEps: result?.eps || cached?.vectorEps,
                  validation: result?.validation || cached?.validation,
                  qualityReport: result?.quality_report || cached?.qualityReport,
                  visualHeatmap: result?.visual_heatmap || cached?.visualHeatmap,
                  optimization: result?.optimization || cached?.optimization,
                  metadata: cached?.metadata,
                  qualityHistory: cached?.qualityHistory,
                };
              } catch (error) {
                console.warn('Could not restore staged batch asset:', error);
                if (cached && cached.file && cached.file.size > 0) {
                  const cachedDone = asset.status === 'done' && Boolean(cached.vectorSvg);
                  // The server's staged file content is missing/unreadable, but we still have the
                  // full File blob in local IndexedDB. Clear stagedAssetId so it re-stages cleanly.
                  return {
                    ...cached,
                    stagedAssetId: undefined,
                    conversionId: asset.conversion_id,
                    status: cachedDone ? 'done' : 'idle',
                    errorMessage: undefined,
                  };
                }
                if (cached) {
                  const cachedDone = asset.status === 'done' && Boolean(cached.vectorSvg);
                  return {
                    ...cached,
                    stagedAssetId: asset.id,
                    conversionId: asset.conversion_id,
                    status: cachedDone ? 'done' : asset.status === 'processing' || asset.has_result ? 'processing' : asset.status === 'error' ? 'error' : 'idle',
                    errorMessage: queueTransferText.restorePreviewFailed,
                  };
                }
                const placeholder = new File([], asset.name || 'restored-image', { type: asset.mime_type || 'application/octet-stream' });
                return {
                  id: asset.client_id,
                  stagedAssetId: asset.id,
                  conversionId: asset.conversion_id,
                  file: placeholder,
                  previewUrl: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22/%3E',
                  name: asset.name,
                  size: Number(asset.size || 0),
                  status: asset.status === 'processing' || asset.has_result ? 'processing' : asset.status === 'error' ? 'error' : 'idle',
                  errorMessage: queueTransferText.restorePreviewFailed,
                };
              }
            }))).filter((item: QueueItem | null): item is QueueItem => Boolean(item));
            const migratedLocal = await Promise.all(localOnly.map(async item => {
              try {
                const upload = new FormData();
                upload.append('file', item.file);
                upload.append('client_id', item.id);
                const response = await authenticatedFetch('/api/batch-assets', { method: 'POST', body: upload });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.asset) return item;
                return { ...item, stagedAssetId: data.asset.id, conversionId: data.asset.conversion_id };
              } catch (error) {
                console.warn('Could not migrate local batch asset to server storage:', error);
                return item;
              }
            }));
            recovered = mergeQueues(serverItems, migratedLocal);
            restored.forEach(item => {
              if (!recovered.some(candidate => candidate.previewUrl === item.previewUrl) && item.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(item.previewUrl);
              }
            });
          } catch (error) {
            console.warn('Could not restore the server batch queue:', error);
          }
        }
        queueRef.current = recovered;
        setQueue(recovered);
        setActiveItemId(recovered[0]?.id || null);
        finishRestore();
      })
      .catch(error => {
        console.warn('Could not restore the batch queue:', error);
        if (!cancelled) {
          finishRestore();
        }
      });
    return () => {
      cancelled = true;
      if (restoreWatchdogId !== undefined) window.clearTimeout(restoreWatchdogId);
    };
  }, [queueStorageKey]);

  useEffect(() => {
    if (hydratedQueueKeyRef.current !== queueStorageKey) return;
    queueRef.current = queue;
    const timeoutId = window.setTimeout(() => {
      void savePersistedQueue(queueStorageKey, queue).catch(error => {
        console.warn('Could not persist the batch queue:', error);
      });
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [queue, queueStorageKey]);

  const hasServerProcessingItems = queue.some(item => item.status === 'processing' && Boolean(item.stagedAssetId));
  useEffect(() => {
    if (!user || isRestoringQueue || !hasServerProcessingItems) return;
    let cancelled = false;
    let timerId: number | undefined;

    const pollServerQueue = async () => {
      try {
        const manifestResponse = await authenticatedFetch('/api/batch-assets', {
          signal: AbortSignal.timeout(QUEUE_MANIFEST_TIMEOUT_MS),
        });
        const manifestData = await manifestResponse.json().catch(() => ({}));
        if (!manifestResponse.ok) throw new Error(apiErrorMessage(manifestData, 'Could not refresh the server batch queue.'));
        const assetsById = new Map<string, any>((manifestData.assets || []).map((asset: any) => [asset.id, asset]));
        const updates = new Map<string, Partial<QueueItem>>();

        await Promise.all(queueRef.current
          .filter(item => item.status === 'processing' && item.stagedAssetId)
          .map(async item => {
            const asset = assetsById.get(item.stagedAssetId!);
            if (!asset) {
              updates.set(item.id, {
                status: 'error',
                stagedAssetId: undefined,
                errorMessage: 'Cloud session expired or asset was cleared. Click retry to convert again.',
              });
              return;
            }
            if (asset.status === 'done' && asset.has_result) {
              const resultResponse = await authenticatedFetch(`/api/batch-assets/${encodeURIComponent(asset.id)}/result`, {
                signal: AbortSignal.timeout(QUEUE_ASSET_TIMEOUT_MS),
              });
              if (!resultResponse.ok) return;
              const result = await resultResponse.json();
              if (result.remaining_credits !== undefined && result.remaining_credits !== null) {
                setUser(previous => {
                  if (!previous || previous.plan === 'agency') return previous;
                  const updated = { ...previous, credits: Math.min(previous.credits, Number(result.remaining_credits)) };
                  try {
                    localStorage.setItem('vectria_auth_user', JSON.stringify(updated));
                  } catch {}
                  return updated;
                });
              }
              updates.set(item.id, {
                status: 'done',
                conversionId: asset.conversion_id,
                errorMessage: undefined,
                vectorSvg: result.svg,
                vectorEps: result.eps,
                validation: result.validation,
                qualityReport: result.quality_report,
                visualHeatmap: result.visual_heatmap,
                optimization: result.optimization,
              });
            } else if (asset.status === 'error') {
              updates.set(item.id, {
                status: 'error',
                stagedAssetId: undefined,
                errorMessage: asset.error_code
                  ? `Cloud vectorization failed (${asset.error_code}). Click retry to convert again.`
                  : 'Cloud vectorization failed. Click retry to convert again.',
              });
            } else if (asset.status === 'uploaded') {
              updates.set(item.id, { status: 'idle', conversionId: asset.conversion_id });
            }
          }));

        if (!cancelled && updates.size > 0) {
          setQueue(previous => {
            const next = previous.map(item => updates.has(item.id) ? { ...item, ...updates.get(item.id)! } : item);
            queueRef.current = next;
            return next;
          });
        }
      } catch (error) {
        console.warn('Could not poll the active server conversion:', error);
      } finally {
        if (!cancelled) timerId = window.setTimeout(pollServerQueue, 2000);
      }
    };

    timerId = window.setTimeout(pollServerQueue, 1200);
    return () => {
      cancelled = true;
      if (timerId !== undefined) window.clearTimeout(timerId);
    };
  }, [user?.id, isRestoringQueue, hasServerProcessingItems, isProcessingBatch]);

  // Viewport & Canvas Controls
  const [viewMode, setViewMode] = useState<ViewMode>('side_by_side');
  const [isWireframe, setIsWireframe] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const panStartRef = useRef({ pointerX: 0, pointerY: 0, originX: 0, originY: 0 });
  const activePointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef<number>(1);
  const pinchStartPanRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const pinchStartMidpointRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const lastTapTimeRef = useRef<number>(0);
  const [sliderPosition, setSliderPosition] = useState<number>(50);
  const [isDraggingSlider, setIsDraggingSlider] = useState<boolean>(false);
  const sliderStageRef = useRef<HTMLDivElement>(null);
  const [useCheckerboard, setUseCheckerboard] = useState<boolean>(false);
  const [metadataMarketplace, setMetadataMarketplace] = useState<MetadataMarketplace>('adobe');

  // Responsive Sidebar Toggles
  const desktopSidebarDefault = () => typeof window !== 'undefined' && window.innerWidth > 1024;
  const [leftSidebarOpen, setLeftSidebarOpen] = useState<boolean>(desktopSidebarDefault);
  const [rightSidebarOpen, setRightSidebarOpen] = useState<boolean>(desktopSidebarDefault);
  const [rightSidebarTab, setRightSidebarTab] = useState<'queue' | 'metadata' | 'export'>('queue');
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 760);
  // Desktop Right Sidebar Accordion State (Collapsible Sections)
  const [desktopQueueExpanded, setDesktopQueueExpanded] = useState<boolean>(true);
  const [desktopMetadataExpanded, setDesktopMetadataExpanded] = useState<boolean>(false);
  const [desktopExportExpanded, setDesktopExportExpanded] = useState<boolean>(false);
  const [qualityDrawerOpen, setQualityDrawerOpen] = useState<boolean>(false);
  const [showVisualHeatmap, setShowVisualHeatmap] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeItem = queue.find(q => q.id === activeItemId) || queue[0] || null;
  useEffect(() => { setQualityDrawerOpen(false); setShowVisualHeatmap(false); }, [activeItem?.id]);
  const closeQualityDrawer = () => {
    setQualityDrawerOpen(false);
  };
  const focusRemediationTarget = (targetId: string) => {
    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus({ preventScroll: true });
      target?.classList.add('quality-remediation-target');
      window.setTimeout(() => target?.classList.remove('quality-remediation-target'), 1800);
    }, 180);
  };
  const startGuidedRemediation = (check: QualityCheck) => {
    const isCompact = typeof window !== 'undefined' && window.innerWidth <= 1024;
    if (check.id === 'metadata_keyword_duplicates') {
      showAppConfirm(quality.confirmDeduplicate, () => {
        closeQualityDrawer();
        void normalizeActiveKeywords(false);
        setRightSidebarOpen(true);
        if (isCompact) setLeftSidebarOpen(false);
        setMetadataEditorExpanded(true);
        focusRemediationTarget('deduplicate-keywords');
      });
      return;
    }
    const metadataTargets: Record<string, string> = {
      metadata_title_present: 'metadata-title-input', metadata_title_length: 'metadata-title-input',
      metadata_description_present: 'metadata-description-input', metadata_keyword_count: 'metadata-keywords-input',
      metadata_category_present: 'metadata-category-select', metadata_validation: 'metadata-editor-panel',
    };
    const tuningTargets: Record<string, string> = {
      complexity_budget: 'speckle-filter-control', tiny_paths: 'speckle-filter-control',
      degenerate_segments: 'curve-tolerance-control', duplicate_paths: 'tuning-controls',
      residual_transforms: 'tuning-controls', native_path_commands: 'tuning-controls',
      visual_fidelity: 'color-limit-control', open_paths: 'curve-tolerance-control', empty_paths: 'speckle-filter-control',
      outside_artboard: 'tuning-controls', artboard_4000: 'output-format-controls', eps_zero_bitmap: 'output-format-controls', eps_vector_paths: 'output-format-controls',
      finite_geometry: 'tuning-controls', vector_paths_present: 'tuning-controls',
    };
    const metadataTarget = metadataTargets[check.id];
    closeQualityDrawer();
    if (metadataTarget) {
      setRightSidebarTab('metadata');
      setRightSidebarOpen(true);
      if (isCompact) setLeftSidebarOpen(false);
      setMetadataEditorExpanded(true);
      focusRemediationTarget(metadataTarget);
    } else {
      setLeftSidebarOpen(true);
      if (isCompact) setRightSidebarOpen(false);
      focusRemediationTarget(tuningTargets[check.id] || 'tuning-controls');
    }
  };
  const qualityBatch = queue.reduce((summary, item) => {
    const status = item.qualityReport?.status;
    if (status === 'passed') summary.passed += 1;
    else if (status === 'warning') summary.warnings += 1;
    else if (status === 'failed' || status === 'attention') summary.failed += 1;
    if (status) summary.analyzed += 1;
    return summary;
  }, { passed: 0, warnings: 0, failed: 0, analyzed: 0 });
  const qualityFilteredQueue = qualityFilter === 'all' ? queue : queue.filter(item => {
    const status = item.qualityReport?.status;
    return qualityFilter === 'failed' ? status === 'failed' || status === 'attention' : status === qualityFilter;
  });
  const issueItems = queue.filter(item => item.qualityReport && item.qualityReport.status !== 'passed');
  const exportReviewItems = queue.filter(item => item.status === 'done' && item.vectorSvg && item.qualityReport && item.qualityReport.status !== 'passed').map(item => ({ id:item.id, name:item.name, status:item.qualityReport!.status as 'warning'|'failed'|'attention' }));
  const exportPassingCount = queue.filter(item => item.status === 'done' && item.vectorSvg && item.qualityReport?.status === 'passed').length;
  const exportWarningCount = exportReviewItems.filter(item => item.status === 'warning').length;
  const exportFailedCount = exportReviewItems.filter(item => item.status === 'failed' || item.status === 'attention').length;
  const exportTotalCount = queue.filter(item => item.status === 'done' && item.vectorSvg).length;
  const selectNextQualityIssue = () => {
    if (!issueItems.length) return;
    const current = issueItems.findIndex(item => item.id === activeItem?.id);
    setActiveItemId(issueItems[(current + 1) % issueItems.length].id);
  };
  const metadataEditableItems = queue.filter(item => item.status === 'done');
  const activeMetadataItemIndex = activeItem ? metadataEditableItems.findIndex(item => item.id === activeItem.id) : -1;
  const selectMetadataItemAt = (index: number) => {
    if (!metadataEditableItems.length) return;
    const normalized = (index + metadataEditableItems.length) % metadataEditableItems.length;
    setActiveItemId(metadataEditableItems[normalized].id);
  };

  const activeQualityHistory = activeItem?.qualityHistory || [];

  const resetCanvasView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setIsPanning(false);
    activePointersRef.current.clear();
    pinchStartDistRef.current = null;
  };

  // Split slider drag handling
  const handleSliderPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {}
    setIsDraggingSlider(true);
  };

  const handleSliderPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!isDraggingSlider || !sliderStageRef.current) return;
    const rect = sliderStageRef.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    const clientX = event.clientX;
    const newPos = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setSliderPosition(newPos);
  };

  const handleSliderPointerEnd = (event: React.PointerEvent<HTMLElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {}
    }
    setIsDraggingSlider(false);
  };

  // Robust Canvas Pointer & Touch Handling (1-finger Pan, 2-finger Pinch-to-zoom, Double-tap)
  const handleCanvasPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('button, a, input, select, [role="button"], .canvas-floating-dock, .canvas-split-handle, .canvas-split-divider')) {
      return;
    }
    if (!activeItem?.previewUrl && !activeItem?.vectorSvg) return;
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {}

    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activePointersRef.current.size === 1) {
      // Check double-tap
      const now = Date.now();
      if (now - lastTapTimeRef.current < 280) {
        if (zoomLevel <= 1.1) {
          setZoomLevel(2.5);
        } else {
          resetCanvasView();
        }
        lastTapTimeRef.current = 0;
        return;
      }
      lastTapTimeRef.current = now;

      panStartRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        originX: panOffset.x,
        originY: panOffset.y,
      };
      setIsPanning(true);
    } else if (activePointersRef.current.size === 2) {
      // Initialize pinch-to-zoom
      const [p1, p2] = Array.from(activePointersRef.current.values());
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      pinchStartDistRef.current = dist > 0 ? dist : 1;
      pinchStartZoomRef.current = zoomLevel;
      pinchStartPanRef.current = { ...panOffset };
      pinchStartMidpointRef.current = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      setIsPanning(true);
    }
  };

  const handleCanvasPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!activePointersRef.current.has(event.pointerId)) return;
    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activePointersRef.current.size === 2 && pinchStartDistRef.current !== null) {
      // 2-finger pinch zoom + multi-touch pan
      const [p1, p2] = Array.from(activePointersRef.current.values());
      const currentDist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const scale = currentDist / pinchStartDistRef.current;
      const newZoom = Math.min(8, Math.max(0.5, pinchStartZoomRef.current * scale));
      setZoomLevel(newZoom);

      const midX = (p1.x + p2.x) / 2;
      const midY = (p1.y + p2.y) / 2;
      const deltaX = midX - pinchStartMidpointRef.current.x;
      const deltaY = midY - pinchStartMidpointRef.current.y;
      setPanOffset({
        x: pinchStartPanRef.current.x + deltaX,
        y: pinchStartPanRef.current.y + deltaY,
      });
    } else if (activePointersRef.current.size === 1 && isPanning) {
      // 1-finger single touch pan
      setPanOffset({
        x: panStartRef.current.originX + event.clientX - panStartRef.current.pointerX,
        y: panStartRef.current.originY + event.clientY - panStartRef.current.pointerY,
      });
    }
  };

  const handleCanvasPointerEnd = (event: React.PointerEvent<HTMLElement>) => {
    activePointersRef.current.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {}
    }

    if (activePointersRef.current.size === 0) {
      setIsPanning(false);
      pinchStartDistRef.current = null;
    } else if (activePointersRef.current.size === 1) {
      const [remaining] = Array.from(activePointersRef.current.values());
      panStartRef.current = {
        pointerX: remaining.x,
        pointerY: remaining.y,
        originX: panOffset.x,
        originY: panOffset.y,
      };
      pinchStartDistRef.current = null;
    }
  };

  useEffect(() => {
    setPanOffset({ x: 0, y: 0 });
    setIsPanning(false);
    activePointersRef.current.clear();
    pinchStartDistRef.current = null;
  }, [activeItemId, viewMode]);

  const waitForUiPaint = () => new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
  });

  const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Could not encode image.'));
    reader.onerror = () => reject(reader.error || new Error('Could not read image.'));
    reader.readAsDataURL(file);
  });

  // Responsive auto-collapse on smaller screens
  useEffect(() => {
    const handleResize = () => {
      setIsMobileScreen(window.innerWidth <= 760);
      if (window.innerWidth <= 1024) {
        setLeftSidebarOpen(false);
        setRightSidebarOpen(false);
      } else {
        setLeftSidebarOpen(true);
        setRightSidebarOpen(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load saved preferences & authenticate from localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem('vectria_auth_token') || sessionStorage.getItem('vectria_auth_token');
    const savedUser = localStorage.getItem('vectria_auth_user') || sessionStorage.getItem('vectria_auth_user');
    if (savedToken && savedUser) {
      try {
        setAuthToken(savedToken);
        setUser(JSON.parse(savedUser));
        if (currentView === 'landing') setCurrentView('studio');
      } catch (e) {
        console.error('Error loading saved user', e);
      }
      // Revalidate fresh user profile (avatar, name, plan, credits) from database
      fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      })
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (data?.user) {
            setUser(data.user);
            const storage = localStorage.getItem('vectria_auth_token') ? localStorage : sessionStorage;
            storage.setItem('vectria_auth_user', JSON.stringify(data.user));
          }
        })
        .catch(() => {});
    }

    const savedProv = (localStorage.getItem('vectria_ai_provider') as any) || 'gemini';
    setAiProvider(savedProv);
    const savedKey = localStorage.getItem(`vectria_ai_key_${savedProv}`) || localStorage.getItem('vectria_ai_key') || '';
    setAiKey(savedKey);
    const defaultModel = savedProv === 'gemini' ? 'gemini-3.8-flash' : savedProv === 'openai' ? 'gpt-6-mini' : 'openrouter/free';
    const defaultFallback = savedProv === 'gemini' ? 'gemini-3.5-flash' : savedProv === 'openai' ? 'gpt-5.6-turbo' : 'none';
    const savedModel = localStorage.getItem(`vectria_ai_model_${savedProv}`) || localStorage.getItem('vectria_ai_model') || defaultModel;
    const savedFallback = localStorage.getItem(`vectria_ai_fallback_${savedProv}`) || localStorage.getItem('vectria_ai_fallback') || defaultFallback;
    setAiModel(savedModel);
    setAiFallbackModel(savedFallback);
    const savedEndpoint = localStorage.getItem('vectria_ai_endpoint') || 'https://openrouter.ai/api/v1/chat/completions';
    setAiEndpoint(savedEndpoint);
    const savedEnabled = localStorage.getItem('vectria_ai_enabled') === 'true';
    setAiEnabled(savedEnabled);
  }, []);

  useEffect(() => {
    if (!user || !authToken || !pendingWorkspaceInvite) return;
    fetch(`${API_BASE}/api/saas/workspace/invites/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ token: pendingWorkspaceInvite })
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.inviteFailed));
        localStorage.removeItem('vectria_workspace_invite');
        if (data.workspace_id) localStorage.setItem(`vectria_active_workspace_${user.id}`, data.workspace_id);
        setPendingWorkspaceInvite('');
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
        setSaveStatusMsg(t('studio.inviteAccepted'));
      })
      .catch(error => setSaveStatusMsg(error instanceof Error ? error.message : t('studio.inviteFailed')));
  }, [user, authToken, pendingWorkspaceInvite, t]);

  // Auth Operations
  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    if (authMode === 'register' && !authLegalConsent) {
      setAuthError(t('auth.consentRequired'));
      return;
    }
    setAuthLoading(true);

    try {
      if (authMode === 'forgot') {
        const isConfirmingReset = Boolean(authResetToken);
        const res = await fetch(
          `${API_BASE}/api/auth/password-reset/${isConfirmingReset ? 'confirm' : 'request'}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
              isConfirmingReset
                ? { token: authResetToken, new_password: authNewPassword }
                : { email: authEmail.trim().toLowerCase(), locale }
            ),
            signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS)
          }
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(apiErrorMessage(data, isConfirmingReset ? appExtraText.resetFailed : appExtraText.resetRequestFailed));
        }
        if (!isConfirmingReset) {
          if (data.dev_token) {
            setAuthResetToken(data.dev_token);
            setAuthSuccessMsg(t('auth.resetDevTokenLoaded'));
          } else {
            setAuthSuccessMsg(data.message || t('auth.resetEmailSent'));
          }
          return;
        }
        setAuthSuccessMsg(data.message || t('auth.resetSuccess'));
        setAuthMode('login');
        setAuthPassword('');
        setAuthNewPassword('');
        setAuthResetToken('');
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
        return;
      }

      const endpoint = authMode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const cleanEmail = authEmail.trim().toLowerCase();
      const cleanName = (authName || authEmail.split('@')[0]).trim();
      const payload = authMode === 'register'
        ? { email: cleanEmail, password: authPassword, name: cleanName, locale }
        : { email: cleanEmail, password: authPassword };

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS)
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.error?.code === 'MAINTENANCE_MODE' || res.status === 503) {
          throw new Error(
            data?.error?.message ||
            (typeof data?.detail === 'string' ? data.detail : null) ||
            flashText.maintenanceRestricted
          );
        }
        throw new Error(apiErrorMessage(data, appExtraText.authFailed));
      }

      setAuthToken(data.token);
      setUser(data.user);
      const selectedStorage = authRememberMe ? localStorage : sessionStorage;
      const otherStorage = authRememberMe ? sessionStorage : localStorage;
      selectedStorage.setItem('vectria_auth_token', data.token);
      selectedStorage.setItem('vectria_auth_refresh_token', data.refresh_token);
      selectedStorage.setItem('vectria_auth_user', JSON.stringify(data.user));
      otherStorage.removeItem('vectria_auth_token');
      otherStorage.removeItem('vectria_auth_refresh_token');
      otherStorage.removeItem('vectria_auth_user');
      setShowAuthModal(false);
      setCurrentView('studio');
      setAuthPassword('');
      setAuthLegalConsent(false);
      setSaveStatusMsg(`${t('auth.welcomeBack')}, ${data.user.name}!`);
      setTimeout(() => setSaveStatusMsg(null), 4000);
    } catch (err: any) {
      if (err.name === 'TimeoutError' || err.message?.includes('timeout') || err.message?.includes('aborted')) {
        setAuthError(t('auth.timeout'));
      } else {
        setAuthError(err.message || runtimeText.authError);
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const loadGoogleGsiScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).google?.accounts?.id || (window as any).google?.accounts?.oauth2) {
        resolve(true);
        return;
      }
      const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true));
        existing.addEventListener('error', () => resolve(false));
        setTimeout(() => resolve(!!((window as any).google?.accounts?.id || (window as any).google?.accounts?.oauth2)), 1500);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  };

  // Unified Fast Google Backend Sign-In
  const sendGoogleProfileToBackend = async (credential: string) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
        signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS)
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.error?.code === 'MAINTENANCE_MODE' || res.status === 503) {
          throw new Error(
            data?.error?.message ||
            (typeof data?.detail === 'string' ? data.detail : null) ||
            flashText.maintenanceRestricted
          );
        }
        throw new Error(apiErrorMessage(data, appExtraText.googleFailed));
      }

      setAuthToken(data.token);
      setUser(data.user);
      localStorage.setItem('vectria_auth_token', data.token);
      localStorage.setItem('vectria_auth_refresh_token', data.refresh_token);
      localStorage.setItem('vectria_auth_user', JSON.stringify(data.user));
      setShowAuthModal(false);
      setCurrentView('studio');
      setSaveStatusMsg(runtimeText.signedGoogle(data.user.name, data.user.email));
      setTimeout(() => setSaveStatusMsg(null), 4500);
    } catch (err: any) {
      setAuthError(err.message || runtimeText.googleAuthFailed);
    } finally {
      setAuthLoading(false);
    }
  };

  // Unified Fast Google Credential Handler (Sub-second login via JWT ID Token)
  const handleGoogleCredentialResponse = async (response: any) => {
    if (!response?.credential) return;
    await sendGoogleProfileToBackend(response.credential);
  };

  // Google One Tap - Frictionless Sign-Up & Sign-In (Option A)
  useEffect(() => {
    if (user || authToken || currentView === 'settings') return;

    // Do not prompt Google One Tap during automated Lighthouse audits or headless crawlers
    if (typeof navigator !== 'undefined' && (/Lighthouse|HeadlessChrome|bot|crawl/i.test(navigator.userAgent) || (navigator as any).webdriver)) {
      return;
    }

    let isMounted = true;
    const setupOneTap = async () => {
      try {
        // Reset Google's cool-down cookie in dev/localhost so One Tap isn't suppressed
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          document.cookie = 'g_state=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT';
        }

        const configRes = await fetch(`${API_BASE}/api/auth/config`);
        if (!configRes.ok) return;
        const config = await configRes.json();
        const clientId = config?.google_client_id?.trim();
        if (!clientId || !clientId.includes('.apps.googleusercontent.com') || !config?.google_enabled || !isMounted) return;

        await loadGoogleGsiScript();
        if (!isMounted || !(window as any).google?.accounts?.id) return;

        (window as any).google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false
        });

        // Trigger Google One Tap floating prompt with status callback to handle dismissal cleanly
        (window as any).google.accounts.id.prompt((notification: any) => {
          if (notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.()) {
            // Handled cleanly without throwing console errors
          }
        });
      } catch (e) {
        // Silently catch network or script errors
      }
    };

    setupOneTap();
    return () => {
      isMounted = false;
    };
  }, [user, authToken]);

  // Render Google Official Instant Button in Auth Modal
  useEffect(() => {
    if (!showAuthModal || authMode === 'forgot' || user) return;
    let isMounted = true;

    const renderModalGoogleBtn = async () => {
      try {
        const configRes = await fetch(`${API_BASE}/api/auth/config`);
        if (!configRes.ok) return;
        const config = await configRes.json();
        const clientId = config?.google_client_id?.trim();
        if (!clientId || !isMounted) return;

        await loadGoogleGsiScript();
        if (!isMounted || !(window as any).google?.accounts?.id) return;

        (window as any).google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true
        });

        if (googleBtnContainerRef.current) {
          googleBtnContainerRef.current.innerHTML = '';
          const calculatedWidth = Math.min(Math.max(googleBtnContainerRef.current.offsetWidth || 360, 240), 380);
          (window as any).google.accounts.id.renderButton(googleBtnContainerRef.current, {
            type: 'standard',
            theme: theme === 'light' ? 'outline' : 'filled_black',
            size: 'large',
            text: authMode === 'register' ? 'signup_with' : 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: calculatedWidth
          });
        }
      } catch (err) {
        console.warn('Google button render error:', err);
      }
    };

    renderModalGoogleBtn();
    return () => {
      isMounted = false;
    };
  }, [showAuthModal, authMode, theme, user]);

  const handleGoogleAuth = async () => {
    if (authMode === 'register' && !authLegalConsent) {
      setAuthError(t('auth.consentRequired'));
      return;
    }
    setAuthLoading(true);
    setAuthError(null);

    try {
      await loadGoogleGsiScript();

      // Check if official Google button is already rendered inside the ref container and click it
      const renderedBtn = (googleBtnContainerRef.current?.querySelector('div[role="button"]') as HTMLElement) ||
                          (googleBtnContainerRef.current?.querySelector('iframe') as HTMLElement);
      if (renderedBtn) {
        renderedBtn.click();
        setAuthLoading(false);
        return;
      }

      // Secure fallback stays inside Google Identity Services so backend always receives a verifiable ID token.
      if ((window as any).google?.accounts?.id) {
        (window as any).google.accounts.id.prompt();
        setAuthLoading(false);
      } else {
        throw new Error(runtimeText.googleUnavailable);
      }
    } catch (err: any) {
      setAuthError(err.message || runtimeText.googlePromptFailed);
      setAuthLoading(false);
    }
  };

  const clearInvalidSession = () => {
    setAuthToken(null); setUser(null); setSaasOverview(null);
    for (const storage of [localStorage, sessionStorage]) {
      storage.removeItem('vectria_auth_token');
      storage.removeItem('vectria_auth_refresh_token');
      storage.removeItem('vectria_auth_user');
    }
  };

  const authenticatedFetch = async (path: string, options: RequestInit = {}, allowRefresh = true): Promise<Response> => {
    if (!allowRefresh) {
      const currentToken = localStorage.getItem('vectria_auth_token') || sessionStorage.getItem('vectria_auth_token') || authToken;
      if (!currentToken) throw new Error(t('auth.signInToContinue'));
      const headers = new Headers(options.headers || {});
      headers.set('Authorization', `Bearer ${currentToken}`);
      const activeWorkspace = user?.id ? localStorage.getItem(`vectria_active_workspace_${user.id}`) : '';
      if (activeWorkspace && (/^\/api\/saas\/(metadata|exports|ai)\//.test(path) || path.startsWith('/api/batch-assets/'))) headers.set('X-Workspace-ID', activeWorkspace);
      return fetch(`${API_BASE}${path}`, { ...options, headers });
    }
    const headers = new Headers(options.headers || {});
    const activeWorkspace = user?.id ? localStorage.getItem(`vectria_active_workspace_${user.id}`) : '';
    if (activeWorkspace && (/^\/api\/saas\/(metadata|exports|ai)\//.test(path) || path.startsWith('/api/batch-assets/'))) headers.set('X-Workspace-ID', activeWorkspace);
    return authenticatedRequest(API_BASE, path, { ...options, headers }, {
      accessTokenFallback: authToken,
      onSessionUpdated: session => {
        setAuthToken(session.token);
        if (session.user) setUser(session.user as UserProfile);
      },
      onSessionExpired: () => {
        clearInvalidSession();
        setAuthMode('login');
        setShowAuthModal(true);
      },
    });
  };

  const optionalAuthFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
    const currentToken = localStorage.getItem('vectria_auth_token') || sessionStorage.getItem('vectria_auth_token') || authToken;
    if (currentToken) {
      try {
        return await authenticatedFetch(path, options);
      } catch (err) {
        if (!user) {
          const headers = new Headers(options.headers || {});
          headers.delete('Authorization');
          return fetch(`${API_BASE}${path}`, { ...options, headers });
        }
        throw err;
      }
    }
    return fetch(`${API_BASE}${path}`, options);
  };

  const fetchNotifications = async () => {
    if (!user) return;
    setNotificationLoading(true);
    try {
      const response = await authenticatedFetch('/api/notifications?limit=5&page=1');
      const data = await response.json().catch(() => ({}));
      if (response.ok) { setNotifications(data.notifications || []); setNotificationUnreadCount(Number(data.unread_count || 0)); }
    } finally { setNotificationLoading(false); }
  };

  const fetchNotificationUnreadCount = async () => {
    if (!user) return;
    const response = await authenticatedFetch('/api/notifications/unread-count');
    const data = await response.json().catch(() => ({}));
    if (response.ok) setNotificationUnreadCount(Number(data.unread_count || 0));
  };

  const fetchNotificationCenter = async (page = notificationCenterPage, unreadOnly = notificationCenterUnreadOnly) => {
    setNotificationLoading(true);
    try {
      const response = await authenticatedFetch(`/api/notifications?limit=20&page=${page}&unread_only=${unreadOnly}`);
      const data = await response.json().catch(() => ({}));
      if (response.ok) { setNotificationCenterItems(data.notifications || []); setNotificationCenterPage(Number(data.pagination?.page || 1)); setNotificationCenterPages(Number(data.pagination?.total_pages || 1)); setNotificationUnreadCount(Number(data.unread_count || 0)); }
    } finally { setNotificationLoading(false); }
  };

  const deleteNotificationItem = async (notificationId: string) => {
    const response = await authenticatedFetch(`/api/notifications/${notificationId}`, { method: 'DELETE' });
    if (response.ok) { setNotifications(items => items.filter(item => item.id !== notificationId)); setNotificationCenterItems(items => items.filter(item => item.id !== notificationId)); await fetchNotificationUnreadCount(); }
  };

  const clearReadNotificationItems = async () => {
    const response = await authenticatedFetch('/api/notifications/read', { method: 'DELETE' });
    if (response.ok) { await fetchNotificationCenter(1, notificationCenterUnreadOnly); await fetchNotifications(); }
  };

  const markNotificationRead = async (notificationId: string) => {
    const response = await authenticatedFetch(`/api/notifications/${notificationId}/read`, { method: 'POST' });
    if (response.ok) { setNotifications(items => items.map(item => item.id === notificationId ? { ...item, read_at: new Date().toISOString() } : item)); setNotificationUnreadCount(count => Math.max(0, count - 1)); }
  };

  const markAllNotificationsRead = async () => {
    const response = await authenticatedFetch('/api/notifications/read-all', { method: 'POST' });
    if (response.ok) { setNotifications(items => items.map(item => ({ ...item, read_at: item.read_at || new Date().toISOString() }))); setNotificationUnreadCount(0); }
  };

  useEffect(() => {
    if (!user) { setNotifications([]); setNotificationUnreadCount(0); return; }
    fetchNotifications();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') fetchNotificationUnreadCount(); }, 60000);
    return () => window.clearInterval(timer);
  }, [user?.id]);

  const openWorkspaceDashboard = async () => {
    setShowUserMenu(false);
    setShowWorkspaceModal(true);
    setSaasLoading(true);
    setSaasError(null);
    try {
      if (!authToken) throw new Error(t('auth.signInToContinue'));
      const request = async (path: string): Promise<any> => {
        const response = await authenticatedFetch(path);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.requestFailed(response.status)));
        return data;
      };
      const data = await request('/api/saas/overview');
      setSaasOverview(data);
      if (data.workspace?.plan && user && data.workspace.plan !== user.plan) {
        const updatedUser={...user,plan:data.workspace.plan};setUser(updatedUser);
        const storage=localStorage.getItem('vectria_auth_user')?localStorage:sessionStorage;
        storage.setItem('vectria_auth_user',JSON.stringify(updatedUser));
      }
      const [peopleResult, usageResult, observabilityResult] = await Promise.allSettled([
        request('/api/saas/workspace/people'),
        request('/api/saas/usage?days=30'),
        request('/api/saas/metadata/observability')
      ]);
      setSaasPeople(peopleResult.status === 'fulfilled' ? peopleResult.value : null);
      setSaasUsage(usageResult.status === 'fulfilled' ? usageResult.value : null);
      setMetadataObservability(observabilityResult.status === 'fulfilled' ? observabilityResult.value : null);
    } catch (error) {
      const message = error instanceof Error && error.name === 'TimeoutError'
        ? 'Server took too long to respond. Try opening the account again.'
        : error instanceof Error ? error.message : 'Account failed to load.';
      setSaasError(message);
    } finally {
      setSaasLoading(false);
    }
  };

  const workspaceRequest = async (path: string, options: RequestInit = {}) => {
    const response = await authenticatedFetch(path, { ...options, headers: { 'Content-Type': 'application/json', ...(options.headers || {}) } });
    const data = await response.json();
    if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.workspaceFailed));
    await openWorkspaceDashboard();
    return data;
  };

  const openBillingPortal = async () => {
    const response = await authenticatedFetch('/api/saas/billing/portal');
    const data = await response.json();
    if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.billingUnavailable));
    window.open(data.portal_url, '_blank', 'noopener,noreferrer');
  };

  const handleSignOut = async () => {
    try {
      (window as any).google?.accounts?.id?.disableAutoSelect?.();
      const refreshToken = localStorage.getItem('vectria_auth_refresh_token') || sessionStorage.getItem('vectria_auth_refresh_token');
      if (refreshToken) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        });
      }
    } catch {}
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem('vectria_auth_token');
    localStorage.removeItem('vectria_auth_refresh_token');
    localStorage.removeItem('vectria_auth_user');
    sessionStorage.removeItem('vectria_auth_token');
    sessionStorage.removeItem('vectria_auth_refresh_token');
    sessionStorage.removeItem('vectria_auth_user');
    setShowUserMenu(false);
    if (window.location.pathname.includes('/settings')) {
      const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
      const localePath = firstSegment && ['en','id','es','pt','pt-br','ru','ru-ru','tr','tr-tr','vi','vi-vn','th','th-th','ar','ar-sa','zh','zh-cn','ja','ko'].includes(firstSegment.toLowerCase()) ? `/${firstSegment}/` : '/';
      window.history.replaceState({}, document.title, localePath);
    }
    setCurrentView('landing');
  };

  const [isCheckingOut, setIsCheckingOut] = useState<boolean>(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordChangeError(null);
    setPasswordChangeSuccess(null);
    const pwdCopy = studio.passwordModal;

    if (!currentPassword) {
      setPasswordChangeError(pwdCopy?.placeholderCurrent || (locale === 'id' ? 'Password saat ini wajib diisi.' : 'Current password is required.'));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordChangeError(pwdCopy?.minLenError || (locale === 'id' ? 'Password baru minimal 8 karakter.' : 'New password must be at least 8 characters long.'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordChangeError(pwdCopy?.mismatchError || (locale === 'id' ? 'Konfirmasi password baru tidak cocok.' : 'New passwords do not match.'));
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordChangeError(pwdCopy?.sameError || (locale === 'id' ? 'Password baru harus berbeda dengan password lama.' : 'New password must be different from current password.'));
      return;
    }

    setPasswordChangeLoading(true);
    try {
      const res = await authenticatedFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errorMsg = data?.detail || data?.error?.message || (locale === 'id' ? 'Gagal memperbarui password.' : 'Failed to change password.');
        setPasswordChangeError(errorMsg);
        return;
      }
      if (data?.token) {
        setAuthToken(data.token);
        const storage = localStorage.getItem('vectria_auth_token') ? localStorage : sessionStorage;
        storage.setItem('vectria_auth_token', data.token);
        if (data?.refresh_token) storage.setItem('vectria_auth_refresh_token', data.refresh_token);
        if (data?.user) {
          setUser(data.user);
          storage.setItem('vectria_auth_user', JSON.stringify(data.user));
        }
      }
      setPasswordChangeSuccess(pwdCopy?.successMsg || (locale === 'id' ? 'Password Anda berhasil diperbarui!' : 'Your password has been changed successfully.'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setPasswordChangeSuccess(null);
      }, 1800);
    } catch (err: any) {
      setPasswordChangeError(err?.message || (locale === 'id' ? 'Terjadi kesalahan sistem.' : 'An error occurred.'));
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const openPolarCheckout = async (checkoutUrl: string, onCompleted?: () => void) => {
    try {
      const { PolarEmbedCheckout } = await import('@polar-sh/checkout/embed');
      const embedInstance = await PolarEmbedCheckout.create(checkoutUrl, {
        theme: theme === 'dark' ? 'dark' : 'light'
      });
      embedInstance.addEventListener('success', async () => {
        if (authToken) {
          const response = await authenticatedFetch('/api/auth/me');
          const data = await response.json().catch(() => ({}));
          if (response.ok && data.user) {
            setUser(data.user);
            const storage = localStorage.getItem('vectria_auth_token') ? localStorage : sessionStorage;
            storage.setItem('vectria_auth_user', JSON.stringify(data.user));
          }
        }
        onCompleted?.();
      });
      embedInstance.addEventListener('close', () => {
        setIsCheckingOut(false);
        setIsTopupLoading(false);
      });
    } catch (err) {
      console.warn('Polar embed fallback to redirect:', err);
      window.location.href = checkoutUrl;
    }
  };

  const handleTopupCredits = async (packId: string, amount: number, price: number, type: 'vector' | 'ai') => {
    if (!user) {
      setAuthMode('login');
      setShowAuthModal(true);
      setShowTopupModal(false);
      return;
    }
    setIsTopupLoading(true);
    const topupCopy = studio.topupModal;
    const connectingText = topupCopy?.connectingCheckout || (locale === 'id' ? 'Menghubungkan ke pembayaran...' : 'Connecting to checkout...');
    setSaveStatusMsg(connectingText);
    try {
      const res = await authenticatedFetch('/api/v1/billing/topup-credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pack_id: packId,
          amount,
          price,
          credit_type: type,
          redirect_url: `${window.location.origin}/?topup=success`
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.checkout_url) {
        setShowTopupModal(false);
        if (data?.provider === 'polar') {
          setSaveStatusMsg(null);
          await openPolarCheckout(data.checkout_url, () => {
            const successMsg = topupCopy?.successAlert(amount, type === 'vector' ? 'Vector' : 'AI') || (locale === 'id' ? `Berhasil menambahkan +${amount} Kredit!` : `Successfully added +${amount} Credits!`);
            setSaveStatusMsg(successMsg);
            setTimeout(() => setSaveStatusMsg(null), 5000);
          });
        } else {
          // Direct redirect to hosted checkout page (e.g. Lemon Squeezy at pay.vectria.app)
          window.location.href = data.checkout_url;
        }
        return;
      }
      if (res.ok && data?.status === 'success' && data?.user) {
        setUser(data.user);
        const storage = localStorage.getItem('vectria_auth_token') ? localStorage : sessionStorage;
        storage.setItem('vectria_auth_user', JSON.stringify(data.user));
        setShowTopupModal(false);
        const successMsg = topupCopy?.successAlert(amount, type === 'vector' ? 'Vector' : 'AI') || (locale === 'id' ? `Berhasil menambahkan +${amount} Kredit!` : `Successfully added +${amount} Credits!`);
        setSaveStatusMsg(successMsg);
        setTimeout(() => setSaveStatusMsg(null), 5000);
        return;
      }
      const fallbackErr = locale === 'id' ? 'Gagal memproses pembelian kredit.' : 'Failed to top up credits.';
      const errMsg = apiErrorMessage(data, data?.detail || data?.error?.message || fallbackErr);
      showAppAlert(errMsg);
    } catch (err: any) {
      showAppAlert(err?.message || (locale === 'id' ? 'Terjadi kesalahan pada top-up kredit.' : 'Error during credit top up.'));
    } finally {
      setIsTopupLoading(false);
    }
  };

  const handleUpgradePlan = async (targetPlan: 'starter' | 'pro' | 'studio', _planCredits: number, planTitle: string) => {
    if (!user) {
      setAuthMode('register');
      setShowAuthModal(true);
      setShowPricingModal(false);
      return;
    }
    const userPlanRank = PLAN_TIER_RANK[user.plan?.toLowerCase() || 'free'] ?? 0;
    const targetPlanRank = PLAN_TIER_RANK[targetPlan.toLowerCase()] ?? 0;
    if (targetPlanRank < userPlanRank) {
      showAppAlert(
        studio.pricingModal?.higherPlanAlert ||
        (locale === 'id' ? 'Paket Anda saat ini lebih tinggi.' : 'Your current plan is higher.')
      );
      return;
    }
    if (targetPlanRank === userPlanRank) {
      showAppAlert(
        studio.pricingModal?.samePlanNotice ||
        (locale === 'id'
          ? 'Anda sudah berlangganan paket ini. Untuk menambah kredit, silakan gunakan fitur Beli Kredit (Top-up).'
          : 'You are already subscribed to this plan. To add more credits, please use the Top-up Credits feature.')
      );
      return;
    }
    const checkoutFailedText = locale === 'id' 
      ? 'Gagal memulai checkout pembayaran.' 
      : 'Failed to start payment checkout.';
    const connectingText = locale === 'id'
      ? `Menghubungkan ke pembayaran paket ${planTitle}...`
      : `Connecting to ${planTitle} plan checkout...`;

    try {
      if (authToken) {
        setIsCheckingOut(true);
        setSaveStatusMsg(connectingText);
        // The server selects the active MoR from global and country routing rules.
        const res = await authenticatedFetch('/api/v1/billing/create-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            plan: targetPlan,
            billing_cycle: pricingBillingCycle,
            redirect_url: `${window.location.origin}/?billing=success`
          })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.checkout_url) {
          setShowPricingModal(false);
          if (data?.provider === 'polar') {
            setSaveStatusMsg(null);
            await openPolarCheckout(data.checkout_url, () => {
              const upgradeSuccessText = locale === 'id' ? `Selamat! Anda telah berlangganan paket ${planTitle}.` : `Congratulations! You are now subscribed to ${planTitle}.`;
              setSaveStatusMsg(upgradeSuccessText);
              setTimeout(() => setSaveStatusMsg(null), 5000);
            });
          } else {
            // Direct redirect to hosted checkout page (e.g. Lemon Squeezy at pay.vectria.app)
            window.location.href = data.checkout_url;
          }
          return;
        } else {
          const detailMsg = data?.detail || data?.error?.message || data?.message;
          const errMsg = detailMsg ? `${checkoutFailedText} (${detailMsg})` : checkoutFailedText;
          setSaveStatusMsg(errMsg);
          setTimeout(() => setSaveStatusMsg(null), 8000);
          return;
        }
      }
    } catch (err: any) {
      console.error('Upgrade error:', err);
      const detailMsg = err?.message;
      setSaveStatusMsg(detailMsg ? `${checkoutFailedText} (${detailMsg})` : checkoutFailedText);
      setTimeout(() => setSaveStatusMsg(null), 8000);
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user) return;
    setIsCancellingSubscription(true);
    try {
      if (authToken) {
        const res = await authenticatedFetch('/api/auth/cancel-subscription', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            setUser(data.user);
            localStorage.setItem('vectria_auth_user', JSON.stringify(data.user));
            setShowCancelConfirm(false);
            setSaveStatusMsg(studio.pricingModal.cancelledSuccess);
            setTimeout(() => setSaveStatusMsg(null), 5000);
            return;
          }
        }
      }
      // Local fallback
      const fallbackStart = user?.subscription?.current_period_start || new Date().toISOString();
      const fallbackEnd = user?.subscription?.current_period_end || new Date(Date.now() + 30 * 86400000).toISOString();
      const updated: UserProfile = {
        ...user,
        subscription: {
          ...user.subscription,
          cancel_at_period_end: true,
          status: 'active',
          current_period_start: fallbackStart,
          current_period_end: fallbackEnd
        }
      };
      setUser(updated);
      localStorage.setItem('vectria_auth_user', JSON.stringify(updated));
      setShowCancelConfirm(false);
      setSaveStatusMsg(studio.pricingModal.cancelledSuccess);
      setTimeout(() => setSaveStatusMsg(null), 5000);
    } catch (err) {
      console.error('Cancel subscription error:', err);
      const fallbackStart = user?.subscription?.current_period_start || new Date().toISOString();
      const fallbackEnd = user?.subscription?.current_period_end || new Date(Date.now() + 30 * 86400000).toISOString();
      const updated: UserProfile = {
        ...user,
        subscription: {
          ...user.subscription,
          cancel_at_period_end: true,
          status: 'active',
          current_period_start: fallbackStart,
          current_period_end: fallbackEnd
        }
      };
      setUser(updated);
      localStorage.setItem('vectria_auth_user', JSON.stringify(updated));
      setShowCancelConfirm(false);
      setSaveStatusMsg(studio.pricingModal.cancelledSuccess);
      setTimeout(() => setSaveStatusMsg(null), 5000);
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const applyWorkspaceAiConfig = (data: any, provider: 'custom' | 'gemini' | 'openai') => {
    const maskedKeys = data.has_api_key ? String(data.api_key_masked || Array.from({ length: Number(data.api_key_count || 1) }, () => '********').join('\n')) : '';
    setAiProvider(provider);
    setAiKey(maskedKeys);
    setAiKeyCount(Number(data.api_key_count || 0));
    setAiStoredMask(maskedKeys);
    setAiModel(data.model || AI_PROVIDER_DEFAULTS[provider].defaultModel);
    setAiFallbackModel(data.fallback_model || AI_PROVIDER_DEFAULTS[provider].defaultFallback);
    setAiEndpoint(data.custom_endpoint || 'https://openrouter.ai/api/v1/chat/completions');
    aiProviderDraftsRef.current[provider] = { key: maskedKeys, keyCount: Number(data.api_key_count || 0), storedMask: maskedKeys, model: data.model || AI_PROVIDER_DEFAULTS[provider].defaultModel, fallbackModel: data.fallback_model || AI_PROVIDER_DEFAULTS[provider].defaultFallback, endpoint: data.custom_endpoint || 'https://openrouter.ai/api/v1/chat/completions' };
    setAiEnabled(Boolean(data.enabled));
    setAllowVectriaFallback(Boolean(data.allow_vectria_fallback));
  };

  const handleProviderChange = async (prov: 'custom' | 'gemini' | 'openai') => {
    aiProviderDraftsRef.current[aiProvider] = {
      key: aiKey,
      keyCount: aiKeyCount,
      storedMask: aiStoredMask,
      model: aiModel,
      fallbackModel: aiFallbackModel,
      endpoint: aiEndpoint
    };
    const draft = aiProviderDraftsRef.current[prov];
    setAiProvider(prov);
    setAiTestResult(null);
    setAiKey(draft?.key || '');
    setAiKeyCount(draft?.keyCount || 0);
    setAiStoredMask(draft?.storedMask || '');
    setAiModel(draft?.model || AI_PROVIDER_DEFAULTS[prov].defaultModel);
    setAiFallbackModel(draft?.fallbackModel || AI_PROVIDER_DEFAULTS[prov].defaultFallback);
    setAiEndpoint(draft?.endpoint || 'https://openrouter.ai/api/v1/chat/completions');
    if (!authToken) return;
    try {
      const response = await authenticatedFetch(`/api/saas/ai/config?provider=${encodeURIComponent(prov)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.aiSettingsLoadFailed));
      applyWorkspaceAiConfig(data, prov);
    } catch (error: any) {
      setAiTestResult({ ok: false, message: error.message });
    }
  };

  useEffect(() => {
    if (!authToken) {
      setAiEnabled(false);
      setAiKeyCount(0);
      setAiKey('');
      setAiStoredMask('');
      return;
    }
    authenticatedFetch('/api/saas/ai/config')
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.aiSettingsLoadFailed));
        const provider = (data.provider || 'gemini') as 'custom' | 'gemini' | 'openai';
        aiProviderDraftsRef.current = {};
        applyWorkspaceAiConfig(data, provider);
      })
      .catch(error => setAiTestResult({ ok: false, message: error.message }));
  }, [authToken]);

  const testAiConnection = async () => {
    if (!authToken) {
      setAiTestResult({ ok: false, message: runtimeText.signInAiTest });
      return;
    }
    setIsTestingAi(true);
    setAiTestResult(null);
    try {
      await persistWorkspaceAiConfig();
      const res = await authenticatedFetch('/api/saas/ai/test', {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(apiErrorMessage(data, appExtraText.connectionFailed));
      setAiTestResult({ ok: true, message: data.message || runtimeText.aiAuthenticated(AI_PROVIDER_DEFAULTS[aiProvider].name) });
    } catch (err: any) {
      setAiTestResult({ ok: false, message: err.message || runtimeText.serverUnreachable });
    } finally {
      setIsTestingAi(false);
    }
  };

  const persistWorkspaceAiConfig = async () => {
    if (!authToken) throw new Error(runtimeText.signInAiSave);
    const activeModelId = aiModel === 'custom' ? aiCustomModel : aiModel;
    const activeFallbackId = aiFallbackModel === 'custom' ? aiCustomFallbackModel : aiFallbackModel;
    const submittedKeys = aiKey === aiStoredMask ? null : aiKey.split(/\r?\n/).map(key => key.trim()).filter((key, index, keys) => key && keys.indexOf(key) === index);
    if (submittedKeys && submittedKeys.length > 10) throw new Error(runtimeText.maxApiKeys);
    const response = await authenticatedFetch('/api/saas/ai/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider: aiProvider, api_key: submittedKeys ? submittedKeys.join('\n') : null, model: activeModelId, fallback_model: activeFallbackId !== 'none' ? activeFallbackId : null, custom_endpoint: aiEndpoint, enabled: aiEnabled || Boolean(submittedKeys?.length) || aiKeyCount > 0, allow_vectria_fallback: allowVectriaFallback })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.aiSettingsSaveFailed));
    const persistedKeyCount = Number(data.api_key_count || submittedKeys?.length || 0);
    const persistedMask = data.has_api_key ? String(data.api_key_masked || Array.from({ length: persistedKeyCount || 1 }, () => '********').join('\n')) : '';
    setAiKey(persistedMask);
    setAiKeyCount(persistedKeyCount);
    setAiStoredMask(persistedMask);
    aiProviderDraftsRef.current[aiProvider] = { key: persistedMask, keyCount: persistedKeyCount, storedMask: persistedMask, model: aiModel, fallbackModel: aiFallbackModel, endpoint: aiEndpoint };
    setAiEnabled(Boolean(data.enabled));
    setAllowVectriaFallback(Boolean(data.allow_vectria_fallback));
    return data;
  };

  const saveAiConfig = async () => {
    try {
      await persistWorkspaceAiConfig();
      setSaveStatusMsg(runtimeText.aiSettingsSaved(AI_PROVIDER_DEFAULTS[aiProvider].name));
      setTimeout(() => setSaveStatusMsg(null), 4000);
    } catch (error: any) {
      setAiTestResult({ ok: false, message: error.message || appExtraText.aiSettingsSaveFailed });
    }
  };

  const resetAiProviderKeys = async () => {
    if (!authToken || !aiKeyCount) return;
    const providerName = AI_PROVIDER_DEFAULTS[aiProvider].name;
    showAppConfirm(runtimeText.resetKeysConfirm(providerName), async () => {
      setIsResettingAiKeys(true);
      setAiTestResult(null);
      try {
        const activeModelId = aiModel === 'custom' ? aiCustomModel : aiModel;
        const activeFallbackId = aiFallbackModel === 'custom' ? aiCustomFallbackModel : aiFallbackModel;
        const response = await authenticatedFetch('/api/saas/ai/config', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider: aiProvider, api_key: null, clear_api_keys: true, model: activeModelId, fallback_model: activeFallbackId !== 'none' ? activeFallbackId : null, custom_endpoint: aiEndpoint, enabled: false, allow_vectria_fallback: allowVectriaFallback })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(apiErrorMessage(data, runtimeText.resetKeysFailed));
        applyWorkspaceAiConfig(data, aiProvider);
        setAiTestResult({ ok: true, message: runtimeText.keysRemoved(providerName) });
      } catch (error: any) {
        setAiTestResult({ ok: false, message: error.message || runtimeText.resetKeysFailed });
      } finally {
        setIsResettingAiKeys(false);
      }
    }, { isDestructive: true });
  };

  const refreshCurrentUser = async () => {
    if (!authToken) return;
    const response = await authenticatedFetch('/api/auth/me');
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.user) return;
    setUser(data.user);
    const storage = localStorage.getItem('vectria_auth_token') ? localStorage : sessionStorage;
    storage.setItem('vectria_auth_user', JSON.stringify(data.user));
  };

  // Always refresh user and subscription data on mount if logged in
  useEffect(() => {
    if (authToken) {
      refreshCurrentUser();
    }
  }, [authToken]);

  // Refresh user and subscription data whenever Pricing Modal opens
  useEffect(() => {
    if (showPricingModal && authToken) {
      refreshCurrentUser();
    }
  }, [showPricingModal, authToken]);


  const getMetadataAiPolicy = (assetCount: number) => {
    const hasByok = aiEnabled && aiKeyCount > 0;
    const managedEligible = Boolean(user && ['pro', 'studio', 'agency'].includes(user.plan));
    const agencyUnlimited = user?.plan === 'agency';
    if (!user || user.plan === 'free') return { allowed: false, source: 'blocked', charge: 0, message: studio.aiModal.insufficientAiCredits };
    if (agencyUnlimited) return { allowed: true, source: 'managed', charge: 0, message: studio.aiModal.sourceManaged };
    if (hasByok) return { allowed: true, source: allowVectriaFallback && managedEligible ? 'fallback' : 'byok', charge: 0, message: allowVectriaFallback && managedEligible ? studio.aiModal.sourceFallback : studio.aiModal.sourceByok };
    if (!managedEligible) return { allowed: false, source: 'blocked', charge: 0, message: studio.aiModal.sourceByok };
    const charge = Math.max(0, assetCount);
    if (!agencyUnlimited && (user.ai_credits ?? 0) < charge) return { allowed: false, source: 'managed', charge, message: studio.aiModal.insufficientAiCredits };
    return { allowed: true, source: 'managed', charge, message: studio.aiModal.sourceManaged };
  };

  const mapMetadataAsset = (asset: any, job: any = {}) => ({
    title: asset.title, description: asset.description, keywords: asset.keywords, category: asset.category,
    marketplace: (asset.marketplace || job.marketplace || metadataMarketplace) as MetadataMarketplace,
    used_model: asset.model || job.model, provider: asset.provider || job.provider, job_id: job.id, asset_id: asset.id,
    validation_status: asset.validation_status, validation: asset.validation, ai_source: asset.ai_source,
    ai_credit_charged: Number(asset.ai_credit_charged || 0), fallback_reason: asset.fallback_reason
  });

  const withMetadataQuality = (item: QueueItem, metadata: NonNullable<QueueItem['metadata']>): QueueItem => ({
    ...item,
    metadata,
    qualityReport: item.qualityReport ? applyMetadataReadiness(item.qualityReport, metadata) : item.qualityReport,
  });

  const changeMetadataMarketplace = (marketplace: MetadataMarketplace) => {
    setMetadataMarketplace(marketplace);
    setQueue(previous => previous.map(item => item.metadata
      ? withMetadataQuality(item, { ...item.metadata, marketplace })
      : item));
  };

  const summarizeMetadataAssets = (assets: any[]) => {
    const byok = assets.filter(asset => asset.ai_source === 'byok').length;
    const fallback = assets.filter(asset => asset.ai_source === 'vectria_fallback').length;
    const managed = assets.filter(asset => asset.ai_source === 'vectria_managed').length;
    const charged = assets.reduce((sum, asset) => sum + Number(asset.ai_credit_charged || 0), 0);
    return studio.aiModal.resultSummary(byok, managed, fallback, charged);
  };

  useEffect(() => {
    if (!authToken || isRestoringQueue || queue.length === 0) return;
    let cancelled = false;
    const rehydrate = async () => {
      let assets: any[] = [];
      try {
        const response = await authenticatedFetch('/api/saas/metadata/assets/latest?limit=500');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data.assets) && data.assets.length > 0) {
            assets = data.assets;
          }
        }
      } catch (err) {
        console.warn('Could not fetch latest metadata assets:', err);
      }

      if (assets.length === 0) {
        try {
          const jobsRes = await authenticatedFetch('/api/saas/metadata/jobs?limit=10');
          if (jobsRes.ok) {
            const jobsData = await jobsRes.json();
            const jobs = Array.isArray(jobsData.jobs) ? jobsData.jobs : [];
            const completedJobs = jobs.filter((j: any) => j.status === 'completed' || Number(j.completed_items || 0) > 0);
            for (const job of completedJobs) {
              if (cancelled) return;
              try {
                const jobDetailRes = await authenticatedFetch(`/api/saas/metadata/jobs/${encodeURIComponent(job.id)}`);
                if (jobDetailRes.ok) {
                  const jobDetail = await jobDetailRes.json();
                  if (Array.isArray(jobDetail.assets)) {
                    assets.push(...jobDetail.assets.map((a: any) => ({ ...a, job_id: job.id, marketplace: job.marketplace })));
                  }
                }
              } catch {
                // Ignore individual job detail errors
              }
            }
          }
        } catch (err) {
          console.warn('Could not fallback-fetch metadata jobs:', err);
        }
      }

      if (cancelled || assets.length === 0) return;

      const byClientId = new Map(assets.filter((asset: any) => asset.source_client_id).map((asset: any) => [asset.source_client_id, asset]));
      const byFilename = new Map<string, any>();
      assets.forEach((asset: any) => { if (!byFilename.has(asset.filename)) byFilename.set(asset.filename, asset); });

      setQueue(previous => previous.map(item => {
        if (item.metadata) return item;
        const asset: any = byClientId.get(item.id) || byFilename.get(item.name);
        return asset ? withMetadataQuality(item, mapMetadataAsset(asset, { id: asset.job_id, marketplace: asset.marketplace })) : item;
      }));
    };

    void rehydrate();
    return () => { cancelled = true; };
  }, [authToken, isRestoringQueue, queue.length]);

  const generateMetadataForActiveItem = async (targetItem?: QueueItem) => {
    const itemToTag = targetItem || activeItem;
    if (!itemToTag) return;
    if (!authToken) {
      setSaveStatusMsg(t('studio.metadataSignIn'));
      setTimeout(() => setSaveStatusMsg(null), 4000);
      return;
    }
    const policy = getMetadataAiPolicy(1);
    if (!policy.allowed) { setSaveStatusMsg(policy.message); setTimeout(() => setSaveStatusMsg(null), 5000); return; }
    setIsGeneratingItemMetadata(true);
    try {
      const imageDataUrl = await fileToDataUrl(itemToTag.file);
      const res = await authenticatedFetch('/api/saas/metadata/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ marketplace: metadataMarketplace, items: [{ image_b64: imageDataUrl, filename: itemToTag.name, client_id: itemToTag.id }] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(apiErrorMessage(data, appExtraText.metadataFailed));
      const asset = data.assets?.[0];
      setActiveMetadataJob(data.job);
      setMetadataJobs(previous=>[data.job,...previous.filter(job=>job.id!==data.job.id)]);
      if (asset) {
        setQueue(prev => prev.map(q => q.id === itemToTag.id ? withMetadataQuality(q, mapMetadataAsset(asset, data.job)) : q));
        setSaveStatusMsg(summarizeMetadataAssets([asset]));
        await refreshCurrentUser();
      } else {
        setSaveStatusMsg(t('studio.metadataJobStarted'));
      }
      setTimeout(() => setSaveStatusMsg(null), 5000);
    } catch (err: any) {
      showAppAlert(`${runtimeText.metadataError}: ${err.message}`);
    } finally {
      setIsGeneratingItemMetadata(false);
    }
  };

  const generateBatchMetadata = async () => {
    const readyItems = queue.filter(item => item.status === 'done' && item.previewUrl);
    if (!readyItems.length) { showAppAlert(metadataSaasText.batchEmpty); return; }
    if (!authToken) { setSaveStatusMsg(t('studio.metadataSignIn')); return; }
    const policy = getMetadataAiPolicy(readyItems.length);
    if (!policy.allowed) { setSaveStatusMsg(policy.message); setTimeout(() => setSaveStatusMsg(null), 5000); return; }
    setIsGeneratingBatchMetadata(true);
    try {
      const batchItems = await Promise.all(readyItems.map(async item => ({ filename: item.name, image_b64: await fileToDataUrl(item.file), client_id: item.id })));
      const response = await authenticatedFetch('/api/saas/metadata/jobs', {
        method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${authToken}`},
        body:JSON.stringify({marketplace:metadataMarketplace,items:batchItems})
      });
      const data=await response.json();
      if(!response.ok) throw new Error(apiErrorMessage(data, appExtraText.batchMetadataFailed));
      const assetsByFilename=new Map((data.assets||[]).map((asset:any)=>[asset.filename,asset]));
      setQueue(previous=>previous.map(item=>{const asset:any=assetsByFilename.get(item.name);return asset?withMetadataQuality(item,mapMetadataAsset(asset,data.job)):item;}));
      setMetadataJobs(previous=>[data.job,...previous.filter(job=>job.id!==data.job.id)]);
      setActiveMetadataJob(data.job);
      setSaveStatusMsg((data.assets||[]).length ? summarizeMetadataAssets(data.assets) : t('studio.batchMetadataJobStarted'));
      if ((data.assets||[]).length) await refreshCurrentUser();
      setTimeout(()=>setSaveStatusMsg(null),5000);
    } catch(error:any) { showAppAlert(`${runtimeText.batchMetadataError}: ${error.message}`); }
    finally { setIsGeneratingBatchMetadata(false); }
  };

  useEffect(() => {
    if (!authToken || !activeMetadataJob?.id || !['queued','processing'].includes(activeMetadataJob.status)) return;
    let stopped = false;
    const poll = async () => {
      try {
        const response = await authenticatedFetch(`/api/saas/metadata/jobs/${activeMetadataJob.id}`);
        const data = await response.json();
        if (!response.ok || stopped) return;
        setActiveMetadataJob(data.job);
        setMetadataJobs(previous=>[data.job,...previous.filter(job=>job.id!==data.job.id)]);
        if (!['queued','processing'].includes(data.job.status)) {
          const assetsByFilename=new Map((data.assets||[]).map((asset:any)=>[asset.filename,asset]));
          setQueue(previous=>previous.map(item=>{const asset:any=assetsByFilename.get(item.name);return asset?withMetadataQuality(item,mapMetadataAsset(asset,data.job)):item;}));
          setIsGeneratingBatchMetadata(false);setIsGeneratingItemMetadata(false);
          const safeStatus = data.job.status === 'completed' ? metadataSaasText.batchDone(data.job.completed_items||0) : runtimeText.metadataJobStatus(localizedStatus(data.job.status, locale), data.job.failed_items || 0);
          setSaveStatusMsg((data.assets||[]).length ? summarizeMetadataAssets(data.assets) : safeStatus);
          await refreshCurrentUser();
          return;
        }
        window.setTimeout(poll,1000);
      } catch { if(!stopped) window.setTimeout(poll,1500); }
    };
    void poll();
    return () => { stopped=true; };
  }, [authToken, activeMetadataJob?.id, activeMetadataJob?.status]);

  useEffect(() => {
    if (!showMetadataHistory || !authToken || !metadataJobs.some(job => job.status === 'queued' || job.status === 'processing')) return;
    const timer = window.setInterval(() => { void loadMetadataHistory(true); }, 2000);
    return () => window.clearInterval(timer);
  }, [showMetadataHistory, authToken, metadataJobs]);

  const loadMetadataHistory = async (silent = false) => {
    if(!authToken) return;
    if(!silent) setMetadataHistoryLoading(true);
    try {
      const response=await authenticatedFetch('/api/saas/metadata/jobs?limit=30');
      const data=await response.json(); if(!response.ok) throw new Error(apiErrorMessage(data, metadataSaasText.loadError));
      setMetadataJobs(data.jobs||[]); setShowMetadataHistory(true);
    } catch(error:any) { if(!silent) setSaveStatusMsg(error.message||metadataSaasText.loadError); }
    finally { if(!silent) setMetadataHistoryLoading(false); }
  };

  const retryMetadataJob = async (jobId:string) => {
    if(!authToken) return;
    const retryCandidates=queue.filter(item=>item.status==='done'&&item.previewUrl&&!item.metadata);
    if(!retryCandidates.length){setSaveStatusMsg(t('studio.noRetryAssets'));return;}
    const retryItems=await Promise.all(retryCandidates.map(async item=>({filename:item.name,image_b64:await fileToDataUrl(item.file),client_id:item.id})));
    setIsGeneratingBatchMetadata(true);
    try{
      const response=await authenticatedFetch(`/api/saas/metadata/jobs/${jobId}/retry`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({marketplace:'adobe',items:retryItems})});
      const data=await response.json();if(!response.ok)throw new Error(apiErrorMessage(data, appExtraText.retryFailed));
      setMetadataJobs(previous=>[data.job,...previous]);await loadMetadataHistory(true);
    }catch(error:any){setSaveStatusMsg(error.message||metadataSaasText.retry); }finally{setIsGeneratingBatchMetadata(false);}
  };

  const downloadMetadataJobCsv = async (jobId:string) => {
    if(!authToken) return;
    const response=await authenticatedFetch(`/api/saas/metadata/jobs/${jobId}/csv`);
    if(!response.ok){const data=await response.json().catch(()=>({}));throw new Error(apiErrorMessage(data, appExtraText.csvExportFailed));}
    const blob=await response.blob();const disposition=response.headers.get('Content-Disposition')||'';const filename=disposition.match(/filename="?([^";]+)"?/i)?.[1]||'vectria_metadata.csv';const url=URL.createObjectURL(blob);const anchor=document.createElement('a');anchor.href=url;anchor.download=filename;anchor.click();URL.revokeObjectURL(url);
  };

  const loadExportHistory = async () => {
    if (!authToken) return;
    setExportHistoryLoading(true);
    try {
      const response = await authenticatedFetch('/api/saas/exports?limit=30');
      const data = await response.json();
      if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.exportHistoryFailed));
      setExportJobs(data.exports || []);
      setShowExportHistory(true);
    } catch (error: any) {
      setSaveStatusMsg(error.message || appExtraText.exportHistoryFailed);
    } finally {
      setExportHistoryLoading(false);
    }
  };

  const downloadExportJob = async (job: any) => {
    if (!authToken || job.status !== 'ready') return;
    setDownloadingExportId(job.id);
    try {
      const response = await authenticatedFetch(`/api/saas/exports/${job.id}/download`);
      if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(apiErrorMessage(data, appExtraText.exportDownloadFailed)); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = job.filename || 'vectria_export.zip'; anchor.click(); URL.revokeObjectURL(url);
    } catch (error: any) {
      setSaveStatusMsg(error.message || appExtraText.exportDownloadFailed);
    } finally {
      setDownloadingExportId(null);
    }
  };

  const saveActiveMetadata = async (patch: { title?: string; description?: string; keywords?: string[]; category?: string }) => {
    if (!activeItem?.metadata?.asset_id || !authToken) return;
    const response = await authenticatedFetch(`/api/saas/metadata/assets/${activeItem.metadata.asset_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.metadataSaveFailed));
    setQueue(prev => prev.map(item => {
      if (item.id !== activeItem.id || !item.metadata) return item;
      const metadata = { ...item.metadata, ...patch, validation_status: data.validation.status, validation: data.validation };
      return withMetadataQuality(item, metadata);
    }));
    setSaveStatusMsg(t('studio.metadataSaved'));
    setTimeout(() => setSaveStatusMsg(null), 3000);
  };

  const normalizeActiveKeywords = async (alphabetical = false) => {
    if (!activeItem?.metadata) return;
    const seen = new Set<string>();
    let keywords = activeItem.metadata.keywords.map(value => value.trim()).filter(value => { const key=value.toLocaleLowerCase(); if(!value||seen.has(key)) return false; seen.add(key); return true; });
    if (alphabetical) keywords = [...keywords].sort((a,b) => a.localeCompare(b));
    setQueue(previous => previous.map(item => item.id===activeItem.id && item.metadata ? withMetadataQuality(item,{...item.metadata,keywords}) : item));
    await saveActiveMetadata({keywords});
  };

  const applyActiveCategoryToBatch = async () => {
    if (!activeItem?.metadata?.category || !authToken) return;
    const targets=queue.filter(item=>item.metadata?.asset_id && item.id!==activeItem.id);
    const results=await Promise.allSettled(targets.map(item=>authenticatedFetch(`/api/saas/metadata/assets/${item.metadata!.asset_id}`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({category:activeItem.metadata!.category})}).then(async response=>{if(!response.ok){const data=await response.json().catch(()=>({}));throw new Error(apiErrorMessage(data, appExtraText.bulkCategoryFailed));}return response.json();})));
    const succeeded=results.filter(result=>result.status==='fulfilled').length;
    setQueue(previous=>previous.map(item=>item.metadata?.asset_id?withMetadataQuality(item,{...item.metadata,category:activeItem.metadata!.category}):item));
    setSaveStatusMsg(runtimeText.categoryApplied(succeeded + 1));
  };

  const exportMarketplaceCsv = async () => {
    const serverJob = activeItem?.metadata?.job_id;
    if (serverJob && authToken) {
      try {
        const response = await authenticatedFetch(`/api/saas/metadata/jobs/${serverJob}/csv`);
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(apiErrorMessage(data, appExtraText.csvExportFailed));
        }
        const blob = await response.blob();
        const disposition = response.headers.get('Content-Disposition') || '';
        const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'vectria_metadata.csv';
        const url = URL.createObjectURL(blob); const anchor = document.createElement('a');
        anchor.href = url; anchor.download = filename; anchor.click(); URL.revokeObjectURL(url);
        return;
      } catch (error: any) {
        showAppAlert(`${appExtraText.csvExportFailed}: ${error.message}`);
        return;
      }
    }
    const doneItems = queue.filter(q => q.status === 'done');
    if (doneItems.length === 0) {
      showAppAlert(runtimeText.convertBeforeCsv);
      return;
    }

    const marketplaceConfig: Record<MetadataMarketplace, { label: string; headers: string[]; filePrefix: string }> = {
      adobe: { label: 'Adobe Stock', headers: ['Filename', 'Title', 'Keywords', 'Category'], filePrefix: 'adobe_stock' },
      shutterstock: { label: 'Shutterstock', headers: ['Filename', 'Description', 'Keywords', 'Categories', 'Illustration', 'Mature Content', 'Editorial'], filePrefix: 'shutterstock' },
      freepik: { label: 'Freepik', headers: ['File name', 'Title', 'Keywords'], filePrefix: 'freepik' },
      dreamstime: { label: 'Dreamstime', headers: ['Filename', 'Title', 'Description', 'Keywords', 'Category'], filePrefix: 'dreamstime' },
      depositphotos: { label: 'Depositphotos', headers: ['Filename', 'Title', 'Description', 'Keywords'], filePrefix: 'depositphotos' }
    };
    const config = marketplaceConfig[metadataMarketplace];
    const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
    let csvContent = `${config.headers.join(',')}\n`;

    doneItems.forEach(item => {
      const baseName = item.name.replace(/\.[^/.]+$/, '');
      const filename = `${baseName}.eps`;
      const title = item.metadata?.title || `${baseName} commercial vector illustration`;
      const description = title;
      const keywordList = item.metadata?.keywords || ['vector', 'illustration', 'graphic', 'clean art', 'commercial', 'design', 'stock'];
      const keywords = keywordList.slice(0, 50).join(', ');
      const category = item.metadata?.category || 'Illustrations';
      let row: string[];

      switch (metadataMarketplace) {
        case 'shutterstock':
          row = [filename, description, keywords, category, 'Yes', 'No', 'No'];
          break;
        case 'freepik':
          row = [filename, title, keywords];
          break;
        case 'dreamstime':
          row = [filename, title, description, keywords, category];
          break;
        case 'depositphotos':
          row = [filename, title, description, keywords];
          break;
        default:
          row = [filename, title.slice(0, 70), keywords, category];
      }
      csvContent += `${row.map(csvCell).join(',')}\n`;
    });

    const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.filePrefix}_metadata_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSaveStatusMsg(runtimeText.csvExported(config.label, doneItems.length));
    setTimeout(() => setSaveStatusMsg(null), 4000);
  };

  // Convert Single Item with explicit parameters
  const convertItemWithParams = async (
    item: QueueItem,
    qColors: number,
    fSpeckle: number,
    cThreshold: number,
    cTolerance: number,
    smoothing: number,
    colorMerge: number,
    lMode: string,
    stripBg: boolean,
    wThreshold: number,
    eHalo: number,
    autoOptimize = false,
  ): Promise<boolean> => {
    const isInterruptedRetry = item.status === 'error' && Boolean(item.conversionId);
    // Pre-flight Credit Check: Non-agency users must have at least 1 credit
    if (user && user.plan !== 'agency' && user.credits <= 0 && !isInterruptedRetry) {
      setShowPricingModal(true);
      setQueue((prev) => {
        const next: QueueItem[] = prev.map((q) => (
          q.id === item.id
            ? { ...q, status: 'error', errorMessage: 'Out of vectorization credits. Please top up or upgrade.' }
            : q
        ));
        queueRef.current = next;
        return next;
      });
      return false;
    }

    // Conversion must not silently leave a batch item IDLE. Entitlement enforcement
    // belongs on the API/export boundary, where an explicit response can be shown.
    const conversionId = isInterruptedRetry && item.conversionId
      ? item.conversionId
      : crypto.randomUUID();
    const processingQueue = queueRef.current.map((q) => (
      q.id === item.id ? { ...q, status: 'processing' as const, conversionId, errorMessage: undefined } : q
    ));
    queueRef.current = processingQueue;
    setQueue(processingQueue);
    try {
      await savePersistedQueue(queueStorageKey, processingQueue);
    } catch (error) {
      console.warn('Could not checkpoint the active conversion:', error);
    }
    // Give React two frames to commit the selected item and its progress
    // animation before the browser starts waiting on the vectorization request.
    await waitForUiPaint();

    // 1. LOCAL EXECUTION (100% on device CPU via Rust WebAssembly - Zero image upload)
    if (computeResource === 'local') {
      try {
        const startLocalTime = performance.now();
        if (user && user.plan !== 'agency') {
          // Atomic credit verification: only tiny JSON ticket, zero image binary uploaded
          const ticketRes = await authenticatedFetch('/api/vector-auth/ticket', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: item.file.name, conversion_id: conversionId }),
          });
          if (ticketRes.status === 402) {
            setShowPricingModal(true);
            throw new Error('Out of vectorization credits. Please top up or upgrade.');
          }
          if (ticketRes.ok) {
            const ticketData = await ticketRes.json().catch(() => ({}));
            if (typeof ticketData.remaining_credits === 'number') {
              setUser(prev => {
                if (!prev) return null;
                const updated = { ...prev, credits: ticketData.remaining_credits };
                try { localStorage.setItem('vectria_auth_user', JSON.stringify(updated)); } catch {}
                return updated;
              });
            }
          }
        }

        // Execute local Rust WebAssembly (vectria_core_bg.wasm)
        const localResult = await vectorizeImageWithVectriaNative(item.file, {
          colors: qColors,
          speckle: fSpeckle,
          corner: cThreshold,
          curveTolerance: cTolerance,
          smoothing,
          colorMerge,
          layerMode: lMode === 'cutout' ? 'cutout' : 'stacked',
          stripBg,
          whiteThreshold: wThreshold,
          erodeHalo: eHalo,
        });

        const elapsedSeconds = parseFloat(((performance.now() - startLocalTime) / 1000).toFixed(2));
        const receivedReport = localResult.qualityReport;
        setQueue((prev) => {
          const next: QueueItem[] = prev.map((q) => {
            if (q.id !== item.id) return q;
            const qualityReport = q.metadata ? applyMetadataReadiness(receivedReport, q.metadata) : receivedReport;
            const snapshot = {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              createdAt: new Date().toISOString(),
              report: structuredClone(qualityReport),
              settings: { colors: qColors, speckle: fSpeckle, corner: cThreshold, curveTolerance: cTolerance, smoothing, colorMerge, layerMode: lMode },
            };
            return {
              ...q,
              status: 'done',
              processedBy: 'local',
              elapsed: elapsedSeconds,
              vectorSvg: localResult.svg,
              vectorEps: localResult.eps,
              validation: localResult.validation,
              qualityReport,
              qualityHistory: [...(q.qualityHistory || []), snapshot].slice(-10),
            };
          });
          queueRef.current = next;
          return next;
        });

        return true;
      } catch (localErr: any) {
        console.error('Local client vectorization failed:', localErr);
        setQueue((prev) => {
          const next: QueueItem[] = prev.map((q) => (
            q.id === item.id
              ? { ...q, status: 'error', errorMessage: localErr?.message || 'Local vectorization failed' }
              : q
          ));
          queueRef.current = next;
          return next;
        });
        return false;
      }
    }

    // 2. CLOUD EXECUTION (Upload image to server for server-side processing)
    const stageFileToCloud = async (): Promise<string | undefined> => {
      if (!user || !item.file || item.file.size === 0) return undefined;
      try {
        const upload = new FormData();
        upload.append('file', item.file);
        upload.append('client_id', item.id);
        const stageRes = await authenticatedFetch('/api/batch-assets', { method: 'POST', body: upload });
        const stageData = await stageRes.json().catch(() => ({}));
        if (stageRes.ok && stageData.asset?.id) {
          item.stagedAssetId = stageData.asset.id;
          item.conversionId = stageData.asset.conversion_id;
          return stageData.asset.id as string;
        }
      } catch (stageErr) {
        console.warn('Could not stage asset prior to vectorization:', stageErr);
      }
      return undefined;
    };

    if (!item.stagedAssetId && user) {
      await stageFileToCloud();
    }

    if (item.stagedAssetId) {
      try {
        const vectorizeBody = JSON.stringify({
          conversion_id: conversionId,
          quantize_colors: qColors,
          filter_speckle: fSpeckle,
          corner_threshold: cThreshold,
          curve_tolerance: cTolerance,
          smooth_factor: smoothing,
          layer_difference: colorMerge,
          layer_mode: lMode,
          strip_white_bg: stripBg,
          white_threshold: wThreshold,
          erode_halo_px: eHalo,
          auto_optimize: autoOptimize,
          compute_resource: computeResource,
        });
        let res = await authenticatedFetch(`/api/batch-assets/${encodeURIComponent(item.stagedAssetId)}/vectorize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: vectorizeBody,
        });
        if (res.status === 404 && item.file && item.file.size > 0) {
          item.stagedAssetId = undefined;
          const restagedId = await stageFileToCloud();
          if (restagedId) {
            res = await authenticatedFetch(`/api/batch-assets/${encodeURIComponent(restagedId)}/vectorize`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: vectorizeBody,
            });
          }
        }
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.job) {
          const activeStagedId = item.stagedAssetId;
          setQueue(previous => {
            const next: QueueItem[] = previous.map(candidate => candidate.id === item.id ? {
              ...candidate,
              stagedAssetId: activeStagedId,
              status: 'processing',
              processedBy: 'cloud',
              conversionId: data.job.conversion_id,
              errorMessage: undefined,
            } : candidate);
            queueRef.current = next;
            return next;
          });
          return true;
        }
        if (res.status === 402 || !item.file || item.file.size === 0) {
          throw new Error(apiErrorMessage(data, `Engine queue error (${res.status}).`));
        }
        console.warn('Staged queue vectorization unavailable, falling back to direct /api/preview-svg:', data);
      } catch (err: any) {
        if (err?.message?.includes('credits') || !item.file || item.file.size === 0) {
          console.error(err);
          setQueue(previous => {
            const next: QueueItem[] = previous.map(candidate => candidate.id === item.id ? {
              ...candidate,
              status: 'error',
              errorMessage: err?.message || 'Vectorization could not be queued',
            } : candidate);
            queueRef.current = next;
            return next;
          });
          return false;
        }
        console.warn('Falling back from staged vectorize to direct /api/preview-svg:', err);
      }
    }

    const formData = new FormData();
    formData.append('file', item.file);
    formData.append('quantize_colors', qColors.toString());
    formData.append('filter_speckle', fSpeckle.toString());
    formData.append('corner_threshold', cThreshold.toString());
    formData.append('curve_tolerance', cTolerance.toString());
    formData.append('smooth_factor', smoothing.toString());
    formData.append('layer_difference', colorMerge.toString());
    formData.append('layer_mode', lMode);
    formData.append('strip_white_bg', stripBg.toString());
    formData.append('white_threshold', wThreshold.toString());
    formData.append('erode_halo_px', eHalo.toString());
    formData.append('engine_type', 'vectria_native');
    formData.append('compute_resource', computeResource);
    formData.append('auto_optimize', autoOptimize.toString());
    formData.append('conversion_id', conversionId);

    try {
      const activeWorkspace = user?.id ? localStorage.getItem(`vectria_active_workspace_${user.id}`) : '';
      const res = await optionalAuthFetch('/api/preview-svg', {
        method: 'POST',
        headers: activeWorkspace ? { 'X-Workspace-ID': activeWorkspace } : undefined,
        body: formData,
      });

      if (!res.ok) {
        throw new Error(`Engine error (${res.status}): ${await res.text()}`);
      }

      const data = await res.json();
      if (data.vectorization_policy !== VECTORIZATION_POLICY_VERSION) {
        throw new Error(runtimeText.engineOutdated);
      }
      if (autoOptimize && data.optimization) {
        applyTraceSettings({
          colors: data.optimization.quantize_colors,
          speckle: data.optimization.filter_speckle,
          corner: data.optimization.corner_threshold,
          curveTolerance: data.optimization.curve_tolerance,
          smoothFactor: data.optimization.smooth_factor,
          layerDifference: data.optimization.layer_difference,
          layer: data.optimization.layer_mode,
          stripBg: data.optimization.strip_white_bg,
          whiteThreshold: data.optimization.white_threshold,
          erodeHalo: data.optimization.erode_halo_px,
        });
        setOptimizeNotice(runtimeText.detectedOptimization(data.optimization.category.replaceAll('_', ' '), data.optimization.significant_colors, data.optimization.palette_error, data.optimization.curve_tolerance));
      }
       const receivedReport = data.quality_report as QualityReport;
       setQueue((prev) => {
         const next: QueueItem[] = prev.map((q) => {
           if (q.id !== item.id) return q;
           const qualityReport = q.metadata ? applyMetadataReadiness(receivedReport, q.metadata) : receivedReport;
           const snapshot = {
             id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
             createdAt: new Date().toISOString(),
             report: structuredClone(qualityReport),
             settings: { colors: qColors, speckle: fSpeckle, corner: cThreshold, curveTolerance: cTolerance, smoothing, colorMerge, layerMode: lMode },
           };
           return {
             ...q,
             status: 'done',
             vectorSvg: data.svg,
             vectorEps: data.eps,
             validation: data.validation,
             qualityReport,
             visualHeatmap: typeof data.visual_heatmap === 'string' && data.visual_heatmap.startsWith('data:image/png;base64,') ? data.visual_heatmap : undefined,
             qualityHistory: [...(q.qualityHistory || []), snapshot].slice(-10),
             optimization: data.optimization || undefined,
           };
         });
         queueRef.current = next;
         return next;
       });

      // Deduct credit only on successful conversion (1 Credit = 1 Vector)
      // Agency has unlimited credits; Free and Pro plans decrement credits.
      if (user && user.plan !== 'agency') {
        setUser(prev => {
          if (!prev) return null;
          const newCredits = (data.remaining_credits !== undefined && data.remaining_credits !== null)
            ? data.remaining_credits
            : Math.max(0, prev.credits - 1);
          const updated = { ...prev, credits: newCredits };
          try {
            localStorage.setItem('vectria_auth_user', JSON.stringify(updated));
          } catch {}
          return updated;
        });
      }
      return true;
    } catch (err: any) {
      console.error(err);
      setQueue((prev) => {
        const next: QueueItem[] = prev.map((q) => (
          q.id === item.id
            ? { ...q, status: 'error', errorMessage: err?.message || 'Vectorization failed' }
            : q
        ));
        queueRef.current = next;
        return next;
      });
      return false;
    }
  };

  // Convert Single Item using current state
  const convertItem = async (item: QueueItem): Promise<boolean> => {
    return await convertItemWithParams(
      item,
      quantizeColors,
      filterSpeckle,
      cornerThreshold,
      curveTolerance,
      smoothFactor,
      layerDifference,
      layerMode,
      stripWhiteBg,
      whiteThreshold,
      erodeHaloPx,
      activePreset === 'auto',
    );
  };

  const applyTraceSettings = (settings: TraceSettings) => {
    setQuantizeColors(settings.colors);
    setFilterSpeckle(settings.speckle);
    setCornerThreshold(settings.corner);
    setCurveTolerance(settings.curveTolerance);
    setSmoothFactor(settings.smoothFactor);
    setLayerDifference(settings.layerDifference);
    setLayerMode(settings.layer);
    setStripWhiteBg(settings.stripBg);
    setWhiteThreshold(settings.whiteThreshold);
    setErodeHaloPx(settings.erodeHalo);
  };

  // Auto-Optimize selects an adaptive preset. Vectorization remains explicit.
  // Auto-Optimize selects an adaptive preset based on CPU-only contour and color analysis
  const handleAutoOptimize = async () => {
    if (!activeItem || queue.length === 0) {
      setOptimizeNotice(t('studio.optimizeUploadFirst'));
      return;
    }
    setOptimizeNotice(t('studio.optimizeAnalyzing'));
    setIsAnalyzingOptimize(true);
    try {
      let profile: NonNullable<QueueItem['optimization']>;
      if (computeResource === 'local' || !user) {
        profile = await analyzeRasterLocally(activeItem.file);
      } else {
        try {
          const formData = new FormData();
          formData.append('file', activeItem.file);
          const response = await optionalAuthFetch('/api/analyze-raster', {
            method: 'POST',
            body: formData,
          });
          if (!response.ok) {
            throw new Error(`Analyzer error (${response.status}): ${await response.text()}`);
          }
          const data = await response.json();
          profile = data.optimization;
        } catch (cloudAnalyzeErr) {
          console.warn('Cloud raster analysis unavailable, falling back to on-device analyzer:', cloudAnalyzeErr);
          profile = await analyzeRasterLocally(activeItem.file);
        }
      }
      applyTraceSettings({
        colors: profile.quantize_colors,
        speckle: profile.filter_speckle,
        corner: profile.corner_threshold,
        curveTolerance: profile.curve_tolerance,
        smoothFactor: profile.smooth_factor,
        layerDifference: profile.layer_difference,
        layer: profile.layer_mode === 'cutout' ? 'cutout' : 'stacked',
        stripBg: profile.strip_white_bg,
        whiteThreshold: profile.white_threshold,
        erodeHalo: profile.erode_halo_px,
      });
      setQueue((previous) => previous.map((queueItem) => (
        queueItem.id === activeItem.id ? { ...queueItem, optimization: profile } : queueItem
      )));
      setActivePreset('auto');
      setOptimizeNotice(t('studio.optimizeReady'));
    } catch (error) {
      setOptimizeNotice(`${t('studio.optimizeFailed')} ${error instanceof Error ? error.message : ''}`.trim());
    } finally {
      setIsAnalyzingOptimize(false);
    }
  };

  // Preset Selection Handler
  const applyPreset = (presetName: Exclude<PresetName, 'auto'>) => {
    const settings = TRACE_PRESETS[presetName];
    applyTraceSettings(settings);
    setActivePreset(presetName);
    setOptimizeNotice(t('studio.presetApplied'));
  };

  // File Upload Handlers
  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const rawFiles = Array.from(files);
    const rejectedNames: string[] = [];
    const selectedFiles = rawFiles.filter((file) => {
      const validType = ALLOWED_RASTER_MIMES.has(file.type.toLowerCase()) || ALLOWED_RASTER_EXTENSIONS.test(file.name);
      const validSize = file.size > 0 && file.size <= MAX_RASTER_UPLOAD_BYTES;
      if (!validType || !validSize) {
        rejectedNames.push(file.name);
        return false;
      }
      return true;
    });
    if (rejectedNames.length > 0) {
      setSaveStatusMsg(`Skipped unsupported or >20MB file(s): ${rejectedNames.slice(0, 3).join(', ')}`);
      setTimeout(() => setSaveStatusMsg(null), 5000);
    }
    const isCloud = computeResource === 'cloud';
    const newItems: QueueItem[] = selectedFiles.map(file => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      status: (user && isCloud) ? 'uploading' : 'idle',
    }));
    if (newItems.length === 0) return;
    setQueue((prev) => {
      const next = [...prev, ...newItems];
      queueRef.current = next;
      void savePersistedQueue(queueStorageKey, next).catch(error => {
        console.warn('Could not checkpoint uploaded batch assets:', error);
      });
      return next;
    });
    if (!activeItemId && newItems.length > 0) {
      setActiveItemId(newItems[0].id);
    }

    // Only stage/upload files to server if user is in Cloud mode!
    // In Local mode, images remain 100% in local browser memory (Blob URL) and are never uploaded.
    if (!user || !isCloud) return;
    for (let offset = 0; offset < newItems.length; offset += CLOUD_STAGE_CONCURRENCY) {
      const chunk = newItems.slice(offset, offset + CLOUD_STAGE_CONCURRENCY);
      await Promise.all(chunk.map(async item => {
        try {
          const upload = new FormData();
          upload.append('file', item.file);
          upload.append('client_id', item.id);
          const response = await authenticatedFetch('/api/batch-assets', { method: 'POST', body: upload });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || !data.asset) throw new Error(apiErrorMessage(data, appExtraText.requestFailed(response.status)));
          setQueue(previous => {
            const next: QueueItem[] = previous.map(candidate => candidate.id === item.id ? {
              ...candidate,
              stagedAssetId: data.asset.id,
              conversionId: data.asset.conversion_id,
              status: 'idle',
              errorMessage: undefined,
            } : candidate);
            queueRef.current = next;
            return next;
          });
        } catch (error: any) {
          const message = error?.message || appExtraText.requestFailed(500);
          setQueue(previous => {
            const next: QueueItem[] = previous.map(candidate => candidate.id === item.id ? { ...candidate, status: 'error', errorMessage: message } : candidate);
            queueRef.current = next;
            return next;
          });
          setSaveStatusMsg(message);
          setTimeout(() => setSaveStatusMsg(null), 5000);
        }
      }));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleClearQueue = () => {
    queue.forEach(item => {
      if (item.previewUrl.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
    });
    queueRef.current = [];
    setQueue([]);
    setActiveItemId(null);
    setBatchProgress(null);
    void clearPersistedQueue(queueStorageKey).catch(error => {
      console.warn('Could not clear the persisted batch queue:', error);
    });
    if (user) {
      void authenticatedFetch('/api/batch-assets', { method: 'DELETE' }).catch(error => {
        console.warn('Could not clear the server batch queue:', error);
      });
    }
  };

  const handleRemoveQueueItem = (id: string) => {
    const target = queue.find(item => item.id === id);
    if (target && target.previewUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(target.previewUrl);
      } catch {}
    }
    const nextQueue = queue.filter(item => item.id !== id);
    queueRef.current = nextQueue;
    setQueue(nextQueue);
    void savePersistedQueue(queueStorageKey, nextQueue).catch(error => {
      console.warn('Could not checkpoint updated batch queue after removing item:', error);
    });

    if (activeItemId === id) {
      setActiveItemId(nextQueue.length > 0 ? nextQueue[0].id : null);
    }

    if (user && target?.stagedAssetId) {
      void authenticatedFetch(`/api/batch-assets/${target.stagedAssetId}`, { method: 'DELETE' }).catch(error => {
        console.warn('Could not delete staged asset from server:', error);
      });
    }
  };

  const handleRetryQueueItem = (item: QueueItem) => {
    setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'idle', errorMessage: undefined } : q));
    setActiveItemId(item.id);
    void convertItem(item);
  };

  const handleConvertSingleItem = (item: QueueItem) => {
    setActiveItemId(item.id);
    void convertItem(item);
  };

  // Convert All in Batch with per-image Auto-Optimize re-analysis
  const handleBatchConvert = async () => {
    if (isProcessingBatch) return;
    const pendingItems = queue.filter((item) => item.status === 'idle' || item.status === 'error');
    if (pendingItems.length === 0) return;

    setIsProcessingBatch(true);
    let succeeded = 0;
    let failed = 0;
    try {
      for (let index = 0; index < pendingItems.length; index += 1) {
        const item = pendingItems[index];
        setActiveItemId(item.id);

        let ok = false;
        if (activePreset === 'auto') {
          // Re-detect every item independently before converting it
          setBatchProgress({
            current: index + 1,
            total: pendingItems.length,
            fileName: `${item.name} (Analyzing contours & colors...)`,
            succeeded,
            failed,
          });
          await waitForUiPaint();

          let profile = item.optimization;
          if (!profile) {
            try {
              if (computeResource === 'local' || !user) {
                profile = await analyzeRasterLocally(item.file);
              } else {
                const analyzeForm = new FormData();
                analyzeForm.append('file', item.file);
                const analyzeRes = await optionalAuthFetch('/api/analyze-raster', {
                  method: 'POST',
                  body: analyzeForm,
                });
                if (analyzeRes.ok) {
                  const analyzeData = await analyzeRes.json();
                  profile = analyzeData.optimization;
                } else {
                  profile = await analyzeRasterLocally(item.file);
                }
              }
              if (profile) {
                setQueue((prev) => prev.map((q) => q.id === item.id ? { ...q, optimization: profile } : q));
              }
            } catch (err) {
              console.warn('Auto-analysis fallback on batch item:', err);
            }
          }

          setBatchProgress({
            current: index + 1,
            total: pendingItems.length,
            fileName: `${item.name} (Vectorizing ${profile?.significant_colors || 'adaptive'} colors...)`,
            succeeded,
            failed,
          });
          await waitForUiPaint();

          if (profile) {
            ok = await convertItemWithParams(
              item,
              profile.quantize_colors,
              profile.filter_speckle,
              profile.corner_threshold,
              profile.curve_tolerance,
              profile.smooth_factor,
              profile.layer_difference,
              profile.layer_mode as 'stacked' | 'cutout',
              profile.strip_white_bg,
              profile.white_threshold,
              profile.erode_halo_px,
              false
            );
          } else {
            ok = await convertItem(item);
          }
        } else {
          setBatchProgress({
            current: index + 1,
            total: pendingItems.length,
            fileName: item.name,
            succeeded,
            failed,
          });
          await waitForUiPaint();
          ok = await convertItem(item);
        }

        if (ok) succeeded += 1;
        else failed += 1;

        setBatchProgress({
          current: index + 1,
          total: pendingItems.length,
          fileName: item.name,
          succeeded,
          failed,
        });
      }
    } finally {
      setIsProcessingBatch(false);
      setBatchProgress(null);
      setSaveStatusMsg(runtimeText.batchComplete(succeeded, failed));
      setTimeout(() => setSaveStatusMsg(null), 5000);
    }
  };

  // Web SaaS Batch ZIP Exporter (Direct browser download)
  const handleExportBatchZip = async (passingOnly = false, ignoreQualityFlags = false) => {
    if (!user) {
      setAuthMode('register');
      setAuthError(runtimeText.downloadAuth);
      setShowAuthModal(true);
      return;
    }

    const exportableItems = queue.filter(q => q.status === 'done' && q.vectorSvg);
    const passingItems = exportableItems.filter(item => item.qualityReport?.status === 'passed');
    const problemCount = exportableItems.filter(item => item.qualityReport && item.qualityReport.status !== 'passed').length;
    const doneItems = passingOnly ? passingItems : exportableItems;
    if (doneItems.length === 0) {
      showAppAlert(passingOnly ? quality.noPassingAssets : runtimeText.convertBeforeZip);
      return;
    }
    if (!passingOnly && problemCount > 0 && !ignoreQualityFlags) {
      setShowExportReviewModal(true);
      return;
    }

    setShowExportReviewModal(false);
    setIsExportingZip(true);
    try {
      const itemsPayload = doneItems.map(item => ({
        filename: item.name,
        svg: item.vectorSvg || '',
        eps: item.vectorEps || '',
        jpg_b64: null,
        title: item.metadata?.title || null,
        description: item.metadata?.description || null,
        keywords: item.metadata?.keywords || null,
        category: item.metadata?.category || null,
      }));

      const res = await authenticatedFetch('/api/saas/exports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsPayload, marketplace: metadataMarketplace })
      });

      if (!res.ok) {
        throw new Error(runtimeText.zipGenerateFailed);
      }

      const exportJob = await res.json();
      const downloadResponse = await authenticatedFetch(`/api/saas/exports/${exportJob.id}/download`);
      if (!downloadResponse.ok) throw new Error(runtimeText.zipDownloadFailed);
      const blob = await downloadResponse.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportJob.filename || `vectria_vectors_batch_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      setSaveStatusMsg(runtimeText.zipExported(doneItems.length));
      setExportJobs(previous => [exportJob, ...previous.filter(job => job.id !== exportJob.id)]);
      setTimeout(() => setSaveStatusMsg(null), 5000);
    } catch (err: any) {
      showAppAlert(`${runtimeText.zipError}: ${err.message}`);
    } finally {
      setIsExportingZip(false);
    }
  };

  // Direct Browser Downloads
  const downloadSvg = (item: QueueItem) => {
    if (!user) {
      setAuthMode('register');
      setAuthError(runtimeText.downloadAuth);
      setShowAuthModal(true);
      return;
    }
    if (!item.vectorSvg) return;
    const blob = new Blob([item.vectorSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${item.name.replace(/\.[^/.]+$/, '')}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadEps = (item: QueueItem) => {
    if (!user) {
      setAuthMode('register');
      setAuthError(runtimeText.downloadAuth);
      setShowAuthModal(true);
      return;
    }
    if (!item.vectorEps) return;
    const blob = new Blob([item.vectorEps], { type: 'application/postscript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${item.name.replace(/\.[^/.]+$/, '')}.eps`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const canWebShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleShareVector = async (item: QueueItem) => {
    if (!item.vectorSvg) return;
    const baseName = item.name.replace(/\.[^/.]+$/, '');
    const filename = `${baseName}.svg`;
    try {
      if (typeof File !== 'undefined' && typeof navigator.canShare === 'function') {
        const file = new File([item.vectorSvg], filename, { type: 'image/svg+xml' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: item.metadata?.title || item.name,
            text: item.metadata?.description || `Vector artwork generated with Vectria: ${filename}`,
            files: [file]
          });
          return;
        }
      }
      await navigator.share({
        title: item.metadata?.title || item.name,
        text: item.metadata?.description || `Vector artwork generated with Vectria: ${filename}`,
        url: window.location.href
      });
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        downloadSvg(item);
      }
    }
  };

  const navigateSection = (sectionId: PublicLandingSection) => {
    if (user) return; // Completely disabled for logged-in users

    const nextPath = publicSectionPath(window.location.pathname, sectionId);
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath !== nextPath) {
      window.history.pushState({}, document.title, nextPath);
    }

    const scrollToSection = () => {
      const element = document.getElementById(sectionId);
      if (!element) return;
      // Each landing section has generous internal top padding. Enter the section
      // far enough to reduce dead space while keeping its heading fully visible.
      const sectionOffsets: Record<string, number> = {
        features: 24,
        pricing: 22,
        faq: 28
      };
      const offset = sectionOffsets[sectionId] ?? -72;
      const targetTop = element.getBoundingClientRect().top + window.scrollY + offset;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    };

    if (currentView !== 'landing') {
      setCurrentView('landing');
      setTimeout(scrollToSection, 100);
    } else {
      scrollToSection();
    }
  };

  const isStudioMode = !!user || currentView === 'studio';
  const studioLayoutRef = useRef<HTMLDivElement | null>(null);
  const studioCanvasRef = useRef<HTMLElement | null>(null);
  const lastRevealedSvgKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeItem || activeItem.status !== 'done' || !activeItem.vectorSvg) {
      return undefined;
    }
    const svgKey = `${activeItem.id}:${activeItem.vectorSvg.length}`;
    if (lastRevealedSvgKeyRef.current === svgKey) {
      return undefined;
    }
    lastRevealedSvgKeyRef.current = svgKey;
    return animateVectorCompletionReveal({
      canvasRoot: studioCanvasRef.current,
      isSplitView: viewMode === 'split',
      onUpdateSplitPosition: setSliderPosition,
    });
  }, [activeItem?.id, activeItem?.status, activeItem?.vectorSvg, viewMode]);

  const toggleLeftSidebar = (event?: React.MouseEvent<HTMLElement>) => {
    const isCompact = typeof window !== 'undefined' && window.innerWidth <= 1024;
    const nextOpen = !leftSidebarOpen;
    runStudioSidebarToggle({
      side: 'left',
      nextOpen,
      layoutRoot: studioLayoutRef.current,
      triggerButton: event?.currentTarget ?? null,
      applyState: () => {
        setLeftSidebarOpen(nextOpen);
        if (nextOpen && isCompact) setRightSidebarOpen(false);
      },
    });
  };

  const toggleRightSidebar = (event?: React.MouseEvent<HTMLElement>) => {
    const isCompact = typeof window !== 'undefined' && window.innerWidth <= 1024;
    const nextOpen = !rightSidebarOpen;
    runStudioSidebarToggle({
      side: 'right',
      nextOpen,
      layoutRoot: studioLayoutRef.current,
      triggerButton: event?.currentTarget ?? null,
      applyState: () => {
        setRightSidebarOpen(nextOpen);
        if (nextOpen && isCompact) setLeftSidebarOpen(false);
      },
    });
  };



  const closeCompactSidebars = () => {
    setLeftSidebarOpen(false);
    setRightSidebarOpen(false);
  };

  const byokSettingsContent = (
    <div className="byok-page" aria-labelledby="settings-page-title" aria-busy={isTestingAi || isResettingAiKeys}>
      <section className="byok-section byok-provider-section">
        <header className="byok-section-copy">
          <h2>{studio.aiModal.selectProvider}</h2>
          <p>{studio.aiModal.subtitle}</p>
        </header>
        <div className="byok-section-panel">
          <div className="byok-provider-tabs" role="group" aria-label={studio.aiModal.selectProvider}>
            {(['gemini', 'openai', 'custom'] as const).map((prov) => (
              <button
                key={prov}
                type="button"
                className={aiProvider === prov ? 'active' : ''}
                onClick={() => handleProviderChange(prov)}
                aria-pressed={aiProvider === prov}
              >
                <span>{prov === 'gemini' ? 'Google Gemini' : prov === 'openai' ? 'OpenAI' : 'OpenRouter / Local'}</span>
                {aiProvider === prov && <CheckCircle2 size={14} aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="byok-section">
        <header className="byok-section-copy">
          <h2>{studio.aiModal.apiKeyLabel(AI_PROVIDER_DEFAULTS[aiProvider].name)}</h2>
          <p>{studio.aiModal.apiKeysHint}</p>
          <a href={AI_PROVIDER_DEFAULTS[aiProvider].keyDocUrl} target="_blank" rel="noopener noreferrer">
            {studio.aiModal.getKey}<ExternalLink size={12} aria-hidden="true" />
          </a>
        </header>
        <div className="byok-section-panel byok-credentials-panel">
          <div className="byok-field-heading">
            <label htmlFor="byok-api-keys">API keys</label>
            <span>{studio.aiModal.apiKeysCount(aiKey.split(/\r?\n/).filter(key => key.trim()).length)}</span>
          </div>
          <textarea
            id="byok-api-keys"
            className="byok-secret-input"
            rows={5}
            spellCheck={false}
            autoComplete="off"
            placeholder={`${AI_PROVIDER_DEFAULTS[aiProvider].keyPlaceholder}\n${AI_PROVIDER_DEFAULTS[aiProvider].keyPlaceholder}`}
            value={aiKey}
            onChange={(event) => {
              const nextValue = event.target.value;
              const lines = nextValue.split(/\r?\n/);
              if (lines.filter(line => line.trim()).length <= 10) setAiKey(nextValue);
              setAiTestResult(null);
            }}
          />
          {aiKeyCount > 0 && (
            <button type="button" className="byok-reset-action" onClick={resetAiProviderKeys} disabled={isResettingAiKeys}>
              {isResettingAiKeys ? t('studio.resettingKeys') : t('studio.resetKeys')}
            </button>
          )}
        </div>
      </section>

      <section className="byok-section">
        <header className="byok-section-copy">
          <h2>{studio.aiModal.primaryModel}</h2>
          <p>{studio.aiModal.fallbackDesc}</p>
        </header>
        <div className="byok-section-panel">
          <div className="byok-model-grid">
            <div className="byok-field">
              <label htmlFor="byok-primary-model">{studio.aiModal.primaryModel}</label>
              <select id="byok-primary-model" value={aiModel} onChange={(event) => { setAiModel(event.target.value); setAiTestResult(null); }}>
                {AI_MODELS_OCT_2026[aiProvider].map((model) => <option key={model.id} value={model.id}>{model.name}: {model.tag}</option>)}
              </select>
              {aiModel === 'custom' && <input type="text" placeholder={studio.aiModal.customModelPlaceholder} aria-label={studio.aiModal.customModelPlaceholder} value={aiCustomModel} onChange={(event) => setAiCustomModel(event.target.value)} />}
            </div>
            <div className="byok-field">
              <label htmlFor="byok-fallback-model">Fallback Model</label>
              <select id="byok-fallback-model" value={aiFallbackModel} onChange={(event) => { setAiFallbackModel(event.target.value); setAiTestResult(null); }}>
                <option value="none">{studio.aiModal.fallbackNone}</option>
                {AI_MODELS_OCT_2026[aiProvider].filter((model) => model.id !== 'custom').map((model) => <option key={`fallback-${model.id}`} value={model.id}>{model.name} ({model.id})</option>)}
                <option value="custom">{studio.aiModal.customFallbackPlaceholder}...</option>
              </select>
              {aiFallbackModel === 'custom' && <input type="text" placeholder={studio.aiModal.customFallbackPlaceholder} aria-label={studio.aiModal.customFallbackPlaceholder} value={aiCustomFallbackModel} onChange={(event) => setAiCustomFallbackModel(event.target.value)} />}
            </div>
          </div>
          {aiProvider === 'custom' && (
            <div className="byok-field byok-endpoint-field">
              <label htmlFor="byok-endpoint">{studio.aiModal.customEndpointLabel}</label>
              <input id="byok-endpoint" type="url" placeholder="https://openrouter.ai/api/v1/chat/completions" value={aiEndpoint} onChange={(event) => setAiEndpoint(event.target.value)} />
              <small>{studio.aiModal.customEndpointDesc}</small>
            </div>
          )}
        </div>
      </section>

      <section className="byok-section">
        <header className="byok-section-copy">
          <h2>{studio.aiModal.testConnection}</h2>
          <p>{studio.aiModal.testConnectionDesc}</p>
        </header>
        <div className="byok-section-panel byok-test-panel">
          <div className="byok-test-row">
            <div>
              <strong>{AI_PROVIDER_DEFAULTS[aiProvider].name}</strong>
              <span>{aiKeyCount > 0 ? studio.aiModal.apiKeysCount(aiKeyCount) : studio.aiModal.apiKeysCount(0)}</span>
            </div>
            <button type="button" className="settings-secondary" onClick={testAiConnection} disabled={isTestingAi}>
              {isTestingAi ? <RefreshCw size={13} className="m3-pulse" /> : <Zap size={13} />}
              {isTestingAi ? studio.aiModal.verifying : studio.aiModal.testConnection}
            </button>
          </div>
          {aiTestResult && (
            <div className={`byok-test-result ${aiTestResult.ok ? 'success' : 'error'}`} role={aiTestResult.ok ? 'status' : 'alert'} aria-live={aiTestResult.ok ? 'polite' : 'assertive'}>
              {aiTestResult.ok ? <CheckCircle2 size={15} aria-hidden="true" /> : <AlertCircle size={15} aria-hidden="true" />}
              <span>{aiTestResult.message}</span>
            </div>
          )}
        </div>
      </section>

      <section className="byok-section">
        <header className="byok-section-copy">
          <h2>{studio.aiModal.autoMetadataTitle}</h2>
          <p>{studio.aiModal.autoMetadataDesc}</p>
        </header>
        <div className="byok-section-panel byok-policy-panel">
          <label className="byok-policy-row">
            <span><strong>{studio.aiModal.autoMetadataTitle}</strong><small>{studio.aiModal.autoMetadataDesc}</small></span>
            <input type="checkbox" checked={aiEnabled} onChange={(event) => setAiEnabled(event.target.checked)} aria-label={studio.aiModal.autoMetadataTitle} />
          </label>
          {user?.plan === 'agency' && <div className="byok-plan-note">{t('studio.agencyAiPolicy')}</div>}
          {user && ['pro', 'studio'].includes(user.plan) && (
            <label className="byok-policy-row">
              <span><strong>{studio.aiModal.vectriaFallbackTitle}</strong><small>{studio.aiModal.vectriaFallbackDesc}</small></span>
              <input type="checkbox" checked={allowVectriaFallback} onChange={(event) => setAllowVectriaFallback(event.target.checked)} aria-label={studio.aiModal.vectriaFallbackTitle} />
            </label>
          )}
        </div>
      </section>

      <footer className="byok-save-row">
        <p>{studio.aiModal.apiKeysHint}</p>
        <button type="button" className="settings-primary" onClick={saveAiConfig}>{studio.aiModal.saveConfig}</button>
      </footer>
    </div>
  );

  return (
    <div
      className="app-shell"
      style={{
        minHeight: '100dvh',
        height: (currentView === 'admin' || currentView === 'settings') ? '100dvh' : undefined,
        overflow: (currentView === 'admin' || currentView === 'settings') ? 'hidden' : undefined,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--md-sys-color-surface)',
        paddingTop: (currentView === 'admin' || currentView === 'settings' || isMaintenanceBlocked || (!statusChecked && !user)) ? 0 : '64px'
      }}
    >
      {!user && ((currentView as string) === 'landing' || (currentView as string) === 'blog') && !isMaintenanceBlocked && (
              <GlobalNavbar
          currentView={currentView === 'blog' ? 'blog' : 'landing'}
          theme={theme}
          t={t}
          onSection={navigateSection}
          onBlog={() => { window.location.assign('/blog'); }}
          onAuth={(mode) => { setAuthMode(mode); setAuthError(null); setShowAuthModal(true); }}
          onTheme={toggleTheme}
          onHome={navigatePublicHome}
        />
      )}

      {/* Legacy app navbar retained for authenticated/studio views only. */}
      {currentView !== 'admin' && currentView !== 'settings' && currentView !== 'landing' && currentView !== 'blog' && !isMaintenanceBlocked && (
        <header
        className="app-navbar"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 100,
          background: 'var(--header-bg)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid var(--md-sys-color-outline-variant)',
          padding: '0 clamp(14px, 2vw, 32px)',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px'
        }}
      >
        {/* Left: Branding, Sidebar Toggle (if studio), & Global Nav Links (only for guest landing) */}
        <div className="app-navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 1.5vw, 16px)' }}>
          {isStudioMode && (
            <button
              className="m3-btn-icon studio-top-panel-toggle"
              onClick={toggleLeftSidebar}
              title={leftSidebarOpen ? t('studio.collapseSettings') : t('studio.expandSettings')}
              aria-label={leftSidebarOpen ? t('studio.collapseSettings') : t('studio.expandSettings')}
              style={{
                borderRadius: '8px',
                color: leftSidebarOpen ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                background: leftSidebarOpen ? 'var(--md-sys-color-surface-container-high)' : 'transparent',
                border: leftSidebarOpen ? '1px solid var(--md-sys-color-outline-variant)' : '1px solid transparent',
                transition: 'all 0.18s ease'
              }}
            >
              <PanelLeft size={16} />
            </button>
          )}

          {/* Clean Borderless Ribbon Emblem & Wordmark Logo */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: user ? 'default' : 'pointer',
              userSelect: 'none'
            }}
            onClick={() => {
              if (!user) {
                setCurrentView('landing');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            title={user ? 'Vectria Studio' : 'Vectria - Go to Home'}
          >
            <img
              src="/emblem.webp"
              alt="Vectria Emblem"
              width="30"
              height="30"
              style={{
                height: '30px',
                width: 'auto',
                objectFit: 'contain',
                filter: theme === 'dark' ? 'drop-shadow(0 0 10px var(--inline-rgba-56-189-248-0-45))' : 'none',
                transition: 'filter 0.2s ease'
              }}
              onError={(e) => {
                const target = e.currentTarget;
                if (!target.src.endsWith('/favicon.webp')) {
                  target.src = '/favicon.webp';
                }
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="brand-wordmark">VECTRIA</span>
            </div>
            {user && (
              <span
                className="studio-navbar-logo-badge"
                style={{
                  fontSize: '9.5px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  padding: '2px 7px',
                  borderRadius: 'var(--shape-full)',
                  background: 'var(--alpha-rgba-56-189-248-0-12)',
                  color: 'var(--md-sys-color-primary)',
                  border: '1px solid var(--color-primary-hover)',
                  textTransform: 'uppercase'
                }}
              >
                Studio
              </span>
            )}
          </div>

          {/* Navigation Links: Features, Quality Demo, Microstock Standard, Pricing, FAQ
              ONLY visible for guests on landing page. Once logged in, completely hidden. */}
          {false && (
            <nav style={{ display: 'flex', alignItems: 'center', gap: '20px' }} className="landing-nav-links">
              <button
                onClick={() => {
                  if (currentView !== 'landing') setCurrentView('landing');
                  navigateSection('features');
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13.5px', fontWeight: 500, color: 'var(--md-sys-color-on-surface-variant)', transition: 'color 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--palette-fff)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--md-sys-color-on-surface-variant)')}
              >
                {t('nav.features')}
              </button>
              <button
                onClick={() => {
                  if (currentView !== 'landing') setCurrentView('landing');
                  navigateSection('pricing');
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13.5px', fontWeight: 500, color: 'var(--md-sys-color-on-surface-variant)', transition: 'color 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--palette-fff)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--md-sys-color-on-surface-variant)')}
              >
                {t('nav.pricing')}
              </button>
              <button
                onClick={() => {
                  if (currentView !== 'landing') setCurrentView('landing');
                  navigateSection('faq');
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13.5px', fontWeight: 500, color: 'var(--md-sys-color-on-surface-variant)', transition: 'color 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--palette-fff)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--md-sys-color-on-surface-variant)')}
              >
                {t('nav.faq')}
              </button>
              <button
                onClick={() => { window.location.assign('/blog'); }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13.5px',
                  fontWeight: currentView === 'blog' ? 700 : 500,
                  color: currentView === 'blog' ? 'var(--palette-00c6ff)' : 'var(--md-sys-color-on-surface-variant)',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--palette-00c6ff)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = currentView === 'blog' ? 'var(--palette-00c6ff)' : 'var(--md-sys-color-on-surface-variant)')}
              >
                Blog
              </button>
            </nav>
          )}
        </div>



        {/* Right side controls */}
        <div className="app-navbar-controls" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {currentView === 'studio' && (
            <div
              className="studio-compute-toggle"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: theme === 'light' ? 'var(--alpha-rgba-0-0-0-0-05)' : 'var(--inline-rgba-255-255-255-0-06-2)',
                padding: '2px',
                borderRadius: 'var(--shape-full)',
                border: theme === 'light' ? '1px solid var(--alpha-rgba-0-0-0-0-08)' : '1px solid var(--inline-rgba-255-255-255-0-1)',
                gap: '2px'
              }}
              role="group"
              aria-label={flashText.computeResource}
              title={computeResource === 'local' ? flashText.usingLocalTitle : flashText.usingServerTitle}
            >
              <button
                type="button"
                onClick={() => {
                  if (computeResource === 'local') return;
                  setComputeResource('local');
                  if (typeof localStorage !== 'undefined') localStorage.setItem('vectria_compute_resource', 'local');
                  setSaveStatusMsg(flashText.usingLocalFlash);
                  setTimeout(() => setSaveStatusMsg(null), 3500);
                }}
                title={flashText.usingLocalTitle}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 11px',
                  borderRadius: 'var(--shape-full)',
                  fontSize: '11px',
                  fontWeight: computeResource === 'local' ? 700 : 500,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  background: computeResource === 'local'
                    ? (theme === 'light' ? 'var(--color-white)' : 'var(--md-sys-color-primary)')
                    : 'transparent',
                  color: computeResource === 'local'
                    ? (theme === 'light' ? 'var(--md-sys-color-primary)' : 'var(--color-black)')
                    : 'var(--md-sys-color-on-surface-variant)',
                  boxShadow: computeResource === 'local' ? '0 1px 3px var(--inline-rgba-0-0-0-0-12)' : 'none'
                }}
              >
                <Cpu size={12} />
                <span>{flashText.localLabel}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (computeResource === 'cloud') return;
                  setComputeResource('cloud');
                  if (typeof localStorage !== 'undefined') localStorage.setItem('vectria_compute_resource', 'cloud');
                  setSaveStatusMsg(flashText.usingServerFlash);
                  setTimeout(() => setSaveStatusMsg(null), 3500);
                }}
                title={flashText.usingServerTitle}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 11px',
                  borderRadius: 'var(--shape-full)',
                  fontSize: '11px',
                  fontWeight: computeResource === 'cloud' ? 700 : 500,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  background: computeResource === 'cloud'
                    ? (theme === 'light' ? 'var(--color-white)' : 'var(--md-sys-color-primary)')
                    : 'transparent',
                  color: computeResource === 'cloud'
                    ? (theme === 'light' ? 'var(--md-sys-color-primary)' : 'var(--color-black)')
                    : 'var(--md-sys-color-on-surface-variant)',
                  boxShadow: computeResource === 'cloud' ? '0 1px 3px var(--inline-rgba-0-0-0-0-12)' : 'none'
                }}
              >
                <UploadCloud size={12} />
                <span>{flashText.cloudLabel}</span>
              </button>
            </div>
          )}
          {!user ? (
            /* Guest Mode */
            <>
              {((currentView as string) === 'landing' || (currentView as string) === 'blog') ? (
                <>
                  <button
                    className="m3-btn m3-btn-tonal m3-btn-sm landing-nav-signin"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError(null);
                      setShowAuthModal(true);
                    }}
                    style={{
                      borderRadius: 'var(--shape-full)',
                      padding: '6px 16px',
                      border: '1px solid var(--md-sys-color-outline-variant)',
                      background: 'var(--md-sys-color-surface-container)'
                    }}
                  >
                    <User size={13} />
                    <span>{t('nav.signIn')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setAuthMode('register');
                      setAuthError(null);
                      setShowAuthModal(true);
                    }}
                    className="m3-btn m3-btn-filled m3-btn-sm landing-nav-register"
                    style={{
                      borderRadius: 'var(--shape-full)',
                      padding: '8px 20px',
                      background: 'var(--accent-gradient)',
                      color: 'var(--color-white)',
                      fontWeight: 600,
                      boxShadow: 'none'
                    }}
                  >
                    <span>{t('nav.register')}</span>
                    <ArrowUpRight size={15} />
                  </button>
                  <button
                    type="button"
                    className="m3-btn-icon mobile-menu-trigger"
                    onClick={() => setMobileNavOpen(prev => !prev)}
                    aria-label="Toggle navigation menu"
                    aria-expanded={mobileNavOpen}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: 'var(--shape-full)',
                      background: 'var(--md-sys-color-surface-container)',
                      border: '1px solid var(--md-sys-color-outline-variant)',
                      color: 'var(--md-sys-color-on-surface)',
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
                  </button>
                </>
              ) : (
                /* Guest previewing studio or reading legal pages */
                <>
                  <button
                    onClick={() => {
                      window.location.hash = '';
                      setCurrentView('landing');
                    }}
                    className="m3-btn m3-btn-tonal m3-btn-sm"
                    style={{
                      borderRadius: 'var(--shape-full)',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      border: theme === 'light' ? '1px solid var(--alpha-rgba-15-23-42-0-12)' : '1px solid var(--alpha-rgba-255-255-255-0-15)',
                      background: theme === 'light' ? 'var(--color-white)' : 'var(--alpha-rgba-255-255-255-0-08)',
                      color: theme === 'light' ? 'var(--palette-0f172a)' : 'var(--color-white)',
                      boxShadow: theme === 'light' ? '0 1px 2px var(--alpha-rgba-0-0-0-0-04)' : 'none'
                    }}
                    title={t('common.back')}
                  >
                    <ArrowLeft size={13} />
                    <span>{t('common.back')}</span>
                  </button>
                  <button
                    className="m3-btn m3-btn-filled m3-btn-sm"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError(null);
                      setShowAuthModal(true);
                    }}
                    style={{
                      borderRadius: 'var(--shape-full)',
                      padding: '6px 16px'
                    }}
                  >
                    <User size={13} />
                    <span>{t('nav.signIn')}</span>
                  </button>
                </>
              )}
            </>
          ) : (
            /* Logged-in User: Prominent Account Type & Credit Badges */
            <div className="studio-account-controls" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
              {/* Account Type Badge */}
              {(() => {
                const badge = getPlanBadgeConfig(user.plan, theme);
                return (
                  <div
                    className="studio-navbar-badge studio-plan-badge"
                    style={{
                      borderRadius: 'var(--shape-full)',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      background: badge.bg,
                      color: badge.color,
                      border: `1px solid ${badge.border}`,
                      boxShadow: theme === 'light' ? '0 1px 2px var(--alpha-rgba-0-0-0-0-04)' : 'none',
                      whiteSpace: 'nowrap'
                    }}
                    title={`Account Plan: ${
                      user.plan === 'studio' ? 'Studio (Best Value)' :
                      user.plan === 'pro' ? 'Pro (Popular)' :
                      user.plan === 'starter' ? 'Starter' :
                      user.plan === 'agency' ? 'Studio Agency' : 'Free Starter'
                    }`}
                  >
                    <span>
                      {studio.header.planNames[user.plan] || badge.label}
                    </span>
                  </div>
                );
              })()}

              {/* Consolidated Credit Badge with Popover */}
              <div ref={creditMenuRef} className="studio-credit-badge-wrap" style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="studio-navbar-badge studio-credit-badge"
                  onClick={() => setShowCreditMenu(prev => !prev)}
                  style={{
                    borderRadius: 'var(--shape-full)', fontSize: '11.5px', fontWeight: 600,
                    background: theme === 'light' ? 'var(--color-white)' : 'var(--inline-rgba-255-255-255-0-06-2)',
                    color: user.plan !== 'agency' && user.credits <= 10 ? 'var(--status-amber)' : (theme === 'light' ? 'var(--palette-0f172a)' : 'var(--md-sys-color-on-surface)'),
                    border: theme === 'light' ? '1px solid var(--alpha-rgba-15-23-42-0-12)' : '1px solid var(--inline-rgba-255-255-255-0-12)',
                    boxShadow: theme === 'light' ? '0 1px 2px var(--alpha-rgba-0-0-0-0-03)' : 'none', cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                  title={studio.header.creditTooltip}
                >
                  <Zap size={13} color={user.plan !== 'agency' && user.credits <= 10 ? 'var(--status-amber)' : (theme === 'light' ? 'var(--status-amber)' : 'var(--color-chart-current)')} fill="currentColor" />
                  <span className="credit-label-full">{user.plan === 'agency' ? studio.header.unlimitedCredits : `${user.credits} Credits`}</span>
                  <span className="credit-label-short">{user.plan === 'agency' ? '∞' : user.credits}</span>
                  <ChevronDown
                    size={11}
                    style={{
                      opacity: 0.7,
                      transform: showCreditMenu ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.18s ease'
                    }}
                  />
                </button>

                {showCreditMenu && (
                  <div
                    className="m3-card studio-credit-popover"
                    style={{
                      position: 'absolute',
                      top: '42px',
                      right: 0,
                      width: '280px',
                      padding: '12px',
                      background: 'var(--md-sys-color-surface-container-high)',
                      boxShadow: 'var(--elevation-3)',
                      zIndex: 100,
                      borderRadius: 'var(--shape-md)',
                      border: '1px solid var(--md-sys-color-outline-variant)'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                      {/* Vector Credits Row */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 10px', borderRadius: '8px',
                        background: 'var(--md-sys-color-surface-container-low)',
                        border: '1px solid var(--md-sys-color-outline-variant)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '26px', height: '26px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--md-sys-color-primary-container)', color: 'var(--color-chart-current)'
                          }}>
                            <Zap size={13} fill="currentColor" />
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600 }}>{studio.header.userMenu.vectorCreditLabel.replace(':', '')}</div>
                            <div style={{ fontSize: '10px', color: 'var(--md-sys-color-on-surface-variant)' }}>SVG & EPS Vectorization</div>
                          </div>
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: user.plan !== 'agency' && user.credits <= 10 ? 'var(--status-amber)' : 'inherit' }}>
                          {user.plan === 'agency' ? studio.header.unlimitedCredits : user.credits.toLocaleString()}
                        </span>
                      </div>

                      {/* AI Credits Row */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 10px', borderRadius: '8px',
                        background: theme === 'light' ? 'var(--palette-f5f3ff)' : 'var(--inline-rgba-139-92-246-0-08)',
                        border: theme === 'light' ? '1px solid var(--inline-rgba-139-92-246-0-16)' : '1px solid var(--inline-rgba-196-181-253-0-12)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '26px', height: '26px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'var(--inline-rgba-192-132-252-0-18)', color: 'var(--palette-c084fc)'
                          }}>
                            <Sparkles size={13} />
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600 }}>{studio.header.userMenu.aiCreditLabel.replace(':', '')}</div>
                            <div style={{ fontSize: '10px', color: 'var(--md-sys-color-on-surface-variant)' }}>AI Title, Keywords & Tags</div>
                          </div>
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: (user.ai_credits ?? 0) <= 10 ? 'var(--palette-d97706)' : 'inherit' }}>
                          {user.plan === 'agency' ? studio.header.unlimitedCredits : (user.ai_credits ?? 0).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', lineHeight: 1.4, marginBottom: '10px', padding: '0 2px' }}>
                      {studio.header.userMenu.creditNote}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <button
                        type="button"
                        className="m3-btn m3-btn-tonal m3-btn-sm"
                        onClick={() => {
                          setShowCreditMenu(false);
                          setShowTopupModal(true);
                        }}
                        style={{
                          borderRadius: 'var(--shape-full)',
                          padding: '7px 8px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '5px'
                        }}
                      >
                        <PlusCircle size={13} />
                        <span>{studio.pricingModal?.topupBtn || (locale === 'id' ? 'Top-up' : 'Top Up')}</span>
                      </button>
                      <button
                        type="button"
                        className="m3-btn m3-btn-filled m3-btn-sm"
                        onClick={() => {
                          setShowCreditMenu(false);
                          setShowPricingModal(true);
                        }}
                        style={{
                          borderRadius: 'var(--shape-full)',
                          padding: '7px 8px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '5px'
                        }}
                      >
                        <CreditCard size={13} />
                        <span>{user.plan === 'free' ? studio.header.upgrade : studio.header.userMenu.upgradePlan}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* In-app notifications */}
              <div ref={notificationMenuRef} style={{ position: 'relative' }}>
                <button type="button" className="m3-btn-icon" aria-label={notificationText.notifications} aria-expanded={showNotificationMenu} onClick={() => { setShowNotificationMenu(value => !value); if (!showNotificationMenu) fetchNotifications(); }} style={{ position: 'relative', borderRadius: '50%' }}>
                  <Bell size={15} />
                  {notificationUnreadCount > 0 && <span style={{ position: 'absolute', top: -3, right: -3, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 9, background: 'var(--palette-ef4444)', color: 'var(--palette-fff)', fontSize: 9, fontWeight: 800, display: 'grid', placeItems: 'center' }}>{notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}</span>}
                </button>
                {showNotificationMenu && <div className="m3-card studio-notification-popover" style={{ position: 'absolute', top: 42, right: 0, width: 340, maxHeight: 430, overflowY: 'auto', padding: 10, zIndex: 110, background: 'var(--md-sys-color-surface-container-high)', border: '1px solid var(--md-sys-color-outline-variant)', boxShadow: 'var(--elevation-3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 7px 10px' }}><b>{notificationText.notifications}</b>{notificationUnreadCount > 0 && <button className="m3-btn m3-btn-text m3-btn-xs" onClick={markAllNotificationsRead}>{notificationText.markAllRead}</button>}</div>
                  {notificationLoading && notifications.length === 0 ? <div style={{ padding: 18, textAlign: 'center', fontSize: 12 }}>{notificationText.loading}</div> : notifications.length === 0 ? <div style={{ padding: 18, textAlign: 'center', fontSize: 12, color: 'var(--md-sys-color-on-surface-variant)' }}>{notificationText.noNotificationsYet}</div> : notifications.map(item => <button key={item.id} type="button" onClick={() => { if (!item.read_at) markNotificationRead(item.id); if (item.cta_url) window.open(item.cta_url, '_blank', 'noopener,noreferrer'); }} style={{ width: '100%', textAlign: 'left', padding: 11, marginBottom: 6, borderRadius: 8, border: '1px solid var(--md-sys-color-outline-variant)', background: item.read_at ? 'transparent' : 'var(--inline-rgba-56-189-248-10)', color: 'inherit', cursor: 'pointer' }}><div style={{ fontSize: 12, fontWeight: item.read_at ? 600 : 800 }}>{item.title}</div><div style={{ fontSize: 11, lineHeight: 1.45, color: 'var(--md-sys-color-on-surface-variant)', marginTop: 4 }}>{item.body_text}</div><div style={{ fontSize: 9.5, marginTop: 6, opacity: .7 }}>{String(item.created_at || '').slice(0, 16).replace('T', ' ')}</div></button>) }
                  <button className="m3-btn m3-btn-tonal m3-btn-sm" style={{ width: '100%', marginTop: 5 }} onClick={() => { setShowNotificationMenu(false); setShowNotificationCenter(true); fetchNotificationCenter(1, false); }}>{notificationText.viewAll}</button>
                </div>}
              </div>

              {/* Quick Upgrade Button for Free accounts */}
              {user.plan === 'free' && (
                <button
                  onClick={() => setShowPricingModal(true)}
                  className="m3-btn m3-btn-filled m3-btn-sm"
                  style={{
                    borderRadius: 'var(--shape-full)',
                    padding: '5px 14px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }}
                  title={studio.header.upgrade}
                >
                  <span>{studio.header.upgrade}</span>
                </button>
              )}

              {/* Owner Admin Console Shortcut Button */}
              {(['writer', 'editor', 'content_manager', 'admin', 'owner'] as string[]).includes(user?.role || '') && (
                <button
                  className="m3-btn m3-btn-tonal m3-btn-xs studio-navbar-badge studio-admin-badge"
                  onClick={() => setCurrentView('admin')}
                  style={{
                    borderRadius: 'var(--shape-full)',
                    background: theme === 'light' ? 'var(--color-white)' : 'var(--inline-rgba-56-189-248-0-10)',
                    borderColor: theme === 'light' ? 'var(--alpha-rgba-15-23-42-0-12)' : 'var(--alpha-rgba-56-189-248-0-35)',
                    color: theme === 'light' ? 'var(--palette-0f172a)' : 'var(--color-chart-current)',
                    fontWeight: 600,
                    boxShadow: theme === 'light' ? '0 1px 2px var(--alpha-rgba-0-0-0-0-03)' : 'none'
                  }}
                  title={appExtraText.adminConsole}
                >
                  <Shield size={12} color={theme === 'light' ? 'var(--palette-0f172a)' : 'var(--color-chart-current)'} />
                  <span>{studio.header.admin}</span>
                </button>
              )}

              {/* User Dropdown */}
              <div ref={userMenuRef} style={{ position: 'relative' }}>
                <button
                  className="m3-btn m3-btn-tonal m3-btn-sm studio-navbar-badge studio-user-menu-button"
                  onClick={() => setShowUserMenu(prev => !prev)}
                  style={{ borderRadius: 'var(--shape-full)' }}
                >
                  <UserAvatar
                    src={user.avatar_url}
                    name={user.name}
                    email={user.email}
                    label={appExtraText.avatar}
                    size={22}
                    style={{ background: 'var(--md-sys-color-primary-container)' }}
                  />
                  <span className="studio-user-name" style={{ fontSize: '11.5px', fontWeight: 600 }}>{user.name}</span>
                  <ChevronDown size={12} />
                </button>

                {showUserMenu && (
                  <div
                    className="m3-card studio-user-popover"
                    style={{
                      position: 'absolute',
                      top: '42px',
                      right: 0,
                      width: '270px',
                      padding: '8px',
                      background: 'var(--md-sys-color-surface-container-high)',
                      boxShadow: 'var(--elevation-3)',
                      zIndex: 100,
                      borderRadius: 'var(--shape-md)'
                    }}
                  >
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--md-sys-color-outline-variant)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700 }}>{user.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)' }}>{user.email}</div>
                      <div className="studio-mobile-profile-plan" style={{ marginTop: 8 }}><span>{locale === 'id' ? 'Paket' : 'Plan'}</span><strong>{studio.header.planNames[user.plan] || user.plan}</strong></div>
                    </div>

                    <div style={{ padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {(['writer', 'editor', 'content_manager', 'admin', 'owner'] as string[]).includes(user.role || '') && (
                        <button
                          className="m3-segmented-item"
                          style={{ justifyContent: 'flex-start', padding: '7px 10px', borderRadius: 'var(--shape-sm)', color: 'var(--palette-00e5ff)', background: 'var(--alpha-rgba-0-229-255-0-08)' }}
                          onClick={() => {
                            setShowUserMenu(false);
                            setCurrentView('admin');
                          }}
                        >
                          <Shield size={14} color="var(--palette-00e5ff)" />
                          <span style={{ fontWeight: 700 }}>{studio.header.userMenu.adminConsole}</span>
                        </button>
                      )}
                      <button
                        className="m3-segmented-item"
                        style={{ justifyContent: 'flex-start', padding: '7px 10px', borderRadius: 'var(--shape-sm)' }}
                        onClick={() => {
                          setShowUserMenu(false);
                          navigateSettings('profile');
                        }}
                      >
                        <Settings size={14} color="var(--md-sys-color-primary)" />
                        <span>{locale === 'id' ? 'Pengaturan Akun' : 'Account Settings'}</span>
                      </button>
                      <button
                        className="m3-segmented-item"
                        style={{ justifyContent: 'flex-start', padding: '7px 10px', borderRadius: 'var(--shape-sm)', color: 'var(--md-sys-color-error)' }}
                        onClick={handleSignOut}
                      >
                        <LogOut size={14} />
                        <span>{studio.header.userMenu.signOut}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Language Selector (placed next to darkmode toggle) */}
          <div className={false ? 'landing-navbar-language' : ''}>
            <LanguageSelector />
          </div>

          {/* Theme Toggle Button (Light Mode / Dark Mode) */}
          <button
            onClick={toggleTheme}
            className={`m3-btn-icon ${false ? 'landing-navbar-theme' : ''}`}
            style={{
              borderRadius: 'var(--shape-full)',
              background: 'var(--md-sys-color-surface-container)',
              border: '1px solid var(--md-sys-color-outline-variant)',
              color: theme === 'dark' ? 'var(--status-amber)' : 'var(--palette-6366f1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'var(--transition-standard)'
            }}
            title={studio.header.toggleTheme}
            aria-label={appExtraText.toggleTheme}
          >
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          {/* Right Sidebar toggle for Studio (Desktop only) */}
          {isStudioMode && (
            <button
              className="m3-btn-icon studio-top-panel-toggle"
              onClick={toggleRightSidebar}
              title={rightSidebarOpen ? studio.header.collapseQueue : studio.header.expandQueue}
              aria-label={rightSidebarOpen ? studio.header.collapseQueue : studio.header.expandQueue}
              style={{
                borderRadius: '8px',
                color: rightSidebarOpen ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                background: rightSidebarOpen ? 'var(--md-sys-color-surface-container-high)' : 'transparent',
                border: rightSidebarOpen ? '1px solid var(--md-sys-color-outline-variant)' : '1px solid transparent',
                transition: 'all 0.18s ease'
              }}
            >
              <PanelRight size={16} />
            </button>
          )}

        </div>
      </header>
      )}

      {/* Mobile Drawer Menu for Landing Page */}
      {false && mobileNavOpen && (
        <div
          className="mobile-nav-drawer-backdrop"
          onClick={() => setMobileNavOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            top: '56px',
            background: 'var(--alpha-rgba-0-0-0-0-6)',
            backdropFilter: 'blur(8px)',
            zIndex: 998,
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <nav
            className="mobile-nav-drawer"
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--md-sys-color-surface-container-high)',
              borderBottom: '1px solid var(--md-sys-color-outline-variant)',
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              boxShadow: '0 12px 32px var(--inline-rgba-0-0-0-0-36)'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                type="button"
                className="mobile-nav-link"
                onClick={() => {
                  setMobileNavOpen(false);
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--md-sys-color-on-surface)',
                  fontSize: '15px',
                  fontWeight: 600,
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {t('nav.features')}
              </button>
              <button
                type="button"
                className="mobile-nav-link"
                onClick={() => {
                  setMobileNavOpen(false);
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--md-sys-color-on-surface)',
                  fontSize: '15px',
                  fontWeight: 600,
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {t('nav.pricing')}
              </button>
              <button
                type="button"
                className="mobile-nav-link"
                onClick={() => {
                  setMobileNavOpen(false);
                  document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--md-sys-color-on-surface)',
                  fontSize: '15px',
                  fontWeight: 600,
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {t('nav.faq')}
              </button>
              <button
                type="button"
                className="mobile-nav-link"
                onClick={() => {
                  setMobileNavOpen(false);
                  window.location.assign('/blog');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--md-sys-color-on-surface)',
                  fontSize: '15px',
                  fontWeight: 600,
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Blog
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content View Switcher: Logged in users NEVER see Landing Page */}
      <Suspense fallback={<div role="status" aria-live="polite" style={{flex:1,display:'grid',placeItems:'center',minHeight:240,color:'var(--md-sys-color-on-surface-variant)'}}><div style={{display:'grid',justifyItems:'center',gap:10}}><RefreshCw size={22} className="m3-spin"/><span style={{fontSize:12}}>{appExtraText.loadingWorkspace}</span></div></div>}>
      {isMaintenanceBlocked ? (
        <MaintenanceScreen
          theme={theme}
          maintenanceMessage={systemStatus?.maintenance_message}
          onOpenLogin={() => {
            setAuthMode('login');
            setAuthError(null);
            setShowAuthModal(true);
          }}
        />
      ) : !statusChecked && !user ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            flex: 1,
            minHeight: '100vh',
            display: 'grid',
            placeItems: 'center',
            backgroundImage: "linear-gradient(var(--inline-rgba-5-8-17-0-45), var(--inline-rgba-5-8-17-0-72)), url('/BG-landingpage-dark.webp')",
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'var(--palette-050811)',
            color: 'var(--md-sys-color-on-surface)',
          }}
        >
          <div style={{ display: 'grid', justifyItems: 'center', gap: 12 }}>
            <img src="/emblem.webp" alt="Vectria" width="32" height="32" style={{ height: 32, width: 'auto' }} />
            <RefreshCw size={20} className="m3-spin" style={{ color: 'var(--palette-0072ff)' }} />
          </div>
        </div>
      ) : currentView === 'admin' && ['writer', 'editor', 'content_manager', 'admin', 'owner'].includes(user?.role || '') ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <AdminConsole
            user={user}
            authToken={authToken || ''}
            authenticatedFetch={authenticatedFetch}
            onBackToStudio={() => {
              fetchSystemStatus();
              setCurrentView('studio');
            }}
            theme={theme}
            onToggleTheme={toggleTheme}
            onOwnershipTransferred={() => {
              setUser(null); setAuthToken(null); setCurrentView('landing'); setAuthMode('login'); setShowAuthModal(true);
              ['vectria_auth_token','vectria_auth_refresh_token','vectria_auth_user'].forEach(key => { localStorage.removeItem(key); sessionStorage.removeItem(key); });
              setAuthSuccessMsg(locale === 'id' ? 'Ownership berhasil dipindahkan. Silakan masuk kembali.' : 'Ownership transferred. Sign in again.');
            }}
            onRefreshUser={() => {
              authenticatedFetch('/api/auth/me')
                .then(async response => {
                  const data = await response.json().catch(() => ({}));
                  if (!response.ok) throw new Error(apiErrorMessage(data, appExtraText.requestFailed(response.status)));
                  if (!data.user) return;
                  setUser(data.user);
                  const storage = localStorage.getItem('vectria_auth_token') ? localStorage : sessionStorage;
                  storage.setItem('vectria_auth_user', JSON.stringify(data.user));
                })
                .catch(() => {});
            }}
          />
        </div>
      ) : currentView === 'settings' && user ? (
        <SettingsPage
          user={user}
          section={settingsSection}
          theme={theme}
          authenticatedFetch={authenticatedFetch}
          onNavigate={navigateSettings}
          onBackToStudio={navigateToStudio}
          onToggleTheme={toggleTheme}
          onOpenPassword={() => {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordChangeError(null);
            setPasswordChangeSuccess(null);
            setShowChangePasswordModal(true);
          }}
          onOpenTopup={() => setShowTopupModal(true)}
          onOpenPlans={() => setShowPricingModal(true)}
          byokContent={byokSettingsContent}
          onUserUpdated={(updatedUser) => {
            setUser(updatedUser as UserProfile);
            const storage = localStorage.getItem('vectria_auth_token') ? localStorage : sessionStorage;
            storage.setItem('vectria_auth_user', JSON.stringify(updatedUser));
          }}
        />
      ) : currentView === 'settings' && !user ? (
        <ErrorPage code="404" />
      ) : currentView === 'blog' ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <Suspense fallback={<div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--md-sys-color-on-surface-variant)' }}>Loading...</div>}>
            <PublicBlog
              initialSlug={blogSlug}
              initialType={blogType}
              currentTheme={theme}
              onNavigateHome={navigatePublicHome}
              onNavigateStudio={() => {
                navigateToStudio();
              }}
              onNavigateBlog={(slug, type) => {
                navigateBlog(slug, type);
              }}
            />
          </Suspense>
        </div>
      ) : !user && (currentView === 'about' || currentView === 'terms' || currentView === 'privacy') ? (
        <LegalPage
          page={currentView}
          theme={theme}
          onBack={() => { window.location.hash = ''; setCurrentView('landing'); }}
          onNavigate={(page) => { window.location.hash = page; setCurrentView(page); window.scrollTo(0, 0); }}
        />
      ) : !user && currentView === 'landing' ? (
        <div style={{ flex: 1 }}>
          <LandingPage
            theme={theme}
            onLaunchStudio={() => setCurrentView('studio')}
            onOpenAuth={(mode) => {
              setAuthMode(mode || 'login');
              setAuthError(null);
              setShowAuthModal(true);
            }}
            onOpenLegal={(page) => {
              window.location.hash = page;
              setCurrentView(page);
              window.scrollTo(0, 0);
            }}
            onOpenBlog={() => { window.location.assign('/blog'); }}
          />
          <CookieConsent onOpenPrivacy={() => {
            window.location.hash = 'privacy';
            setCurrentView('privacy');
            window.scrollTo(0, 0);
          }} />
        </div>
      ) : (
        <div className="studio-workspace-shell" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 64px)', overflow: 'hidden' }}>
          {systemStatus?.maintenance_mode && (
            <div style={{
              background: 'linear-gradient(90deg, var(--inline-rgba-245-158-11-0-95), var(--inline-rgba-217-119-6-0-95))',
              color: 'var(--color-white)',
              padding: '7px 20px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px var(--inline-rgba-0-0-0-0-15)',
              zIndex: 90
            }}>
              <ShieldAlert size={14} />
              <span>{locale === 'id' ? 'Mode Pemeliharaan Aktif — Akses publik sedang dibatasi (Hanya Staf Admin)' : 'Maintenance Mode Active — Public access is temporarily restricted to administrators'}</span>
            </div>
          )}

      {/* Floating Notifications */}
      {optimizeNotice && (
        <div
          style={{
            background: 'linear-gradient(90deg, var(--inline-rgba-56-189-248-0-2), var(--inline-rgba-99-102-241-0-2))',
            borderBottom: '1px solid var(--alpha-rgba-56-189-248-0-4)',
            padding: '7px 20px',
            fontSize: '12.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'var(--md-sys-color-primary)',
            fontWeight: 500,
            zIndex: 35
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={15} />
            <span>{optimizeNotice}</span>
          </div>
          <button
            style={{ background: 'transparent', border: 'none', color: 'var(--color-white)', cursor: 'pointer', fontSize: '15px' }}
            onClick={() => setOptimizeNotice(null)}
          >
            ×
          </button>
        </div>
      )}

      {saveStatusMsg && (
        <div
          style={{
            position: 'fixed',
            left: '20px',
            bottom: '20px',
            maxWidth: 'min(520px, calc(100vw - 40px))',
            background: 'color-mix(in srgb, var(--status-emerald) 16%, var(--md-sys-color-surface-container-high))',
            border: '1px solid color-mix(in srgb, var(--status-emerald) 45%, transparent)',
            borderRadius: '12px',
            padding: '10px 14px',
            fontSize: '12.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--status-emerald)',
            fontWeight: 650,
            boxShadow: '0 14px 38px var(--inline-rgba-0-0-0-28)',
            zIndex: 130,
            pointerEvents: 'none'
          }}
        >
          <Check size={15} />
          <span>{saveStatusMsg}</span>
        </div>
      )}

      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => { void handleFiles(e.target.files); e.target.value = ''; }}
      />

      {/* ====================================================================
          STUDIO MAIN THREE-COLUMN WORKSPACE
          ==================================================================== */}
      <div ref={studioLayoutRef} className="studio-layout">
        {(leftSidebarOpen || rightSidebarOpen) && (
          <button
            type="button"
            className="studio-drawer-scrim"
            onClick={closeCompactSidebars}
            aria-label={appExtraText.close}
          />
        )}
        {/* ==================================================================
            LEFT SIDEBAR: PRESETS & VECTORIZATION ENGINE PARAMETERS
            ================================================================== */}
        <aside
          className={`studio-sidebar studio-sidebar-left ${!leftSidebarOpen ? 'sidebar-collapsed-left' : ''}`}
          style={{
            padding: leftSidebarOpen ? 'clamp(18px, 1.8vw, 24px)' : '0',
            gap: '16px'
          }}
        >
          {/* Mobile Drawer Header with Close Button */}
          <div
            className="mobile-drawer-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: '8px',
              borderBottom: '1px solid var(--md-sys-color-outline-variant)'
            }}
          >
            <strong style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)' }}>
              {studio.leftSidebar.tuningTitle || 'Settings'}
            </strong>
            <button
              type="button"
              className="m3-btn-icon studio-drawer-close"
              onClick={() => setLeftSidebarOpen(false)}
              title={appExtraText.close}
              aria-label={appExtraText.close}
            >
              <X size={16} />
            </button>
          </div>

          {/* Smart Auto-Optimize Button with CPU-Only Detection */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <button
              className={`m3-btn m3-btn-md ${activePreset === 'auto' ? 'm3-btn-filled' : 'm3-btn-tonal'}`}
              style={{
                width: '100%',
                borderRadius: '8px',
                opacity: queue.length === 0 ? 0.55 : 1,
                cursor: queue.length === 0 ? 'not-allowed' : 'pointer',
              }}
              onClick={handleAutoOptimize}
              disabled={isAnalyzingOptimize || activeItem?.status === 'processing' || isProcessingBatch}
              title={
                queue.length === 0
                  ? studio.leftSidebar.autoTitleEmpty
                  : studio.leftSidebar.autoTitleActive
              }
            >
              <RefreshCw size={16} className={isAnalyzingOptimize ? 'm3-pulse' : ''} />
              <span>{isAnalyzingOptimize ? studio.leftSidebar.analyzing : studio.leftSidebar.autoOptimize}</span>
            </button>
            {queue.length === 0 && (
              <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', textAlign: 'center', lineHeight: 1.4 }}>
                {studio.leftSidebar.autoNoticeEmpty}
              </div>
            )}
          </div>

          {activePreset === 'auto' && activeItem?.optimization && (
            <div className="m3-card" style={{ padding: '10px 12px', fontSize: '10.5px', lineHeight: 1.55 }}>
              <div style={{ color: 'var(--md-sys-color-primary)', fontWeight: 700, textTransform: 'uppercase' }}>
                {studio.leftSidebar.perImagePrefix} {activeItem.optimization.category.replaceAll('_', ' ')}
              </div>
              <div style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                {studio.leftSidebar.colorsUnit(activeItem.optimization.quantize_colors)} · palette error {activeItem.optimization.palette_error}
              </div>
              <div style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                Curve {activeItem.optimization.curve_tolerance}px · smoothing {activeItem.optimization.smooth_factor}
                {' · '}merge Δ {activeItem.optimization.layer_difference}
              </div>
            </div>
          )}

          {/* Quick Tracing Presets in Chips */}
          <div className="m3-card" style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)' }}>
                {studio.leftSidebar.quickPresets}
              </label>
              <button
                type="button"
                onClick={() => setShowPresetGuide(!showPresetGuide)}
                style={{
                  background: showPresetGuide ? 'var(--md-sys-color-surface-container-highest)' : 'transparent',
                  border: '1px solid var(--md-sys-color-outline-variant)',
                  borderRadius: 'var(--shape-xs)',
                  color: showPresetGuide ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                  fontSize: '11px',
                  padding: '2px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'var(--transition-standard)',
                }}
                title={studio.leftSidebar.guideTitle}
              >
                <HelpCircle size={12} />
                <span>{studio.leftSidebar.guideBtn}</span>
              </button>
            </div>

            {/* Clean Professional Technical Reference Card */}
            {showPresetGuide && (
              <div
                style={{
                  background: 'var(--md-sys-color-surface-container-high)',
                  border: '1px solid var(--md-sys-color-outline-variant)',
                  borderRadius: 'var(--shape-sm)',
                  padding: '10px 12px',
                  fontSize: '11px',
                  lineHeight: 1.45,
                  color: 'var(--md-sys-color-on-surface-variant)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--md-sys-color-outline-variant)', paddingBottom: '6px' }}>
                  <span style={{ fontWeight: 600, color: 'var(--md-sys-color-on-surface)', fontSize: '11.5px' }}>
                    {studio.leftSidebar.guideTitle}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowPresetGuide(false)}
                    style={{ background: 'transparent', border: 'none', color: 'var(--md-sys-color-on-surface-variant)', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}
                  >
                    ×
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {studio.leftSidebar.guideItems.map((item) => (
                    <div key={item.name}>
                      <div style={{ fontWeight: 600, color: 'var(--md-sys-color-on-surface)' }}>{item.name}</div>
                      <div style={{ fontSize: '10.5px' }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              {(['flat-6', 'vibrant-12', 'detailed-32', 'ultra-64', 'truecolor', 'monoline'] as const).map((key) => (
                <button
                  key={key}
                  className={`m3-chip ${activePreset === key ? 'm3-chip-active' : ''}`}
                  style={{ justifyContent: 'center', minHeight: '34px', fontSize: '11.5px', padding: '6px 8px' }}
                  onClick={() => applyPreset(key)}
                  title={studio.leftSidebar.presetTooltips[key]}
                >
                  {studio.leftSidebar.presetChips[key]}
                </button>
              ))}
            </div>
          </div>


          {/* Export Output Formats */}
          <div id="output-format-controls" tabIndex={-1} className="m3-card" style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)' }}>
              {studio.leftSidebar.outputFormats}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
              <button
                type="button"
                className={`m3-chip ${outputFormat === 'both' ? 'm3-chip-active' : ''}`}
                style={{ padding: '6px 4px', minHeight: '38px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}
                onClick={() => setOutputFormat('both')}
              >
                <span style={{ whiteSpace: 'nowrap', fontSize: '11px', lineHeight: 1.2 }}>{studio.leftSidebar.formatBoth}</span>
              </button>
              <button
                type="button"
                className={`m3-chip ${outputFormat === 'svg_only' ? 'm3-chip-active' : ''}`}
                style={{ padding: '6px 4px', minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: '11.5px' }}
                onClick={() => setOutputFormat('svg_only')}
              >
                <span>{studio.leftSidebar.formatSvg}</span>
              </button>
              <button
                type="button"
                className={`m3-chip ${outputFormat === 'eps_only' ? 'm3-chip-active' : ''}`}
                style={{ padding: '6px 4px', minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: '11.5px' }}
                onClick={() => setOutputFormat('eps_only')}
              >
                <span>{studio.leftSidebar.formatEps}</span>
              </button>
            </div>
          </div>

          {/* Engine Parameters Tuning */}
          <div id="tuning-controls" tabIndex={-1} className="m3-card" style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)' }}>
              {studio.leftSidebar.tuningTitle}
            </label>

            {/* Color Palette Limit */}
            <div id="color-limit-control" tabIndex={-1} className="m3-slider-group">
              <div className="m3-slider-header">
                <span className="m3-slider-title">{studio.leftSidebar.colorLimit}</span>
                <span className="m3-slider-value">
                  {quantizeColors >= 256 ? studio.leftSidebar.truecolorLabel : studio.leftSidebar.colorsUnit(quantizeColors)}
                </span>
              </div>
              <input
                type="range"
                min="2"
                max="256"
                step="2"
                value={quantizeColors}
                onChange={(e) => { setQuantizeColors(Number(e.target.value)); setActivePreset(null); }}
                className="m3-slider"
              />
              {/* Clean professional recommendation label based on color slider value */}
              <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '4px' }}>
                {quantizeColors <= 8 && studio.leftSidebar.colorHints.flat}
                {quantizeColors > 8 && quantizeColors <= 16 && studio.leftSidebar.colorHints.vibrant}
                {quantizeColors > 16 && quantizeColors <= 32 && studio.leftSidebar.colorHints.detailed}
                {quantizeColors > 32 && studio.leftSidebar.colorHints.photo}
              </div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                {[6, 8, 12, 16, 24, 32, 64, 256].map((cnt) => (
                  <button
                    key={cnt}
                    type="button"
                    className={`m3-chip ${quantizeColors === cnt ? 'm3-chip-active' : ''}`}
                    style={{ fontSize: '11px', padding: '2px 8px', height: '24px' }}
                    onClick={() => { setQuantizeColors(cnt); setActivePreset(null); }}
                  >
                    {cnt === 256 ? 'Truecolor' : `${cnt}c`}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter Speckle Noise */}
            <div id="speckle-filter-control" tabIndex={-1} className="m3-slider-group">
              <div className="m3-slider-header">
                <span className="m3-slider-title">{studio.leftSidebar.speckleFilter}</span>
                <span className="m3-slider-value">{filterSpeckle} px</span>
              </div>
              <input
                type="range"
                min="0"
                max="50"
                value={filterSpeckle}
                onChange={(e) => { setFilterSpeckle(Number(e.target.value)); setActivePreset(null); }}
                className="m3-slider"
              />
            </div>

            {/* Corner Smoothness Threshold */}
            <div className="m3-slider-group">
              <div className="m3-slider-header">
                <span className="m3-slider-title">{studio.leftSidebar.cornerThreshold}</span>
                <span className="m3-slider-value">{cornerThreshold}°</span>
              </div>
              <input
                type="range"
                min="20"
                max="80"
                value={cornerThreshold}
                onChange={(e) => { setCornerThreshold(Number(e.target.value)); setActivePreset(null); }}
                className="m3-slider"
              />
            </div>

            <div className="m3-slider-group">
              <div className="m3-slider-header">
                <span className="m3-slider-title">{studio.leftSidebar.curveSmoothness}</span>
                <span className="m3-slider-value">{Math.round(smoothFactor * 100)}%</span>
              </div>
              <input
                type="range"
                min="5"
                max="35"
                step="1"
                value={Math.round(smoothFactor * 100)}
                onChange={(e) => { setSmoothFactor(Number(e.target.value) / 100); setActivePreset(null); }}
                className="m3-slider"
              />
            </div>

            <div id="curve-tolerance-control" tabIndex={-1} className="m3-slider-group">
              <div className="m3-slider-header">
                <span className="m3-slider-title">{studio.leftSidebar.curveTolerance}</span>
                <span className="m3-slider-value">{curveTolerance.toFixed(1)} px</span>
              </div>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={curveTolerance}
                onChange={(e) => { setCurveTolerance(Number(e.target.value)); setActivePreset(null); }}
                className="m3-slider"
              />
            </div>

            <div className="m3-slider-group">
              <div className="m3-slider-header">
                <span className="m3-slider-title">{studio.leftSidebar.colorMerge}</span>
                <span className="m3-slider-value">Δ {layerDifference}</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                step="1"
                value={layerDifference}
                onChange={(e) => { setLayerDifference(Number(e.target.value)); setActivePreset(null); }}
                className="m3-slider"
              />
            </div>
          </div>

          {/* Background & Halo Cleaner */}
          <div className="m3-card" style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '12.5px', fontWeight: 600 }}>{studio.leftSidebar.stripWhiteBg}</div>
                <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)' }}>{studio.leftSidebar.stripWhiteDesc}</div>
              </div>
              <label className="m3-switch">
                <input
                  type="checkbox"
                  checked={stripWhiteBg}
                  onChange={(e) => { setStripWhiteBg(e.target.checked); setActivePreset(null); }}
                />
                <span className="m3-switch-slider"></span>
              </label>
            </div>

            {stripWhiteBg && (
              <>
                <div className="m3-slider-group">
                  <div className="m3-slider-header">
                    <span className="m3-slider-title">{studio.leftSidebar.whiteSensitivity}</span>
                    <span className="m3-slider-value">{whiteThreshold}</span>
                  </div>
                  <input
                    type="range"
                    min="180"
                    max="255"
                    value={whiteThreshold}
                    onChange={(e) => { setWhiteThreshold(Number(e.target.value)); setActivePreset(null); }}
                    className="m3-slider"
                  />
                </div>

                <div className="m3-slider-group">
                  <div className="m3-slider-header">
                    <span className="m3-slider-title">{studio.leftSidebar.antiHalo}</span>
                    <span className="m3-slider-value">{erodeHaloPx} px</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    value={erodeHaloPx}
                    onChange={(e) => { setErodeHaloPx(Number(e.target.value)); setActivePreset(null); }}
                    className="m3-slider"
                  />
                </div>
              </>
            )}
          </div>
        </aside>

        {/* ==================================================================
            CENTER MAIN CANVAS: IMMERSIVE WORKSPACE & INTERACTIVE SPLIT SLIDER
            ================================================================== */}
        <main ref={studioCanvasRef} className="studio-main-canvas">
          {queue.length === 0 ? (
            /* Empty State / Full-Size Canvas Drag & Drop Upload Zone */
            <div
              className="canvas-stage m3-card-interactive"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(event) => { if (!isRestoringQueue) handleDrop(event); }}
              onClick={() => { if (!isRestoringQueue) fileInputRef.current?.click(); }}
              style={{
                flex: 1,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '18px',
                padding: '40px 24px',
                border: theme === 'light' ? '2px dashed var(--alpha-rgba-15-23-42-0-16)' : '2px dashed var(--md-sys-color-outline)',
                background: theme === 'light' ? 'var(--palette-f4f6f9)' : 'var(--md-sys-color-surface-dim)',
                borderRadius: 'var(--shape-lg)',
                cursor: isRestoringQueue ? 'wait' : 'pointer',
                transition: 'var(--transition-standard)'
              }}
            >
              <div
                style={{
                  width: '68px',
                  height: '68px',
                  borderRadius: '16px',
                  background: theme === 'light' ? 'var(--color-white)' : 'var(--md-sys-color-surface-container)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--md-sys-color-primary)',
                  boxShadow: 'none',
                  border: '1px solid var(--md-sys-color-outline-variant)'
                }}
              >
                {isRestoringQueue ? <RefreshCw size={34} className="m3-pulse" /> : <UploadCloud size={34} />}
              </div>
              <div style={{ textAlign: 'center' }}>
                <h1 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px', color: 'var(--md-sys-color-on-surface)', letterSpacing: '-0.01em' }}>
                  {isRestoringQueue ? queueTransferText.restoring : studio.canvas.dropTitle}
                </h1>
                <p style={{ fontSize: '13.5px', color: 'var(--md-sys-color-on-surface-variant)', maxWidth: '440px', lineHeight: 1.5, margin: 0 }}>
                  {isRestoringQueue ? queueTransferText.restoringDesc : studio.canvas.dropDesc}
                </p>
              </div>
              {!isRestoringQueue && <button
                className="m3-btn m3-btn-filled m3-btn-md"
                style={{
                  borderRadius: '8px',
                  padding: '10px 28px',
                  fontWeight: 600,
                  fontSize: '13.5px'
                }}
              >
                {studio.canvas.browseBtn}
              </button>}
            </div>
          ) : (
            /* Active Inspection Stage */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', height: '100%', overflow: 'hidden' }}>
              {/* Workspace Action Bar */}
              <div
                className="workspace-action-bar"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'var(--md-sys-color-surface-container-lowest)',
                  padding: '6px 12px',
                  borderRadius: 'var(--shape-md)',
                  border: '1px solid var(--md-sys-color-outline-variant)',
                  gap: '10px',
                  minHeight: '40px'
                }}
              >
                {/* Visual View Mode Tabs (Center/Left of Canvas Toolbar) */}
                <div className="m3-segmented-bar workspace-view-modes" style={{ display: 'flex', padding: '2px', borderRadius: 'var(--shape-full)', flexShrink: 0 }}>
                  <button
                    className={`m3-segmented-item ${viewMode === 'side_by_side' ? 'active' : ''}`}
                    onClick={() => setViewMode('side_by_side')}
                    title={studio.canvas.dualViewTitle}
                    style={{ padding: '3px 9px', fontSize: '11px', height: '24px', gap: '4px', borderRadius: 'var(--shape-full)' }}
                  >
                    <Columns2 size={12} />
                    <span>{studio.canvas.dualView}</span>
                  </button>
                  <button
                    className={`m3-segmented-item ${viewMode === 'split' ? 'active' : ''}`}
                    onClick={() => setViewMode('split')}
                    title={appExtraText.splitViewTitle}
                    style={{ padding: '3px 9px', fontSize: '11px', height: '24px', gap: '4px', borderRadius: 'var(--shape-full)' }}
                  >
                    <Split size={12} />
                    <span>{appExtraText.splitView}</span>
                  </button>
                  <button
                    className={`m3-segmented-item ${viewMode === 'vector' && !isWireframe ? 'active' : ''}`}
                    onClick={() => { setViewMode('vector'); setIsWireframe(false); }}
                    title={studio.canvas.vectorViewTitle}
                    style={{ padding: '3px 9px', fontSize: '11px', height: '24px', gap: '4px', borderRadius: 'var(--shape-full)' }}
                  >
                    <PenTool size={12} />
                    <span>{studio.canvas.vectorView}</span>
                  </button>
                  <button
                    className={`m3-segmented-item ${viewMode === 'vector' && isWireframe ? 'active' : ''}`}
                    onClick={() => { setViewMode('vector'); setIsWireframe(true); }}
                    title={studio.canvas.outlineViewTitle}
                    style={{ padding: '3px 9px', fontSize: '11px', height: '24px', gap: '4px', borderRadius: 'var(--shape-full)' }}
                  >
                    <Layers size={12} />
                    <span>{studio.canvas.outlineView}</span>
                  </button>
                </div>

                {/* Conversion & Download Actions (Right of Toolbar) */}
                <div className="workspace-actions" style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                  {activeItem?.status === 'done' ? (
                    <>
                      {/* Unified Download Dropdown Button & Popover */}
                      <div ref={downloadMenuRef} style={{ position: 'relative' }}>
                        <button
                          className="m3-btn m3-btn-tonal m3-btn-xs"
                          onClick={() => setShowDownloadMenu(prev => !prev)}
                          aria-expanded={showDownloadMenu}
                          title={appExtraText.downloadExport || 'Download'}
                          style={{
                            height: '28px',
                            padding: '0 9px',
                            fontSize: '11px',
                            fontWeight: 600,
                            gap: '4px',
                            borderRadius: 'var(--shape-full)',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                        >
                          <Download size={12} />
                          <span>{appExtraText.downloadExport || 'Download'}</span>
                          <ChevronDown size={11} style={{ transform: showDownloadMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s ease' }} />
                        </button>

                        {showDownloadMenu && (
                          <div
                            className="m3-elevation-3"
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 6px)',
                              right: 0,
                              minWidth: '220px',
                              padding: '6px',
                              background: 'var(--md-sys-color-surface-container-high)',
                              border: '1px solid var(--md-sys-color-outline-variant)',
                              borderRadius: 'var(--shape-md)',
                              boxShadow: '0 8px 24px var(--inline-rgba-0-0-0-0-45)',
                              zIndex: 100,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              animation: 'm3-fade-scale 0.15s cubic-bezier(0.2, 0, 0, 1)'
                            }}
                          >
                            <button
                              onClick={() => {
                                setShowDownloadMenu(false);
                                downloadSvg(activeItem);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '7px 9px',
                                borderRadius: 'var(--shape-sm)',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                width: '100%',
                                textAlign: 'left',
                                color: 'var(--md-sys-color-on-surface)',
                                transition: 'background-color 0.15s ease'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--md-sys-color-surface-container-highest)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              <div
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '6px',
                                  background: 'var(--md-sys-color-primary-container)',
                                  color: 'var(--md-sys-color-on-primary-container)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                  fontWeight: 800,
                                  flexShrink: 0
                                }}
                              >
                                SVG
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)' }}>
                                  SVG Vector
                                </span>
                                <span style={{ fontSize: '9.5px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                                  Modern pure scalable vector
                                </span>
                              </div>
                            </button>

                            <button
                              onClick={() => {
                                setShowDownloadMenu(false);
                                downloadEps(activeItem);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '7px 9px',
                                borderRadius: 'var(--shape-sm)',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                width: '100%',
                                textAlign: 'left',
                                color: 'var(--md-sys-color-on-surface)',
                                transition: 'background-color 0.15s ease'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--md-sys-color-surface-container-highest)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              <div
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '6px',
                                  background: 'var(--md-sys-color-secondary-container)',
                                  color: 'var(--md-sys-color-on-secondary-container)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '9px',
                                  fontWeight: 800,
                                  flexShrink: 0
                                }}
                              >
                                EPS
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)' }}>
                                  EPS 10 Vector
                                </span>
                                <span style={{ fontSize: '9.5px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                                  Microstock, Illustrator & Corel
                                </span>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>

                      {canWebShare && (
                        <button
                          className="m3-btn m3-btn-tonal m3-btn-xs"
                          onClick={() => void handleShareVector(activeItem)}
                          title={appExtraText.shareTitle}
                          aria-label={appExtraText.share}
                          style={{ height: '28px', padding: '0 8px', fontSize: '11px', fontWeight: 600, gap: '4px', borderRadius: 'var(--shape-full)' }}
                        >
                          <Share2 size={12} />
                          <span className="share-btn-text">{appExtraText.share}</span>
                        </button>
                      )}
                      <button
                        className="m3-btn m3-btn-filled m3-btn-xs"
                        onClick={() => void convertItem(activeItem)}
                        disabled={isProcessingBatch}
                        title={studio.canvas.revectorizeTitle}
                        style={{ height: '28px', padding: '0 12px', fontSize: '11px', fontWeight: 700, gap: '5px', borderRadius: 'var(--shape-full)' }}
                      >
                        <RefreshCw size={12} />
                        <span>{studio.canvas.revectorize}</span>
                      </button>
                    </>
                  ) : (
                    <button
                      className="m3-btn m3-btn-filled m3-btn-xs"
                      onClick={() => {
                        if (!activeItem) return;
                        const remaining = queue.filter((item) => item.status !== 'done');
                        if (remaining.length > 1) void handleBatchConvert();
                        else void convertItem(activeItem);
                      }}
                      disabled={activeItem?.status === 'processing' || isProcessingBatch}
                      style={{ height: '28px', padding: '0 12px', fontSize: '11px', fontWeight: 700, gap: '5px', borderRadius: 'var(--shape-full)' }}
                    >
                      <RefreshCw
                        size={12}
                        className={activeItem?.status === 'processing' ? 'm3-pulse' : ''}
                      />
                      <span>{activeItem?.status === 'processing'
                        ? studio.canvas.vectorizing
                        : queue.filter((item) => item.status !== 'done').length > 1
                          ? studio.canvas.convertAll
                          : studio.canvas.convertVector}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* The Visual Canvas Stage */}
              <div
                className={`canvas-stage ${useCheckerboard ? 'canvas-checkerboard' : ''}`}
                style={{ flex: 1, minHeight: 0 }}
              >
                {/* Floating Document Tab / Header (Solusi B) */}
                {activeItem && (
                  <div
                    className="canvas-floating-dock canvas-document-tab"
                    onPointerDown={(e) => e.stopPropagation()}
                    title={`${activeItem.name} (${((activeItem.size || 0) / 1024).toFixed(0)} KB)`}
                  >
                    <ImageIcon size={13} color="var(--md-sys-color-primary)" style={{ flexShrink: 0 }} />
                    <h1 className="canvas-document-tab-name" style={{ margin: 0, padding: 0, fontSize: '11.5px', fontWeight: 600 }}>
                      {activeItem.name}
                    </h1>
                    <span className="canvas-document-tab-size">
                      ({((activeItem.size || 0) / 1024).toFixed(0)} KB)
                    </span>
                  </div>
                )}

                {/* 1. Vector Result Only Mode (Default & Cleanest) */}
                {viewMode === 'vector' && (
                  <div
                    onPointerDown={handleCanvasPointerDown}
                    onPointerMove={handleCanvasPointerMove}
                    onPointerUp={handleCanvasPointerEnd}
                    onPointerCancel={handleCanvasPointerEnd}
                    style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isPanning ? 'grabbing' : 'grab', touchAction: 'none', overflow: 'hidden' }}
                  >
                    {activeItem?.vectorSvg ? (
                      <div
                        className="vector-stage-wrapper"
                        style={{
                          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                          transformOrigin: 'center center',
                          transition: 'transform 0.15s ease',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '85%',
                          height: '85%',
                        }}
                      >
                        <div
                          className={`vector-svg-container ${isWireframe ? 'is-wireframe' : ''}`}
                          style={{ width: '100%', height: '100%' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeSvg(activeItem.vectorSvg) }}
                        />
                      </div>
                    ) : activeItem?.status === 'processing' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', color: 'var(--md-sys-color-primary)' }}>
                        <RefreshCw size={28} className="m3-pulse" />
                        <span style={{ fontSize: '13px', fontWeight: 500 }}>{studio.canvas.vectorizing}</span>
                        <span style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', opacity: 0.8 }}>{studio.canvas.computingCurves}</span>
                      </div>
                    ) : (
                      <div
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '24px', textAlign: 'center' }}
                      >
                        <div style={{
                          width: '46px',
                          height: '46px',
                          borderRadius: '50%',
                          background: 'var(--alpha-rgba-56-189-248-0-12)',
                          border: '1px solid var(--alpha-rgba-56-189-248-0-28)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--color-chart-current)'
                        }}>
                          <PenTool size={20} />
                        </div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)', marginBottom: '4px' }}>
                            {studio.canvas.noVectorTitle}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--md-sys-color-on-surface-variant)', maxWidth: '260px', lineHeight: 1.4 }}>
                            {studio.canvas.noVectorDesc}
                          </div>
                        </div>
                        {activeItem?.status === 'error' && (
                          <div style={{
                            background: 'var(--md-sys-color-error-container)',
                            color: 'var(--md-sys-color-error)',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            fontSize: '11px',
                            maxWidth: '280px',
                            lineHeight: 1.4,
                            border: '1px solid var(--inline-rgba-239-68-68-0-3)'
                          }}>
                            {activeItem.errorMessage || t('studio.conversionFailed')}
                          </div>
                        )}
                        {activeItem && (
                          <button
                            type="button"
                            className="m3-btn m3-btn-filled m3-btn-sm"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              void convertItem(activeItem);
                            }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '7px 16px', borderRadius: '8px', cursor: 'pointer' }}
                          >
                            <RefreshCw size={13} />
                            <span>{studio.canvas.convertVector}</span>
                          </button>
                        )}
                      </div>
                    )}
                    <div className="view-tag view-tag-right">
                      {isWireframe ? studio.canvas.tagVectorWireframe : studio.canvas.tagVectorSvg}
                    </div>
                  </div>
                )}

                {/* 3. Original Raster Only Mode */}
                {viewMode === 'raster' && (
                  <div
                    onPointerDown={handleCanvasPointerDown}
                    onPointerMove={handleCanvasPointerMove}
                    onPointerUp={handleCanvasPointerEnd}
                    onPointerCancel={handleCanvasPointerEnd}
                    style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isPanning ? 'grabbing' : 'grab', touchAction: 'none', overflow: 'hidden' }}
                  >
                    {activeItem?.previewUrl && (
                      <img
                        src={activeItem.previewUrl}
                        alt={appExtraText.original}
                        style={{
                          maxHeight: '90%',
                          maxWidth: '90%',
                          objectFit: 'contain',
                          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                          transformOrigin: 'center center',
                          transition: 'transform 0.15s ease'
                        }}
                      />
                    )}
                    <div className="view-tag view-tag-left">{studio.canvas.tagRaster}</div>
                  </div>
                )}

                {/* 4. Side-by-Side Dual View Mode */}
                {viewMode === 'side_by_side' && (
                  <div className="dual-view-grid" style={{ width: '100%', height: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '12px' }}>
                    {/* Left Pane: Original */}
                    <div
                      onPointerDown={handleCanvasPointerDown}
                      onPointerMove={handleCanvasPointerMove}
                      onPointerUp={handleCanvasPointerEnd}
                      onPointerCancel={handleCanvasPointerEnd}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--inline-rgba-0-0-0-0-3)', borderRadius: 'var(--shape-md)', position: 'relative', overflow: 'hidden', cursor: isPanning ? 'grabbing' : 'grab', touchAction: 'none' }}
                    >
                      <img
                        src={activeItem?.previewUrl}
                        alt={appExtraText.original}
                        style={{
                          maxHeight: '85%',
                          maxWidth: '85%',
                          objectFit: 'contain',
                          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                          transformOrigin: 'center center',
                          transition: 'transform 0.15s ease'
                        }}
                      />
                      <div className="view-tag view-tag-left" style={{ bottom: '8px', left: '8px' }}>{studio.canvas.tagOriginal}</div>
                    </div>

                    {/* Right Pane: Vector */}
                    <div
                      onPointerDown={handleCanvasPointerDown}
                      onPointerMove={handleCanvasPointerMove}
                      onPointerUp={handleCanvasPointerEnd}
                      onPointerCancel={handleCanvasPointerEnd}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--inline-rgba-0-0-0-0-3)', borderRadius: 'var(--shape-md)', position: 'relative', overflow: 'hidden', cursor: isPanning ? 'grabbing' : 'grab', touchAction: 'none' }}
                    >
                      {activeItem?.vectorSvg ? (
                        <div
                          className={`vector-svg-container ${isWireframe ? 'is-wireframe' : ''}`}
                          style={{
                            width: '85%',
                            height: '85%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                            transformOrigin: 'center center',
                            transition: 'transform 0.15s ease'
                          }}
                          dangerouslySetInnerHTML={{ __html: sanitizeSvg(activeItem.vectorSvg) }}
                        />
                      ) : activeItem?.status === 'processing' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', color: 'var(--md-sys-color-primary)' }}>
                          <RefreshCw size={26} className="m3-pulse" />
                          <span style={{ fontSize: '13px', fontWeight: 500 }}>{studio.canvas.vectorizing}</span>
                          <span style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', opacity: 0.8 }}>{studio.canvas.computingCurves}</span>
                        </div>
                      ) : (
                        <div
                          onPointerDown={(e) => e.stopPropagation()}
                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '24px', textAlign: 'center' }}
                        >
                          <div className="vector-ready-icon" aria-hidden="true">
                            <PenTool size={20} />
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)', marginBottom: '4px' }}>
                              {studio.canvas.vectorReadyTitle}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', maxWidth: '220px', lineHeight: 1.4 }}>
                              {studio.canvas.vectorReadyDesc}
                            </div>
                          </div>
                          {activeItem?.status === 'error' && (
                            <div style={{
                              background: 'var(--md-sys-color-error-container)',
                              color: 'var(--md-sys-color-error)',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              maxWidth: '280px',
                              lineHeight: 1.4,
                              border: '1px solid var(--inline-rgba-239-68-68-0-3)'
                            }}>
                              {activeItem.errorMessage || t('studio.conversionFailed')}
                            </div>
                          )}
                          {activeItem && (
                            <button
                              type="button"
                              className="m3-btn m3-btn-filled m3-btn-sm"
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                void convertItem(activeItem);
                              }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '7px 16px', borderRadius: '8px', cursor: 'pointer' }}
                            >
                              <RefreshCw size={13} />
                              <span>{studio.canvas.convertVector}</span>
                            </button>
                          )}
                        </div>
                      )}
                      <div className="view-tag view-tag-right" style={{ bottom: '8px', right: '8px' }}>
                        {isWireframe ? studio.canvas.tagOutlinePure : studio.canvas.tagVectorSvg}
                      </div>
                    </div>
                  </div>
                )}

                {/* 5. Interactive Before/After Split Comparison Slider */}
                {viewMode === 'split' && (
                  <div
                    ref={sliderStageRef}
                    onPointerDown={handleCanvasPointerDown}
                    onPointerMove={handleCanvasPointerMove}
                    onPointerUp={handleCanvasPointerEnd}
                    onPointerCancel={handleCanvasPointerEnd}
                    className="canvas-split-stage"
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isPanning ? 'grabbing' : 'grab',
                      touchAction: 'none'
                    }}
                  >
                    {/* Layer 1 (Base Layer): Vector SVG */}
                    <div
                      className="canvas-split-layer"
                      style={{
                        transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                        transformOrigin: 'center center',
                        transition: isPanning ? 'none' : 'transform 0.15s ease',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {activeItem?.vectorSvg ? (
                        <div
                          className={`vector-svg-container ${isWireframe ? 'is-wireframe' : ''}`}
                          style={{
                            maxWidth: '90%',
                            maxHeight: '90%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          dangerouslySetInnerHTML={{ __html: sanitizeSvg(activeItem.vectorSvg) }}
                        />
                      ) : activeItem?.status === 'processing' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', color: 'var(--md-sys-color-primary)' }}>
                          <RefreshCw size={26} className="m3-pulse" />
                          <span style={{ fontSize: '13px', fontWeight: 500 }}>{studio.canvas.vectorizing}</span>
                        </div>
                      ) : (
                        <div style={{ fontSize: '12px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                          {studio.canvas.noVectorTitle}
                        </div>
                      )}
                    </div>

                    {/* Layer 2 (Clipped Layer to the Left): Original Raster Image */}
                    {activeItem?.previewUrl && (
                      <div
                        className="canvas-split-layer"
                        style={{
                          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                          transformOrigin: 'center center',
                          transition: isPanning ? 'none' : 'transform 0.15s ease',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`
                        }}
                      >
                        <img
                          src={activeItem.previewUrl}
                          alt={appExtraText.original}
                          style={{
                            maxHeight: '90%',
                            maxWidth: '90%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    )}

                    {/* Draggable Divider Bar & Handle */}
                    <div
                      className="canvas-split-divider"
                      style={{ left: `${sliderPosition}%` }}
                      onPointerDown={handleSliderPointerDown}
                      onPointerMove={handleSliderPointerMove}
                      onPointerUp={handleSliderPointerEnd}
                      onPointerCancel={handleSliderPointerEnd}
                    >
                      <div
                        className="canvas-split-handle"
                        aria-label="Comparison slider handle"
                        role="slider"
                        aria-valuenow={Math.round(sliderPosition)}
                      >
                        <MoveHorizontal size={16} />
                      </div>
                    </div>

                    {/* Dynamic View Tags */}
                    <div className="view-tag view-tag-left">
                      {studio.canvas.tagOriginal}
                    </div>
                    <div className="view-tag view-tag-right">
                      {isWireframe ? studio.canvas.tagVectorWireframe : studio.canvas.tagVectorSvg}
                    </div>
                  </div>
                )}
              </div>

              {/* Canvas Control Bar (Zoom, Transparency & Wireframe Toggle - Positioned above Technical Readiness) */}
              <div
                className="canvas-floating-dock canvas-control-bar"
                onPointerDown={(e) => e.stopPropagation()}
                aria-hidden={metadataEditorExpanded}
                style={metadataEditorExpanded ? { display: 'none' } : undefined}
              >
                <button
                  type="button"
                  className="m3-btn-icon"
                  onClick={(e) => { e.stopPropagation(); setZoomLevel(prev => Math.max(0.5, prev - 0.25)); }}
                  title={studio.canvas.zoomOut}
                >
                  <ZoomOut size={14} />
                </button>

                <button
                  type="button"
                  className="dock-zoom-text"
                  onClick={(e) => { e.stopPropagation(); resetCanvasView(); }}
                  title={studio.canvas.resetZoom}
                >
                  {(zoomLevel * 100).toFixed(0)}%
                </button>

                <button
                  type="button"
                  className="m3-btn-icon"
                  onClick={(e) => { e.stopPropagation(); setZoomLevel(prev => Math.min(8, prev + 0.25)); }}
                  title={studio.canvas.zoomIn}
                >
                  <ZoomIn size={14} />
                </button>

                <div className="dock-divider" />

                {/* Wireframe Outline Mode Toggle */}
                <button
                  type="button"
                  className="m3-btn-icon"
                  onClick={(e) => { e.stopPropagation(); setIsWireframe(prev => !prev); }}
                  title={isWireframe ? studio.canvas.wireframeFills : studio.canvas.wireframeInspect}
                  style={{
                    background: isWireframe ? 'var(--color-primary-hover)' : 'transparent',
                    color: isWireframe ? 'var(--md-sys-color-primary)' : 'inherit',
                    border: isWireframe ? '1px solid var(--md-sys-color-primary)' : 'none'
                  }}
                >
                  <Layers size={14} />
                </button>

                <button
                  type="button"
                  className="m3-btn-icon"
                  onClick={(e) => { e.stopPropagation(); setUseCheckerboard(prev => !prev); }}
                  title={useCheckerboard ? studio.canvas.checkerboardSolid : studio.canvas.checkerboardTrans}
                  style={{
                    background: useCheckerboard ? 'var(--md-sys-color-primary-container)' : 'transparent',
                    color: useCheckerboard ? 'var(--md-sys-color-primary)' : 'inherit'
                  }}
                >
                  <Grid size={14} />
                </button>
              </div>

              {/* Bottom Quality Report Trigger */}
              {activeItem?.validation && (
                <button
                  type="button"
                  className={`quality-report-trigger quality-status-${activeItem.qualityReport?.status || (activeItem.validation.valid ? 'passed' : 'failed')}`}
                  onClick={() => activeItem.qualityReport && setQualityDrawerOpen(true)}
                  disabled={!activeItem.qualityReport}
                  aria-haspopup="dialog"
                  aria-expanded={qualityDrawerOpen}
                >
                  <ShieldCheck size={17} />
                  <span className="quality-trigger-copy">
                    <strong>{activeItem.qualityReport ? quality.readiness : studio.canvas.verifiedTitle}</strong>
                    <small>{activeItem.qualityReport ? `${activeItem.qualityReport.score}/100 · ${activeItem.qualityReport.summary.warnings} ${quality.warning.toLowerCase()}` : studio.canvas.verifiedSpecs}</small>
                  </span>
                  <span className="quality-trigger-action">{activeItem.qualityReport ? quality.view : studio.canvas.verifiedBadge}</span>
                  {activeItem.qualityReport && <ChevronRight size={15} aria-hidden="true" />}
                </button>
              )}
            </div>
          )}
        </main>

        {qualityDrawerOpen && activeItem?.qualityReport && (
          <Suspense fallback={null}>
            <QualityReportDrawer
              report={activeItem.qualityReport}
              copy={quality}
              marketplace={activeItem.metadata?.marketplace}
              history={activeQualityHistory}
              heatmap={activeItem.visualHeatmap}
              previewUrl={activeItem.previewUrl}
              showHeatmap={showVisualHeatmap}
              onClose={closeQualityDrawer}
              onToggleHeatmap={() => setShowVisualHeatmap(value => !value)}
              onRemediate={startGuidedRemediation}
            />
          </Suspense>
        )}

        {showExportReviewModal && (
          <Suspense fallback={null}>
            <ExportReviewModal
              items={exportReviewItems}
              totalCount={exportTotalCount}
              passingCount={exportPassingCount}
              warningCount={exportWarningCount}
              failedCount={exportFailedCount}
              busy={isExportingZip}
              text={quality}
              onClose={() => setShowExportReviewModal(false)}
              onExportPassing={() => void handleExportBatchZip(true, true)}
              onExportAll={() => void handleExportBatchZip(false, true)}
            />
          </Suspense>
        )}

        {/* ==================================================================
            RIGHT SIDEBAR: BATCH QUEUE, ADOBE CSV & SAAS EXPORTER
            ================================================================== */}
        <aside
          className={`studio-sidebar studio-sidebar-right ${!rightSidebarOpen ? 'sidebar-collapsed-right' : ''}`}
        >
          {/* Section 1: Batch Queue Accordion */}
          {(!isMobileScreen || rightSidebarTab === 'queue') && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: !isMobileScreen && desktopQueueExpanded ? '1 1 0%' : '0 0 auto',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <Suspense fallback={null}>
                <BatchQueuePanel
                  queue={queue}
                  filteredQueue={qualityFilteredQueue}
                  activeItemId={activeItemId}
                  progress={batchProgress}
                  isProcessing={isProcessingBatch || hasServerProcessingItems}
                  isRestoring={isRestoringQueue}
                  qualityBatch={qualityBatch}
                  qualityFilter={qualityFilter}
                  issueCount={issueItems.length}
                  studio={studio}
                  quality={quality}
                  labels={{
                    clearQueue: appExtraText.clearQueue,
                    thumbnail: appExtraText.thumbnail,
                    downloadSvg: metadataSaasText.downloadSvg,
                    vectorizationFailed: metadataSaasText.vectorizationFailed,
                    uploading: queueTransferText.uploading,
                    restoring: queueTransferText.restoring,
                    restoringDesc: queueTransferText.restoringDesc,
                    removeItem: queueTransferText.removeItem,
                    retry: queueTransferText.retry,
                    convertItem: queueTransferText.convertItem,
                    readyStatus: queueTransferText.readyStatus,
                    uploadingBatch: queueTransferText.uploadingBatch,
                    browseFiles: queueTransferText.browseFiles,
                    closeQueue: queueTransferText.closeQueue,
                  }}
                  isCollapsed={!isMobileScreen && !desktopQueueExpanded}
                  onToggleCollapse={!isMobileScreen ? () => setDesktopQueueExpanded(prev => !prev) : undefined}
                  onClear={handleClearQueue}
                  onConvertAll={handleBatchConvert}
                  onSelect={setActiveItemId}
                  onDownloadSvg={downloadSvg}
                  onQualityFilter={setQualityFilter}
                  onNextIssue={selectNextQualityIssue}
                  onRemoveItem={handleRemoveQueueItem}
                  onRetryItem={handleRetryQueueItem}
                  onConvertItem={handleConvertSingleItem}
                  onClose={() => setRightSidebarOpen(false)}
                  onBrowseFiles={() => fileInputRef.current?.click()}
                />
              </Suspense>
            </div>
          )}

          {/* Section 2: Metadata Accordion */}
          {(!isMobileScreen || rightSidebarTab === 'metadata') && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0
              }}
            >
              {/* Desktop Collapsible Accordion Header Bar */}
              {!isMobileScreen && (
                <button
                  type="button"
                  className="sidebar-accordion-header"
                  onClick={() => setDesktopMetadataExpanded(prev => !prev)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '11px 14px',
                    borderTop: '1px solid var(--md-sys-color-outline-variant)',
                    borderBottom: desktopMetadataExpanded ? '1px solid var(--md-sys-color-outline-variant)' : 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    background: 'var(--md-sys-color-surface-container-low)',
                    color: 'var(--md-sys-color-on-surface)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    textAlign: 'left'
                  }}
                  aria-expanded={desktopMetadataExpanded}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <Tag size={14} color="var(--md-sys-color-primary)" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      Metadata
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--md-sys-color-on-surface-variant)', textTransform: 'capitalize' }}>
                      {metadataMarketplace}
                    </span>
                    <ChevronDown
                      size={14}
                      color="var(--md-sys-color-on-surface-variant)"
                      style={{
                        transform: desktopMetadataExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    />
                  </div>
                </button>
              )}

              {/* Mobile Header Bar */}
              {isMobileScreen && (
                <div
                  className="mobile-drawer-header"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                    background: 'var(--md-sys-color-surface-container-low)'
                  }}
                >
                  <strong style={{ fontSize: '13px', color: 'var(--md-sys-color-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Metadata
                  </strong>
                  <button
                    type="button"
                    className="m3-btn-icon studio-drawer-close"
                    onClick={() => setRightSidebarOpen(false)}
                    title={appExtraText.close}
                    aria-label={appExtraText.close}
                  >
                    <X size={17} />
                  </button>
                </div>
              )}
              {isMobileScreen && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--md-sys-color-outline-variant)' }}>
                  <button
                    className="m3-btn m3-btn-filled m3-btn-sm"
                    style={{ width: '100%', justifyContent: 'center', gap: '8px' }}
                    onClick={() => {
                      if (activeItem && activeItem.status === 'done') {
                        setMetadataEditorExpanded(true);
                      } else if (metadataEditableItems.length > 0) {
                        setActiveItemId(metadataEditableItems[0].id);
                        setMetadataEditorExpanded(true);
                      } else if (activeItem) {
                        setSaveStatusMsg(
                          locale === 'id'
                            ? 'Konversi gambar ke vektor terlebih dahulu untuk mengedit metadata.'
                            : 'Convert image to vector first to edit metadata.'
                        );
                        setTimeout(() => setSaveStatusMsg(null), 3500);
                      } else {
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    <Tag size={15} />
                    <span>{metadataSaasText.previewEdit || 'Open Metadata Editor'}</span>
                  </button>
                </div>
              )}

              {/* Accordion Content Body: Visible when mobile OR when desktop accordion is expanded */}
              {(isMobileScreen || desktopMetadataExpanded) && (
                <div style={{ overflowY: 'auto', maxHeight: !isMobileScreen ? '440px' : undefined }}>
                  {/* Active Item AI Metadata Card */}
                  {activeItem && activeItem.status === 'done' && metadataEditorExpanded && <Suspense fallback={null}>
                    <MetadataEditorPanel
                      item={activeItem}
                      items={metadataEditableItems}
                      activeIndex={activeMetadataItemIndex}
                      marketplace={metadataMarketplace}
                      categories={MARKETPLACE_CATEGORIES}
                      categoryNotes={MARKETPLACE_CATEGORY_NOTES}
                      studio={studio}
                      text={metadataSaasText}
                      generating={isGeneratingItemMetadata}
                      onClose={() => setMetadataEditorExpanded(false)}
                      onGenerate={item => generateMetadataForActiveItem(item)}
                      onSelect={setActiveItemId}
                      onSelectIndex={selectMetadataItemAt}
                      onChange={patch => setQueue(previous => previous.map(item => item.id === activeItem.id && item.metadata ? withMetadataQuality(item, { ...item.metadata, ...patch }) : item))}
                      onSave={saveActiveMetadata}
                      onApplyCategory={() => void applyActiveCategoryToBatch()}
                      onNormalizeKeywords={alphabetical => void normalizeActiveKeywords(alphabetical)}
                      onCopied={() => { setSaveStatusMsg(studio.rightSidebar.copiedKeywords); setTimeout(() => setSaveStatusMsg(null), 3000); }}
                    />
                  </Suspense>}

                  {/* Active Item Metadata Summary Card */}
                  {activeItem && activeItem.status === 'done' && activeItem.metadata && (
                    <div style={{
                      margin: '10px 12px 12px',
                      padding: '12px',
                      borderRadius: 10,
                      border: '1px solid var(--md-sys-color-outline-variant)',
                      background: 'var(--md-sys-color-surface-container)',
                      display: 'grid',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                          <Tag size={13} color="var(--md-sys-color-primary)" style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--md-sys-color-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {activeItem.name}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: '6px',
                          background: 'var(--inline-rgba-34-211-238-15)',
                          color: 'var(--md-sys-color-primary)',
                          textTransform: 'capitalize',
                          flexShrink: 0
                        }}>
                          {activeItem.metadata.category || 'Vector'}
                        </span>
                      </div>

                      <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--md-sys-color-on-surface)', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {activeItem.metadata.title || activeItem.name}
                      </div>

                      {Array.isArray(activeItem.metadata.keywords) && activeItem.metadata.keywords.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxHeight: '52px', overflow: 'hidden' }}>
                          {activeItem.metadata.keywords.slice(0, 6).map((kw, idx) => (
                            <span key={idx} style={{
                              fontSize: '9.5px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: 'var(--md-sys-color-surface-container-high)',
                              color: 'var(--md-sys-color-on-surface-variant)',
                              lineHeight: 1.2
                            }}>
                              {kw}
                            </span>
                          ))}
                          {activeItem.metadata.keywords.length > 6 && (
                            <span style={{ fontSize: '9.5px', padding: '2px 4px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                              +{activeItem.metadata.keywords.length - 6}
                            </span>
                          )}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                        <button
                          id="active-item-edit-metadata"
                          className="m3-btn m3-btn-filled m3-btn-xs"
                          style={{ flex: 1, justifyContent: 'center', gap: '6px', fontSize: '10.5px' }}
                          onClick={() => setMetadataEditorExpanded(true)}
                        >
                          <Tag size={12} />
                          <span>{metadataSaasText.previewEdit || 'Edit Metadata'}</span>
                        </button>
                        <button
                          className="m3-btn m3-btn-tonal m3-btn-xs"
                          onClick={() => generateMetadataForActiveItem(activeItem)}
                          disabled={isGeneratingItemMetadata}
                          title={metadataSaasText.regenerateHint || 'Regenerate Metadata'}
                        >
                          {isGeneratingItemMetadata ? <RefreshCw size={11} className="m3-pulse" /> : <Sparkles size={11} />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Active Item Metadata Prompt when not generated yet */}
                  {activeItem && activeItem.status === 'done' && !activeItem.metadata && (
                    <div style={{
                      margin: '10px 12px 12px',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px dashed var(--md-sys-color-outline-variant)',
                      background: 'var(--md-sys-color-surface-container)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--md-sys-color-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {activeItem.name}
                        </div>
                        <div style={{ fontSize: '9.5px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                          {locale === 'id' ? 'Belum ada metadata' : 'No metadata generated yet'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button
                          className="m3-btn m3-btn-tonal m3-btn-xs"
                          style={{ fontSize: '10px', gap: '4px' }}
                          onClick={() => generateMetadataForActiveItem(activeItem)}
                          disabled={isGeneratingItemMetadata}
                        >
                          {isGeneratingItemMetadata ? <RefreshCw size={10} className="m3-pulse" /> : <Sparkles size={10} />}
                          <span>Generate</span>
                        </button>
                        <button
                          className="m3-btn m3-btn-text m3-btn-xs"
                          style={{ fontSize: '10px' }}
                          onClick={() => setMetadataEditorExpanded(true)}
                        >
                          <Tag size={10} />
                          <span>{locale === 'id' ? 'Buka' : 'Open'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                  {activeMetadataJob && (()=>{const processed=Number(activeMetadataJob.completed_items||0)+Number(activeMetadataJob.failed_items||0);const total=Number(activeMetadataJob.total_items||0);const progress=total?Math.round(processed/total*100):0;const running=['queued','processing'].includes(activeMetadataJob.status);return <div style={{margin:'0 12px 10px',padding:'12px',borderRadius:10,border:'1px solid var(--md-sys-color-outline-variant)',background:'linear-gradient(135deg,var(--inline-rgba-34-211-238-08),var(--inline-rgba-99-102-241-08))',display:'grid',gap:9}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}><span style={{display:'flex',alignItems:'center',gap:7,fontSize:10.5,fontWeight:800,textTransform:'uppercase',letterSpacing:'.05em'}}>{running&&<span className="metadata-activity-dot" aria-hidden="true"/>}{running?metadataSaasText.generating:metadataSaasText.metadataStatus(localizedStatus(activeMetadataJob.status,locale))}</span><div style={{display:'flex',alignItems:'center',gap:7}}><b style={{fontSize:10.5,color:'var(--md-sys-color-primary)'}}>{processed}/{total} · {progress}%</b>{!running && activeMetadataJob.status==='completed' && <button id="preview-edit-metadata" className="m3-btn m3-btn-tonal m3-btn-xs" onClick={()=>{setOptimizeNotice(null);setMetadataEditorExpanded(true);}} style={{fontSize:10,padding:'4px 9px',whiteSpace:'nowrap'}}>{metadataSaasText.previewEdit}</button>}</div></div><div className={`metadata-progress-track${running?' is-running':''}`}><div className="metadata-progress-fill" style={{width:`${progress}%`,background:activeMetadataJob.failed_items?'var(--status-amber)':undefined}}/></div><div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,fontSize:9.5,color:'var(--md-sys-color-on-surface-variant)'}}><span>{activeMetadataJob.provider} · {activeMetadataJob.model||metadataSaasText.defaultModel}{activeMetadataJob.failed_items?` · ${activeMetadataJob.failed_items} ${metadataSaasText.failed}`:''}</span>{running&&<span className="metadata-working-label">{metadataSaasText.analyzing}</span>}</div>{!running&&activeMetadataJob.error_code&&<div style={{fontSize:10,color:'var(--palette-fca5a5)',lineHeight:1.35,overflowWrap:'anywhere'}}>{runtimeText.metadataError}</div>}</div>})()}

                  {/* Multi-Marketplace Metadata CSV Exporter */}
                  <div
                    style={{
                      padding: '12px 16px',
                      borderTop: isMobileScreen ? 'none' : '1px solid var(--md-sys-color-outline-variant)',
                      background: 'var(--md-sys-color-surface-container-low)'
                    }}
                  >
                    <div style={{ display: 'grid', gap: '9px', marginBottom: '10px' }}>
                      <span style={{ fontSize: '10.5px', fontWeight: 800, color: 'var(--md-sys-color-on-surface-variant)', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.25 }}>
                        {studio.rightSidebar.csvExporterTitle}
                      </span>
                      {(() => {
                        const readyCount = queue.filter(item => item.status === 'done' && item.previewUrl).length;
                        const policy = getMetadataAiPolicy(Math.max(1, readyCount));
                        return <div style={{display:'grid',gap:5,padding:'8px 9px',borderRadius:8,background:'var(--md-sys-color-surface-container)',border:`1px solid ${policy.allowed?'var(--md-sys-color-outline-variant)':'var(--inline-rgba-245-158-11-35)'}`}}>
                          <div style={{display:'flex',justifyContent:'space-between',gap:8,fontSize:10.5,fontWeight:700}}><span>{policy.message}</span><span>{policy.source==='byok'?studio.aiModal.byokNoCharge:studio.aiModal.estimatedCharge(policy.charge)}</span></div>
                          <div style={{fontSize:9.5,color:'var(--md-sys-color-on-surface-variant)'}}>{studio.header.aiCredits(user?.ai_credits ?? 0)}</div>
                        </div>;
                      })()}
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '6px' }}>
                        <button className="m3-btn m3-btn-tonal m3-btn-xs" style={{ width: '100%', justifyContent: 'center' }} onClick={generateBatchMetadata} disabled={isGeneratingBatchMetadata || !getMetadataAiPolicy(Math.max(1, queue.filter(item => item.status === 'done' && item.previewUrl).length)).allowed} title={metadataSaasText.generateAllTitle}>
                          {isGeneratingBatchMetadata?<RefreshCw size={11} className="m3-pulse"/>:<Sparkles size={11}/>} {metadataSaasText.batch}
                        </button>
                        <button className="m3-btn m3-btn-text m3-btn-xs" style={{ width: '100%', justifyContent: 'center' }} onClick={()=>showMetadataHistory?setShowMetadataHistory(false):loadMetadataHistory()} disabled={metadataHistoryLoading}>
                          {metadataHistoryLoading?<RefreshCw size={11} className="m3-pulse"/>:null} {showMetadataHistory?metadataSaasText.hideHistory:metadataSaasText.history}
                        </button>
                        <button
                          className="m3-btn m3-btn-tonal m3-btn-xs"
                          style={{ gridColumn: '1 / -1', width: '100%', justifyContent: 'center' }}
                          onClick={exportMarketplaceCsv}
                          title={metadataSaasText.downloadCsvTitle}
                        >
                          <Download size={11} /> {studio.rightSidebar.csvBtn}
                        </button>
                      </div>
                    </div>
                    {showMetadataHistory && <div style={{display:'grid',gap:6,margin:'8px 0 10px',maxHeight:150,overflowY:'auto'}}>
                      {metadataJobs.length===0?<div style={{fontSize:10.5,color:'var(--md-sys-color-on-surface-variant)'}}>{metadataSaasText.empty}</div>:metadataJobs.map(job=>{const processed=(job.completed_items||0)+(job.failed_items||0);const progress=job.total_items?Math.min(100,Math.round(processed/job.total_items*100)):0;return <div key={job.id} style={{display:'grid',gap:5,padding:'7px 8px',border:'1px solid var(--md-sys-color-outline-variant)',borderRadius:8}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}><div style={{minWidth:0}}><div style={{fontSize:10.5,fontWeight:700,textTransform:'capitalize'}}>{job.marketplace} · {localizedStatus(job.status,locale)}</div><div style={{fontSize:9.5,color:'var(--md-sys-color-on-surface-variant)'}}>{job.completed_items}/{job.total_items} {metadataSaasText.assets}{job.failed_items?` · ${job.failed_items} ${metadataSaasText.failed}`:''}</div></div><div style={{display:'flex',gap:4}}>{(job.status==='failed'||job.status==='partial')&&<button className="m3-btn m3-btn-text m3-btn-xs" disabled={isGeneratingBatchMetadata} onClick={()=>retryMetadataJob(job.id)}><RefreshCw size={10}/> {metadataSaasText.retry}</button>}<button className="m3-btn m3-btn-text m3-btn-xs" disabled={!job.completed_items} onClick={()=>downloadMetadataJobCsv(job.id).catch(error=>setSaveStatusMsg(error.message))}><Download size={10}/> {metadataSaasText.download}</button></div></div><div style={{height:3,borderRadius:3,overflow:'hidden',background:'var(--md-sys-color-surface-container-highest)'}}><div style={{height:'100%',width:`${progress}%`,background:job.failed_items?'var(--status-amber)':'var(--md-sys-color-primary)',transition:'width .25s ease'}}/></div></div>})}
                    </div>}
                    <select
                      className="m3-input"
                      value={metadataMarketplace}
                      onChange={(e) => changeMetadataMarketplace(e.target.value as MetadataMarketplace)}
                      aria-label={metadataSaasText.targetMarketplace}
                      style={{ width: '100%', height: '32px', marginBottom: '7px', fontSize: '11.5px', cursor: 'pointer' }}
                    >
                      <option value="adobe">Adobe Stock</option>
                      <option value="shutterstock">Shutterstock</option>
                      <option value="freepik">Freepik Contributor</option>
                      <option value="dreamstime">Dreamstime</option>
                      <option value="depositphotos">Depositphotos</option>
                    </select>
                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', lineHeight: 1.45 }}>
                      {studio.rightSidebar.csvColumnsLabel}{' '}
                      <code style={{ color: 'var(--md-sys-color-primary)', fontFamily: 'var(--font-mono)', whiteSpace: 'normal' }}>
                        {metadataMarketplace === 'adobe' && 'Filename, Title, Keywords, Category'}
                        {metadataMarketplace === 'shutterstock' && 'Filename, Description, Keywords, Categories, Illustration, Mature Content, Editorial'}
                        {metadataMarketplace === 'freepik' && 'File name, Title, Keywords'}
                        {metadataMarketplace === 'dreamstime' && 'Filename, Title, Description, Keywords, Category'}
                        {metadataMarketplace === 'depositphotos' && 'Filename, Title, Description, Keywords'}
                      </code>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 3: Batch Vector Exporter Accordion */}
          {(!isMobileScreen || rightSidebarTab === 'export') && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0
              }}
            >
              {/* Desktop Collapsible Accordion Header Bar */}
              {!isMobileScreen && (
                <button
                  type="button"
                  className="sidebar-accordion-header"
                  onClick={() => setDesktopExportExpanded(prev => !prev)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '11px 14px',
                    borderTop: '1px solid var(--md-sys-color-outline-variant)',
                    borderBottom: desktopExportExpanded ? '1px solid var(--md-sys-color-outline-variant)' : 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    background: 'var(--md-sys-color-surface-container-lowest)',
                    color: 'var(--md-sys-color-on-surface)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    textAlign: 'left'
                  }}
                  aria-expanded={desktopExportExpanded}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <Package size={14} color="var(--md-sys-color-primary)" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      File Exporter
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {queue.filter(q => q.status === 'done').length > 0 && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--status-emerald)', background: 'color-mix(in srgb, var(--status-emerald) 14%, transparent)', padding: '1px 6px', borderRadius: '4px' }}>
                        {queue.filter(q => q.status === 'done').length} {locale === 'id' ? 'siap' : 'ready'}
                      </span>
                    )}
                    <ChevronDown
                      size={14}
                      color="var(--md-sys-color-on-surface-variant)"
                      style={{
                        transform: desktopExportExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    />
                  </div>
                </button>
              )}

              {/* Mobile Header Bar */}
              {isMobileScreen && (
                <div
                  className="mobile-drawer-header"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--md-sys-color-outline-variant)'
                  }}
                >
                  <strong style={{ fontSize: '13px', color: 'var(--md-sys-color-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    File Exporter
                  </strong>
                  <button
                    type="button"
                    className="m3-btn-icon studio-drawer-close"
                    onClick={() => setRightSidebarOpen(false)}
                    title={appExtraText.close}
                    aria-label={appExtraText.close}
                  >
                    <X size={17} />
                  </button>
                </div>
              )}

              {/* Accordion Content Body: Visible when mobile OR when desktop accordion is expanded */}
              {(isMobileScreen || desktopExportExpanded) && (
                <div
                  style={{
                    padding: '14px 16px',
                    background: 'var(--md-sys-color-surface-container-lowest)',
                    overflowY: 'auto',
                    maxHeight: !isMobileScreen ? '360px' : undefined
                  }}
                >
                  {/* Single Vector Quick Export in Mobile Export Drawer */}
                  {isMobileScreen && activeItem?.vectorSvg && (
                    <div style={{ padding: '10px 12px', marginBottom: '14px', borderRadius: 10, border: '1px solid var(--md-sys-color-outline-variant)', background: 'var(--md-sys-color-surface-container)', display: 'grid', gap: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--md-sys-color-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {locale === 'id' ? 'Ekspor File Aktif' : 'Active Vector Export'}
                      </div>
                      <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {activeItem.name}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '6px' }}>
                        <button
                          className="m3-btn m3-btn-tonal m3-btn-sm"
                          style={{ justifyContent: 'center', gap: '5px' }}
                          onClick={() => downloadSvg(activeItem)}
                        >
                          <Download size={13} /> SVG
                        </button>
                        <button
                          className="m3-btn m3-btn-tonal m3-btn-sm"
                          style={{ justifyContent: 'center', gap: '5px' }}
                          onClick={() => downloadEps(activeItem)}
                        >
                          <Download size={13} /> EPS 10
                        </button>
                      </div>
                      {canWebShare && (
                        <button
                          className="m3-btn m3-btn-filled m3-btn-sm"
                          style={{ justifyContent: 'center', gap: '6px' }}
                          onClick={() => void handleShareVector(activeItem)}
                        >
                          <Share2 size={13} /> {appExtraText.share}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Info if queue has no done items */}
                  {isMobileScreen && queue.filter(q => q.status === 'done').length === 0 && (
                    <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--md-sys-color-surface-container)', fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', lineHeight: 1.45, marginBottom: '12px' }}>
                      {locale === 'id'
                        ? 'Belum ada gambar yang selesai dikonversi ke vektor. Konversi gambar terlebih dahulu untuk mengunduh paket ZIP.'
                        : 'No vectors converted yet. Convert images first to download ZIP packages.'}
                    </div>
                  )}

                  {isMobileScreen && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                      <Package size={15} color="var(--md-sys-color-primary)" />
                      <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--md-sys-color-on-surface)', letterSpacing: '0.04em' }}>
                        {studio.rightSidebar.batchExporterTitle}
                      </span>
                    </div>
                  )}

                  {/* Instant ZIP Package Exporter Button */}
                  <button
                    className="m3-btn m3-btn-filled m3-btn-sm"
                    style={{ width: '100%', marginBottom: '8px', borderRadius: 'var(--shape-md)' }}
                    onClick={() => void handleExportBatchZip(false)}
                    disabled={isExportingZip || queue.filter(q => q.status === 'done').length === 0}
                  >
                    {isExportingZip ? (
                      <>
                        <RefreshCw size={14} className="m3-pulse" />
                        <span>{studio.rightSidebar.packagingZip}</span>
                      </>
                    ) : (
                      <>
                        <Download size={14} />
                        <span>{studio.rightSidebar.downloadZipBtn}</span>
                      </>
                    )}
                  </button>
                  {qualityBatch.failed + qualityBatch.warnings > 0 && <button
                    id="export-passing-assets"
                    className="m3-btn m3-btn-tonal m3-btn-sm"
                    style={{ width: '100%', marginBottom: '8px', borderRadius: 'var(--shape-md)' }}
                    onClick={() => void handleExportBatchZip(true)}
                    disabled={isExportingZip || qualityBatch.passed === 0}
                  ><ShieldCheck size={14}/><span>{quality.exportPassing(qualityBatch.passed)}</span></button>}
                  <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', textAlign: 'center' }}>
                    {studio.rightSidebar.batchZipDesc}
                  </div>
                  {authToken && <div style={{marginTop:10,borderTop:'1px solid var(--md-sys-color-outline-variant)',paddingTop:8}}>
                    <button id="toggle-export-history" className="m3-btn m3-btn-text m3-btn-xs" style={{width:'100%'}} onClick={() => showExportHistory ? setShowExportHistory(false) : void loadExportHistory()} disabled={exportHistoryLoading}>
                      {exportHistoryLoading ? <RefreshCw size={11} className="m3-pulse"/> : <Download size={11}/>} {showExportHistory ? metadataSaasText.hideExports : metadataSaasText.exportHistory}
                    </button>
                    {showExportHistory && <div style={{display:'grid',gap:6,marginTop:7,maxHeight:190,overflowY:'auto'}}>
                      {exportJobs.length === 0 ? <div style={{fontSize:10,color:'var(--md-sys-color-on-surface-variant)',textAlign:'center'}}>{metadataSaasText.noExports}</div> : exportJobs.map(job => {
                        const expired = job.status !== 'ready' || (job.expires_at && new Date(job.expires_at).getTime() <= Date.now());
                        const size = job.size_bytes >= 1048576 ? `${(job.size_bytes/1048576).toFixed(1)} MB` : `${Math.max(1,Math.round((job.size_bytes||0)/1024))} KB`;
                        return <div key={job.id} style={{padding:'7px 8px',border:'1px solid var(--md-sys-color-outline-variant)',borderRadius:8,display:'grid',gap:4}}>
                          <div style={{fontSize:10.5,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={job.filename}>{job.filename}</div>
                          <div style={{display:'flex',justifyContent:'space-between',fontSize:9.5,color:'var(--md-sys-color-on-surface-variant)'}}><span>{job.item_count} items · {size}</span><span style={{textTransform:'capitalize'}}>{expired?'expired':job.status}</span></div>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:6}}><span style={{fontSize:9,color:'var(--md-sys-color-on-surface-variant)'}}>{job.created_at ? new Date(job.created_at).toLocaleString(locale) : ''}</span><button id={`download-export-${job.id}`} className="m3-btn m3-btn-text m3-btn-xs" disabled={expired || downloadingExportId===job.id} onClick={() => void downloadExportJob(job)}>{downloadingExportId===job.id?<RefreshCw size={10} className="m3-pulse"/>:<Download size={10}/>} {metadataSaasText.downloadExport}</button></div>
                        </div>;
                      })}
                    </div>}
                  </div>}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Mobile Bottom Action Bar (Smartphones <= 760px) */}
      <nav className="mobile-bottom-bar" aria-label="Mobile Studio Navigation">
        {/* 1. Settings Drawer Toggle (Far Left) */}
        <button
          type="button"
          className={`mobile-bar-btn ${leftSidebarOpen ? 'active' : ''}`}
          onClick={toggleLeftSidebar}
          title={leftSidebarOpen ? t('studio.collapseSettings') : t('studio.expandSettings')}
          aria-label={leftSidebarOpen ? t('studio.collapseSettings') : t('studio.expandSettings')}
          aria-pressed={leftSidebarOpen}
        >
          <Sliders size={20} />
          <span className="mobile-bar-label">{appExtraText.mobileSettings}</span>
        </button>

        {/* 2. Dedicated Metadata Panel Toggle */}
        <button
          type="button"
          className={`mobile-bar-btn ${rightSidebarOpen && rightSidebarTab === 'metadata' ? 'active' : ''}`}
          onClick={() => {
            setLeftSidebarOpen(false);
            if (rightSidebarOpen && rightSidebarTab === 'metadata') {
              setRightSidebarOpen(false);
            } else {
              setRightSidebarTab('metadata');
              setRightSidebarOpen(true);
            }
          }}
          title={appExtraText.mobileMetadata}
          aria-label={appExtraText.mobileMetadata}
          aria-pressed={rightSidebarOpen && rightSidebarTab === 'metadata'}
        >
          <Tag size={20} />
          <span className="mobile-bar-label">{appExtraText.mobileMetadata}</span>
        </button>

        {/* 3. CENTER HERO CTA: Convert Vector (Core Vectria Action) */}
        <button
          type="button"
          className={`mobile-bar-btn mobile-bar-primary ${
            activeItem?.status === 'processing' || isProcessingBatch ? 'is-processing' : ''
          } ${!activeItem ? 'is-disabled' : ''}`}
          onClick={() => {
            if (!activeItem) {
              fileInputRef.current?.click();
              return;
            }
            closeCompactSidebars();
            const remaining = queue.filter(item => item.status !== 'done');
            if (remaining.length > 1) {
              void handleBatchConvert();
            } else {
              void convertItem(activeItem);
            }
          }}
          disabled={activeItem?.status === 'processing' || isProcessingBatch}
          title={
            activeItem?.status === 'processing'
              ? studio.canvas.vectorizing
              : activeItem?.status === 'done'
              ? studio.canvas.revectorize
              : queue.filter(item => item.status !== 'done').length > 1
              ? studio.canvas.convertAll
              : studio.canvas.convertVector
          }
          aria-label={
            activeItem?.status === 'processing'
              ? studio.canvas.vectorizing
              : activeItem?.status === 'done'
              ? studio.canvas.revectorize
              : queue.filter(item => item.status !== 'done').length > 1
              ? studio.canvas.convertAll
              : studio.canvas.convertVector
          }
        >
          <div className="mobile-bar-primary-icon">
            {activeItem?.status === 'processing' || isProcessingBatch ? (
              <RefreshCw size={19} className="m3-pulse" />
            ) : activeItem?.status === 'done' ? (
              <RefreshCw size={19} />
            ) : (
              <Zap size={19} />
            )}
          </div>
          <span className="mobile-bar-label">
            {activeItem?.status === 'processing'
              ? studio.canvas.vectorizing
              : activeItem?.status === 'done'
              ? studio.canvas.revectorize
              : queue.filter(item => item.status !== 'done').length > 1
              ? studio.canvas.convertAll
              : studio.canvas.convertVector}
          </span>
        </button>

        {/* 4. Dedicated Queue Panel Toggle with Badge */}
        <button
          type="button"
          className={`mobile-bar-btn ${rightSidebarOpen && rightSidebarTab === 'queue' ? 'active' : ''}`}
          onClick={() => {
            setLeftSidebarOpen(false);
            if (rightSidebarOpen && rightSidebarTab === 'queue') {
              setRightSidebarOpen(false);
            } else {
              setRightSidebarTab('queue');
              setRightSidebarOpen(true);
            }
          }}
          title={studio.header.expandQueue}
          aria-label={studio.header.expandQueue}
          aria-pressed={rightSidebarOpen && rightSidebarTab === 'queue'}
        >
          <div className="mobile-bar-icon-wrap">
            <Layers size={20} />
            {queue.length > 0 && (
              <span className="mobile-bar-badge" aria-label={`${queue.length} items`}>
                {queue.length > 99 ? '99+' : queue.length}
              </span>
            )}
          </div>
          <span className="mobile-bar-label">{appExtraText.mobileQueue}</span>
        </button>

        {/* 5. Dedicated Export Panel Toggle */}
        <button
          type="button"
          className={`mobile-bar-btn ${rightSidebarOpen && rightSidebarTab === 'export' ? 'active' : ''}`}
          onClick={() => {
            setLeftSidebarOpen(false);
            if (rightSidebarOpen && rightSidebarTab === 'export') {
              setRightSidebarOpen(false);
            } else {
              setRightSidebarTab('export');
              setRightSidebarOpen(true);
            }
          }}
          title={appExtraText.mobileExport}
          aria-label={appExtraText.mobileExport}
          aria-pressed={rightSidebarOpen && rightSidebarTab === 'export'}
        >
          <Download size={20} />
          <span className="mobile-bar-label">{appExtraText.mobileExport}</span>
        </button>
      </nav>
    </div>
  )}
      </Suspense>

      {/* ====================================================================
          GLOBAL AUTHENTICATION MODAL (Google Auth & Email/Password)
          ==================================================================== */}
      {showAuthModal && (
        <AccessibleModal
          onClose={() => setShowAuthModal(false)}
          ariaLabelledBy="auth-modal-title"
          ariaDescribedBy="auth-modal-description"
          backdropClassName="auth-modal-backdrop"
          backdropStyle={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--inline-rgba-0-0-0-0-64)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '16px',
            overflowY: 'auto',
            overscrollBehavior: 'contain'
          }}
          dialogClassName="m3-card auth-modal-card"
          dialogStyle={{
            width: '100%',
            maxWidth: '420px',
            maxHeight: 'min(90dvh, 620px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            padding: '22px 24px 20px',
            background: 'var(--md-sys-color-surface-container-high)',
            boxShadow: 'var(--elevation-4)',
            borderRadius: 'var(--shape-xl)'
          }}
        >
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src="/emblem.webp" alt="" width="24" height="24" style={{ height: '24px', width: 'auto' }} />
                <h3 id="auth-modal-title" style={{ fontSize: '17px', fontWeight: 700 }}>
                  {authMode === 'login' ? t('auth.signInTitle') : authMode === 'register' ? t('auth.createAccountTitle') : t('auth.resetTitle')}
                </h3>
              </div>
              <button
                className="m3-btn-icon"
                onClick={() => setShowAuthModal(false)}
                style={{ width: '30px', height: '30px' }}
                aria-label={appExtraText.close}
              >
                <X size={16} />
              </button>
            </div>

            {/* Mode Segmented Switcher Tab */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', background: 'var(--md-sys-color-surface-container-low)', padding: '4px', borderRadius: 'var(--shape-md)', marginBottom: '12px' }}>
              <button
                type="button"
                onClick={() => { setAuthMode('login'); setAuthError(null); setAuthSuccessMsg(null); }}
                aria-pressed={authMode === 'login'}
                style={{
                  padding: '7px 12px',
                  borderRadius: 'var(--shape-sm)',
                  fontSize: '12px',
                  fontWeight: authMode === 'login' ? 700 : 500,
                  border: 'none',
                  cursor: 'pointer',
                  background: authMode === 'login' ? 'var(--md-sys-color-surface-container-highest)' : 'transparent',
                  color: authMode === 'login' ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                  transition: 'background-color 0.15s ease, color 0.15s ease'
                }}
              >
                {t('auth.signIn')}
              </button>
              <button
                type="button"
                onClick={() => { setAuthMode('register'); setAuthError(null); setAuthSuccessMsg(null); }}
                aria-pressed={authMode === 'register'}
                style={{
                  padding: '7px 12px',
                  borderRadius: 'var(--shape-sm)',
                  fontSize: '12px',
                  fontWeight: authMode === 'register' ? 700 : 500,
                  border: 'none',
                  cursor: 'pointer',
                  background: authMode === 'register' ? 'var(--md-sys-color-surface-container-highest)' : 'transparent',
                  color: authMode === 'register' ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                  transition: 'background-color 0.15s ease, color 0.15s ease'
                }}
              >
                {t('auth.createAccount')}
              </button>
            </div>

            <p id="auth-modal-description" style={{ fontSize: '12px', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '12px', lineHeight: 1.45 }}>
              {authMode === 'login'
                ? t('auth.signInDesc')
                : authMode === 'register'
                ? t('auth.registerDesc')
                : t('auth.resetDesc')}
            </p>

            {/* Google Identity Services Official Instant Sign-In Button */}
            {authMode !== 'forgot' && (
              <>
                <div
                  ref={googleBtnContainerRef}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: '44px',
                    borderRadius: 'var(--shape-md)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Fallback button if Google script is loading */}
                  <button
                    type="button"
                    onClick={handleGoogleAuth}
                    disabled={authLoading}
                    className="m3-btn m3-btn-tonal m3-btn-md"
                    style={{
                      width: '100%',
                      background: 'var(--color-white)',
                      color: 'var(--palette-1f2937)',
                      border: '1px solid var(--palette-e5e7eb)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      fontWeight: 600,
                      borderRadius: 'var(--shape-md)',
                      boxShadow: '0 2px 6px var(--inline-rgba-0-0-0-0-1)'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="var(--palette-4285f4)" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="var(--palette-34a853)" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="var(--palette-fbbc05)" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                      <path fill="var(--palette-ea4335)" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                    </svg>
                    <span>{t('auth.google')}</span>
                  </button>
                </div>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', margin: '12px 0', gap: '10px' }}>
                  <div style={{ flex: 1, height: '1px', background: 'var(--md-sys-color-outline-variant)' }} />
                  <span style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('auth.orEmail')}
                  </span>
                  <div style={{ flex: 1, height: '1px', background: 'var(--md-sys-color-outline-variant)' }} />
                </div>
              </>
            )}

            {/* Success Message */}
            {authSuccessMsg && (
              <div role="status" aria-live="polite" style={{ background: 'var(--inline-rgba-34-197-94-0-15)', border: '1px solid var(--inline-rgba-34-197-94-0-4)', color: 'var(--palette-22c55e)', padding: '8px 12px', borderRadius: 'var(--shape-md)', fontSize: '12px', marginBottom: '10px', lineHeight: 1.45 }}>
                {authSuccessMsg}
              </div>
            )}

            {/* Error Message */}
            {authError && (
              <div role="alert" aria-live="assertive" style={{ background: 'var(--md-sys-color-error-container)', color: 'var(--md-sys-color-error)', padding: '8px 12px', borderRadius: 'var(--shape-md)', fontSize: '12px', marginBottom: '10px', lineHeight: 1.45 }}>
                {authError}
              </div>
            )}

            {/* Email & Password Form */}
            <form onSubmit={handleAuthSubmit} aria-busy={authLoading} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {authMode === 'register' && (
                <div>
                  <label htmlFor="auth-full-name" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '4px', display: 'block' }}>
                    {t('auth.fullName')}
                  </label>
                  <input
                    id="auth-full-name"
                    type="text"
                    required
                    className="m3-input"
                    placeholder="Alex Carter"
                    value={authName}
                    onChange={(e) => setAuthName(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label htmlFor={authMode === 'forgot' && authResetToken ? 'auth-reset-token' : 'auth-email'} style={{ fontSize: '12px', fontWeight: 600, color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '4px', display: 'block' }}>
                  {authMode === 'forgot' && authResetToken ? t('auth.resetTokenLabel') : t('auth.email')}
                </label>
                {authMode === 'forgot' && authResetToken ? (
                  <input
                    id="auth-reset-token"
                    type="text"
                    required
                    className="m3-input"
                    autoComplete="one-time-code"
                    placeholder={appExtraText.resetToken}
                    value={authResetToken}
                    onChange={(e) => setAuthResetToken(e.target.value.trim())}
                  />
                ) : (
                  <input
                    id="auth-email"
                    type="email"
                    required
                    className="m3-input"
                    autoComplete="email"
                    placeholder="alex@creator.com"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                  />
                )}
              </div>

              {authMode !== 'forgot' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <label htmlFor="auth-password" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--md-sys-color-on-surface-variant)' }}>
                      {t('auth.password')}
                    </label>
                    {authMode === 'login' && (
                      <button
                        type="button"
                        onClick={() => { setAuthMode('forgot'); setAuthError(null); setAuthSuccessMsg(null); }}
                        style={{ background: 'none', border: 'none', padding: 0, fontSize: '11px', color: 'var(--md-sys-color-primary)', cursor: 'pointer', fontWeight: 600 }}
                      >
                        {t('auth.forgot')}
                      </button>
                    )}
                  </div>
                  <input
                    id="auth-password"
                    type="password"
                    required
                    className="m3-input"
                    placeholder="••••••••"
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                  />
                </div>
              )}

              {authMode === 'forgot' && authResetToken && (
                <div>
                  <label htmlFor="auth-new-password" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '4px', display: 'block' }}>
                    {t('auth.newPassword')}
                  </label>
                  <input
                    id="auth-new-password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="m3-input"
                    placeholder={appExtraText.newPassword}
                    value={authNewPassword}
                    onChange={(e) => setAuthNewPassword(e.target.value)}
                  />
                </div>
              )}

              {/* Login Options: Remember Me Checkbox */}
              {authMode === 'login' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11.5px', color: 'var(--md-sys-color-on-surface-variant)', userSelect: 'none', marginTop: '2px' }}>
                  <input
                    type="checkbox"
                    checked={authRememberMe}
                    onChange={(e) => setAuthRememberMe(e.target.checked)}
                    style={{ width: '15px', height: '15px', accentColor: 'var(--md-sys-color-primary)', cursor: 'pointer' }}
                  />
                  <span>{t('auth.remember')}</span>
                </label>
              )}

              {/* Registration Options: Mandatory Legal Consent Checkbox */}
              {authMode === 'register' && (
                <div style={{ background: 'var(--md-sys-color-surface-container-low)', border: '1px solid var(--md-sys-color-outline-variant)', borderRadius: '10px', padding: '8px 10px', marginTop: '0px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: 'pointer', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--md-sys-color-on-surface)' }}>
                    <input
                      type="checkbox"
                      checked={authLegalConsent}
                      onChange={(e) => setAuthLegalConsent(e.target.checked)}
                      required
                      style={{ width: '16px', height: '16px', marginTop: '1px', accentColor: 'var(--md-sys-color-primary)', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span>
                      {t('auth.consentPrefix')}{' '}
                      <button
                        type="button"
                        onClick={() => { setShowAuthModal(false); window.location.hash = 'terms'; setCurrentView('terms'); window.scrollTo(0, 0); }}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--md-sys-color-primary)', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                      >
                        {t('auth.terms')}
                      </button>
                      {' '}{t('auth.and')}{' '}
                      <button
                        type="button"
                        onClick={() => { setShowAuthModal(false); window.location.hash = 'privacy'; setCurrentView('privacy'); window.scrollTo(0, 0); }}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--md-sys-color-primary)', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                      >
                        {t('auth.privacy')}
                      </button>.
                    </span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={authLoading || (authMode === 'register' && !authLegalConsent)}
                className="m3-btn m3-btn-filled m3-btn-md"
                style={{ width: '100%', marginTop: '4px', borderRadius: 'var(--shape-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {authLoading && <RefreshCw size={14} className="m3-spin" />}
                <span>
                  {authLoading
                    ? (authMode === 'login' ? t('auth.signingIn') : authMode === 'register' ? t('auth.creating') : t('auth.updating'))
                    : authMode === 'forgot'
                    ? (authResetToken ? t('auth.reset') : t('auth.sendResetLink'))
                    : authMode === 'login'
                    ? t('auth.signIn')
                    : authMode === 'register'
                    ? t('auth.createFreeAccount')
                    : t('auth.reset')}
                </span>
              </button>
            </form>

            {/* Mode Switcher Footer */}
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: 'var(--md-sys-color-on-surface-variant)' }}>
              {authMode === 'login' ? (
                <>
                  {t('auth.noAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('register');
                      setAuthError(null);
                      setAuthSuccessMsg(null);
                    }}
                    className="auth-mode-link"
                  >
                    {t('auth.registerFreeBonus')}
                  </button>
                </>
              ) : authMode === 'register' ? (
                <>
                  {t('auth.alreadyHaveAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError(null);
                      setAuthSuccessMsg(null);
                    }}
                    className="auth-mode-link"
                  >
                    {t('auth.signInHere')}
                  </button>
                </>
              ) : (
                <>
                  {t('auth.rememberPassword')}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError(null);
                      setAuthSuccessMsg(null);
                    }}
                    className="auth-mode-link"
                  >
                    {t('auth.backToSignIn')}
                  </button>
                </>
              )}
            </div>
        </AccessibleModal>
      )}

      {showWorkspaceModal && user?.plan?.toLowerCase() === 'agency' && (
        <AccessibleModal
          onClose={() => setShowWorkspaceModal(false)}
          ariaLabelledBy="workspace-modal-title"
          ariaDescribedBy="workspace-modal-description"
          ariaBusy={saasLoading}
          backdropClassName="workspace-modal-backdrop"
          backdropStyle={{ position:'fixed',inset:0,zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:20,background:'var(--inline-rgba-2-8-23-68)',overflow:'hidden' }}
          dialogClassName="workspace-modal-card"
          dialogStyle={{ width:'min(920px,100%)',maxHeight:'86dvh',display:'flex',flexDirection:'column',overflow:'hidden',borderRadius:24,background:'var(--md-sys-color-surface-container)',border:'1px solid var(--md-sys-color-outline-variant)',boxShadow:'0 20px 56px var(--alpha-rgba-0-0-0-32)' }}
        >
            <div className="workspace-modal-header" style={{display:'flex',justifyContent:'space-between',gap:16,padding:'28px 28px 18px',flex:'0 0 auto',background:'var(--md-sys-color-surface-container)',zIndex:2}}><div><div style={{fontSize:11,fontWeight:800,letterSpacing:'.14em',color:'var(--md-sys-color-primary)'}}>{workspaceText.eyebrow}</div><h2 id="workspace-modal-title" style={{margin:'7px 0 4px'}}>{saasOverview?.workspace?.name || workspaceText.fallback}</h2><p id="workspace-modal-description" style={{margin:0,fontSize:13,color:'var(--md-sys-color-on-surface-variant)'}}>{workspaceText.description}</p></div><button type="button" className="m3-btn-icon" aria-label={workspaceText.close} onClick={() => setShowWorkspaceModal(false)}><X size={18}/></button></div>
            <div className="workspace-modal-scroll" style={{overflowY:'auto',overscrollBehavior:'contain',scrollbarGutter:'stable',padding:'0 28px 28px'}}>
            {saasLoading ? <div style={{padding:48,textAlign:'center'}}><RefreshCw size={24} className="m3-spin"/><div style={{fontSize:12,marginTop:12,color:'var(--md-sys-color-on-surface-variant)'}}>{workspaceText.loading}</div></div> : saasError ? <div style={{marginTop:24,padding:18,borderRadius:14,background:'var(--md-sys-color-error-container)',color:'var(--md-sys-color-on-error-container)'}}><b>{workspaceText.failed}</b><div style={{fontSize:12,marginTop:6}}>{saasError}</div><button className="m3-btn m3-btn-tonal m3-btn-sm" style={{marginTop:12}} onClick={openWorkspaceDashboard}>{workspaceText.retry}</button></div> : saasOverview && <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(145px,1fr))',gap:12,marginTop:24}}>{[[workspaceText.plan,String(saasOverview.workspace.plan).toUpperCase()],[workspaceText.conversions,`${saasOverview.usage.vectorization} / ${saasOverview.usage.limit < 0 ? '∞' : saasOverview.usage.limit}`],[saasOverview.entitlements.team_enabled?workspaceText.members:workspaceExtraText.accountType,saasOverview.entitlements.team_enabled?`${saasOverview.members} / ${saasOverview.entitlements.max_members}`:workspaceExtraText.personal],[workspaceText.batch,saasOverview.entitlements.batch_size]].map(([l,v]) => <div key={l} style={{padding:16,borderRadius:16,background:'var(--md-sys-color-surface-container-high)',border:'1px solid var(--md-sys-color-outline-variant)'}}><small style={{color:'var(--md-sys-color-on-surface-variant)'}}>{l}</small><div style={{fontSize:20,fontWeight:800,marginTop:5}}>{v}</div></div>)}</div>
              <div style={{marginTop:18,padding:17,borderRadius:16,background:'linear-gradient(135deg,var(--inline-rgba-34-211-238-1),var(--inline-rgba-129-140-248-1))',border:'1px solid var(--inline-rgba-34-211-238-25)',display:'flex',justifyContent:'space-between',alignItems:'center',gap:12}}><div><b>{saasOverview.plan_source==='internal'?workspaceExtraText.access:workspaceText.subscription}</b><div style={{fontSize:12,marginTop:4,color:'var(--md-sys-color-on-surface-variant)'}}>{saasOverview.subscription ? `${saasOverview.subscription.status} • ${saasOverview.subscription.billing_cycle}` : saasOverview.plan_source==='internal' ? workspaceExtraText.internalAccess : String(saasOverview.workspace.plan).toLowerCase()==='free' ? workspaceExtraText.freePlan : workspaceText.noSubscription}</div></div>{saasOverview.subscription && <button className="m3-btn m3-btn-tonal m3-btn-sm" onClick={() => openBillingPortal().catch(e => setSaveStatusMsg(e.message))}>{workspaceText.billing}</button>}</div>
              {saasOverview.entitlements.team_enabled ? <><h3 style={{fontSize:15,margin:'23px 0 10px'}}>{workspaceText.team}</h3>
              <div style={{display:'flex',gap:8,marginBottom:10}}><input className="m3-input" type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder={workspaceText.email}/><button className="m3-btn m3-btn-filled m3-btn-sm" onClick={()=>workspaceRequest('/api/saas/workspace/invites',{method:'POST',body:JSON.stringify({email:inviteEmail,role:'member',locale})}).then(()=>setInviteEmail('')).catch(e=>setSaveStatusMsg(e.message))}>{workspaceText.invite}</button></div>
              <div style={{display:'grid',gap:7}}>{saasPeople?.members?.map((m:any)=><div key={m.user_id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,padding:11,borderRadius:11,border:'1px solid var(--md-sys-color-outline-variant)'}}><div><b style={{fontSize:13}}>{m.name}</b><div style={{fontSize:11,color:'var(--md-sys-color-on-surface-variant)'}}>{m.email}</div></div><div style={{display:'flex',gap:7,alignItems:'center'}}><select className="m3-input" value={m.role} disabled={m.role==='owner'} onChange={e=>workspaceRequest(`/api/saas/workspace/members/${m.user_id}`,{method:'PATCH',body:JSON.stringify({role:e.target.value})}).catch(x=>setSaveStatusMsg(x.message))} style={{padding:'6px 8px',width:100}}><option value="owner">{workspaceText.owner}</option><option value="admin">{workspaceText.admin}</option><option value="member">{workspaceText.member}</option><option value="viewer">{workspaceText.viewer}</option></select>{m.role!=='owner'&&<button className="m3-btn m3-btn-text m3-btn-sm" onClick={()=>workspaceRequest(`/api/saas/workspace/members/${m.user_id}`,{method:'DELETE'}).catch(e=>setSaveStatusMsg(e.message))}>{workspaceText.remove}</button>}</div></div>)}</div>
              {!!saasPeople?.invites?.length&&<><h3 style={{fontSize:15,margin:'20px 0 10px'}}>{workspaceText.pending}</h3>{saasPeople.invites.map((i:any)=><div key={i.id} style={{display:'flex',justifyContent:'space-between',padding:10,borderBottom:'1px solid var(--md-sys-color-outline-variant)'}}><span style={{fontSize:12}}>{i.email} • {i.role}</span><button className="m3-btn m3-btn-text m3-btn-sm" onClick={()=>workspaceRequest(`/api/saas/workspace/invites/${i.id}`,{method:'DELETE'}).catch(e=>setSaveStatusMsg(e.message))}>{workspaceText.revoke}</button></div>)}</>}</> : <div style={{marginTop:20,padding:16,borderRadius:14,border:'1px solid var(--md-sys-color-outline-variant)',background:'var(--md-sys-color-surface-container-high)'}}><b>{workspaceExtraText.personalTitle}</b><div style={{fontSize:12,marginTop:5,color:'var(--md-sys-color-on-surface-variant)'}}>{workspaceExtraText.personalDesc}</div><button className="m3-btn m3-btn-tonal m3-btn-sm" style={{marginTop:10}} onClick={()=>setShowPricingModal(true)}>{workspaceExtraText.teamPlans}</button></div>}
              <h3 style={{fontSize:15,margin:'23px 0 10px'}}>{workspaceText.usage}</h3><div style={{display:'flex',gap:8,flexWrap:'wrap'}}>{Object.entries(saasUsage?.totals || {}).map(([metric,total])=><div key={metric} style={{padding:'9px 12px',borderRadius:10,background:'var(--md-sys-color-surface-container-high)',fontSize:12}}><b>{metric}</b>: {String(total)}</div>)}</div>
              {metadataObservability && (()=>{const statuses=metadataObservability.by_status||[];const providers=metadataObservability.by_provider||[];const usage=Object.fromEntries((metadataObservability.usage||[]).map((row:any)=>[row.metric,Number(row.total||0)]));const totalAssets=statuses.reduce((sum:number,row:any)=>sum+Number(row.completed||0)+Number(row.failed||0),0);const failedAssets=statuses.reduce((sum:number,row:any)=>sum+Number(row.failed||0),0);const failureRate=totalAssets?Math.round(failedAssets/totalAssets*100):0;const aiLimit=saasOverview.entitlements.monthly_ai_metadata;const exportLimit=saasOverview.entitlements.monthly_exports;return <section aria-label={workspaceExtraText.observability} style={{marginTop:22,padding:16,borderRadius:16,background:'linear-gradient(135deg,var(--inline-rgba-129-140-248-08),var(--inline-rgba-34-211-238-07))',border:'1px solid var(--md-sys-color-outline-variant)'}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}><h3 style={{fontSize:15,margin:0}}>{workspaceExtraText.observability}</h3><span style={{fontSize:10,fontWeight:800,color:failureRate>10?'var(--md-sys-color-error)':'var(--status-emerald)'}}>{failureRate}% {workspaceExtraText.failureRate}</span></div><div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:8,marginTop:12}}><div style={{padding:11,borderRadius:11,background:'var(--md-sys-color-surface-container-high)'}}><small>{workspaceExtraText.aiMetadata}</small><div style={{fontWeight:800,fontSize:18}}>{usage.ai_metadata||0} / {aiLimit<0?'∞':aiLimit}</div></div><div style={{padding:11,borderRadius:11,background:'var(--md-sys-color-surface-container-high)'}}><small>{workspaceExtraText.metadataExports}</small><div style={{fontWeight:800,fontSize:18}}>{usage.metadata_export||0} / {exportLimit<0?'∞':exportLimit}</div></div><div style={{padding:11,borderRadius:11,background:'var(--md-sys-color-surface-container-high)'}}><small>{workspaceExtraText.processed}</small><div style={{fontWeight:800,fontSize:18}}>{totalAssets}</div></div><div style={{padding:11,borderRadius:11,background:'var(--md-sys-color-surface-container-high)'}}><small>{workspaceExtraText.failedAssets}</small><div style={{fontWeight:800,fontSize:18}}>{failedAssets}</div></div></div><div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:8,marginTop:10}}><div><b style={{fontSize:11}}>{workspaceExtraText.jobsStatus}</b>{statuses.map((row:any)=><div key={row.status} style={{display:'flex',justifyContent:'space-between',fontSize:10.5,marginTop:5,textTransform:'capitalize'}}><span>{row.status}</span><b>{row.total}</b></div>)}</div><div><b style={{fontSize:11}}>{workspaceExtraText.providers}</b>{providers.map((row:any)=><div key={row.provider||workspaceExtraText.unknown} style={{display:'flex',justifyContent:'space-between',fontSize:10.5,marginTop:5,gap:8}}><span>{row.provider||workspaceExtraText.unknown} · {row.jobs||0} {workspaceExtraText.jobs}</span><b style={{textAlign:'right'}}>{row.assets||0} {workspaceExtraText.assets} · {row.failure_rate||0}% {workspaceExtraText.failed}{row.avg_duration_ms!=null?` · ${Number(row.avg_duration_ms).toFixed(0)} ms ${workspaceExtraText.average}`:''}</b></div>)}</div></div></section>})()}
              <h3 style={{fontSize:15,margin:'23px 0 10px'}}>{workspaceText.invoices}</h3><div style={{display:'grid',gap:8}}>{saasOverview.invoices?.length ? saasOverview.invoices.map((inv:any) => <div key={inv.id} style={{padding:12,borderRadius:12,border:'1px solid var(--md-sys-color-outline-variant)',display:'flex',justifyContent:'space-between',alignItems:'center',gap:12}}><div><b style={{fontSize:13}}>{inv.invoice_number || inv.id}</b><div style={{fontSize:11,color:'var(--md-sys-color-on-surface-variant)'}}>{inv.plan_id} • {inv.status}</div></div><div style={{display:'flex',alignItems:'center',gap:9}}><b>{inv.currency} {Number(inv.amount).toFixed(2)}</b><button className="m3-btn m3-btn-tonal m3-btn-sm" onClick={async()=>{const r=await fetch(`${API_BASE}/api/saas/invoices/${inv.id}/download`,{headers:{Authorization:`Bearer ${authToken}`}});const b=await r.blob();const u=URL.createObjectURL(b);const a=document.createElement('a');a.href=u;a.download=`${inv.invoice_number || inv.id}.html`;a.click();URL.revokeObjectURL(u)}}>{workspaceText.download}</button></div></div>) : <div style={{padding:20,textAlign:'center',border:'1px dashed var(--md-sys-color-outline-variant)',borderRadius:14,color:'var(--md-sys-color-on-surface-variant)'}}>{workspaceText.noInvoices}</div>}</div>
            </>}
            </div>
        </AccessibleModal>
      )}

      {/* ====================================================================
          GLOBAL PRICING & PAYMENT GATEWAY MODAL (Stripe / Lemon Squeezy Ready)
          ==================================================================== */}
      {showPricingModal && (
        <AccessibleModal
          onClose={() => setShowPricingModal(false)}
          ariaLabelledBy="pricing-modal-title"
          ariaDescribedBy="pricing-modal-description"
          backdropClassName="pricing-modal-backdrop"
          backdropStyle={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--inline-rgba-0-0-0-0-8)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
            overflow: 'hidden'
          }}
          dialogClassName="m3-card pricing-modal-card"
          dialogStyle={{
            width: '100%',
            maxWidth: '1040px',
            maxHeight: '92dvh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--md-sys-color-surface-container-high)',
            boxShadow: 'var(--elevation-4)',
            borderRadius: '24px',
            border: '1px solid var(--md-sys-color-outline-variant)'
          }}
        >
            {/* Modal Header */}
            <div className="pricing-modal-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', padding: '28px 32px 16px', flex: '0 0 auto', background: 'var(--md-sys-color-surface-container-high)' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--md-sys-color-primary)', display: 'block', marginBottom: '4px' }}>
                  {landingPricing.eyebrow}
                </span>
                <h3 id="pricing-modal-title" style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: 'var(--md-sys-color-on-surface)' }}>
                  {landingPricing.title}
                </h3>
                <p id="pricing-modal-description" style={{ fontSize: '13px', color: 'var(--md-sys-color-on-surface-variant)', margin: '4px 0 0 0' }}>
                  {landingPricing.subtitle}
                </p>
              </div>
              <button
                className="m3-btn-icon"
                onClick={() => setShowPricingModal(false)}
                style={{ width: '32px', height: '32px', flexShrink: 0 }}
                aria-label={appExtraText.close}
              >
                <X size={18} />
              </button>
            </div>

            <div
              className="pricing-modal-scroll"
              style={{
                minHeight: 0,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                scrollbarGutter: 'stable',
                padding: '0 32px 28px'
              }}
            >
            {/* Refined Billing Switcher matching Landing Page */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
              <div className="pricing-toggle-wrap" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className={`pricing-toggle-btn ${pricingBillingCycle === 'monthly' ? 'is-active' : ''}`}
                  onClick={() => setPricingBillingCycle('monthly')}
                  aria-pressed={pricingBillingCycle === 'monthly'}
                >
                  {landingPricing.monthly}
                </button>
                <button
                  type="button"
                  className={`pricing-toggle-btn ${pricingBillingCycle === 'yearly' ? 'is-active' : ''}`}
                  onClick={() => setPricingBillingCycle('yearly')}
                  aria-pressed={pricingBillingCycle === 'yearly'}
                >
                  <span>{landingPricing.annual}</span>
                  <span className="pricing-save-chip">{landingPricing.save}</span>
                </button>
              </div>
            </div>

            {/* 3-Cards Grid matching Landing Page (Upgrade-Only Flow) */}
            {(() => {
              const userPlanRank = PLAN_TIER_RANK[user?.plan?.toLowerCase() || 'free'] ?? 0;
              const starterRank = PLAN_TIER_RANK.starter; // 1
              const proRank = PLAN_TIER_RANK.pro; // 2
              const studioRank = PLAN_TIER_RANK.studio; // 3

              const isStarterCurrent = user?.plan?.toLowerCase() === 'starter';
              const isStarterDowngrade = !isStarterCurrent && userPlanRank > starterRank;

              const isProCurrent = user?.plan?.toLowerCase() === 'pro';
              const isProDowngrade = !isProCurrent && userPlanRank > proRank;

              const isStudioCurrent = user?.plan?.toLowerCase() === 'studio';
              const isStudioDowngrade = !isStudioCurrent && userPlanRank > studioRank;

              return (
                <div className="pricing-cards-grid" style={{ gap: '18px', alignItems: 'stretch', paddingTop: '8px' }}>
                  {/* 1. Starter Plan */}
                  <article
                    className={`pricing-card pricing-card-standard ${isStarterDowngrade ? 'pricing-card-readonly' : ''}`}
                    style={{
                      padding: '28px 22px 22px',
                      ...(isStarterDowngrade ? {
                        opacity: 0.52,
                        filter: 'grayscale(0.4)',
                        background: theme === 'light' ? 'var(--alpha-rgba-0-0-0-0-02)' : 'var(--inline-rgba-255-255-255-0-02-2)',
                        border: '1px dashed var(--md-sys-color-outline-variant)'
                      } : {})
                    }}
                  >
                    {isStarterDowngrade && (
                      <div style={{ marginBottom: '10px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 10px',
                            borderRadius: 'var(--shape-full)',
                            fontSize: '11px',
                            fontWeight: 700,
                            background: theme === 'light' ? 'var(--alpha-rgba-0-0-0-0-05)' : 'var(--alpha-rgba-255-255-255-0-08)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                            border: '1px solid var(--md-sys-color-outline-variant)'
                          }}
                        >
                          <Check size={12} />
                          <span>{studio.pricingModal?.includedBadge || (locale === 'id' ? 'Termasuk' : 'Included')}</span>
                        </span>
                      </div>
                    )}
                    <div className="pricing-card-header" style={{ marginBottom: '18px' }}>
                      <span className="plan-name">{landingPricing.plans[0].name}</span>
                      <p className="plan-desc" style={{ minHeight: '38px' }}>{landingPricing.plans[0].desc}</p>
                      <div className="plan-price">
                        <span className="price-num">{planPriceText(billingContext, 'starter', pricingBillingCycle).text}</span>
                        <span className="price-cycle">{pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year}</span>
                      </div>
                      <div className="plan-allowance">
                        <span className="plan-allowance-line"><strong>{pricingBillingCycle === 'monthly' ? '200' : '2,400'}</strong> {landingPricing.vectorCredits} {pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year} <span className="unit-cost">({pricingBillingCycle === 'monthly' ? '$0.025' : '$0.021'} {landingPricing.perCredit})</span></span>
                        <span className="plan-allowance-line"><strong>0</strong> AI Credits {pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year} · BYOK</span>
                      </div>
                    </div>

                    <ul className="plan-features" style={{ margin: '0 0 24px 0', gap: '10px' }}>
                      {landingPricing.plans[0].features.map((item) => (
                        <li key={item} style={{ fontSize: '12.5px', lineHeight: 1.45 }}>
                          <Check size={15} className="feature-check" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="plan-cta-wrap">
                      <button
                        type="button"
                        className="m3-btn m3-btn-tonal m3-btn-md plan-cta-btn"
                        disabled={isStarterCurrent || isCheckingOut}
                        onClick={() => handleUpgradePlan('starter', pricingBillingCycle === 'monthly' ? 200 : 2400, 'Starter')}
                        style={
                          isStarterCurrent
                            ? { opacity: 0.75, cursor: 'not-allowed' }
                            : isStarterDowngrade
                              ? { opacity: 0.82 }
                              : undefined
                        }
                      >
                        {isStarterCurrent ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Check size={16} /> {studio.pricingModal.currentPlan}
                          </span>
                        ) : isStarterDowngrade ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Check size={14} /> {studio.pricingModal.includedInPlan || (locale === 'id' ? 'Termasuk dalam paket Anda' : 'Included in your plan')}
                          </span>
                        ) : (
                          <span>{studio.pricingModal.choosePlan('Starter', planPriceWithCycle(billingContext, 'starter', pricingBillingCycle, { month: landingPricing.month, year: landingPricing.year }))}</span>
                        )}
                      </button>
                    </div>
                  </article>

                  {/* 2. Pro Plan (Single Featured Option) */}
                  <article
                    className={`pricing-card pricing-card-featured ${isProDowngrade ? 'pricing-card-readonly' : ''}`}
                    style={{
                      padding: '28px 22px 22px',
                      ...(isProDowngrade ? {
                        opacity: 0.52,
                        filter: 'grayscale(0.4)',
                        background: theme === 'light' ? 'var(--alpha-rgba-0-0-0-0-02)' : 'var(--inline-rgba-255-255-255-0-02-2)',
                        border: '1px dashed var(--md-sys-color-outline-variant)'
                      } : {})
                    }}
                  >
                    {!isProDowngrade && (
                      <div className="featured-badge-wrap">
                        <span className="featured-badge">{landingPricing.popular}</span>
                      </div>
                    )}
                    {isProDowngrade && (
                      <div style={{ marginBottom: '10px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 10px',
                            borderRadius: 'var(--shape-full)',
                            fontSize: '11px',
                            fontWeight: 700,
                            background: theme === 'light' ? 'var(--alpha-rgba-0-0-0-0-05)' : 'var(--alpha-rgba-255-255-255-0-08)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                            border: '1px solid var(--md-sys-color-outline-variant)'
                          }}
                        >
                          <Check size={12} />
                          <span>{studio.pricingModal?.includedBadge || (locale === 'id' ? 'Termasuk' : 'Included')}</span>
                        </span>
                      </div>
                    )}

                    <div className="pricing-card-header" style={{ marginBottom: '18px' }}>
                      <span className="plan-name">{landingPricing.plans[1].name}</span>
                      <p className="plan-desc" style={{ minHeight: '38px' }}>{landingPricing.plans[1].desc}</p>
                      <div className="plan-price">
                        <span className="price-num">{planPriceText(billingContext, 'pro', pricingBillingCycle).text}</span>
                        <span className="price-cycle">{pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year}</span>
                      </div>
                      <div className="plan-allowance plan-allowance-pro">
                        <span className="plan-allowance-line"><strong>{pricingBillingCycle === 'monthly' ? '500' : '6,000'}</strong> {landingPricing.vectorCredits} {pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year} <span className="unit-cost">({pricingBillingCycle === 'monthly' ? '$0.020' : '$0.016'} {landingPricing.perCredit})</span></span>
                        <span className="plan-allowance-line"><strong>{pricingBillingCycle === 'monthly' ? '500' : '6,000'}</strong> AI Credits {pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year}</span>
                      </div>
                    </div>

                    <ul className="plan-features" style={{ margin: '0 0 24px 0', gap: '10px' }}>
                      {landingPricing.plans[1].features.map((item) => (
                        <li key={item} style={{ fontSize: '12.5px', lineHeight: 1.45 }}>
                          <Check size={15} className="feature-check feature-check-pro" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="plan-cta-wrap">
                      <button
                        type="button"
                        className="m3-btn m3-btn-filled m3-btn-md plan-cta-btn plan-cta-btn-featured"
                        disabled={isProCurrent || isCheckingOut}
                        onClick={() => handleUpgradePlan('pro', pricingBillingCycle === 'monthly' ? 500 : 6000, 'Pro')}
                        style={
                          isProCurrent
                            ? { opacity: 0.85, cursor: 'not-allowed' }
                            : isProDowngrade
                              ? { opacity: 0.82 }
                              : undefined
                        }
                      >
                        {isProCurrent ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Check size={16} /> {studio.pricingModal.currentPlan}
                          </span>
                        ) : isProDowngrade ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Check size={14} /> {studio.pricingModal.includedInPlan || (locale === 'id' ? 'Termasuk dalam paket Anda' : 'Included in your plan')}
                          </span>
                        ) : (
                          <span>{studio.pricingModal.choosePlan('Pro', planPriceWithCycle(billingContext, 'pro', pricingBillingCycle, { month: landingPricing.month, year: landingPricing.year }))}</span>
                        )}
                      </button>
                    </div>
                  </article>

                  {/* 3. Studio Plan */}
                  <article
                    className={`pricing-card pricing-card-standard ${isStudioDowngrade ? 'pricing-card-readonly' : ''}`}
                    style={{
                      padding: '28px 22px 22px',
                      ...(isStudioDowngrade ? {
                        opacity: 0.52,
                        filter: 'grayscale(0.4)',
                        background: theme === 'light' ? 'var(--alpha-rgba-0-0-0-0-02)' : 'var(--inline-rgba-255-255-255-0-02-2)',
                        border: '1px dashed var(--md-sys-color-outline-variant)'
                      } : {})
                    }}
                  >
                    {isStudioDowngrade && (
                      <div style={{ marginBottom: '10px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 10px',
                            borderRadius: 'var(--shape-full)',
                            fontSize: '11px',
                            fontWeight: 700,
                            background: theme === 'light' ? 'var(--alpha-rgba-0-0-0-0-05)' : 'var(--alpha-rgba-255-255-255-0-08)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                            border: '1px solid var(--md-sys-color-outline-variant)'
                          }}
                        >
                          <Check size={12} />
                          <span>{studio.pricingModal?.includedBadge || (locale === 'id' ? 'Termasuk' : 'Included')}</span>
                        </span>
                      </div>
                    )}
                    <div className="pricing-card-header" style={{ marginBottom: '18px' }}>
                      <span className="plan-name">{landingPricing.plans[2].name}</span>
                      <p className="plan-desc" style={{ minHeight: '38px' }}>{landingPricing.plans[2].desc}</p>
                      <div className="plan-price">
                        <span className="price-num">{planPriceText(billingContext, 'studio', pricingBillingCycle).text}</span>
                        <span className="price-cycle">{pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year}</span>
                      </div>
                      <div className="plan-allowance">
                        <span className="plan-allowance-line"><strong>{pricingBillingCycle === 'monthly' ? '1,500' : '18,000'}</strong> {landingPricing.vectorCredits} {pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year} <span className="unit-cost">({pricingBillingCycle === 'monthly' ? '$0.017' : '$0.013'} {landingPricing.perCredit})</span></span>
                        <span className="plan-allowance-line"><strong>{pricingBillingCycle === 'monthly' ? '1,500' : '18,000'}</strong> AI Credits {pricingBillingCycle === 'monthly' ? landingPricing.month : landingPricing.year}</span>
                      </div>
                    </div>

                    <ul className="plan-features" style={{ margin: '0 0 24px 0', gap: '10px' }}>
                      {landingPricing.plans[2].features.map((item) => (
                        <li key={item} style={{ fontSize: '12.5px', lineHeight: 1.45 }}>
                          <Check size={15} className="feature-check" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="plan-cta-wrap">
                      <button
                        type="button"
                        className="m3-btn m3-btn-tonal m3-btn-md plan-cta-btn"
                        disabled={isStudioCurrent || isCheckingOut}
                        onClick={() => handleUpgradePlan('studio', pricingBillingCycle === 'monthly' ? 1500 : 18000, 'Studio')}
                        style={
                          isStudioCurrent
                            ? { opacity: 0.75, cursor: 'not-allowed' }
                            : isStudioDowngrade
                              ? { opacity: 0.82 }
                              : undefined
                        }
                      >
                        {isStudioCurrent ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Check size={16} /> {studio.pricingModal.currentPlan}
                          </span>
                        ) : isStudioDowngrade ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Check size={14} /> {studio.pricingModal.includedInPlan || (locale === 'id' ? 'Termasuk dalam paket Anda' : 'Included in your plan')}
                          </span>
                        ) : (
                          <span>{studio.pricingModal.choosePlan('Studio', planPriceWithCycle(billingContext, 'studio', pricingBillingCycle, { month: landingPricing.month, year: landingPricing.year }))}</span>
                        )}
                      </button>
                    </div>
                  </article>
                </div>
              );
            })()}

            {/* In-Modal Credit Top-Up Prompt Banner */}
            <div
              style={{
                marginTop: '20px',
                padding: '14px 20px',
                borderRadius: 'var(--shape-md)',
                background: theme === 'light' ? 'var(--inline-rgba-0-114-255-0-05)' : 'var(--inline-rgba-56-189-248-0-07)',
                border: '1px solid var(--md-sys-color-outline-variant)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px'
              }}
            >
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--md-sys-color-on-surface)' }}>
                  {studio.pricingModal?.topupPrompt || (locale === 'id' ? 'Butuh kredit tambahan sebelum perpanjangan?' : 'Need more credits before renewal?')}
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '2px' }}>
                  {studio.topupModal?.subtitle || (locale === 'id' ? 'Beli kredit ekstra secara instan tanpa memengaruhi tanggal siklus tagihan langganan Anda.' : 'Add extra Vector or AI Credits instantly without changing your subscription billing cycle.')}
                </div>
              </div>
              <button
                type="button"
                className="m3-btn m3-btn-filled m3-btn-sm"
                onClick={() => {
                  setShowTopupModal(true);
                }}
                style={{
                  borderRadius: 'var(--shape-full)',
                  padding: '7px 18px',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <PlusCircle size={14} />
                <span>{studio.pricingModal?.topupBtn || (locale === 'id' ? 'Beli Kredit (Top-up)' : 'Top Up Credits')}</span>
              </button>
            </div>

            {/* Rollover & Refund Guarantee Disclosure */}
            <div style={{ marginTop: '24px', textAlign: 'center', paddingTop: '16px', borderTop: '1px solid var(--md-sys-color-outline-variant)' }}>
              <span className="pricing-note">
                {landingPricing.note}
              </span>
              {billingContext.one_time_period && landingPricing.oneTime ? (
                <span className="pricing-note pricing-note-one-time">{landingPricing.oneTime}</span>
              ) : null}
              {billingContext.currency === 'IDR' && landingPricing.fxNote ? (
                <span className="pricing-note pricing-note-fx">
                  {landingPricing.fxNote}
                  {billingContext.fx_attribution ? ` ${billingContext.fx_attribution}.` : ''}
                </span>
              ) : null}
            </div>

            {/* Active Subscription Management & Cancellation Bar */}
            {user?.plan && user.plan !== 'free' && (() => {
              const sub = user.subscription;
              const isCancelScheduled = Boolean(sub?.cancel_at_period_end);

              // Calculate safe start and end dates with fallbacks
              const startDateStr = sub?.current_period_start || sub?.created_at || new Date().toISOString();
              const endDateStr = sub?.current_period_end || new Date(Date.now() + 30 * 86400000).toISOString();

              const formattedStart = formatSubscriptionDate(startDateStr, locale) || startDateStr;
              const formattedEnd = formatSubscriptionDate(endDateStr, locale) || endDateStr;

              const planName = user.plan.toUpperCase();
              const isYearly = sub?.billing_cycle === 'yearly';
              const summaryPlan = (['starter', 'pro', 'studio'] as const).includes(user.plan as any)
                ? (user.plan as 'starter' | 'pro' | 'studio')
                : null;
              const priceStr = summaryPlan
                ? planPriceWithCycle(billingContext, summaryPlan, isYearly ? 'yearly' : 'monthly', {
                    month: locale === 'id' ? '/bulan' : '/month',
                    year: locale === 'id' ? '/tahun' : '/year'
                  })
                : '';

              return (
                <div
                  style={{
                    marginTop: '20px',
                    padding: '16px 20px',
                    borderRadius: 'var(--shape-lg)',
                    background: isCancelScheduled ? 'var(--inline-rgba-239-68-68-0-04)' : 'var(--md-sys-color-surface)',
                    border: isCancelScheduled ? '1px solid var(--inline-rgba-239-68-68-0-35)' : '1px solid var(--md-sys-color-outline-variant)',
                    boxShadow: 'var(--elevation-1)'
                  }}
                >
                  {/* Top Line: Active Plan & Credits | Single Action / Status Badge */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 800, color: 'var(--md-sys-color-on-surface)' }}>
                      {studio.pricingModal.activeSubscription(planName, user.credits || 0)}
                    </div>
                    <div>
                      {isCancelScheduled ? (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '5px 12px',
                            borderRadius: 'var(--shape-full)',
                            background: 'var(--inline-rgba-239-68-68-0-12)',
                            border: '1px solid var(--inline-rgba-239-68-68-0-35)',
                            color: 'var(--md-sys-color-error)',
                            fontSize: '11.5px',
                            fontWeight: 700
                          }}
                        >
                          <Clock size={13} />
                          <span>{studio.pricingModal.cancellationScheduledBadge}</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="m3-btn m3-btn-outlined m3-btn-xs"
                          style={{
                            color: 'var(--md-sys-color-error)',
                            borderColor: 'var(--inline-rgba-239-68-68-0-35)',
                            fontWeight: 600
                          }}
                          onClick={() => setShowCancelConfirm(true)}
                        >
                          {studio.pricingModal.cancelSubscription}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Period Line: From [Start] to [End] */}
                  <div style={{ fontSize: '12px', color: 'var(--md-sys-color-on-surface-variant)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <Calendar size={13} style={{ color: 'var(--md-sys-color-primary)', flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>
                      {studio.pricingModal.activePeriod(formattedStart, formattedEnd)}
                    </span>
                  </div>

                  {/* Status & Next Step Detail Line */}
                  <div style={{ fontSize: '12px', lineHeight: 1.45 }}>
                    {isCancelScheduled ? (
                      <div style={{ color: 'var(--md-sys-color-error)', display: 'flex', alignItems: 'flex-start', gap: '6px', fontWeight: 500 }}>
                        <AlertCircle size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                        <span>{studio.pricingModal.cancelsOnNotice(planName, formattedEnd)}</span>
                      </div>
                    ) : (
                      <div style={{ color: 'var(--md-sys-color-primary)', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}>
                        <RefreshCw size={13} style={{ flexShrink: 0 }} />
                        <span>{studio.pricingModal.renewsOnNotice(formattedEnd, priceStr)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            </div>
        </AccessibleModal>
      )}

      {/* ====================================================================
          CANCEL SUBSCRIPTION CONFIRMATION MODAL
          ==================================================================== */}
      {showCancelConfirm && (
        <AccessibleModal
          onClose={() => {
            if (!isCancellingSubscription) setShowCancelConfirm(false);
          }}
          ariaLabelledBy="cancel-subscription-title"
          ariaDescribedBy="cancel-subscription-description"
          ariaBusy={isCancellingSubscription}
          closeOnBackdrop={!isCancellingSubscription}
          closeOnEscape={!isCancellingSubscription}
          initialFocusSelector="#cancel-subscription-keep"
          backdropStyle={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--alpha-rgba-0-0-0-0-68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 120,
            padding: '20px'
          }}
          dialogClassName="m3-card"
          dialogStyle={{
              maxWidth: '440px',
              width: '100%',
              padding: '24px',
              background: 'var(--md-sys-color-surface-container-high)',
              borderRadius: 'var(--shape-xl)',
              border: '1px solid var(--md-sys-color-outline-variant)',
              boxShadow: '0 18px 48px var(--alpha-rgba-0-0-0-0-3)'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', color: 'var(--md-sys-color-error)' }}>
              <AlertCircle size={22} />
              <h4 id="cancel-subscription-title" style={{ fontSize: '17px', fontWeight: 800 }}>{studio.pricingModal.cancelConfirmTitle}</h4>
            </div>
            <p id="cancel-subscription-description" style={{ fontSize: '13px', lineHeight: 1.5, color: 'var(--md-sys-color-on-surface-variant)', marginBottom: user?.subscription?.current_period_end ? '12px' : '20px' }}>
              {studio.pricingModal.cancelConfirmDesc(user?.credits || 0)}
            </p>
            {user?.subscription?.current_period_end && (
              <div style={{
                marginBottom: '20px',
                padding: '10px 14px',
                borderRadius: 'var(--shape-md)',
                background: 'var(--alpha-rgba-239-68-68-0-08)',
                border: '1px solid var(--inline-rgba-239-68-68-0-25)',
                fontSize: '12px',
                color: 'var(--md-sys-color-error)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Clock size={16} />
                <span>
                  {studio.pricingModal.cancelsOnNotice(user.plan.toUpperCase(), formatSubscriptionDate(user.subscription.current_period_end || new Date(Date.now() + 30 * 86400000).toISOString(), locale) || '')}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                id="cancel-subscription-keep"
                type="button"
                className="m3-btn m3-btn-tonal m3-btn-sm"
                onClick={() => setShowCancelConfirm(false)}
                disabled={isCancellingSubscription}
              >
                {studio.pricingModal.cancelKeepBtn}
              </button>
              <button
                type="button"
                className="m3-btn m3-btn-filled m3-btn-sm"
                style={{ background: 'var(--palette-ef4444)', color: 'var(--palette-fff)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                onClick={handleCancelSubscription}
                disabled={isCancellingSubscription}
              >
                {isCancellingSubscription ? <RefreshCw size={13} className="m3-pulse" /> : null}
                <span>{studio.pricingModal.cancelProceedBtn}</span>
              </button>
            </div>
        </AccessibleModal>
      )}

      {/* ====================================================================
          BYOK AI SETTINGS MODAL      {/* BYOK settings render inside SettingsPage. */}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <AccessibleModal
          onClose={() => {
            if (!passwordChangeLoading) setShowChangePasswordModal(false);
          }}
          ariaLabelledBy="password-modal-title"
          ariaDescribedBy="password-modal-description"
          ariaBusy={passwordChangeLoading}
          closeOnBackdrop={!passwordChangeLoading}
          closeOnEscape={!passwordChangeLoading}
          backdropClassName="m3-modal-backdrop"
          backdropStyle={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--alpha-rgba-0-0-0-0-68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 110,
            padding: '20px'
          }}
          dialogClassName="m3-card"
          dialogStyle={{
              width: '100%',
              maxWidth: '430px',
              backgroundColor: 'var(--md-sys-color-surface-container-high)',
              borderRadius: 'var(--shape-lg)',
              boxShadow: '0 18px 48px var(--alpha-rgba-0-0-0-0-3)',
              border: '1px solid var(--md-sys-color-outline-variant)',
              padding: '26px 28px'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '50%',
                    background: 'var(--md-sys-color-primary-container)',
                    color: 'var(--md-sys-color-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Lock size={18} />
                </div>
                <div>
                  <h3 id="password-modal-title" style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
                    {studio.passwordModal?.title || (locale === 'id' ? 'Ubah Password' : 'Change Password')}
                  </h3>
                  <p id="password-modal-description" style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                    {studio.passwordModal?.subtitle || (locale === 'id' ? 'Perbarui password akun Anda.' : 'Update your account password.')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="m3-btn-icon"
                onClick={() => setShowChangePasswordModal(false)}
                disabled={passwordChangeLoading}
                aria-label={appExtraText.close}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--md-sys-color-on-surface-variant)' }}
              >
                <X size={18} />
              </button>
            </div>

            {passwordChangeError && (
              <div
                role="alert"
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--shape-sm)',
                  backgroundColor: 'var(--md-sys-color-error-container)',
                  color: 'var(--md-sys-color-on-error-container)',
                  fontSize: '12.5px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <AlertCircle size={15} />
                <span>{passwordChangeError}</span>
              </div>
            )}

            {passwordChangeSuccess && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--shape-sm)',
                  backgroundColor: 'var(--inline-rgba-16-185-129-0-15)',
                  color: 'var(--status-emerald)',
                  fontSize: '12.5px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <CheckCircle2 size={15} />
                <span>{passwordChangeSuccess}</span>
              </div>
            )}

            <form onSubmit={handleChangePassword} aria-busy={passwordChangeLoading} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label htmlFor="current-password" style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                  {studio.passwordModal?.currentPasswordLabel || (locale === 'id' ? 'Password Saat Ini' : 'Current Password')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    aria-labelledby="byok-endpoint-label"
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="m3-input"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    placeholder={studio.passwordModal?.placeholderCurrent || '••••••••'}
                    required
                    style={{ width: '100%', paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    aria-label={showCurrentPassword ? (locale === 'id' ? 'Sembunyikan password saat ini' : 'Hide current password') : (locale === 'id' ? 'Tampilkan password saat ini' : 'Show current password')}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--md-sys-color-on-surface-variant)'
                    }}
                  >
                    {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="new-account-password" style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                  {studio.passwordModal?.newPasswordLabel || (locale === 'id' ? 'Password Baru' : 'New Password')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="new-account-password"
                    type={showNewPassword ? 'text' : 'password'}
                    className="m3-input"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder={studio.passwordModal?.placeholderNew || (locale === 'id' ? 'Minimal 8 karakter' : 'Minimum 8 characters')}
                    minLength={8}
                    required
                    style={{ width: '100%', paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    aria-label={showNewPassword ? (locale === 'id' ? 'Sembunyikan password baru' : 'Hide new password') : (locale === 'id' ? 'Tampilkan password baru' : 'Show new password')}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--md-sys-color-on-surface-variant)'
                    }}
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-account-password" style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                  {studio.passwordModal?.confirmPasswordLabel || (locale === 'id' ? 'Konfirmasi Password Baru' : 'Confirm New Password')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="confirm-account-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="m3-input"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={studio.passwordModal?.placeholderConfirm || (locale === 'id' ? 'Ketik ulang password baru' : 'Repeat new password')}
                    minLength={8}
                    required
                    style={{ width: '100%', paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? (locale === 'id' ? 'Sembunyikan konfirmasi password' : 'Hide password confirmation') : (locale === 'id' ? 'Tampilkan konfirmasi password' : 'Show password confirmation')}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--md-sys-color-on-surface-variant)'
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="m3-btn m3-btn-tonal m3-btn-sm"
                  onClick={() => setShowChangePasswordModal(false)}
                  disabled={passwordChangeLoading}
                  style={{ borderRadius: 'var(--shape-full)', minWidth: '80px' }}
                >
                  {studio.passwordModal?.cancelBtn || (locale === 'id' ? 'Batal' : 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="m3-btn m3-btn-filled m3-btn-sm"
                  disabled={passwordChangeLoading}
                  style={{ borderRadius: 'var(--shape-full)', minWidth: '120px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {passwordChangeLoading && <RefreshCw size={14} className="m3-spin" />}
                  <span>
                    {passwordChangeLoading
                      ? (studio.passwordModal?.updating || 'Updating...')
                      : (studio.passwordModal?.submitBtn || (locale === 'id' ? 'Simpan' : 'Update Password'))}
                  </span>
                </button>
              </div>
            </form>
        </AccessibleModal>
      )}

      {/* Top Up Credits Modal */}
      {showTopupModal && (
        <AccessibleModal
          onClose={() => {
            if (!isTopupLoading) setShowTopupModal(false);
          }}
          ariaLabelledBy="topup-modal-title"
          ariaDescribedBy="topup-modal-description"
          ariaBusy={isTopupLoading}
          closeOnBackdrop={!isTopupLoading}
          closeOnEscape={!isTopupLoading}
          initialFocusSelector="[aria-pressed='true']"
          backdropClassName="m3-modal-backdrop"
          backdropStyle={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--inline-rgba-0-0-0-0-78)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 110,
            padding: '20px'
          }}
          dialogClassName="m3-card topup-modal-card"
          dialogStyle={{
            width: '100%',
            maxWidth: '680px',
            maxHeight: '92dvh',
            overflowY: 'auto',
            backgroundColor: 'var(--md-sys-color-surface-container)',
            borderRadius: 'var(--shape-xl)',
            boxShadow: '0 18px 52px var(--alpha-rgba-0-0-0-0-32)',
            padding: '28px'
          }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="topup-modal-icon">
                    <PlusCircle size={20} />
                  </div>
                  <h3 id="topup-modal-title" style={{ margin: 0, fontSize: '19px', fontWeight: 800 }}>
                    {studio.topupModal?.title || (locale === 'id' ? 'Beli Kredit Tambahan (Top-up)' : 'Top Up Credits')}
                  </h3>
                </div>
                <p id="topup-modal-description" style={{ margin: '6px 0 0', fontSize: '12.5px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {studio.topupModal?.subtitle || (locale === 'id' ? 'Beli kredit ekstra secara instan tanpa memengaruhi tanggal siklus tagihan langganan Anda.' : 'Purchase add-on credits instantly without affecting your subscription renewal date.')}
                </p>
              </div>
              <button
                type="button"
                className="m3-btn-icon"
                onClick={() => setShowTopupModal(false)}
                disabled={isTopupLoading}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--md-sys-color-on-surface-variant)' }}
                aria-label={appExtraText.close}
              >
                <X size={20} />
              </button>
            </div>

            {/* Current Balance Pill */}
            {user && (
              <div
                className="topup-balance"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                  borderRadius: 'var(--shape-md)',
                  marginBottom: '20px',
                  fontSize: '12px'
                }}
              >
                <span style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {studio.topupModal?.currentBalanceLabel || (locale === 'id' ? 'Saldo Akun Saat Ini:' : 'Current Account Balance:')}
                </span>
                <span className="topup-balance-values" style={{ fontWeight: 700, display: 'inline-flex', gap: '14px' }}>
                  <span>{(user.credits || 0).toLocaleString()} {studio.topupModal?.vectorTab || 'Vector Credits'}</span>
                  <span>{(user.ai_credits || 0).toLocaleString()} {studio.topupModal?.aiTab || 'AI Credits'}</span>
                </span>
              </div>
            )}

            {/* Credit Type Segmented Selector */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '22px' }}>
              <div
                className="m3-segmented-button-container topup-credit-tabs"
                style={{
                  display: 'inline-flex',
                  padding: '3px',
                  borderRadius: 'var(--shape-full)',
                  gap: '4px'
                }}
              >
                <button
                  type="button"
                  className="m3-btn-text topup-credit-tab"
                  onClick={() => setTopupCreditType('vector')}
                  aria-pressed={topupCreditType === 'vector'}
                  style={{
                    borderRadius: 'var(--shape-full)',
                    padding: '7px 22px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {studio.topupModal?.vectorTab || (locale === 'id' ? 'Vector Credit' : 'Vector Credits')}
                </button>
                <button
                  type="button"
                  className="m3-btn-text topup-credit-tab"
                  onClick={() => setTopupCreditType('ai')}
                  aria-pressed={topupCreditType === 'ai'}
                  style={{
                    borderRadius: 'var(--shape-full)',
                    padding: '7px 22px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {studio.topupModal?.aiTab || (locale === 'id' ? 'AI Credit' : 'AI Credits')}
                </button>
              </div>
            </div>

            {/* Top-Up Packs Grid */}
            {topupCreditType === 'vector' ? (
              <div className="topup-pack-grid">
                {/* Pack 1: Mini Booster */}
                <div
                  className="topup-pack"
                  style={{
                    padding: '20px 16px',
                    borderRadius: 'var(--shape-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{studio.topupModal?.packs?.vec_200 || 'Mini Booster'}</div>
                    <div className="topup-pack-amount" style={{ fontSize: '24px', fontWeight: 800, margin: '10px 0 2px' }}>
                      +200
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '14px' }}>
                      {studio.topupModal?.vectorTab || 'Vector Credits'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 800 }}>{topupPrice('vec_200').text}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '2px' }}>
                      {studio.topupModal?.perCredit ? studio.topupModal.perCredit(topupPrice('vec_200').perCreditValue) : topupPrice('vec_200').perCreditValue && `(${topupPrice('vec_200').perCreditValue} / credit)`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="m3-btn m3-btn-tonal m3-btn-sm topup-pack-action"
                    disabled={isTopupLoading}
                    onClick={() => handleTopupCredits('vec_200', 200, 4.0, 'vector')}
                    style={{ marginTop: '18px', width: '100%', borderRadius: 'var(--shape-full)', fontWeight: 700 }}
                  >
                    {isTopupLoading ? (studio.topupModal?.connectingCheckout || (locale === 'id' ? 'Menghubungkan ke pembayaran...' : 'Connecting to checkout...')) : (studio.topupModal?.buyPack ? studio.topupModal.buyPack(topupPrice('vec_200').text) : (locale === 'id' ? `Beli (${topupPrice('vec_200').text})` : `Get Pack (${topupPrice('vec_200').text})`))}
                  </button>
                </div>

                {/* Pack 2: Growth Booster */}
                <div
                  className="topup-pack topup-pack-featured"
                  style={{
                    padding: '20px 16px',
                    borderRadius: 'var(--shape-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  <span
                    className="topup-pack-badge"
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 'var(--shape-full)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {studio.topupModal?.saveTag ? studio.topupModal.saveTag(10) : (locale === 'id' ? 'Hemat 10%' : 'Save 10%')}
                  </span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{studio.topupModal?.packs?.vec_500 || 'Growth Booster'}</div>
                    <div className="topup-pack-amount" style={{ fontSize: '24px', fontWeight: 800, margin: '10px 0 2px' }}>
                      +500
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '14px' }}>
                      {studio.topupModal?.vectorTab || 'Vector Credits'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 800 }}>{topupPrice('vec_500').text}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '2px' }}>
                      {studio.topupModal?.perCredit ? studio.topupModal.perCredit(topupPrice('vec_500').perCreditValue) : topupPrice('vec_500').perCreditValue && `(${topupPrice('vec_500').perCreditValue} / credit)`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="m3-btn m3-btn-filled m3-btn-sm topup-pack-action"
                    disabled={isTopupLoading}
                    onClick={() => handleTopupCredits('vec_500', 500, 9.0, 'vector')}
                    style={{ marginTop: '18px', width: '100%', borderRadius: 'var(--shape-full)', fontWeight: 700 }}
                  >
                    {isTopupLoading ? (studio.topupModal?.connectingCheckout || (locale === 'id' ? 'Menghubungkan ke pembayaran...' : 'Connecting to checkout...')) : (studio.topupModal?.buyPack ? studio.topupModal.buyPack(topupPrice('vec_500').text) : (locale === 'id' ? `Beli (${topupPrice('vec_500').text})` : `Get Pack (${topupPrice('vec_500').text})`))}
                  </button>
                </div>

                {/* Pack 3: Power Booster */}
                <div
                  className="topup-pack"
                  style={{
                    padding: '20px 16px',
                    borderRadius: 'var(--shape-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  <span
                    className="topup-pack-badge"
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 'var(--shape-full)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {studio.topupModal?.saveTag ? studio.topupModal.saveTag(20) : (locale === 'id' ? 'Hemat 20%' : 'Save 20%')}
                  </span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{studio.topupModal?.packs?.vec_1500 || 'Power Booster'}</div>
                    <div className="topup-pack-amount" style={{ fontSize: '24px', fontWeight: 800, margin: '10px 0 2px' }}>
                      +1,500
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '14px' }}>
                      {studio.topupModal?.vectorTab || 'Vector Credits'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 800 }}>{topupPrice('vec_1500').text}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '2px' }}>
                      {studio.topupModal?.perCredit ? studio.topupModal.perCredit(topupPrice('vec_1500').perCreditValue) : topupPrice('vec_1500').perCreditValue && `(${topupPrice('vec_1500').perCreditValue} / credit)`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="m3-btn m3-btn-tonal m3-btn-sm topup-pack-action"
                    disabled={isTopupLoading}
                    onClick={() => handleTopupCredits('vec_1500', 1500, 24.0, 'vector')}
                    style={{ marginTop: '18px', width: '100%', borderRadius: 'var(--shape-full)', fontWeight: 700 }}
                  >
                    {isTopupLoading ? (studio.topupModal?.connectingCheckout || (locale === 'id' ? 'Menghubungkan ke pembayaran...' : 'Connecting to checkout...')) : (studio.topupModal?.buyPack ? studio.topupModal.buyPack(topupPrice('vec_1500').text) : (locale === 'id' ? `Beli (${topupPrice('vec_1500').text})` : `Get Pack (${topupPrice('vec_1500').text})`))}
                  </button>
                </div>
              </div>
            ) : (
              <div className="topup-pack-grid">
                {/* AI Pack 1: Basic */}
                <div
                  className="topup-pack"
                  style={{
                    padding: '20px 16px',
                    borderRadius: 'var(--shape-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{studio.topupModal?.packs?.ai_500 || 'AI Mini Pack'}</div>
                    <div className="topup-pack-amount" style={{ fontSize: '24px', fontWeight: 800, margin: '10px 0 2px' }}>
                      +500
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '14px' }}>
                      {studio.topupModal?.aiTab || 'AI Credits'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 800 }}>{topupPrice('ai_500').text}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '2px' }}>
                      {studio.topupModal?.perCredit ? studio.topupModal.perCredit(topupPrice('ai_500').perCreditValue) : topupPrice('ai_500').perCreditValue && `(${topupPrice('ai_500').perCreditValue} / credit)`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="m3-btn m3-btn-tonal m3-btn-sm topup-pack-action"
                    disabled={isTopupLoading}
                    onClick={() => handleTopupCredits('ai_500', 500, 4.0, 'ai')}
                    style={{ marginTop: '18px', width: '100%', borderRadius: 'var(--shape-full)', fontWeight: 700 }}
                  >
                    {isTopupLoading ? (studio.topupModal?.connectingCheckout || (locale === 'id' ? 'Menghubungkan ke pembayaran...' : 'Connecting to checkout...')) : (studio.topupModal?.buyPack ? studio.topupModal.buyPack(topupPrice('ai_500').text) : (locale === 'id' ? `Beli (${topupPrice('ai_500').text})` : `Get Pack (${topupPrice('ai_500').text})`))}
                  </button>
                </div>

                {/* AI Pack 2: Ultra */}
                <div
                  className="topup-pack topup-pack-featured"
                  style={{
                    padding: '20px 16px',
                    borderRadius: 'var(--shape-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  <span
                    className="topup-pack-badge"
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 'var(--shape-full)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {studio.topupModal?.popularBadge || studio.pricingModal?.popularBadge || (locale === 'id' ? 'Paling Populer' : 'Popular')}
                  </span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{studio.topupModal?.packs?.ai_1500 || 'AI Ultra Pack'}</div>
                    <div className="topup-pack-amount" style={{ fontSize: '24px', fontWeight: 800, margin: '10px 0 2px' }}>
                      +1,500
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '14px' }}>
                      {studio.topupModal?.aiTab || 'AI Credits'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 800 }}>{topupPrice('ai_1500').text}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '2px' }}>
                      {studio.topupModal?.perCredit ? studio.topupModal.perCredit(topupPrice('ai_1500').perCreditValue) : topupPrice('ai_1500').perCreditValue && `(${topupPrice('ai_1500').perCreditValue} / credit)`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="m3-btn m3-btn-filled m3-btn-sm topup-pack-action"
                    disabled={isTopupLoading}
                    onClick={() => handleTopupCredits('ai_1500', 1500, 10.0, 'ai')}
                    style={{ marginTop: '18px', width: '100%', borderRadius: 'var(--shape-full)', fontWeight: 700 }}
                  >
                    {isTopupLoading ? (studio.topupModal?.connectingCheckout || (locale === 'id' ? 'Menghubungkan ke pembayaran...' : 'Connecting to checkout...')) : (studio.topupModal?.buyPack ? studio.topupModal.buyPack(topupPrice('ai_1500').text) : (locale === 'id' ? `Beli (${topupPrice('ai_1500').text})` : `Get Pack (${topupPrice('ai_1500').text})`))}
                  </button>
                </div>

                {/* AI Pack 3: Studio */}
                <div
                  className="topup-pack"
                  style={{
                    padding: '20px 16px',
                    borderRadius: 'var(--shape-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  <span
                    className="topup-pack-badge"
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 'var(--shape-full)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {studio.topupModal?.bestValueBadge || studio.pricingModal?.bestValueBadge || (locale === 'id' ? 'Nilai Terbaik' : 'Best Value')}
                  </span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{studio.topupModal?.packs?.ai_4000 || 'AI Studio Pack'}</div>
                    <div className="topup-pack-amount" style={{ fontSize: '24px', fontWeight: 800, margin: '10px 0 2px' }}>
                      +4,000
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '14px' }}>
                      {studio.topupModal?.aiTab || 'AI Credits'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 800 }}>{topupPrice('ai_4000').text}</div>
                    <div style={{ fontSize: '10.5px', color: 'var(--md-sys-color-on-surface-variant)', marginTop: '2px' }}>
                      {studio.topupModal?.perCredit ? studio.topupModal.perCredit(topupPrice('ai_4000').perCreditValue) : topupPrice('ai_4000').perCreditValue && `(${topupPrice('ai_4000').perCreditValue} / credit)`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="m3-btn m3-btn-tonal m3-btn-sm topup-pack-action"
                    disabled={isTopupLoading}
                    onClick={() => handleTopupCredits('ai_4000', 4000, 22.0, 'ai')}
                    style={{ marginTop: '18px', width: '100%', borderRadius: 'var(--shape-full)', fontWeight: 700 }}
                  >
                    {isTopupLoading ? (studio.topupModal?.connectingCheckout || (locale === 'id' ? 'Menghubungkan ke pembayaran...' : 'Connecting to checkout...')) : (studio.topupModal?.buyPack ? studio.topupModal.buyPack(topupPrice('ai_4000').text) : (locale === 'id' ? `Beli (${topupPrice('ai_4000').text})` : `Get Pack (${topupPrice('ai_4000').text})`))}
                  </button>
                </div>
              </div>
            )}
        </AccessibleModal>
      )}

      {showNotificationCenter && (
        <AccessibleModal
          onClose={() => setShowNotificationCenter(false)}
          ariaLabelledBy="notification-center-title"
          ariaDescribedBy="notification-center-description"
          backdropClassName=""
          backdropStyle={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            backgroundColor: 'var(--inline-rgba-2-6-23-0-78)',
            backdropFilter: 'blur(6px)'
          }}
          dialogClassName="m3-card notification-center-dialog"
          dialogStyle={{ width: 'min(680px, calc(100vw - 24px))', maxHeight: 'min(760px, calc(100dvh - 32px))', padding: 22, overflowY: 'auto', background: 'var(--md-sys-color-surface-container-high)', border: '1px solid var(--md-sys-color-outline-variant)', borderRadius: 'var(--shape-lg)', boxShadow: 'var(--elevation-4)' }}
        >
          <header className="notification-center-header">
            <div>
              <h2 id="notification-center-title">{notificationText.centerTitle}</h2>
              <p id="notification-center-description">{notificationText.centerDescription}</p>
            </div>
            <button className="m3-btn-icon notification-center-close" aria-label={notificationText.closeCenter} onClick={() => setShowNotificationCenter(false)}><X size={18} /></button>
          </header>
          <div className="notification-center-toolbar">
            <div className="notification-center-filters"><button className={`m3-btn m3-btn-xs ${!notificationCenterUnreadOnly ? 'm3-btn-filled' : 'm3-btn-tonal'}`} onClick={() => { setNotificationCenterUnreadOnly(false); fetchNotificationCenter(1, false); }}>{notificationText.filterAll}</button><button className={`m3-btn m3-btn-xs ${notificationCenterUnreadOnly ? 'm3-btn-filled' : 'm3-btn-tonal'}`} onClick={() => { setNotificationCenterUnreadOnly(true); fetchNotificationCenter(1, true); }}>{notificationText.filterUnread}</button></div>
            <div className="notification-center-actions"><button className="m3-btn m3-btn-tonal m3-btn-xs" aria-label={notificationText.markAllRead} title={notificationText.markAllRead} onClick={markAllNotificationsRead}><CheckCircle2 className="notification-center-action-icon" size={15} aria-hidden="true" /><span className="notification-center-action-label">{notificationText.markAllRead}</span></button><button className="m3-btn m3-btn-tonal m3-btn-xs" aria-label={notificationText.clearRead} title={notificationText.clearRead} onClick={clearReadNotificationItems}><Trash2 className="notification-center-action-icon" size={15} aria-hidden="true" /><span className="notification-center-action-label">{notificationText.clearRead}</span></button></div>
          </div>
          <div className={`notification-center-list ${notificationCenterItems.length === 0 ? 'is-empty' : ''}`}>{notificationCenterItems.length === 0 ? <div className="notification-center-empty"><Bell size={22} /><strong>{notificationText.emptyTitle}</strong><span>{notificationCenterUnreadOnly ? notificationText.allCaughtUp : notificationText.broadcastsAppearHere}</span></div> : notificationCenterItems.map(item => <div key={item.id} style={{ padding: 13, border: '1px solid var(--md-sys-color-outline-variant)', borderRadius: 9, background: item.read_at ? 'transparent' : 'var(--inline-rgba-56-189-248-09)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}><button type="button" onClick={() => { if (!item.read_at) markNotificationRead(item.id); if (item.cta_url) window.open(item.cta_url, '_blank', 'noopener,noreferrer'); }} style={{ flex: 1, textAlign: 'left', border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer' }}><b style={{ fontSize: 13 }}>{item.title}</b><div style={{ fontSize: 11.5, color: 'var(--md-sys-color-on-surface-variant)', marginTop: 5, lineHeight: 1.5 }}>{item.body_text}</div><div style={{ fontSize: 10, marginTop: 6, opacity: .65 }}>{String(item.created_at || '').slice(0,16).replace('T',' ')}</div></button><button className="m3-btn-icon" aria-label={notificationText.deleteNotification} onClick={() => deleteNotificationItem(item.id)}><Trash2 size={15} /></button></div></div>)}</div>
          {notificationCenterPages > 1 && <footer className="notification-center-pagination"><button className="m3-btn m3-btn-tonal m3-btn-xs" disabled={notificationCenterPage <= 1} onClick={() => fetchNotificationCenter(notificationCenterPage - 1, notificationCenterUnreadOnly)}>{notificationText.previous}</button><span>{notificationText.pageStatus(notificationCenterPage, notificationCenterPages)}</span><button className="m3-btn m3-btn-tonal m3-btn-xs" disabled={notificationCenterPage >= notificationCenterPages} onClick={() => fetchNotificationCenter(notificationCenterPage + 1, notificationCenterUnreadOnly)}>{notificationText.next}</button></footer>}
        </AccessibleModal>
      )}

      {/* Custom Application Dialog Modal (Replaces browser alert and confirm) */}
      {appModal.isOpen && (
        <AccessibleModal
          onClose={() => setAppModal(prev => ({ ...prev, isOpen: false }))}
          ariaLabelledBy="app-dialog-title"
          ariaDescribedBy="app-dialog-description"
          initialFocusSelector={appModal.type === 'confirm' && appModal.isDestructive ? '#app-dialog-cancel' : '#app-dialog-confirm'}
          backdropClassName="m3-modal-backdrop"
          backdropStyle={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'var(--alpha-rgba-0-0-0-0-68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
          dialogClassName="m3-card"
          dialogStyle={{
              width: '100%',
              maxWidth: '430px',
              padding: '22px 24px',
              backgroundColor: 'var(--md-sys-color-surface-container-high)',
              borderRadius: 'var(--shape-lg)',
              boxShadow: '0 18px 48px var(--alpha-rgba-0-0-0-0-3)',
              border: '1px solid var(--md-sys-color-outline-variant)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  backgroundColor: appModal.isDestructive ? 'var(--inline-rgba-239-68-68-0-15)' : 'var(--md-sys-color-primary-container)',
                  color: appModal.isDestructive ? 'var(--palette-ef4444)' : 'var(--md-sys-color-primary)'
                }}
              >
                {appModal.isDestructive ? (
                  <AlertTriangle size={18} />
                ) : appModal.type === 'confirm' ? (
                  <HelpCircle size={18} />
                ) : (
                  <Info size={18} />
                )}
              </div>
              <h3 id="app-dialog-title" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--md-sys-color-on-surface)' }}>
                {appModal.title || (appModal.type === 'confirm' ? flashText.confirmationTitle : flashText.notificationTitle)}
              </h3>
            </div>
            <p
              id="app-dialog-description"
              style={{
                margin: '0 0 22px',
                fontSize: '13.5px',
                lineHeight: 1.55,
                color: 'var(--md-sys-color-on-surface-variant)',
                whiteSpace: 'pre-wrap'
              }}
            >
              {appModal.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              {appModal.type === 'confirm' && (
                <button
                  id="app-dialog-cancel"
                  type="button"
                  className="m3-btn m3-btn-tonal m3-btn-sm"
                  onClick={() => setAppModal(prev => ({ ...prev, isOpen: false }))}
                  style={{ minWidth: '80px', borderRadius: 'var(--shape-full)' }}
                >
                  {appModal.cancelLabel || (locale === 'id' ? 'Batal' : 'Cancel')}
                </button>
              )}
              <button
                id="app-dialog-confirm"
                type="button"
                className={`m3-btn ${appModal.isDestructive ? 'm3-btn-destructive' : 'm3-btn-filled'} m3-btn-sm`}
                onClick={() => {
                  const cb = appModal.onConfirm;
                  setAppModal(prev => ({ ...prev, isOpen: false }));
                  if (cb) cb();
                }}
                style={{
                  minWidth: '85px',
                  borderRadius: 'var(--shape-full)',
                  backgroundColor: appModal.isDestructive ? 'var(--palette-dc2626)' : undefined,
                  color: appModal.isDestructive ? 'var(--color-white)' : undefined,
                  fontWeight: 650
                }}
              >
                {appModal.confirmLabel || (appModal.type === 'confirm' ? (locale === 'id' ? 'Lanjutkan' : 'Continue') : 'OK')}
              </button>
            </div>
        </AccessibleModal>
      )}
    </div>
  );
}
